/**
 * Buildings — prédios 3D com VARIEDADE de tipos (inspirado em docs/mockup2):
 *   office   — laje verde-limão + logo + rótulo (padrão)
 *   security — igual office, com escudo no telhado
 *   glass    — fachada de vidro azul/ciano
 *   solar    — telhado com placas solares
 *   cafe     — ícone de xícara + toldo listrado
 *   house    — casa residencial com telhado de duas águas
 *
 * Peças comuns (corpo/fachada/entrada) são compartilhadas; cada tipo só
 * troca o telhado e alguns detalhes.
 */

import * as THREE from "three";
import { FontLoader } from "three/addons/loaders/FontLoader.js";
import { TextGeometry } from "three/addons/geometries/TextGeometry.js";
import helvetikerBold from "three/examples/fonts/helvetiker_bold.typeface.json";
import { COLORS } from "../config.js";
import { finishBuilding } from "./buildingFinish.js";
import { TEX_TIER } from "../scene/qualityLevel.js";
import { BUILDINGS, makeRandom } from "./cityLayout.js";
import { t } from '../i18n.js';
import { registerLedMarquee } from './Decor.js';
import { buildCarParts } from "../entities/carModel.js";
import { makeFountain, makeBench, makeWhatsAppSign } from "./Decor.js"; // p/ a quadra-parque reusar a PRAÇA

const wallCache = new Map();
const DOLLAR_FONT = new FontLoader().parse(helvetikerBold);

/** "$" 3D em LETRA CAIXA: frente dourada acesa, laterais pretas (sem moldura). */
function makeDollarGlyph(size, depth) {
    const geo = new TextGeometry("$", {
        font: DOLLAR_FONT,
        size,
        depth,
        curveSegments: 5,
        bevelEnabled: false,
    });
    geo.center();
    const front = new THREE.MeshStandardMaterial({
        color: "#ffcf5a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.85,
        roughness: 0.5,
    });
    const sides = new THREE.MeshStandardMaterial({ color: "#0a0a0a", roughness: 0.6 });
    // grupo 0 = faces (frente/verso) douradas; grupo 1 = paredes da extrusão pretas
    const mesh = new THREE.Mesh(geo, [front, sides]);
    mesh.castShadow = true;
    return mesh;
}

const PALETTES = {
    amber: {
        on: "#ffb43c",
        off: "#13502a",
        litTop: "#ffd27a",
        glow: "#ffb43c",
        glowA: "#ffd98a",
        glowB: "#ffb150",
        frame: "#0c4422",
        frameLo: "#06280f",
        glassTop: "#1c6b3a",
        glassBot: "#0e4525",
        sill: "#10401f",
    },
    blue: {
        on: "#5fd0ff",
        off: "#123a4a",
        litTop: "#bdeeff",
        glow: "#5fd0ff",
        glowA: "#a7e9ff",
        glowB: "#54c6ff",
        frame: "#0c3a44",
        frameLo: "#06232b",
        glassTop: "#15566a",
        glassBot: "#0c3340",
        sill: "#0c3a44",
    },
    // banco: paredes no VERDE PADRÃO dos prédios (= 'amber') + janelas DOURADAS
    bank: {
        on: "#ffd27a",
        off: "#13502a",
        litTop: "#ffe6b0",
        glow: "#ffcf5a",
        glowA: "#ffe2a0",
        glowB: "#ffc24e",
        frame: "#0c4422",
        frameLo: "#06280f",
        glassTop: "#1c6b3a",
        glassBot: "#0e4525",
        sill: "#10401f",
    },
};

export function createBuildings() {
    const group = new THREE.Group();
    group.name = "buildings";
    const rand = makeRandom(424242);
    const shared = makeShared();

    for (const b of BUILDINGS) {
        const bg = new THREE.Group();
        bg.position.set(b.x, 0, b.z);
        if (b.rot90) {
            // MESMO modelo, girado 90°: o builder recebe as dims NATURAIS (pré-rotação,
            // w↔d) e o grupo gira p/ caber no slot mesclado na outra orientação. Ex.:
            // shopping em A3/A4 (slot 64×18) é construído 18×64 (arco/abóbada certos) e girado.
            bg.rotation.y = Math.PI / 2;
            (BUILDERS[b.type] || buildOffice)(bg, { ...b, w: b.d, d: b.w }, shared, rand);
        } else {
            (BUILDERS[b.type] || buildOffice)(bg, b, shared, rand);
        }
        group.add(finishBuilding(bg));
    }
    return group;
}

const BUILDERS = {
    office: buildOffice,
    security: (bg, b, s, r) => buildOffice(bg, b, s, r, { icon: "shield" }),
    glass: (bg, b, s, r) => buildOffice(bg, b, s, r, { palette: "blue" }),
    solar: buildSolar,
    cafe: buildCafe,
    house: buildHouse,
    tower: buildTower,
    modern: buildModern,
    houses: buildHousesCluster,
    school: buildSchool,
    shopping: buildShopping,
    fintech: buildFintech,
    academia: buildAcademia,
    boutique: buildBoutique2, // boutique MODERNA (salto-alto no teto); buildBoutique clássico segue no arquivo, sem uso
    boutiqueClassic: buildBoutique,
    florist: buildFlorist,
    pharmacy: buildPharmacy,
    hotel: buildHotel,
    resort: buildResort, // 2º hotel (piscina no teto/sacadas), distinto do clássico
    kinder: buildKinder, // 2ª escola (infantil lúdica: blocos ABC/lápis), distinta da institucional
    travel: buildTravel, // agência de viagens (globo+avião no teto, pôsteres de destino)
    research: buildResearch, // 2º laboratório (química/pesquisa: erlenmeyer no teto), distinto do clínico
    library: buildLibrary, // biblioteca (pilha de livros + coruja no teto, vitrines de estante)
    electronics: buildElectronics, // loja de eletrônicos (smartphone gigante no teto, vitrines de produto)
    spa: buildSpa, // spa/bem-estar (jardim zen no teto: cairn+bambu+lótus, vitrines de spa)
    pizza: buildPizza, // pizzaria (fatia gigante + forno a lenha no teto, toldo xadrez)
    coworking: buildCoworking, // hub de trabalho (lâmpada-ideia no teto, lounge/mesa de trabalho, vitrines-lounge)
    autos: buildAutos, // concessionária (carro no palco giratório do teto, showroom envidraçado)
    realestate: buildRealEstate, // imobiliária (chave de ouro no teto, maquete de imóveis)
    insurance: buildInsurance, // financeiro: SEGURADORA (guarda-chuva gigante no teto, friso de escudos)
    mint: buildMint, // financeiro: CASA DA MOEDA (moeda gigante no teto, medalhões + entrada monumental)
    stocks: buildStocks, // financeiro: bolsa de valores (barras + seta de alta no teto)
    vitamins: buildVitamins, // loja de vitaminas/suplementos (cápsula gigante no teto, vitrine de potes)
    cereals: buildCereals, // CPG: cereais/snacks/leite (caixa de cereal gigante no teto, vitrine de embalagens)
    park: buildPark, // QUADRA-PARQUE: caminho em cruz + árvores (sem prédio) — ocupa a célula inteira
    lab: buildLab,
    clinic: buildClinic,
    bullion: buildBullion, // financeiro: BANCO DE OURO E PRATA (bullion) — bimetal, placas de ouro/prata no teto
    ecommerce: buildEcommerce, // comércio: E-COMMERCE (logística + loja online) — caixa no palete + doca de carga
    beauty: buildBeauty, // CPG: ESTÉTICA/COSMÉTICOS (beleza) — rosa+rose-gold, batom gigante no teto
};

/** Lista de TIPOS de edifício disponíveis (chaves de BUILDERS). */
export const BUILDING_TYPES = Object.keys(BUILDERS);

/**
 * Monta UM edifício isolado na origem (fora do layout da cidade) — para
 * PRÉVIA/admin (ver models.html). `dims` = { w, h, d }; `seed` controla os
 * equipamentos de telhado randomizados (mude para variar o topo). Reusa os
 * mesmos builders/materiais da cidade, então a prévia é fiel ao jogo.
 */
export function buildBuilding(type, dims, seed = 424242) {
    const bg = new THREE.Group();
    const shared = makeShared();
    const rand = makeRandom(seed);
    const b = { x: 0, z: 0, w: dims.w, h: dims.h, d: dims.d, type };
    (BUILDERS[type] || buildOffice)(bg, b, shared, rand);
    return finishBuilding(bg);
}

/* --------------------------- materiais base --------------------------- */

function makeShared() {
    const dark = new THREE.MeshStandardMaterial({
        color: COLORS.buildingWallDark,
        roughness: 0.9,
    });
    return {
        darkMat: dark,
        fasciaMat: new THREE.MeshStandardMaterial({
            color: COLORS.buildingRoofEdge,
            roughness: 0.75,
        }),
        roofSideMat: new THREE.MeshStandardMaterial({
            color: COLORS.buildingRoof,
            roughness: 0.6,
        }),
        awningMat: new THREE.MeshStandardMaterial({
            color: COLORS.buildingWallDark,
            roughness: 0.8,
        }),
        cafeAwningMat: new THREE.MeshStandardMaterial({
            map: makeStripeTexture(),
            roughness: 0.7,
        }),
        doorMat: new THREE.MeshStandardMaterial({
            color: "#1c7a39",
            emissive: new THREE.Color("#ffce7a"),
            emissiveIntensity: 0.95,
            roughness: 0.35,
        }),
        columnMat: new THREE.MeshStandardMaterial({
            color: COLORS.buildingWallDark,
            roughness: 0.8,
        }),
        gableMat: new THREE.MeshStandardMaterial({
            color: "#14622f",
            roughness: 0.85,
            flatShading: true,
        }),
        roofTrimMat: new THREE.MeshStandardMaterial({
            color: "#0d3f1f",
            roughness: 0.8,
            flatShading: true,
        }),
        solarPanelMat: new THREE.MeshStandardMaterial({
            map: makeSolarTexture(),
            roughness: 0.25,
            metalness: 0.55,
            emissive: new THREE.Color("#0a2452"),
            emissiveIntensity: 0.18,
        }),
        bushMat: new THREE.MeshStandardMaterial({
            color: "#2f9e4a",
            roughness: 0.95,
            flatShading: true,
        }),
        trunkMat: new THREE.MeshStandardMaterial({ color: "#5b3a22", roughness: 1 }),
        benchMat: new THREE.MeshStandardMaterial({ color: "#7a5230", roughness: 0.85 }),
        roofEquipMat: new THREE.MeshStandardMaterial({
            color: "#8d9a92",
            roughness: 0.65,
            metalness: 0.35,
        }),
    };
}

/* ------------------------------- tipos -------------------------------- */

function buildOffice(bg, b, shared, rand, opts = {}) {
    addBody(bg, b, shared, rand, { palette: opts.palette, door: true });
    flatRoof(bg, b, shared, rand);
    addRoofDetails(bg, b, shared, rand);
    addEntrance(bg, b, shared, {});
}

/**
 * Equipamentos de cobertura RANDOMIZADOS (parapeito + um conjunto variado de
 * casa de acesso, ar-condicionados, caixa d'água, ventilação e dutos), para
 * que o teto não se repita entre os edifícios. Sem logo/rótulo.
 */
function addRoofDetails(bg, b, shared, rand) {
    const topY = b.h + 1.45; // topo da laje do telhado
    const equip = shared.roofEquipMat;
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;

    // Parapeito (mureta) na borda do telhado
    const ph = 0.8;
    const geoX = new THREE.BoxGeometry(b.w + 1.6, ph, 0.3);
    const geoZ = new THREE.BoxGeometry(0.3, ph, b.d + 1.6);
    for (const sz of [-1, 1]) {
        const m = new THREE.Mesh(geoX, shared.fasciaMat);
        m.position.set(0, topY + ph / 2, sz * (hd + 0.85));
        m.castShadow = true;
        bg.add(m);
    }
    for (const sx of [-1, 1]) {
        const m = new THREE.Mesh(geoZ, shared.fasciaMat);
        m.position.set(sx * (hw + 0.85), topY + ph / 2, 0);
        m.castShadow = true;
        bg.add(m);
    }

    // Pontos candidatos: grade jitterada sobre a laje (com margem do parapeito)
    const spots = [];
    const nx = 3;
    const nz = 3;
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < nz; j++) {
            spots.push({
                x: (-(nx - 1) / 2 + i) * (b.w / nx) + (rand() - 0.5) * 1.4,
                z: (-(nz - 1) / 2 + j) * (b.d / nz) + (rand() - 0.5) * 1.4,
            });
        }
    }
    for (let i = spots.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [spots[i], spots[j]] = [spots[j], spots[i]];
    }
    const place = () => (spots.length ? spots.pop() : null);

    // Casa de acesso (escada) — ~65% das vezes
    if (rand() > 0.35) {
        const s = place();
        if (s) {
            const hs = Math.min(b.w * 0.24, 5);
            const house = new THREE.Mesh(new THREE.BoxGeometry(hs, 2.2, Math.min(b.d * 0.26, 4.5)), equip);
            house.position.set(s.x, topY + 1.1, s.z);
            house.castShadow = true;
            bg.add(house);
        }
    }

    // Ar-condicionados: 1 a 3
    const nAC = 1 + Math.floor(rand() * 3);
    for (let k = 0; k < nAC; k++) {
        const s = place();
        if (!s) break;
        const ac = new THREE.Mesh(new THREE.BoxGeometry(2.0, 1.0, 1.5), equip);
        ac.position.set(s.x, topY + 0.5, s.z);
        ac.castShadow = true;
        bg.add(ac);
        const lid = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.16, 0.95), dark);
        lid.position.set(s.x, topY + 1.05, s.z);
        bg.add(lid);
    }

    // Caixa d'água — ~45%
    if (rand() > 0.55) {
        const s = place();
        if (s) {
            const tank = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.1, 2.0, 12), equip);
            tank.position.set(s.x, topY + 1.0, s.z);
            tank.castShadow = true;
            bg.add(tank);
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 0.7, 0.45, 12), dark);
            cap.position.set(s.x, topY + 2.12, s.z);
            bg.add(cap);
        }
    }

    // Caixa de ventilação — ~55%
    if (rand() > 0.45) {
        const s = place();
        if (s) {
            const vent = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.7, 1.6), equip);
            vent.position.set(s.x, topY + 0.35, s.z);
            vent.castShadow = true;
            bg.add(vent);
            const grill = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.5, 0.12), dark);
            grill.position.set(s.x, topY + 0.45, s.z + 0.82);
            bg.add(grill);
        }
    }

    // Dutos / canos: 0 a 2 (agrupados num ponto)
    const nPipe = Math.floor(rand() * 3);
    if (nPipe > 0) {
        const s = place();
        for (let k = 0; s && k < nPipe; k++) {
            const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 1.2, 10), equip);
            pipe.position.set(s.x + (k - (nPipe - 1) / 2) * 0.7, topY + 0.6, s.z);
            pipe.castShadow = true;
            bg.add(pipe);
        }
    }
}

function buildSolar(bg, b, shared, rand) {
    addBody(bg, b, shared, rand, { door: true });
    // laje base do telhado (verde)
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.4, 1.0, b.d + 1.4), shared.roofSideMat);
    base.position.y = b.h + 0.5;
    base.castShadow = true;
    bg.add(base);

    // Arranjo de placas solares cobrindo o telhado, sobre uma estrutura escura
    const dark = shared.darkMat;
    const aw = b.w * 0.88;
    const ad = b.d * 0.8;
    const frame = new THREE.Mesh(new THREE.BoxGeometry(aw + 0.7, 0.5, ad + 0.7), dark);
    frame.position.set(0, b.h + 1.2, 0);
    frame.castShadow = true;
    bg.add(frame);
    const panel = new THREE.Mesh(new THREE.BoxGeometry(aw, 0.3, ad), [dark, dark, shared.solarPanelMat, dark, dark, dark]);
    panel.position.set(0, b.h + 1.55, 0);
    panel.rotation.x = -0.1;
    panel.castShadow = true;
    bg.add(panel);

    addEntrance(bg, b, shared, {});
}

/**
 * CAFETERIA (benchmark = fintech): assinaturas em TODAS as escalas —
 *   • XÍCARA GIGANTE no telhado (pires + alça + café + VAPOR emissivo no bloom),
 *     legível do alto como o brasão do banco;
 *   • térreo de CAFETERIA: vitrines QUENTES flanqueando a porta com prateleiras
 *     de xícaras dentro, TOLDO listrado contínuo inclinado com LUZINHAS de varal;
 *   • TERRAÇO de calçada: mesinhas redondas com guarda-sol listrado + banquinhos;
 *   • menu de CAVALETE (chalkboard) e uma xícara-mascote sobre o toldo.
 */
function buildCafe(bg, b, shared, rand) {
    addBody(bg, b, shared, rand, { door: true });
    flatRoof(bg, b, shared, rand);

    const hw = b.w / 2;
    const hd = b.d / 2;
    const dark = shared.darkMat;
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f2e8d8", roughness: 0.55 });
    const coffee = new THREE.MeshStandardMaterial({ color: "#4a2f1d", roughness: 0.7 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.58,
        roughness: 0.4,
    });
    const bulbMat = new THREE.MeshStandardMaterial({
        color: "#ffe9b8",
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.95,
        roughness: 0.4,
    });
    const { w: doorW, h: doorH } = doorDims(b.w, b.h);

    // ---- VITRINES do térreo (flanqueiam a porta): vidro QUENTE + moldura + peitoril
    //      + prateleiras com XÍCARAS dentro (display, como a vitrine da academia) ----
    const vitW = Math.min((b.w - doorW) / 2 - 2.2, 5.4);
    const vitH = 3.1;
    const vitCX = doorW / 2 + 1.2 + vitW / 2;
    for (const sx of [-1, 1]) {
        const cx = sx * vitCX;
        const glass = new THREE.Mesh(new THREE.BoxGeometry(vitW, vitH, 0.3), warmGlass);
        glass.position.set(cx, 0.7 + vitH / 2, hd + 0.12);
        bg.add(glass);
        // moldura escura + peitoril de pedra
        for (const [bw, bh, ox, oy] of [
            [vitW + 0.5, 0.25, 0, vitH + 0.12],
            [vitW + 0.5, 0.25, 0, -0.12],
            [0.25, vitH + 0.4, -vitW / 2 - 0.12, vitH / 2],
            [0.25, vitH + 0.4, vitW / 2 + 0.12, vitH / 2],
        ]) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.36), dark);
            m.position.set(cx + ox, 0.7 + oy, hd + 0.14);
            bg.add(m);
        }
        const sill = new THREE.Mesh(new THREE.BoxGeometry(vitW + 0.8, 0.35, 0.7), trim);
        sill.position.set(cx, 0.55, hd + 0.3);
        sill.castShadow = true;
        bg.add(sill);
        // prateleira interna com FILEIRA de xicrinhas (display da vitrine)
        const shelf = new THREE.Mesh(new THREE.BoxGeometry(vitW - 0.7, 0.12, 0.5), trim);
        shelf.position.set(cx, 2.15, hd - 0.15);
        bg.add(shelf);
        const nCups = 4;
        for (let i = 0; i < nCups; i++) {
            const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.13, 0.26, 10), cream);
            cup.position.set(cx - (vitW - 1.3) / 2 + ((vitW - 1.3) * i) / (nCups - 1), 2.35, hd - 0.15);
            bg.add(cup);
        }
    }

    // ---- TOLDO listrado CONTÍNUO (cobre porta + vitrines), inclinado, com
    //      SUPORTES na parede e LUZINHAS de varal na borda ----
    const awnW = 2 * (vitCX + vitW / 2) + 1.2;
    const awnD = 3.0;
    const awnY = doorH + 0.7;
    const tilt = 0.24; // inclina p/ baixo na frente (charme de café)
    // listras REPETIDAS na largura (como a farmácia) — a textura compartilhada esticava
    // 1 tile no toldo inteiro; aqui clonamos com repeat ∝ largura (par de listras ~2.2u)
    const awnTex = makeStripeTexture();
    awnTex.wrapS = THREE.RepeatWrapping;
    awnTex.repeat.set(Math.max(4, Math.round(awnW / 2.2)), 1);
    const awnMat = new THREE.MeshStandardMaterial({ map: awnTex, roughness: 0.7 });
    const awning = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.22, awnD), [dark, dark, awnMat, awnMat, dark, dark]);
    awning.position.set(0, awnY, hd + awnD / 2 - 0.2);
    awning.rotation.x = tilt;
    awning.castShadow = true;
    bg.add(awning);
    // babado (borda pendurada) na frente do toldo
    const edgeZ = hd + (awnD - 0.2) * Math.cos(tilt);
    const valance = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.5, 0.16), [dark, dark, dark, dark, awnMat, awnMat]);
    valance.position.set(0, awnY - awnD * Math.sin(tilt) * 0.5 - 0.28, edgeZ);
    valance.castShadow = true;
    bg.add(valance);
    // suportes diagonais na parede (sem postes — calçada livre)
    for (const sx of [-1, 1]) {
        const brk = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 2.2), dark);
        brk.position.set(sx * (awnW / 2 - 0.6), awnY - 0.7, hd + 0.9);
        brk.rotation.x = -0.5;
        bg.add(brk);
    }
    // LUZINHAS (varal) penduradas na borda do toldo
    const nB = Math.max(7, Math.round(awnW / 1.6));
    for (let i = 0; i < nB; i++) {
        const t = i / (nB - 1) - 0.5;
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.11, 8, 6), bulbMat);
        // leve "catenária": as do meio penduram um tico mais baixo
        bulb.position.set(t * (awnW - 0.8), awnY - awnD * Math.sin(tilt) * 0.5 - 0.62 - Math.cos(t * Math.PI) * 0.1, edgeZ + 0.05);
        bg.add(bulb);
    }

    // ---- XÍCARA-MASCOTE sobre o toldo (acima da porta) ----
    const mascot = makeCoffeeCup(cream, coffee);
    mascot.scale.setScalar(0.6);
    mascot.position.set(0, awnY + 0.35, hd + 0.9);
    mascot.rotation.y = 0.5;
    bg.add(mascot);

    // ---- XÍCARA GIGANTE no TELHADO (o ícone aéreo, 2× — bem óbvia) + VAPOR ----
    const roofY = b.h + 1.45;
    const CUP_S = 2.56; // escala da xícara do teto (4.6 → 3.2 → 2.56; -20% a pedido)
    const cupG = makeCoffeeCup(cream, coffee);
    cupG.scale.setScalar(CUP_S);
    cupG.position.set(0, roofY, 0);
    cupG.rotation.y = -0.6; // alça visível da vista 3/4
    bg.add(cupG);
    // VAPOR: bolhas emissivas subindo em "S" (brilham no bloom — lê como fumaça),
    // nascendo logo acima da borda (~1.72×CUP_S) e na proporção da xícara
    const steamMat = new THREE.MeshStandardMaterial({
        color: "#f6efe2",
        emissive: new THREE.Color("#fff2d8"),
        emissiveIntensity: 0.55,
        roughness: 0.6,
    });
    const steam = [
        [1.0, 0.6, 1.0],
        [-0.9, 2.3, 0.9],
        [0.8, 3.9, 0.8],
        [-0.6, 5.4, 0.64],
        [0.3, 6.7, 0.48],
    ];
    const steamS = CUP_S / 4.6; // vapor PROPORCIONAL à xícara (o raio era fixo p/ a xícara 4.6)
    for (const [ox, oy, r] of steam) {
        const puff = new THREE.Mesh(new THREE.SphereGeometry(r * steamS, 10, 8), steamMat);
        puff.scale.y = 1.35; // alongadas p/ cima (sobem)
        puff.position.set(ox * steamS, roofY + 1.72 * CUP_S + 0.2 + oy * steamS, 0);
        bg.add(puff);
    }

    // ---- TORRÕES DE AÇÚCAR (branco + mascavo) nas laterais do teto: ao redor
    //      da xícara o telhado ficava vazio; reforça o tema café ----
    const sugarWhite = new THREE.MeshStandardMaterial({ color: "#f4f1e8", roughness: 0.75 });
    const sugarBrown = new THREE.MeshStandardMaterial({ color: "#a06a3a", roughness: 0.8 });
    const CS = 1.3;
    const CH = 1.02;
    const addSugarPile = (cx, cz, pat) => {
        for (const [gx, gz, layer, white] of pat) {
            const cube = new THREE.Mesh(new THREE.BoxGeometry(CS, CH, CS), white ? sugarWhite : sugarBrown);
            cube.position.set(cx + gx * (CS + 0.06), roofY + CH * (layer + 0.5), cz + gz * (CS + 0.06));
            cube.rotation.y = (gx - gz) * 0.12; // leve giro p/ não ficar robótico
            cube.castShadow = true;
            bg.add(cube);
        }
    };
    // pilha 2×2 + 1 no topo; pileW predominante branca, pileB predominante mascavo
    const pileW = [[-0.5, -0.5, 0, true], [0.5, -0.5, 0, false], [-0.5, 0.5, 0, true], [0.5, 0.5, 0, true], [0, 0, 1, true]];
    const pileB = [[-0.5, -0.5, 0, false], [0.5, -0.5, 0, true], [-0.5, 0.5, 0, false], [0.5, 0.5, 0, false], [0, 0, 1, false]];
    // ANEL de torrões contornando TODO o perímetro do teto (frente/trás também,
    // não só as laterais) — cantos + meios das arestas, equidistantes
    const R = 6.6;
    const M = 2.2;
    const ring = [
        [-R, -R], [-M, -R], [M, -R], [R, -R], // trás (esq→dir)
        [R, -M], [R, M], // direita (meios)
        [R, R], [M, R], [-M, R], [-R, R], // frente (dir→esq)
        [-R, M], [-R, -M] // esquerda (meios)
    ];
    ring.forEach(([x, z], i) => addSugarPile(x, z, i % 2 ? pileB : pileW));

    // ---- TERRAÇO: mesinhas com GUARDA-SOL listrado ----
    for (const sx of [-1, 1]) {
        const tx = sx * (doorW / 2 + 4.6);
        const tz = hd + 2.5;
        const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 1.0, 8), dark);
        leg.position.set(tx, 0.5, tz);
        bg.add(leg);
        const top = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.1, 14), cream);
        top.position.set(tx, 1.05, tz);
        top.castShadow = true;
        bg.add(top);
        // guarda-sol listrado (mastro + cone)
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.6, 8), dark);
        pole.position.set(tx, 2.1, tz);
        bg.add(pole);
        const shade = new THREE.Mesh(new THREE.ConeGeometry(1.7, 0.8, 10), shared.cafeAwningMat);
        shade.position.set(tx, 3.35, tz);
        shade.castShadow = true;
        bg.add(shade);
        // xicrinha na mesa (os banquinhos marrons foram retirados a pedido)
        const mcup = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.11, 0.2, 10), coffee);
        mcup.position.set(tx + 0.25, 1.2, tz + 0.15);
        bg.add(mcup);
    }

    // ---- MENU de cavalete (chalkboard) ao lado da porta ----
    {
        const mx = -(doorW / 2 + 2.1);
        const mz = hd + 1.5;
        for (const s of [-1, 1]) {
            const leaf = new THREE.Mesh(new THREE.BoxGeometry(1.15, 1.7, 0.08), shared.benchMat);
            leaf.position.set(mx, 0.82, mz + s * 0.26);
            leaf.rotation.x = s * 0.3;
            leaf.castShadow = true;
            bg.add(leaf);
        }
        const board = new THREE.Mesh(new THREE.BoxGeometry(0.92, 1.3, 0.05), dark);
        board.position.set(mx, 0.9, mz - 0.3);
        board.rotation.x = -0.3;
        bg.add(board);
        // "escritas" de giz (tracinhos claros)
        for (let i = 0; i < 3; i++) {
            const chalk = new THREE.Mesh(new THREE.BoxGeometry(0.62 - i * 0.12, 0.06, 0.03), cream);
            chalk.position.set(mx - i * 0.04, 1.28 - i * 0.3, mz - 0.42 + (1.28 - i * 0.3 - 0.9) * -0.31);
            chalk.rotation.x = -0.3;
            bg.add(chalk);
        }
    }

    // degrau da entrada (a porta é a da textura da fachada)
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.3), trim);
    step.position.set(0, 0.22, hd + 0.65);
    step.castShadow = true;
    bg.add(step);
}

/** Xícara de café 3D (corpo em lathe + pires + alça + café) — mascote/ícone do café. */
function makeCoffeeCup(cream, coffee) {
    const g = new THREE.Group();
    // pires
    const saucer = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.15, 0.22, 20), cream);
    saucer.position.y = 0.11;
    saucer.castShadow = true;
    g.add(saucer);
    // corpo da xícara (lathe: base estreita → boca larga, com LÁBIO e PAREDE INTERNA —
    // o perfil desce por dentro depois do lábio, senão a borda fica "vazada" vista de
    // cima, pois o lathe não tem espessura e a face interna seria culada)
    const pts = [
        new THREE.Vector2(0.62, 0.0),
        new THREE.Vector2(0.78, 0.16),
        new THREE.Vector2(0.94, 0.7),
        new THREE.Vector2(1.02, 1.28),
        new THREE.Vector2(1.06, 1.5),
        new THREE.Vector2(0.98, 1.53), // lábio (topo da borda)
        new THREE.Vector2(0.93, 1.26), // parede INTERNA descendo até sob o café
    ];
    const body = new THREE.Mesh(new THREE.LatheGeometry(pts, 20), cream);
    body.position.y = 0.2;
    body.castShadow = true;
    g.add(body);
    // café: disco escuro SELANDO contra a parede interna (r maior que o lábio interno
    // 0.93 → sem anel vazado entre o café e a borda)
    const brew = new THREE.Mesh(new THREE.CylinderGeometry(0.97, 0.97, 0.12, 18), coffee);
    brew.position.y = 1.6;
    g.add(brew);
    // alça na lateral +X: arco de 270° com a abertura voltada p/ o corpo, e as duas
    // pontas ENTERRADAS na parede (o tubo do torus é aberto nas extremidades — rente
    // à superfície o furo do cilindro aparecia; enterrado, some sob o café/parede)
    const handle = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.13, 8, 18, Math.PI * 1.5), cream);
    handle.position.set(1.06, 1.0, 0);
    handle.rotation.z = -Math.PI * 0.75; // centra o vão do arco apontando p/ -X (a xícara)
    handle.castShadow = true;
    g.add(handle);
    // faixa decorativa (verde do jogo) no corpo
    const band = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 0.97, 0.22, 20), new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.6 }));
    band.position.y = 1.05;
    g.add(band);
    return g;
}

function buildHouse(bg, b, shared, rand) {
    const w = Math.min(b.w * 0.72, 14);
    const d = Math.min(b.d * 0.72, 14);
    bg.add(makeHouse(shared, rand, w, d, 7));
}

/** Torre/Matriz: prédio alto + ROOFTOP (guarda-corpo, mesinha e banquinhos). */
function buildTower(bg, b, shared, rand) {
    addBody(bg, b, shared, rand, { door: true });
    flatRoof(bg, b, shared, rand);
    addRooftopTerrace(bg, b, shared);
    addEntrance(bg, b, shared, {});
}

/** Moderno: fachada de vidro azul + brise de placas solares inclinado. */
function buildModern(bg, b, shared, rand) {
    addBody(bg, b, shared, rand, { palette: "blue", door: true });
    flatRoof(bg, b, shared, rand);

    const dark = shared.darkMat;
    const tilt = 0.6; // inclina a frente para BAIXO (toldo apontando para baixo)
    const depth = 3.8;
    const cy = b.h * 0.6;
    const cz = b.d / 2 + 1.5;
    // toldo listrado verde-e-branco (em vez do brise solar azul)
    const stripeTex = makeStripeTexture();
    stripeTex.wrapS = THREE.RepeatWrapping;
    stripeTex.repeat.set(Math.max(2, Math.round(b.w * 0.18)), 1);
    const awningMat = new THREE.MeshStandardMaterial({
        map: stripeTex,
        roughness: 0.7,
    });
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(b.w * 0.9, 0.3, depth), [dark, dark, awningMat, awningMat, dark, dark]);
    canopy.position.set(0, cy, cz);
    canopy.rotation.x = tilt;
    canopy.castShadow = true;
    bg.add(canopy);

    // postes sustentando a borda dianteira (a mais baixa)
    const half = depth / 2;
    const frontY = cy - half * Math.sin(tilt);
    const frontZ = cz + half * Math.cos(tilt);
    for (const sx of [-1, 1]) {
        const sup = new THREE.Mesh(new THREE.BoxGeometry(0.3, frontY, 0.3), dark);
        sup.position.set(sx * b.w * 0.4, frontY / 2, frontZ - 0.15);
        sup.castShadow = true;
        bg.add(sup);
    }

    addEntrance(bg, b, shared, {});
}

/** Condomínio: 3 casas + playground + arbustos no lote. */
function buildHousesCluster(bg, b, shared, rand) {
    const spots = [
        { x: -6.5, z: -5, rot: 0.06, w: 7, d: 6 },
        { x: 6.5, z: -5.5, rot: -0.09, w: 7.5, d: 6 },
        { x: -6.5, z: 6, rot: 0.12, w: 7, d: 6 },
    ];
    for (const s of spots) {
        const house = makeHouse(shared, rand, s.w, s.d, 6.5);
        house.position.set(s.x, 0, s.z);
        house.rotation.y = s.rot;
        bg.add(house);
    }
    const pg = makePlayground(shared);
    pg.position.set(6.5, 0, 6);
    bg.add(pg);
    for (const [x, z, r] of [
        [0, -9, 1.2],
        [11, 0, 1.0],
        [-11, 0, 1.0],
        [1, 11, 1.1],
    ]) {
        const bush = makeBush(shared, r);
        bush.position.set(x, 0, z);
        bg.add(bush);
    }
}

/** Janela de sala de aula (vidro quente + moldura/cruz clara). Voltada p/ +Z. */
function makeClassWindow(w, h, glassMat, frameMat) {
    const g = new THREE.Group();
    const glass = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.2), glassMat);
    g.add(glass);
    const ft = 0.3;
    const bars = [
        [w + 2 * ft, ft, 0, h / 2 + ft / 2],
        [w + 2 * ft, ft, 0, -h / 2 - ft / 2],
        [ft, h, -w / 2 - ft / 2, 0],
        [ft, h, w / 2 + ft / 2, 0],
    ];
    for (const [bw, bh, x, y] of bars) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.3), frameMat);
        m.position.set(x, y, 0.05);
        g.add(m);
    }
    const vm = new THREE.Mesh(new THREE.BoxGeometry(0.12, h, 0.28), frameMat);
    vm.position.z = 0.06;
    g.add(vm);
    const hm = new THREE.Mesh(new THREE.BoxGeometry(w, 0.12, 0.28), frameMat);
    hm.position.z = 0.06;
    g.add(hm);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Maçã (símbolo de escola): fruta + cabinho + folha. */
function makeApple() {
    const g = new THREE.Group();
    const apple = new THREE.Mesh(new THREE.SphereGeometry(0.5, 14, 12), new THREE.MeshStandardMaterial({ color: "#e0392b", roughness: 0.5 }));
    apple.scale.set(1, 0.92, 1);
    apple.castShadow = true;
    g.add(apple);
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.35, 6), new THREE.MeshStandardMaterial({ color: "#5b3a22", roughness: 0.9 }));
    stem.position.y = 0.5;
    g.add(stem);
    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), new THREE.MeshStandardMaterial({ color: "#3ddc84", roughness: 0.8 }));
    leaf.scale.set(1.7, 0.3, 0.8);
    leaf.position.set(0.22, 0.55, 0);
    leaf.rotation.z = 0.5;
    g.add(leaf);
    return g;
}

/**
 * Escola (Acquisition Town · C3): escola institucional de verdade — corpo de
 * SALAS DE AULA (fileiras de janelas), PÓRTICO de colunas com FRONTÃO + RELÓGIO
 * + maçã, CUPOLA com SINO no telhado, MASTRO com bandeira e CESTA de basquete no
 * pátio, placa "ESCOLA".
 */
function buildSchool(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });

    // CORPO sólido (sem janelas antigas) + base + cornija + laje de teto
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 1.0, b.d + 0.9), trim);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    // TELHADO de 4 águas (hip) em VERDE-ESCURO p/ contraste (como os outros tetos);
    // topo plano p/ a cupola, flat-shading mostra as quedas. + chaminé (mais detalhe)
    const darkRoof = new THREE.MeshStandardMaterial({
        color: COLORS.buildingRoofEdge,
        roughness: 0.85,
        flatShading: true,
    });
    const hipH = 2.6;
    const hip = new THREE.Mesh(new THREE.CylinderGeometry(4.6, (b.w / 2 + 1) * Math.SQRT2, hipH, 4), darkRoof);
    hip.rotation.y = Math.PI / 4;
    hip.position.y = b.h + hipH / 2;
    hip.castShadow = true;
    hip.receiveShadow = true;
    bg.add(hip);
    const roofY = b.h + hipH; // topo plano do hip = base da cupola
    const chimney = new THREE.Mesh(new THREE.BoxGeometry(1.3, 2.6, 1.3), wallMat);
    chimney.position.set(hw - 3.5, b.h + 1.3, -hd + 3.5);
    chimney.castShadow = true;
    bg.add(chimney);
    const chimCap = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.45, 1.6), darkRoof);
    chimCap.position.set(hw - 3.5, b.h + 2.8, -hd + 3.5);
    chimCap.castShadow = true;
    bg.add(chimCap);

    // JANELAS de sala de aula: fileiras em 2 andares. Na FRENTE, só 2 bem AFASTADAS
    // do pórtico central (antes ficavam coladas/atrás dele).
    const faces = [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ];
    for (const f of faces) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const us = f.nz === 1 ? [-7.0, 7.0] : [-4.7, 0, 4.7];
        for (const wy of [3.7, 8.3]) {
            for (const u of us) {
                const win = makeClassWindow(2.2, 3.0, warmGlass, trim);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // CUPOLA com SINO no centro do telhado — GRANDE, p/ ser evidente no mapa todo
    const cuW = 4.8;
    const cuBase = new THREE.Mesh(new THREE.BoxGeometry(cuW + 1.0, 1.1, cuW + 1.0), trim);
    cuBase.position.set(0, roofY + 0.55, 0);
    cuBase.castShadow = true;
    bg.add(cuBase);
    const belfryY = roofY + 1.1;
    const belfryH = 3.6;
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.5, belfryH, 0.5), trim);
            post.position.set(sx * (cuW / 2 - 0.3), belfryY + belfryH / 2, sz * (cuW / 2 - 0.3));
            post.castShadow = true;
            bg.add(post);
        }
    }
    const cuLintel = new THREE.Mesh(new THREE.BoxGeometry(cuW, 0.55, cuW), trim);
    cuLintel.position.set(0, belfryY + belfryH, 0);
    cuLintel.castShadow = true;
    bg.add(cuLintel);
    const bell = makeBell(shared);
    bell.scale.setScalar(1.45);
    bell.position.set(0, belfryY + belfryH - 0.6 - 1.6, 0);
    bg.add(bell);
    const cuCap = new THREE.Mesh(new THREE.ConeGeometry(cuW * 0.8, 3.4, 4), darkRoof);
    cuCap.rotation.y = Math.PI / 4;
    cuCap.position.set(0, belfryY + belfryH + 1.7, 0);
    cuCap.castShadow = true;
    bg.add(cuCap);
    const cuBall = new THREE.Mesh(new THREE.SphereGeometry(0.35, 10, 8), shared.doorMat);
    cuBall.position.set(0, belfryY + belfryH + 3.8, 0);
    bg.add(cuBall);

    // ENTRADA (frente +Z): degraus + PÓRTICO de colunas + entablamento + FRONTÃO
    // com RELÓGIO, porta dupla, placa "ESCOLA" e maçã no topo
    const { w: doorW, h: doorH } = doorDims(b.w, b.h);
    const portW = b.w * 0.5;
    const colH = b.h * 0.78; // pórtico bem ALTO — quase toda a altura da fachada
    const entH = colH - 0.6; // entrada envidraçada de ~2 pavimentos
    const zp = hd + 1.6; // linha das colunas (pórtico projetado)
    // PISO do alpendre — continua o chão dos degraus ATÉ o prédio (preenche o vão)
    const porchFloor = new THREE.Mesh(new THREE.BoxGeometry(portW + 2.6, 0.5, 2.85), trim);
    porchFloor.position.set(0, 0.25, hd + 1.125);
    porchFloor.receiveShadow = true;
    porchFloor.castShadow = true;
    bg.add(porchFloor);
    // degraus descendo do alpendre (topo 0.5) até o chão
    for (let s = 0; s < 2; s++) {
        const top = 0.36 - s * 0.18;
        const step = new THREE.Mesh(new THREE.BoxGeometry(portW + 2.6 - s * 1.0, top, 1.1), trim);
        step.position.set(0, top / 2, zp + 0.95 + s * 0.85);
        step.castShadow = true;
        bg.add(step);
    }
    // colunas (4)
    for (let i = 0; i < 4; i++) {
        const t = (i / 3 - 0.5) * portW;
        const c = makeBankColumn(colH, trim);
        c.position.set(t, 0.5, zp);
        bg.add(c);
    }
    // entablamento + TETO do alpendre (liga/"cola" o pórtico à parede) + frontão
    const entab = new THREE.Mesh(new THREE.BoxGeometry(portW + 1.4, 1.0, 1.4), trim);
    entab.position.set(0, colH + 0.5, zp);
    entab.castShadow = true;
    bg.add(entab);
    const porchRoof = new THREE.Mesh(new THREE.BoxGeometry(portW + 1.4, 1.0, 1.3), trim);
    porchRoof.position.set(0, colH + 0.5, hd + 0.35);
    porchRoof.castShadow = true;
    bg.add(porchRoof);
    const ped = makePediment(portW + 1.4, 2.0, 1.4, trim);
    ped.position.set(0, colH + 1.0, zp);
    bg.add(ped);
    const clockMat = new THREE.MeshStandardMaterial({
        map: makeClockTexture(),
        emissive: new THREE.Color("#ffe6b0"),
        emissiveIntensity: 0.4,
        roughness: 0.7,
    });
    const clock = new THREE.Mesh(new THREE.CircleGeometry(0.95, 24), clockMat);
    clock.position.set(0, colH + 1.5, zp + 0.75);
    bg.add(clock);
    const apple = makeApple();
    apple.scale.setScalar(1.1);
    apple.position.set(0, colH + 3.3, zp);
    bg.add(apple);
    // ENTRADA ENVIDRAÇADA ALTA (~2 pavimentos): porta embaixo + transons acima (sem placa)
    const doors = new THREE.Mesh(new THREE.BoxGeometry(doorW, entH, 0.4), warmGlass);
    doors.position.set(0, entH / 2 + 0.4, hd + 0.15);
    bg.add(doors);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.18, entH, 0.5), trim);
    dmull.position.set(0, entH / 2 + 0.4, hd + 0.25);
    bg.add(dmull);
    for (const my of [3.0, 5.2, 7.4]) {
        const hmull = new THREE.Mesh(new THREE.BoxGeometry(doorW, 0.16, 0.5), trim);
        hmull.position.set(0, my, hd + 0.25);
        bg.add(hmull);
    }
}

/** Sino de bronze (perfil revolvido em lathe) com alça e badalo. */
function makeBell(shared) {
    const g = new THREE.Group();
    const bronze = new THREE.MeshStandardMaterial({
        color: "#b5793a",
        roughness: 0.5,
        metalness: 0.55,
        emissive: new THREE.Color("#3a1f08"),
        emissiveIntensity: 0.15,
    });
    const pts = [
        new THREE.Vector2(0.5, 0.0),
        new THREE.Vector2(0.62, 0.03),
        new THREE.Vector2(0.6, 0.12),
        new THREE.Vector2(0.42, 0.36),
        new THREE.Vector2(0.3, 0.58),
        new THREE.Vector2(0.22, 0.82),
        new THREE.Vector2(0.18, 1.0),
        new THREE.Vector2(0.07, 1.06),
    ];
    const body = new THREE.Mesh(new THREE.LatheGeometry(pts, 18), bronze);
    body.castShadow = true;
    g.add(body);
    const loop = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.04, 8, 14), bronze);
    loop.position.y = 1.12;
    loop.rotation.x = Math.PI / 2;
    g.add(loop);
    const clapper = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), shared.darkMat);
    clapper.position.y = 0.12;
    g.add(clapper);
    return g;
}

/** Halter (dumbbell) metálico — emblema da Academia. Barra ao longo de X. */
function makeDumbbell(shared) {
    const g = new THREE.Group();
    const metal = new THREE.MeshStandardMaterial({
        color: "#cfd6db",
        roughness: 0.4,
        metalness: 0.6,
    });
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 3.4, 10), metal);
    bar.rotation.z = Math.PI / 2;
    bar.castShadow = true;
    g.add(bar);
    for (const sx of [-1, 1]) {
        for (const dx of [1.25, 1.65]) {
            const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.32, 16), shared.darkMat);
            plate.rotation.z = Math.PI / 2;
            plate.position.x = sx * dx;
            plate.castShadow = true;
            g.add(plate);
        }
    }
    return g;
}

/**
 * Emblema de COFRE (vault) voltado para +Z: porta-disco de aço + anel, parafusos,
 * volante (aro + raios) e um "$" dourado no centro. (Fintech — substitui a placa.)
 */
function makeVaultEmblem(shared) {
    const g = new THREE.Group();
    // cofre no VERDE PADRÃO (= pórticos); mecanismo no verde-escuro padrão p/ definir
    const steel = new THREE.MeshStandardMaterial({
        color: COLORS.buildingRoof,
        roughness: 0.7,
        metalness: 0.12,
    });
    const steelDark = new THREE.MeshStandardMaterial({
        color: COLORS.buildingWallDark,
        roughness: 0.55,
        metalness: 0.3,
    });
    // Porta do cofre: segmentos reduzidos, desvios de silhueta medidos entre 0,14
    // e 0,23 px. O anel externo mantém 20 lados num diâmetro de 26,9 px.
    const door = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 0.35, 16), steel);
    door.rotation.x = Math.PI / 2;
    door.castShadow = true;
    g.add(door);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.0, 0.12, 6, 20), steelDark);
    ring.position.z = 0.18;
    g.add(ring);
    for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.2, 8), steelDark);
        bolt.rotation.x = Math.PI / 2;
        bolt.position.set(Math.cos(a) * 0.78, Math.sin(a) * 0.78, 0.26);
        g.add(bolt);
    }
    const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.07, 5, 16), steelDark);
    wheel.position.z = 0.3;
    g.add(wheel);
    for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.07, 1.04, 0.07), steelDark);
        spoke.position.z = 0.3;
        spoke.rotation.z = a;
        g.add(spoke);
    }
    // "$" 3D em letra caixa no centro (frente dourada, laterais pretas)
    const dollar = makeDollarGlyph(0.95, 0.18);
    dollar.position.z = 0.42;
    g.add(dollar);
    return g;
}

/** Totem/banner de chão de shopping: base + moldura escura + banner colorido aceso. */
function makeMallTotem(color) {
    const g = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({ color: "#2a2e33", roughness: 0.6, metalness: 0.3 });
    // base ALTA: sobe do chão até a moldura (fundo em y=0.8), conectando base e estrutura superior
    const base = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.0, 0.8), dark);
    base.position.y = 0.5;
    g.add(base);
    // moldura escura
    const frame = new THREE.Mesh(new THREE.BoxGeometry(1.3, 3.0, 0.3), dark);
    frame.position.y = 2.3;
    g.add(frame);
    // banner (lightbox) colorido aceso — frente e verso
    const banner = new THREE.Mesh(
        new THREE.BoxGeometry(1.05, 2.7, 0.36),
        new THREE.MeshStandardMaterial({
            color,
            emissive: new THREE.Color(color),
            emissiveIntensity: 0.35,
            roughness: 0.5,
        }),
    );
    banner.position.y = 2.3;
    g.add(banner);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Shopping (Acquisition Town · A5/B5): MALL. ABÓBADA DE VIDRO (skylight) ao longo
 * do comprimento (assinatura de shopping), VITRINES com toldos coloridos nas
 * laterais, ENTRADA grande com marquise + CARRINHO de compras e SACOLAS, e
 * letreiros "SHOPPING".
 */
function buildShopping(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;

    // materiais: paredes VERDE sólido (sem janelas antigas), estrutura verde, e
    // VIDRO AMARELADO ACESO (igual às janelas dos outros prédios — luz interna)
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const frameGreen = new THREE.MeshStandardMaterial({
        color: COLORS.buildingRoofEdge,
        roughness: 0.65,
        metalness: 0.2,
    });
    const warmGlass = new THREE.MeshStandardMaterial({
        // mesmo DOURADO das janelas acesas dos outros prédios (COLORS.windowOn), não creme
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.62,
        roughness: 0.4,
    });
    const storeGlass = new THREE.MeshStandardMaterial({
        color: "#243029",
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.55,
        roughness: 0.3,
    });
    // moldura verde-escura (delimita os elementos coloridos — painéis e faixas)
    const darkFrameMat = new THREE.MeshStandardMaterial({ color: "#0e3a1c", roughness: 0.8 });

    // CORPO sólido (paredes verdes, sem as janelas antigas) + base
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    base.castShadow = true;
    bg.add(base);

    // Laje de teto (em volta da abóbada)
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.2, 0.9, b.d + 1.2), new THREE.MeshStandardMaterial({ color: COLORS.buildingWallDark, roughness: 0.9 }));
    roofSlab.position.y = b.h + 0.45;
    roofSlab.receiveShadow = true;
    roofSlab.castShadow = true;
    bg.add(roofSlab);

    // ABÓBADA: VIDRO azul-esverdeado + ESTRUTURA VERDE (arcos, cumeeira, longarinas)
    const R = b.w * 0.46;
    const Lv = b.d; // abóbada ocupa TODO o comprimento do teto (antes b.d−7 deixava teto plano nas pontas)
    const vy = b.h + 0.9;
    const arch = new THREE.Shape();
    arch.moveTo(-R, 0);
    arch.absarc(0, 0, R, Math.PI, 0, true);
    arch.lineTo(-R, 0);
    const vaultGeo = new THREE.ExtrudeGeometry(arch, {
        depth: Lv,
        bevelEnabled: false,
        curveSegments: 18,
    });
    vaultGeo.translate(0, 0, -Lv / 2);
    const vault = new THREE.Mesh(vaultGeo, warmGlass);
    vault.position.set(0, vy, 0);
    vault.castShadow = true;
    bg.add(vault);
    // ARCOS (ribs) com HIERARQUIA: principais grossos a cada 3, intermediários finos
    const nribs = Math.max(10, Math.round(Lv / 4));
    for (let i = 0; i <= nribs; i++) {
        const main = i % 3 === 0;
        const rib = new THREE.Mesh(new THREE.TorusGeometry(R + 0.05, main ? 0.17 : 0.09, 6, 24, Math.PI), frameGreen);
        rib.position.set(0, vy, -Lv / 2 + (Lv * i) / nribs);
        rib.castShadow = true;
        bg.add(rib);
    }
    // LONGARINAS finas (longitudinais) — completam a grade de vidraças; cumeeira grossa
    for (let k = 0; k <= 10; k++) {
        const a = (k / 10) * Math.PI;
        const thick = k === 5 ? 0.2 : 0.11;
        const bar = new THREE.Mesh(new THREE.BoxGeometry(thick, thick, Lv), frameGreen);
        bar.position.set(R * Math.cos(a), vy + R * Math.sin(a), 0);
        bg.add(bar);
    }
    // GABLES nas 2 pontas: moldura grossa + LEQUE radial + hub central
    const fanAngles = [Math.PI / 6, Math.PI / 3, Math.PI / 2, (2 * Math.PI) / 3, (5 * Math.PI) / 6];
    for (const ez of [-Lv / 2, Lv / 2]) {
        const gable = new THREE.Mesh(new THREE.TorusGeometry(R + 0.05, 0.3, 8, 28, Math.PI), frameGreen);
        gable.position.set(0, vy, ez);
        gable.castShadow = true;
        bg.add(gable);
        const gbase = new THREE.Mesh(new THREE.BoxGeometry(2 * R + 0.5, 0.42, 0.42), frameGreen);
        gbase.position.set(0, vy, ez);
        bg.add(gbase);
        for (const a of fanAngles) {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(R, 0.13, 0.22), frameGreen);
            bar.position.set((R * Math.cos(a)) / 2, vy + (R * Math.sin(a)) / 2, ez);
            bar.rotation.z = a;
            bg.add(bar);
        }
        const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.45, 14), frameGreen);
        hub.rotation.x = Math.PI / 2;
        hub.position.set(0, vy + 0.2, ez);
        hub.castShadow = true;
        bg.add(hub);
    }
    // CRISTA (ridge) elevada + finiais decorativos nas pontas
    const crest = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, Lv + 0.8), frameGreen);
    crest.position.set(0, vy + R + 0.28, 0);
    crest.castShadow = true;
    bg.add(crest);
    for (const ez of [-Lv / 2 - 0.4, Lv / 2 + 0.4]) {
        const ball = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 10), frameGreen);
        ball.position.set(0, vy + R + 0.55, ez);
        ball.castShadow = true;
        bg.add(ball);
        const spike = new THREE.Mesh(new THREE.ConeGeometry(0.22, 1.0, 10), frameGreen);
        spike.position.set(0, vy + R + 1.3, ez);
        spike.castShadow = true;
        bg.add(spike);
    }

    // VITRINES no térreo (faixa de vidro acesa) ao longo das LATERAIS LONGAS
    const storeH = 4.0;
    const storeCY = 0.7 + storeH / 2;
    const storeLen = b.d - 4;
    for (const sx of [-1, 1]) {
        const band = new THREE.Mesh(new THREE.BoxGeometry(0.3, storeH, storeLen), storeGlass);
        band.position.set(sx * (hw + 0.06), storeCY, 0);
        bg.add(band);
        const nshops = Math.max(4, Math.round(storeLen / 4.5));
        for (let i = 0; i <= nshops; i++) {
            const mull = new THREE.Mesh(new THREE.BoxGeometry(0.45, storeH, 0.22), frameGreen);
            mull.position.set(sx * (hw + 0.14), storeCY, (-0.5 + i / nshops) * storeLen);
            bg.add(mull);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.5, storeLen + 0.4), frameGreen);
        lintel.position.set(sx * (hw + 0.16), 0.7 + storeH, 0);
        bg.add(lintel);
    }

    // TOLDOS coloridos (lojas) — SUBIDOS, no topo das vitrines
    const awnColors = ["#e6584d", "#f2c14e", "#3ddc84", "#5b8def", "#c879e6", "#ff8c42"];
    const ns = Math.max(5, Math.round(b.d / 8));
    for (const sx of [-1, 1]) {
        for (let i = 0; i < ns; i++) {
            const t = (i + 0.5) / ns - 0.5;
            if (sx < 0 && Math.abs(t * b.d) < b.w * 0.31 + 1.8) continue; // vão da ENTRADA LATERAL (face -X, de frente p/ a praça no rot90)
            const awn = new THREE.Mesh(
                new THREE.BoxGeometry(2.4, 0.3, (b.d / ns) * 0.8),
                new THREE.MeshStandardMaterial({
                    color: awnColors[(i + (sx > 0 ? 0 : 3)) % awnColors.length],
                    roughness: 0.7,
                }),
            );
            awn.position.set(sx * (hw + 0.95), 5.4, t * b.d);
            awn.rotation.z = sx > 0 ? -0.34 : 0.34;
            awn.castShadow = true;
            bg.add(awn);
        }
    }

    // ENTRADA na frente (+Z): PORTAS de vidro teal com MOLDURA/mulhões verdes (não
    // mais um bloco todo azul) + lintel + marquise VERDE (waGreen) + colunas
    const atriumW = b.w * 0.62;
    const doorH = 7;
    const doors = new THREE.Mesh(new THREE.BoxGeometry(atriumW, doorH, 0.4), warmGlass);
    doors.position.set(0, doorH / 2 + 0.4, hd + 0.3);
    doors.castShadow = true;
    bg.add(doors);
    const ndoors = 5;
    for (let i = 0; i <= ndoors; i++) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.3, doorH, 0.55), frameGreen);
        mull.position.set((-0.5 + i / ndoors) * atriumW, doorH / 2 + 0.4, hd + 0.42);
        bg.add(mull);
    }
    const sill = new THREE.Mesh(new THREE.BoxGeometry(atriumW + 0.6, 0.5, 0.6), frameGreen);
    sill.position.set(0, 0.45, hd + 0.42);
    bg.add(sill);
    // degrauzinho de entrada (igual aos outros prédios)
    const step = new THREE.Mesh(new THREE.BoxGeometry(atriumW + 1.6, 0.45, 1.6), frameGreen);
    step.position.set(0, 0.22, hd + 1.2);
    step.castShadow = true;
    bg.add(step);
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(atriumW + 0.8, 0.7, 0.7), frameGreen);
    lintel.position.set(0, doorH + 0.8, hd + 0.42);
    lintel.castShadow = true;
    bg.add(lintel);
    const marquise = new THREE.Mesh(new THREE.BoxGeometry(atriumW + 3, 0.7, 4), new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.6 }));
    marquise.position.set(0, doorH + 1.6, hd + 2.4);
    marquise.castShadow = true;
    bg.add(marquise);
    for (const sx of [-1, 1]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.32, doorH + 1.6, 0.32), frameGreen);
        post.position.set(sx * (atriumW / 2 + 1.3), (doorH + 1.6) / 2, hd + 4.2);
        post.castShadow = true;
        bg.add(post);
    }

    // TOTENS/banners de chão ladeando a entrada (no lugar dos bancos) — tidy,
    // dentro do corredor livre da porta (sem árvores/calçada)
    for (const side of [-1, 1]) {
        const t = makeMallTotem(side < 0 ? "#e6584d" : "#5b8def");
        t.position.set(side * 4.3, 0, hd + 2.6);
        t.rotation.y = side * -0.12;
        bg.add(t);
    }

    // ===== ENTRADA LATERAL (face longa -X, centrada) — MESMA entrada da principal, para quando o
    // shopping é posicionado na HORIZONTAL (rot90): esta face fica de frente para a praça do distrito.
    const latX = -hw;
    const ldoors = new THREE.Mesh(new THREE.BoxGeometry(0.4, doorH, atriumW), warmGlass);
    ldoors.position.set(latX - 0.3, doorH / 2 + 0.4, 0); ldoors.castShadow = true; bg.add(ldoors);
    for (let i = 0; i <= ndoors; i++) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.55, doorH, 0.3), frameGreen);
        mull.position.set(latX - 0.42, doorH / 2 + 0.4, (-0.5 + i / ndoors) * atriumW); bg.add(mull);
    }
    const lsill = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.5, atriumW + 0.6), frameGreen);
    lsill.position.set(latX - 0.42, 0.45, 0); bg.add(lsill);
    const lstep = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.45, atriumW + 1.6), frameGreen);
    lstep.position.set(latX - 1.2, 0.22, 0); lstep.castShadow = true; bg.add(lstep);
    const llintel = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, atriumW + 0.8), frameGreen);
    llintel.position.set(latX - 0.42, doorH + 0.8, 0); llintel.castShadow = true; bg.add(llintel);
    const lmarquise = new THREE.Mesh(new THREE.BoxGeometry(4, 0.7, atriumW + 3), new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.6 }));
    lmarquise.position.set(latX - 2.4, doorH + 1.6, 0); lmarquise.castShadow = true; bg.add(lmarquise);
    for (const sz of [-1, 1]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.32, doorH + 1.6, 0.32), frameGreen);
        post.position.set(latX - 4.2, (doorH + 1.6) / 2, sz * (atriumW / 2 + 1.3)); post.castShadow = true; bg.add(post);
    }
    for (const side of [-1, 1]) {
        const tt = makeMallTotem(side < 0 ? "#e6584d" : "#5b8def");
        tt.position.set(latX - 2.6, 0, side * 4.3); tt.rotation.y = -Math.PI / 2 + side * 0.12; bg.add(tt);
    }

    // DETALHES DE ARQUITETURA na parte superior (paredes não-lisas): fascia acima
    // dos toldos, pilastras + painéis coloridos, cornija com faixa, e um pylon
    // elevado acima da entrada.
    const accents = ["#e6584d", "#f2c14e", "#3ddc84", "#5b8def", "#c879e6", "#ff8c42"];
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const fasciaY = 6.4; // logo acima dos toldos
    const corniceY = b.h - 0.6;
    const midY = (fasciaY + corniceY) / 2;
    const upH = corniceY - fasciaY;
    for (const sx of [-1, 1]) {
        // fascia (faixa horizontal acima dos toldos) + cornija + faixa colorida no topo
        const fascia = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.0, b.d), frameGreen);
        fascia.position.set(sx * (hw + 0.22), fasciaY, 0);
        fascia.castShadow = true;
        bg.add(fascia);
        const cornice = new THREE.Mesh(new THREE.BoxGeometry(0.55, 1.0, b.d + 0.5), frameGreen);
        cornice.position.set(sx * (hw + 0.28), corniceY, 0);
        cornice.castShadow = true;
        bg.add(cornice);
        const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.45, b.d + 0.5), accentMat("#e6584d"));
        stripe.position.set(sx * (hw + 0.3), corniceY + 0.55, 0);
        bg.add(stripe);
        // pilastras (nervuras verticais) + painéis coloridos entre elas
        const nb = Math.max(6, Math.round(b.d / 8));
        const ph = upH * 0.62;
        const pw = (b.d / nb) * 0.5;
        for (let i = 0; i < nb; i++) {
            const z = (-0.5 + (i + 0.5) / nb) * b.d;
            // sobre a ENTRADA LATERAL (face -X), o painel fica MENOR e SOBE p/ ficar acima da marquise (não cortado)
            const overEntry = sx < 0 && Math.abs(z) < atriumW / 2 + 1.5;
            const pTop = midY + ph / 2;                                   // topo alinhado aos demais painéis
            const pBot = overEntry ? doorH + 1.6 + 0.75 : midY - ph / 2;  // base acima da marquise se estiver sobre a entrada
            const pH = pTop - pBot;
            const pCY = (pTop + pBot) / 2;
            // moldura verde-escura (delimita o quadrado colorido) atrás do painel
            const frame = new THREE.Mesh(new THREE.BoxGeometry(0.18, pH + 0.6, pw + 0.6), darkFrameMat);
            frame.position.set(sx * (hw + 0.1), pCY, z);
            bg.add(frame);
            const panel = new THREE.Mesh(new THREE.BoxGeometry(0.22, pH, pw), accentMat(accents[i % accents.length]));
            panel.position.set(sx * (hw + 0.16), pCY, z);
            bg.add(panel);
        }
        for (let i = 0; i <= nb; i++) {
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.45, upH, 0.7), frameGreen);
            pil.position.set(sx * (hw + 0.2), midY, (-0.5 + i / nb) * b.d);
            pil.castShadow = true;
            bg.add(pil);
        }
    }
    // FRENTE: cornija + PYLON elevado acima da entrada (sem texto) com faixas coloridas
    const frontCornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.0, 0.55), frameGreen);
    frontCornice.position.set(0, corniceY, hd + 0.28);
    frontCornice.castShadow = true;
    bg.add(frontCornice);
    const pylonW = atriumW + 1.6;
    const pylon = new THREE.Mesh(new THREE.BoxGeometry(pylonW, 6, 1.0), frameGreen);
    pylon.position.set(0, b.h, hd + 0.5);
    pylon.castShadow = true;
    bg.add(pylon);
    for (let i = 0; i < 4; i++) {
        const y = b.h - 2.4 + i * 1.3;
        // moldura verde-escura (igual aos quadrados das laterais) atrás da faixa
        const fr = new THREE.Mesh(new THREE.BoxGeometry(pylonW * 0.84 + 0.45, 0.8 + 0.4, 0.22), darkFrameMat);
        fr.position.set(0, y, hd + 0.96);
        bg.add(fr);
        const fs = new THREE.Mesh(new THREE.BoxGeometry(pylonW * 0.84, 0.8, 0.3), accentMat(accents[i]));
        fs.position.set(0, y, hd + 1.08);
        bg.add(fs);
    }
}

/** Coluna de banco: fuste cilíndrico + base/capitel quadrados (material à escolha). */
function makeBankColumn(h, mat) {
    const g = new THREE.Group();
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.42, h, 16), mat);
    shaft.position.y = h / 2;
    shaft.castShadow = true;
    g.add(shaft);
    for (const y of [0.2, h - 0.2]) {
        const cap = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.4, 1.1), mat);
        cap.position.y = y;
        cap.castShadow = true;
        g.add(cap);
    }
    return g;
}

/** Frontão (pediment) triangular voltado para +Z — coroamento clássico de banco. */
function makePediment(w, h, d, mat) {
    const shape = new THREE.Shape();
    shape.moveTo(-w / 2, 0);
    shape.lineTo(w / 2, 0);
    shape.lineTo(0, h);
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, { depth: d, bevelEnabled: false });
    geo.translate(0, 0, -d / 2);
    const ped = new THREE.Mesh(geo, mat);
    ped.castShadow = true;
    ped.receiveShadow = true;
    return ped;
}

/** Janelão vertical APAGADO: vidro bem ESCURO + moldura clara (pedra) + mulhões
 *  verde-escuros (mantêm a janela escura, contrastando com as janelas acesas). +Z. */
function makeBigWindow(w, h, trimMat) {
    const g = new THREE.Group();
    const glass = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, 0.2),
        // ACESO em AMARELO, igual às demais janelas do banco (on/glow da paleta)
        new THREE.MeshStandardMaterial({
            color: "#ffd27a",
            emissive: new THREE.Color("#ffcf5a"),
            emissiveIntensity: 0.72,
            roughness: 0.45,
        }),
    );
    g.add(glass);
    // moldura externa fina em pedra clara (respeita o pórtico)
    const ft = 0.35;
    const bars = [
        [w + 2 * ft, ft, 0, h / 2 + ft / 2],
        [w + 2 * ft, ft, 0, -h / 2 - ft / 2],
        [ft, h, -w / 2 - ft / 2, 0],
        [ft, h, w / 2 + ft / 2, 0],
    ];
    for (const [bw, bh, x, y] of bars) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.42), trimMat);
        m.position.set(x, y, 0.04);
        g.add(m);
    }
    // mulhões em VERDE-ESCURO (mantêm a janela escura) — vertical + horizontais
    const mullMat = new THREE.MeshStandardMaterial({
        color: COLORS.buildingWallDark,
        roughness: 0.7,
    });
    const vm = new THREE.Mesh(new THREE.BoxGeometry(0.14, h, 0.26), mullMat);
    vm.position.z = 0.06;
    g.add(vm);
    const rows = Math.max(3, Math.round(h / 2.6));
    for (let i = 1; i < rows; i++) {
        const hm = new THREE.Mesh(new THREE.BoxGeometry(w, 0.12, 0.26), mullMat);
        hm.position.set(0, -h / 2 + (h * i) / rows, 0.06);
        g.add(hm);
    }
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Fintech (Acquisition Town · B2): BANCO clássico-moderno, paleta harmônica
 * VERDE + PEDRA (calcário sage) + DOURADO. Colunata de pedra (peristilo) no
 * térreo com ENTRADA nas 4 FACES, FRONTÃO (pediment) de pedra sobre cada entrada
 * com o brasão do COFRE+"$", paredes verde-profundo de janelas douradas, friso
 * de LED dourado, cornija de pedra e o brasão grande no telhado (visto de longe).
 */
function buildFintech(bg, b, shared, rand) {
    // paredes verde-profundo com janelas douradas; sem fáscia (uso cornija de pedra)
    addBody(bg, b, shared, rand, { palette: "bank", fascia: false });
    // teto no VERDE PADRÃO (topo) com BORDA no verde-escuro padrão (buildingRoofEdge)
    const roofTopMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const roofEdgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.8 });
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 2, 1.4, b.d + 2), [roofEdgeMat, roofEdgeMat, roofTopMat, roofEdgeMat, roofEdgeMat, roofEdgeMat]);
    roofSlab.position.y = b.h + 0.75;
    roofSlab.castShadow = true;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const roofY = b.h + 1.45;
    const co = 1.0; // recuo das colunas além da face
    const colH = Math.min(b.h * 0.36, 7); // altura do térreo/colunata
    const entHalf = 2.7; // meia-largura da entrada
    const GOLD = "#ffcf5a";

    // VERDE PADRÃO dos prédios (COLORS.buildingRoof) — colunas, entablamento, pódio,
    // frontões, cornija. Mais claro que as paredes (verde padrão um tom mais escuro).
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.82 });
    // entrada: vidro AMARELO ACESO igual às janelas (mesmo on/glow da paleta bank)
    const glassMat = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.72,
        roughness: 0.4,
    });
    // friso de LED dourado (cotação/finanças) — toque moderno, na paleta
    const ledMat = new THREE.MeshStandardMaterial({
        color: "#2a2410",
        emissive: new THREE.Color(GOLD),
        emissiveIntensity: 0.95,
        roughness: 0.5,
    });

    // Pódio de pedra sob a colunata
    const podium = new THREE.Mesh(new THREE.BoxGeometry(b.w + 2 * (co + 0.9), 0.5, b.d + 2 * (co + 0.9)), trim);
    podium.position.y = 0.25;
    podium.receiveShadow = true;
    bg.add(podium);

    // Colunas de canto (4)
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const c = makeBankColumn(colH, trim);
            c.position.set(sx * (hw + co), 0.5, sz * (hd + co));
            bg.add(c);
        }
    }

    // 4 FACES: colunas flanqueando + entrada de vidro + degrau de pedra
    const faces = [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ];
    // entrada ALTA: cobre quase todo o vão (até a viga), p/ não deixar a janela da
    // TEXTURA da parede aparecer atrás/acima da porta (parecia 2 portas sobrepostas)
    const doorH = colH - 0.2;
    for (const f of faces) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        const rotY = f.nx !== 0 ? Math.PI / 2 : 0;
        for (const su of [-1, 1]) {
            const c = makeBankColumn(colH, trim);
            c.position.set(f.nx * (faceDist + co) + tx * su * entHalf, 0.5, f.nz * (faceDist + co) + tz * su * entHalf);
            bg.add(c);
        }
        const door = new THREE.Mesh(new THREE.BoxGeometry(entHalf * 2, doorH, 0.5), glassMat);
        door.position.set(f.nx * (faceDist + 0.1), 0.5 + doorH / 2, f.nz * (faceDist + 0.1));
        door.rotation.y = rotY;
        bg.add(door);
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.18, doorH, 0.6), dark);
        mull.position.set(f.nx * (faceDist + 0.16), 0.5 + doorH / 2, f.nz * (faceDist + 0.16));
        mull.rotation.y = rotY;
        bg.add(mull);
        // travessas horizontais (transoms) — dividem o envidraçado alto em porta + bandeiras
        for (const ty of [0.5 + doorH * 0.5, 0.5 + doorH * 0.76]) {
            const tr = new THREE.Mesh(new THREE.BoxGeometry(entHalf * 2, 0.16, 0.62), dark);
            tr.position.set(f.nx * (faceDist + 0.16), ty, f.nz * (faceDist + 0.16));
            tr.rotation.y = rotY;
            bg.add(tr);
        }
        const step = new THREE.Mesh(new THREE.BoxGeometry(entHalf * 2 + 2.4, 0.4, 1.6), trim);
        step.position.set(f.nx * (faceDist + 1.4), 0.2, f.nz * (faceDist + 1.4));
        step.rotation.y = rotY;
        bg.add(step);
    }

    // Entablamento de pedra envolvendo as 4 faces, sobre as colunas
    const entY = colH + 1.0;
    for (const sz of [-1, 1]) {
        const beam = new THREE.Mesh(new THREE.BoxGeometry(b.w + 2 * co + 1.2, 1.0, 1.2), trim);
        beam.position.set(0, entY, sz * (hd + co));
        beam.castShadow = true;
        bg.add(beam);
    }
    for (const sx of [-1, 1]) {
        const beam = new THREE.Mesh(new THREE.BoxGeometry(1.2, 1.0, b.d + 2 * co + 1.2), trim);
        beam.position.set(sx * (hw + co), entY, 0);
        beam.castShadow = true;
        bg.add(beam);
    }

    // Friso de LED dourado logo acima do entablamento (4 faces)
    const ledY = entY + 1.0;
    for (const sz of [1, -1]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 0.4, 0.12), ledMat);
        m.position.set(0, ledY, sz * (hd + 0.16));
        bg.add(m);
    }
    for (const sx of [1, -1]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.4, b.d + 0.2), ledMat);
        m.position.set(sx * (hw + 0.16), ledY, 0);
        bg.add(m);
    }

    // FRONTÃO de pedra sobre cada entrada, com o brasão do COFRE+"$" no tímpano
    const pedW = b.w * 0.6;
    const pedH = 3.2;
    for (const f of faces) {
        const faceDist = f.nz !== 0 ? hd : hw;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const ped = makePediment(pedW, pedH, 1.2, trim);
        ped.rotation.y = ry;
        ped.position.set(f.nx * (faceDist + co), entY + 0.5, f.nz * (faceDist + co));
        bg.add(ped);
        const em = makeVaultEmblem(shared);
        em.scale.setScalar(1.35);
        em.rotation.y = ry;
        em.position.set(f.nx * (faceDist + co + 0.75), entY + 0.5 + pedH * 0.4, f.nz * (faceDist + co + 0.75));
        bg.add(em);
    }

    // Andares superiores: no lugar das 2 janelas centrais, um FRONTISPÍCIO do banco
    // (painel verde-claro + pilastras + "$" dourado) — respeita o pórtico/colunata.
    const fpW = b.w * 0.36;
    const fpBot = entY;
    const fpTop = b.h - 0.9;
    const fpH = fpTop - fpBot;
    const fpCY = (fpBot + fpTop) / 2;
    for (const f of faces) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        // painel que cobre as 2 janelas centrais
        const panel = new THREE.Mesh(new THREE.BoxGeometry(fpW, fpH, 0.4), trim);
        panel.rotation.y = ry;
        panel.position.set(f.nx * (faceDist + 0.06), fpCY, f.nz * (faceDist + 0.06));
        panel.castShadow = true;
        bg.add(panel);
        // pilastras laterais (respeitam a colunata do pórtico) — pouco salientes
        for (const su of [-1, 1]) {
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.7, fpH, 0.4), trim);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.1) + tx * su * (fpW / 2 - 0.45), fpCY, f.nz * (faceDist + 0.1) + tz * su * (fpW / 2 - 0.45));
            pil.castShadow = true;
            bg.add(pil);
        }
        // JANELÃO vertical central ACESO (amarelo) — SALIENTE (à frente da parede);
        // pega os 2 andares. (O "$" daqui foi removido — já há no frontão e no teto.)
        const win = makeBigWindow(fpW - 2.6, fpH - 1.2, trim);
        win.rotation.y = ry;
        win.position.set(f.nx * (faceDist + 0.26), fpCY, f.nz * (faceDist + 0.26));
        bg.add(win);
    }

    // Cornija de pedra coroando o prédio (substitui a fáscia verde)
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.8, 1.1, b.d + 1.8), trim);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);

    // BRASÃO GIGANTE no TELHADO (voltado p/ cima) — ~90% do teto, óbvio que é banco.
    // Escala não-uniforme: grande no plano (X,Y) e mais raso na profundidade (Z→altura).
    const roofEm = makeVaultEmblem(shared);
    roofEm.scale.set(8, 8, 2.5);
    roofEm.rotation.x = -Math.PI / 2;
    roofEm.position.set(0, roofY + 0.6, 0);
    bg.add(roofEm);
}

/**
 * SEGURADORA FINANCEIRA (→ ASEGURADORA) — redesenho TOTAL do antigo "cards": instituição de SEGUROS.
 * Vocabulário da cidade (prédio VERDE) + identidade de CONFIANÇA/PROTEÇÃO em AZUL: cornija + pilastras
 * azuis + FRISO de ESCUDOS (proteção) no lugar do último andar contornando as 4 faces + ENTRADA
 * MONUMENTAL (colunas prateadas c/ capitéis azuis + grande ESCUDO com ✓ + letreiro). TETO limpo, só com
 * SAÍDAS DE AR / ventilação em AZUL (sem herói de teto — pedido do usuário).
 */
function buildInsurance(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const blue = new THREE.MeshStandardMaterial({ color: "#2f6fb0", emissive: new THREE.Color("#1a4e85"), emissiveIntensity: 0.18, roughness: 0.5, metalness: 0.25 });
    const blueDk = new THREE.MeshStandardMaterial({ color: "#20507f", roughness: 0.55, metalness: 0.2 });
    const silver = new THREE.MeshStandardMaterial({ color: "#e6ecf3", roughness: 0.6, metalness: 0.2 });

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    /** ESCUDO de proteção (frente = +Z): corpo azul + borda prateada + (opcional) ✓ branco. */
    function mkShield(s, withCheck) {
        const g = new THREE.Group();
        const w = 1.0 * s, h = 1.25 * s;
        const sh = new THREE.Shape();
        sh.moveTo(-w / 2, h / 2);
        sh.lineTo(w / 2, h / 2);
        sh.lineTo(w / 2, -h * 0.12);
        sh.quadraticCurveTo(w / 2, -h * 0.55, 0, -h / 2);
        sh.quadraticCurveTo(-w / 2, -h * 0.55, -w / 2, -h * 0.12);
        sh.closePath();
        const rim = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: 0.16 * s, bevelEnabled: false }), silver);
        rim.scale.set(1.16, 1.13, 1); g.add(rim);
        const bodyS = new THREE.Mesh(new THREE.ExtrudeGeometry(sh, { depth: 0.2 * s, bevelEnabled: false }), blue);
        bodyS.position.z = 0.03; g.add(bodyS);
        if (withCheck) {
            const zc = 0.2 * s + 0.08;
            const a = new THREE.Mesh(new THREE.BoxGeometry(0.16 * s, 0.55 * s, 0.1), silver);
            a.position.set(-0.16 * s, -0.14 * s, zc); a.rotation.z = 0.85; g.add(a);
            const bb = new THREE.Mesh(new THREE.BoxGeometry(0.16 * s, 1.0 * s, 0.1), silver);
            bb.position.set(0.2 * s, 0.1 * s, zc); bb.rotation.z = -0.62; g.add(bb);
        }
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== corpo verde + base + CORNIJA AZUL + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), blue);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas quentes + PILASTRAS AZUIS; último andar = FRISO DE ESCUDOS =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return; // friso de escudos no lugar
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < 5.4 && y < 9) || (Math.abs(u) < 1.8 && y < 12))) continue;
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) { // pilastras azuis
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.24, H - 2.4, 0.3), blue);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO de ESCUDOS (proteção) no último andar — contorna as 4 faces
        const friezeBand = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 2.4, 0.2), blueDk);
        friezeBand.rotation.y = ry;
        friezeBand.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1));
        bg.add(friezeBand);
        for (const cu of [-6.8, -3.4, 0, 3.4, 6.8]) {
            const shield = mkShield(0.72, false);
            shield.rotation.y = ry;
            shield.position.set(f.nx * (faceDist + 0.18) + tx * cu, topRowY, f.nz * (faceDist + 0.18) + tz * cu);
            bg.add(shield);
        }
    }

    // ===== ENTRADA MONUMENTAL: plataforma + 2 colunas prateadas (capitéis azuis) + entablamento azul =====
    const colH = 6.6, colZ = hd + 2.0;
    const plat = new THREE.Mesh(new THREE.BoxGeometry(13, 0.7, 4.2), trim);
    plat.position.set(0, 0.35, hd + 2.2); plat.castShadow = true; plat.receiveShadow = true; bg.add(plat);
    for (let s = 0; s < 2; s++) {
        const st = new THREE.Mesh(new THREE.BoxGeometry(13 - s * 1.4, 0.26, 0.8), silver);
        st.position.set(0, 0.5 - (s + 1) * 0.24, hd + 4.5 + s * 0.7); bg.add(st);
    }
    for (const cx of [-3.8, 3.8]) {
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.54, colH, 18), silver);
        shaft.position.set(cx, 0.7 + colH / 2, colZ); shaft.castShadow = true; bg.add(shaft);
        for (const cyp of [0.7 + 0.35, 0.7 + colH - 0.35]) {
            const cap = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.55, 1.4), blue);
            cap.position.set(cx, cyp, colZ); cap.castShadow = true; bg.add(cap);
        }
    }
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(10.2, 1.3, 1.7), blue);
    lintel.position.set(0, 0.7 + colH + 0.65, colZ); lintel.castShadow = true; bg.add(lintel);
    const signTex = makeSignTexture(t("SEGURADORA"), { bg: "#10233a", fg: "#dbe9fb", border: "#2f6fb0", fontPx: 44 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#bcd6f5"), emissiveIntensity: 0.5, roughness: 0.5 });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(8.8, 0.92, 0.12), [blue, blue, blue, blue, signMat, blue]);
    sign.position.set(0, 0.7 + colH + 0.65, colZ + 0.9); sign.castShadow = true; bg.add(sign);
    // grande ESCUDO com ✓ acima da entrada (o brasão da seguradora)
    const emblem = mkShield(2.2, true);
    emblem.position.set(0, 10.4, hd + 0.4); bg.add(emblem);
    // portas de vidro quente + mulhões azuis
    const doorW = 5.4, doorH = 6.0;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.7, hd + 0.15); bg.add(door);
    for (const mx of [-doorW / 2, -doorW / 6, doorW / 6, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), blue); ml.position.set(mx, doorH / 2 + 0.7, hd + 0.24); bg.add(ml); }

    // ===== bolardos prateados (cap azul) flanqueando a escadaria =====
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.42, 1.6, 14), silver);
        boll.position.set(bx * 6.6, 0.8, hd + 4.7); boll.castShadow = true; bg.add(boll);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.34, 12, 8), blue);
        cap.position.set(bx * 6.6, 1.75, hd + 4.7); bg.add(cap);
    }

    // ===== TETO: SAÍDAS DE AR / VENTILAÇÃO (azul, combinando com os detalhes da fachada) =====
    const roofT = roofY; // topo da laje
    const mkHVAC = (w, h, d) => { // unidade de ar-condicionado: caixa c/ aletas + ventoinha no topo
        const g = new THREE.Group();
        const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), blue); box.position.y = h / 2; g.add(box);
        for (let i = 0; i < 4; i++) {
            const lou = new THREE.Mesh(new THREE.BoxGeometry(w * 0.82, 0.09, 0.06), blueDk);
            lou.position.set(0, h * 0.28 + i * 0.24, d / 2 + 0.02); g.add(lou);
        }
        const rim = new THREE.Mesh(new THREE.BoxGeometry(w + 0.15, 0.12, d + 0.15), blueDk); rim.position.y = h; g.add(rim);
        const grille = new THREE.Mesh(new THREE.CylinderGeometry(Math.min(w, d) * 0.34, Math.min(w, d) * 0.34, 0.14, 20), silver);
        grille.position.y = h + 0.13; g.add(grille);
        const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.18, 10), blueDk); hub.position.y = h + 0.18; g.add(hub);
        for (let k = 0; k < 4; k++) {
            const bl = new THREE.Mesh(new THREE.BoxGeometry(Math.min(w, d) * 0.52, 0.03, 0.15), silver);
            bl.position.y = h + 0.15; bl.rotation.y = k * Math.PI / 4; g.add(bl);
        }
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };
    const mkCowl = (r, h) => { // exaustor: base + tubo + capuz (meia-esfera)
        const g = new THREE.Group();
        const bs = new THREE.Mesh(new THREE.BoxGeometry(r * 2.4, 0.3, r * 2.4), blueDk); bs.position.y = 0.15; g.add(bs);
        const pipe = new THREE.Mesh(new THREE.CylinderGeometry(r, r, h, 16), blue); pipe.position.y = 0.3 + h / 2; g.add(pipe);
        const cowl = new THREE.Mesh(new THREE.SphereGeometry(r * 1.18, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), silver); cowl.position.y = 0.3 + h; g.add(cowl);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };
    const mkAHU = (w, h, d) => { // unidade de tratamento de ar (grande): caixa + tampa + aletas 2 lados + cobertura curva + stub
        const g = new THREE.Group();
        const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), blue); box.position.y = h / 2; g.add(box);
        const lid = new THREE.Mesh(new THREE.BoxGeometry(w + 0.2, 0.2, d + 0.2), blueDk); lid.position.y = h; g.add(lid);
        for (const sz of [1, -1]) for (let i = 0; i < 5; i++) {
            const lou = new THREE.Mesh(new THREE.BoxGeometry(w * 0.85, 0.1, 0.05), blueDk);
            lou.position.set(0, h * 0.24 + i * 0.24, sz * (d / 2 + 0.02)); g.add(lou);
        }
        const cover = new THREE.Mesh(new THREE.CylinderGeometry(d * 0.34, d * 0.34, w * 0.62, 16), silver);
        cover.rotation.z = Math.PI / 2; cover.position.y = h + 0.12; g.add(cover);
        const stub = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.7, 12), silver); stub.position.set(w * 0.32, h + 0.45, -d * 0.22); g.add(stub);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };
    const mkBulkhead = (w, h, d) => { // guarita de acesso ao teto: caixa + laje + porta
        const g = new THREE.Group();
        const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), blue); box.position.y = h / 2; g.add(box);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(w + 0.3, 0.2, d + 0.3), blueDk); cap.position.y = h + 0.1; g.add(cap);
        const door = new THREE.Mesh(new THREE.BoxGeometry(w * 0.4, h * 0.68, 0.1), silver); door.position.set(0, h * 0.34, d / 2 + 0.02); g.add(door);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };
    const mkAntenna = () => { // mastro de comunicação + antena parabólica
        const g = new THREE.Group();
        const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 3.2, 8), silver); mast.position.y = 1.6; g.add(mast);
        for (const my of [1.7, 2.3, 2.9]) {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(1.1 - (my - 1.7) * 0.5, 0.06, 0.06), silver); bar.position.y = my; g.add(bar);
        }
        const tip = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), blue); tip.position.y = 3.25; g.add(tip);
        const dmast = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.9, 6), blueDk); dmast.position.set(0.95, 0.45, 0.3); g.add(dmast);
        const dish = new THREE.Mesh(new THREE.SphereGeometry(0.62, 16, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), silver);
        dish.position.set(0.95, 0.95, 0.3); dish.rotation.x = -1.0; g.add(dish);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };

    // --- LAYOUT ORGANIZADO (tudo alinhado a 0°, em zonas) ---
    for (const gx of [-5.5, -1.6]) for (const gz of [-5.2, -0.8]) { // bloco 2×2 de condensadores de AR-CONDICIONADO (fundo-esquerda)
        const u = mkHVAC(3.2, 1.5, 2.2); u.position.set(gx, roofT, gz); bg.add(u);
    }
    for (const gx of [-5.3, -1.8, 1.8, 5.3]) { // fila de 4 EXAUSTORES cowl (frente)
        const c = mkCowl(0.62, 1.15); c.position.set(gx, roofT, 5.3); bg.add(c);
    }
    const ahu = mkAHU(5.0, 1.9, 3.0); ahu.position.set(4.6, roofT, -4.6); bg.add(ahu); // grande UNIDADE DE TRATAMENTO DE AR (fundo-direita)
    const duct = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.5, 0.6), blueDk); duct.position.set(0.8, roofT + 0.3, -5.2); duct.castShadow = true; bg.add(duct); // duto ligando ao bloco de AC
    const bulk = mkBulkhead(3.0, 2.6, 2.4); bulk.position.set(5.2, roofT, 1.2); bg.add(bulk); // GUARITA de acesso (direita-centro)
    const ant = mkAntenna(); ant.position.set(5.0, roofT + 2.7, 1.2); bg.add(ant); // antena + parabólica no topo da guarita
    for (const [px, pz, ph] of [[0.6, 2.4, 1.4], [-6.8, 2.6, 1.2]]) { // 2 tubos de exaustão finos (centro-frente)
        const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, ph, 12), blue);
        pipe.position.set(px, roofT + ph / 2, pz); pipe.castShadow = true; bg.add(pipe);
        const capv = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.12, 12), silver);
        capv.position.set(px, roofT + ph + 0.06, pz); bg.add(capv);
    }
}

/**
 * TEXTURA da FACE de MOEDA (Casa da Moeda): disco dourado com aros em relevo, "$" central,
 * texto curvo no topo (nome, traduzido pela cidade) e estrelas na base. Aplicada em CircleGeometry.
 */
function makeCoinTexture(rimText) {
    const S = 640;
    const canvas = document.createElement("canvas");
    canvas.width = S; canvas.height = S;
    const ctx = canvas.getContext("2d");
    const cx = S / 2, cy = S / 2, R = S / 2 - 8;
    const grad = ctx.createRadialGradient(cx - 80, cy - 80, 30, cx, cy, R);
    grad.addColorStop(0, "#ffe79c"); grad.addColorStop(0.55, "#f4b942"); grad.addColorStop(1, "#bd8524");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#8f6416"; ctx.lineWidth = 22;
    ctx.beginPath(); ctx.arc(cx, cy, R - 20, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = "#f6d98f"; ctx.lineWidth = 7;
    ctx.beginPath(); ctx.arc(cx, cy, R - 44, 0, Math.PI * 2); ctx.stroke();
    const arc = (text, rTxt, a0, a1, font) => {
        ctx.save(); ctx.fillStyle = "#6e4d12"; ctx.font = font; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        const n = text.length;
        for (let i = 0; i < n; i++) {
            const a = a0 + (a1 - a0) * (n === 1 ? 0.5 : i / (n - 1));
            ctx.save();
            ctx.translate(cx + Math.cos(a) * rTxt, cy + Math.sin(a) * rTxt);
            ctx.rotate(a + Math.PI / 2);
            ctx.fillText(text[i], 0, 0);
            ctx.restore();
        }
        ctx.restore();
    };
    arc(rimText, R - 74, Math.PI * 1.22, Math.PI * 1.78, "800 42px Inter, Arial, sans-serif");
    arc("★ ★ ★ ★ ★", R - 74, Math.PI * 0.72, Math.PI * 0.28, "700 38px Inter, Arial, sans-serif");
    ctx.fillStyle = "#7a5410"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.font = "900 300px Georgia, 'Times New Roman', serif";
    ctx.fillText("$", cx, cy + 22);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * CASA DA MOEDA (→ CASA DE MONEDA) — redesenho TOTAL do antigo ATM/Cash: a instituição que CUNHA o
 * dinheiro. Vocabulário da cidade (prédio VERDE) + identidade GRANDIOSA com OURO (é uma casa da
 * moeda!): pilastras + CORNIJA douradas, FRISO de MEDALHÕES DE MOEDA dourados contornando as 4 faces
 * (no lugar do último andar), ENTRADA MONUMENTAL (colunata de pedra c/ capitéis dourados + grande
 * EMBLEMA de moeda + letreiro) e HERÓI de teto = MOEDA GIGANTE dourada (face "$" + nome) inclinada no
 * palco giratório, cercada de pilhas de moedas e barras de ouro.
 */
function buildMint(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const gold = new THREE.MeshStandardMaterial({ color: "#f4b942", emissive: new THREE.Color("#b9821f"), emissiveIntensity: 0.25, roughness: 0.32, metalness: 0.9 });
    const goldDk = new THREE.MeshStandardMaterial({ color: "#c8912f", roughness: 0.4, metalness: 0.8 });
    const stone = new THREE.MeshStandardMaterial({ color: "#e9e0c8", roughness: 0.85 });
    const accent = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.6 });
    const coinTex = makeCoinTexture(t("CASA DA MOEDA"));
    const coinFaceMat = new THREE.MeshStandardMaterial({ map: coinTex, emissiveMap: coinTex, emissive: new THREE.Color("#5a3d0a"), emissiveIntensity: 0.25, roughness: 0.35, metalness: 0.8 });

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    /** Disco de MOEDA dourada (frente = +Z): corpo + aro em relevo; withTex → face com "$"/nome, senão bossa central. */
    function mkCoinDisc(r, thick, withTex, light) {
        const g = new THREE.Group();
        const seg = light ? 20 : 30;
        const body = new THREE.Mesh(new THREE.CylinderGeometry(r, r, thick, seg), gold);
        body.rotation.x = Math.PI / 2; g.add(body);
        if (!light) { // aro em relevo (torus) só nas moedas GRANDES; medalhões pequenos usam bossa (leve)
            const rim = new THREE.Mesh(new THREE.TorusGeometry(r * 0.93, thick * 0.5, 6, 26), goldDk);
            rim.position.z = thick / 2; g.add(rim);
        }
        if (withTex) {
            const face = new THREE.Mesh(new THREE.CircleGeometry(r * 0.9, seg), coinFaceMat);
            face.position.z = thick / 2 + 0.02; g.add(face);
        } else {
            const boss = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.42, r * 0.42, thick * 0.5, 16), goldDk);
            boss.rotation.x = Math.PI / 2; boss.position.z = thick * 0.42; g.add(boss);
        }
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== corpo verde + base + CORNIJA DOURADA + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), gold);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas quentes + PILASTRAS DOURADAS; último andar = FRISO DE MEDALHÕES =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1]; // último andar → friso de moedas
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return; // sem janelas no último andar (friso de moedas)
            for (const u of grid.cols) {
                // frente: vão da entrada monumental (2 andares no centro) + faixa do emblema (só coluna central)
                if (isFront && ((Math.abs(u) < 5.4 && y < 9) || (Math.abs(u) < 1.8 && y < 12))) continue;
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) { // pilastras douradas entre baias
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.24, H - 2.4, 0.3), gold);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO de MEDALHÕES DE MOEDA (dourados) no lugar do último andar — contorna as 4 faces
        const friezeBand = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 2.4, 0.2), goldDk);
        friezeBand.rotation.y = ry;
        friezeBand.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1));
        bg.add(friezeBand);
        for (const cu of [-6.8, -3.4, 0, 3.4, 6.8]) {
            const medal = mkCoinDisc(0.85, 0.24, false, true);
            medal.rotation.y = ry;
            medal.position.set(f.nx * (faceDist + 0.32) + tx * cu, topRowY, f.nz * (faceDist + 0.32) + tz * cu);
            bg.add(medal);
        }
    }

    // ===== ENTRADA MONUMENTAL: plataforma + 2 colunas de pedra (capitéis dourados) + entablamento =====
    const colH = 6.6, colZ = hd + 2.0;
    const plat = new THREE.Mesh(new THREE.BoxGeometry(13, 0.7, 4.2), trim);
    plat.position.set(0, 0.35, hd + 2.2); plat.castShadow = true; plat.receiveShadow = true; bg.add(plat);
    for (let s = 0; s < 2; s++) {
        const st = new THREE.Mesh(new THREE.BoxGeometry(13 - s * 1.4, 0.26, 0.8), stone);
        st.position.set(0, 0.5 - (s + 1) * 0.24, hd + 4.5 + s * 0.7); bg.add(st);
    }
    for (const cx of [-3.8, 3.8]) {
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.54, colH, 18), stone);
        shaft.position.set(cx, 0.7 + colH / 2, colZ); shaft.castShadow = true; bg.add(shaft);
        for (const cyp of [0.7 + 0.35, 0.7 + colH - 0.35]) {
            const cap = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.55, 1.4), gold);
            cap.position.set(cx, cyp, colZ); cap.castShadow = true; bg.add(cap);
        }
    }
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(10.2, 1.3, 1.7), gold);
    lintel.position.set(0, 0.7 + colH + 0.65, colZ); lintel.castShadow = true; bg.add(lintel);
    // LETREIRO "CASA DA MOEDA" (traduzido pela cidade) no entablamento
    const signTex = makeSignTexture(t("CASA DA MOEDA"), { bg: "#2a1e06", fg: "#ffe6a6", border: "#7a5410", fontPx: 40 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffdf9a"), emissiveIntensity: 0.5, roughness: 0.5 });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(8.8, 0.92, 0.12), [gold, gold, gold, gold, signMat, gold]);
    sign.position.set(0, 0.7 + colH + 0.65, colZ + 0.9); sign.castShadow = true; bg.add(sign);
    // grande EMBLEMA de MOEDA acima da entrada (o brasão da Casa da Moeda)
    const emblem = mkCoinDisc(2.0, 0.45, true);
    emblem.position.set(0, 10.4, hd + 0.4); bg.add(emblem);
    // portas de vidro quente + mulhões dourados
    const doorW = 5.4, doorH = 6.0;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.7, hd + 0.15); bg.add(door);
    for (const mx of [-doorW / 2, -doorW / 6, doorW / 6, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), gold); ml.position.set(mx, doorH / 2 + 0.7, hd + 0.24); bg.add(ml); }

    // ===== bolardos de pedra c/ moeda no topo, flanqueando a escadaria =====
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.42, 1.6, 14), stone);
        boll.position.set(bx * 6.6, 0.8, hd + 4.7); boll.castShadow = true; bg.add(boll);
        const cap = mkCoinDisc(0.6, 0.18, false, true);
        cap.rotation.x = -Math.PI / 2; cap.position.set(bx * 6.6, 1.66, hd + 4.7); bg.add(cap);
    }

    // ===== TETO: palco giratório + MOEDA GIGANTE (herói) + pilhas de moedas + barras de ouro =====
    const deckR = Math.min(b.w, b.d) * 0.30;
    const baseT = new THREE.Mesh(new THREE.CylinderGeometry(deckR + 0.5, deckR + 0.7, 0.5, 32), goldDk);
    baseT.position.set(0, roofY + 0.25, 0); baseT.castShadow = true; bg.add(baseT);
    const deck = new THREE.Mesh(new THREE.CylinderGeometry(deckR, deckR, 0.35, 32), trim);
    deck.position.set(0, roofY + 0.6, 0); deck.receiveShadow = true; bg.add(deck);
    const ringR = new THREE.Mesh(new THREE.TorusGeometry(deckR - 0.12, 0.13, 8, 44), gold);
    ringR.rotation.x = Math.PI / 2; ringR.position.set(0, roofY + 0.8, 0); bg.add(ringR);
    const deckTop = roofY + 0.78;
    for (const sx of [-1, 1]) { // casas de máquinas discretas atrás
        const uu = new THREE.Mesh(new THREE.BoxGeometry(2.0, 1.1, 1.7), goldDk);
        uu.position.set(sx * (hw - 2.2), roofY + 0.8, -hd + 1.8); uu.castShadow = true; bg.add(uu);
    }
    // MOEDA GIGANTE inclinada (face p/ cima-frente, lê da câmera aérea)
    const gcoin = mkCoinDisc(3.9, 0.7, true);
    const coinWrap = new THREE.Group();
    coinWrap.add(gcoin);
    coinWrap.rotation.x = -0.34;
    coinWrap.rotation.y = -0.28;
    coinWrap.position.set(0, deckTop + 3.7, 0.2);
    bg.add(coinWrap);
    const cradle = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.7, 1.3), goldDk); // suporte da moeda
    cradle.position.set(0, deckTop + 0.35, 0.15); cradle.castShadow = true; bg.add(cradle);
    for (const px of [-1, 1]) {
        const prong = new THREE.Mesh(new THREE.BoxGeometry(0.35, 3.2, 0.45), goldDk);
        prong.rotation.x = -0.34; prong.position.set(px * 1.45, deckTop + 1.9, -0.35); prong.castShadow = true; bg.add(prong);
    }
    // pilhas de moedas (+x) + barras de ouro/lingotes (-x)
    for (const [sx, sz, n] of [[3.5, 1.1, 6], [4.3, 0.2, 4], [3.1, -1.0, 5]]) {
        for (let k = 0; k < n; k++) {
            const c = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.17, 16), gold);
            c.position.set(sx, deckTop + 0.17 + k * 0.18, sz); c.castShadow = true; bg.add(c);
        }
    }
    for (const [ix, iy, iz, iry] of [[-3.7, deckTop + 0.3, 1.2, 0.3], [-4.0, deckTop + 0.3, 0.1, -0.2], [-3.75, deckTop + 0.85, 0.65, 0.08]]) {
        const bar = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.55, 0.95), gold);
        bar.geometry.translate(0, 0, 0);
        bar.position.set(ix, iy, iz); bar.rotation.y = iry; bar.castShadow = true; bg.add(bar);
        const top = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.12, 0.6), goldDk); // leve chanfro no topo (aspecto de lingote)
        top.position.set(ix, iy + 0.33, iz); top.rotation.y = iry; bg.add(top);
    }
}

/**
 * BANCO DE OURO E PRATA (bullion) — irmão financeiro do mint/stocks, mas com identidade
 * BIMETÁLICA (ouro+prata) e motivo do LINGOTE em várias escalas: FRISO de lingotes, PILARES DE
 * BARRAS na entrada, TRIO de lingotes sobre a porta e HERÓI de teto = PIRÂMIDE DE LINGOTES. Cornija
 * dividida prata/ouro + pilares de quina 2 ouro/2 prata = coroa bimetal. SEM moeda (é do mint), SEM
 * roda de cofre (é da fintech), SEM touro (é da bolsa), SEM balança (leria "tribunal").
 */
function buildBullion(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    // OURO (mesmos valores do mint: emissive BAIXO 0.25 p/ não estourar o bloom) + PRATA (metálico frio, emissive 0)
    // metalness MODERADO: numa cena noturna metalness alto reflete o céu escuro e o ouro fica marrom;
    // baixando, o ALBEDO dourado/prateado aparece e o bimetal "pop". Emissive do ouro baixo (<bloom).
    const gold = new THREE.MeshStandardMaterial({ color: "#f6c247", emissive: new THREE.Color("#caa32a"), emissiveIntensity: 0.34, roughness: 0.42, metalness: 0.45 });
    const goldDk = new THREE.MeshStandardMaterial({ color: "#d19a2f", roughness: 0.5, metalness: 0.4 });
    const silver = new THREE.MeshStandardMaterial({ color: "#eef2f8", emissive: new THREE.Color("#7f8896"), emissiveIntensity: 0.12, roughness: 0.4, metalness: 0.38 });
    const silverDk = new THREE.MeshStandardMaterial({ color: "#aab4c0", roughness: 0.46, metalness: 0.38 });

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    // LINGOTE (base em y=0): corpo + chanfro no topo → silhueta de barra de ouro/prata
    function mkIngot(len, h, dep, isGold) {
        const g = new THREE.Group();
        const m = isGold ? gold : silver, dm = isGold ? goldDk : silverDk;
        const body = new THREE.Mesh(new THREE.BoxGeometry(len, h, dep), m);
        body.position.y = h / 2; body.castShadow = true; g.add(body);
        const bevel = new THREE.Mesh(new THREE.BoxGeometry(len * 0.78, h * 0.3, dep * 0.72), dm);
        bevel.position.y = h; bevel.castShadow = true; g.add(bevel);
        return g;
    }
    // pilar da entrada: barras empilhadas e CRUZADAS (bimetal) — leitura ESTRUTURAL de "bullion"
    function mkBarPier(ph, w) {
        const g = new THREE.Group();
        const n = Math.max(4, Math.round(ph / 0.8)), bh = ph / n;
        for (let k = 0; k < n; k++) {
            const ing = mkIngot(w, bh * 0.9, w, k % 2 === 0);
            ing.rotation.y = (k % 2) * Math.PI / 2; // camadas cruzadas (pilha de lenha)
            ing.position.y = k * bh; g.add(ing);
        }
        return g;
    }

    // ===== CASCA: corpo verde + plinto + CORNIJA BIMETAL (prata+ouro) + laje + PILARES DE QUINA 2+2 =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornSil = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.18, 0.5, b.d + 1.18), silver);
    cornSil.position.y = H - 0.72; cornSil.castShadow = true; bg.add(cornSil);
    const cornGold = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.06, 0.5, b.d + 1.06), gold);
    cornGold.position.y = H - 0.26; cornGold.castShadow = true; bg.add(cornGold);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);
    const cornersP = [[hw - 0.2, hd - 0.2, true], [-(hw - 0.2), -(hd - 0.2), true], [hw - 0.2, -(hd - 0.2), false], [-(hw - 0.2), hd - 0.2, false]];
    for (const [px, pz, isG] of cornersP) {
        const pier = new THREE.Mesh(new THREE.BoxGeometry(0.95, H - 1.4, 0.95), isG ? gold : silver);
        pier.position.set(px, (H - 1.4) / 2 + 0.9, pz); pier.castShadow = true; bg.add(pier);
    }

    // ===== FACHADA: janelas quentes + PILASTRAS bimetal + FRISO DE LINGOTES (último andar) =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return; // último andar = friso de lingotes
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < hw * 0.6 && y < H * 0.5) || (Math.abs(u) < hw * 0.22 && y < H * 0.72))) continue; // vão da entrada
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) { // pilastras bimetal (par=ouro, ímpar=prata)
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.24, H - 2.4, 0.3), i % 2 === 0 ? gold : silver);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO: banda escura + 5 LINGOTES DE LADO (face larga p/ fora) alternando ouro/prata
        const band = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 2.4, 0.2), goldDk);
        band.rotation.y = ry; band.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1)); bg.add(band);
        [-0.38, -0.19, 0, 0.19, 0.38].forEach((fr, k) => {
            const cu = fr * faceW;
            const wrap = new THREE.Group();
            const ing = mkIngot(1.7, 0.72, 0.5, k % 2 === 0);
            ing.position.y = -0.36; wrap.add(ing); // centraliza o lingote na banda
            wrap.rotation.y = ry;
            wrap.position.set(f.nx * (faceDist + 0.36) + tx * cu, topRowY, f.nz * (faceDist + 0.36) + tz * cu);
            bg.add(wrap);
        });
    }

    // ===== ENTRADA MONUMENTAL (+Z): plataforma + PILARES DE BARRAS bimetal + entablamento + TRIO de lingotes =====
    const colH = 6.4, colZ = hd + 2.0, platW = b.w * 0.72;
    const plat = new THREE.Mesh(new THREE.BoxGeometry(platW, 0.7, 4.2), trim);
    plat.position.set(0, 0.35, hd + 2.2); plat.castShadow = true; plat.receiveShadow = true; bg.add(plat);
    for (let s = 0; s < 2; s++) {
        const st = new THREE.Mesh(new THREE.BoxGeometry(platW - s * 1.4, 0.26, 0.8), edgeMat);
        st.position.set(0, 0.5 - (s + 1) * 0.24, hd + 4.5 + s * 0.7); bg.add(st);
    }
    for (const cx of [-hw * 0.42, hw * 0.42]) { // 2 pilares de barras no lugar de colunas
        const pier = mkBarPier(colH, 1.15);
        pier.position.set(cx, 0.7, colZ); bg.add(pier);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.4, 1.5), gold);
        cap.position.set(cx, 0.7 + colH + 0.2, colZ); cap.castShadow = true; bg.add(cap);
    }
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(platW - 2, 1.2, 1.7), gold);
    lintel.position.set(0, 0.7 + colH + 0.9, colZ); lintel.castShadow = true; bg.add(lintel);
    const dentil = new THREE.Mesh(new THREE.BoxGeometry(platW - 1.8, 0.34, 1.8), silver);
    dentil.position.set(0, 0.7 + colH + 0.25, colZ); bg.add(dentil);
    // LETREIRO prata-frio (diferencia do letreiro dourado do mint)
    const signTex = makeSignTexture(t("OURO E PRATA"), { bg: "#101a22", fg: "#eaf2fa", border: "#8f9aa6", fontPx: 40 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#dfe8f2"), emissiveIntensity: 0.5, roughness: 0.5 });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(b.w * 0.49, 0.9, 0.12), [silverDk, silverDk, silverDk, silverDk, signMat, silverDk]);
    sign.position.set(0, 0.7 + colH + 0.9, colZ + 0.9); sign.castShadow = true; bg.add(sign);
    // EMBLEMA: TRIO de lingotes (ouro-prata-ouro) em nicho iluminado sobre a porta
    const niche = new THREE.Mesh(new THREE.BoxGeometry(5.2, 2.0, 0.3), goldDk);
    niche.position.set(0, 10.2, hd + 0.14); bg.add(niche);
    [-1.55, 0, 1.55].forEach((ex, i) => {
        const ing = mkIngot(1.25, 0.85, 0.5, i % 2 === 0);
        ing.position.set(ex, 9.78, hd + 0.42); bg.add(ing);
    });
    // portas de vidro quente + mulhões bimetal
    const doorW = 5.2, doorH = 5.6;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.7, hd + 0.15); bg.add(door);
    [-doorW / 2, -doorW / 6, doorW / 6, doorW / 2].forEach((mx, i) => { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), i % 2 === 0 ? gold : silver); ml.position.set(mx, doorH / 2 + 0.7, hd + 0.24); bg.add(ml); });
    // bolardos de PRATA com lingote de OURO no topo (tell de "bullion" no térreo)
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.4, 1.5, 14), silver);
        boll.position.set(bx * hw * 0.73, 0.75, hd + 4.6); boll.castShadow = true; bg.add(boll);
        const cap = mkIngot(0.72, 0.4, 0.72, true); cap.position.set(bx * hw * 0.73, 1.5, hd + 4.6); bg.add(cap);
    }

    // ===== TETO (linha criativa do CAFÉ): telhado PLANO, HERÓI central + ANEL de PILHAS contornando
    //      TODO o perímetro — aqui pilhas de LINGOTES ouro/prata no lugar dos torrões de açúcar =====
    // ANEL de pilhas de lingotes (cantos + meios das arestas, equidistantes) — como o anel de torrões
    const IS = 1.15, IH = 0.5;
    const addIngotPile = (cx, cz, goldFirst) => {
        const pat = [[-0.5, -0.5, 0], [0.5, -0.5, 0], [-0.5, 0.5, 0], [0.5, 0.5, 0], [0, 0, 1]];
        pat.forEach(([gx, gz, layer], i) => {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(IS, IH, IS * 0.82), ((i % 2 === 0) === goldFirst) ? gold : silver);
            bar.position.set(cx + gx * (IS + 0.08), roofY + IH * (layer + 0.5), cz + gz * (IS + 0.08));
            bar.rotation.y = (gx - gz) * 0.12; bar.castShadow = true; bg.add(bar);
        });
    };
    const R = hw - 2.2, Mm = 2.2;
    const ring = [
        [-R, -R], [-Mm, -R], [Mm, -R], [R, -R], // trás
        [R, -Mm], [R, Mm],                       // direita (meios)
        [R, R], [Mm, R], [-Mm, R], [-R, R],       // frente
        [-R, Mm], [-R, -Mm]                       // esquerda (meios)
    ];
    ring.forEach(([x, z], i) => addIngotPile(x, z, i % 2 === 0));
    // HERÓI central: DUAS PLACAS grandes deitadas — PRATA embaixo, OURO em cima (uma sob a outra)
    const slabSil = mkIngot(8.6, 1.5, 4.4, false);
    slabSil.position.set(0, roofY, 0); bg.add(slabSil);
    const slabGold = mkIngot(7.7, 1.5, 3.6, true);
    slabGold.position.set(0, roofY + 1.5, 0); bg.add(slabGold);
}

/**
 * BOLSA DE VALORES — redesenho no vocabulário da cidade (prédio VERDE padrão) com um
 * PÓRTICO CLÁSSICO de bolsa (colunata de aço + frontão, leitura "casa da bolsa"/NYSE que
 * conversa com o banco) + friso de TICKER LED (cotações verde/vermelho) + QUADRO de cotações
 * na entrada, e um HERÓI de teto ORIGINAL: o TOURO (Charging Bull) de bronze em pedestal
 * girável — símbolo da bolsa em ALTA. SEM dourado.
 */
function buildStocks(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    // Materiais — VERDE da cidade + AÇO frio + LED de cotação. ZERO dourado.
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const steel = new THREE.MeshStandardMaterial({ color: "#cdd3d8", roughness: 0.5, metalness: 0.45 });
    const steelDk = new THREE.MeshStandardMaterial({ color: "#6f7b83", roughness: 0.5, metalness: 0.4 });
    const accent = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.6 });
    const glassDoor = new THREE.MeshStandardMaterial({ color: "#bfe6ff", emissive: new THREE.Color("#7fb8e0"), emissiveIntensity: 0.5, roughness: 0.25, metalness: 0.2 });
    const tickerTex = makeExchangeTickerTexture(); // tickers FICTÍCIOS (símbolo AMARELO + variação verde/vermelha), estilo placa
    registerLedMarquee(tickerTex, 0.14); // ROLA o ticker (mesmo motor dos painéis de LED; ~2,5 u/s)
    const tickerMat = new THREE.MeshStandardMaterial({ map: tickerTex, emissiveMap: tickerTex, emissive: new THREE.Color("#ffffff"), emissiveIntensity: 1.15, roughness: 0.5 });
    const boardTex = makeStockPanelTexture();
    const boardMat = new THREE.MeshStandardMaterial({ map: boardTex, emissiveMap: boardTex, emissive: new THREE.Color("#ffffff"), emissiveIntensity: 1.1, roughness: 0.5 });

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    // ===== corpo verde + base + cornija verde-escuro + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 0.9, b.d + 1.0), edgeMat);
    cornice.position.y = H - 0.35; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas quentes (facadeGrid) + pilastras de aço; último andar = PAINEL DE LED =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];   // 5º andar → painel de LED
    const bandRowY = grid.rows[grid.rows.length - 2];  // 4º andar → também vira painel de LED
    const bandY = (topRowY + bandRowY) / 2;            // centro da faixa (cobre os 2 últimos andares)
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY || y === bandRowY) return; // sem janelas nos 2 últimos andares (painel de LED no lugar)
            for (const u of grid.cols) {
                if (isFront && Math.abs(u) < 5.4 && y < 8.5) continue; // vão do pórtico (alinhado à colunata; sem cobrir janelas)
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) {
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, H - 2.2, 0.28), steelDk);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.2, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // PAINEL DE LED contínuo cobrindo os 2 ÚLTIMOS ANDARES: bezel de aço + tira de ticker fictício (4 faces)
        const bezel = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.4, 6.6, 0.22), steelDk);
        bezel.rotation.y = ry;
        bezel.position.set(f.nx * (faceDist + 0.12), bandY, f.nz * (faceDist + 0.12));
        bezel.castShadow = true; bg.add(bezel);
        const strip = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.1, 6.0, 0.1), [steelDk, steelDk, steelDk, steelDk, tickerMat, steelDk]);
        strip.rotation.y = ry;
        strip.position.set(f.nx * (faceDist + 0.24), bandY, f.nz * (faceDist + 0.24));
        bg.add(strip);
    }

    // ===== PÓRTICO CLÁSSICO da BOLSA (colunata + frontão) =====
    const colH = 5.1, colZ = hd + 2.2, portW = 10.8;
    const plat = new THREE.Mesh(new THREE.BoxGeometry(portW, 0.7, 4.2), trim);
    plat.position.set(0, 0.35, hd + 2.2); plat.castShadow = true; plat.receiveShadow = true; bg.add(plat);
    for (let s = 0; s < 2; s++) { // 2 degraus frontais
        const st = new THREE.Mesh(new THREE.BoxGeometry(portW - s * 1.4, 0.26, 0.8), steelDk);
        st.position.set(0, 0.5 - (s + 1) * 0.24, hd + 4.5 + s * 0.7); bg.add(st);
    }
    for (const cx of [-4.8, -1.6, 1.6, 4.8]) { // colunata de aço (4 colunas, alinhada às baias centrais)
        const col = makeBankColumn(colH, steel);
        col.position.set(cx, 0.7, colZ); bg.add(col);
    }
    const archi = new THREE.Mesh(new THREE.BoxGeometry(portW - 0.2, 1.1, 1.6), edgeMat); // arquitrave
    archi.position.set(0, 0.7 + colH + 0.55, colZ); archi.castShadow = true; bg.add(archi);
    const ped = makePediment(portW + 0.2, 2.2, 1.7, trim); // frontão verde (ápice 9.1 — abaixo da fileira de janelas y=10.55)
    ped.position.set(0, 0.7 + colH + 1.1, colZ); bg.add(ped);
    const chev = new THREE.Mesh(new THREE.ConeGeometry(0.62, 1.3, 3), accent); // seta de ALTA no tímpano
    chev.position.set(0, 0.7 + colH + 1.95, colZ + 0.9); bg.add(chev);
    // LETREIRO "BOLSA" na arquitrave (traduzido pela cidade)
    const signTex = makeSignTexture(t("BOLSA"), { bg: "#0e2a1c", fg: "#7dffb0", border: "#1f8f3e", fontPx: 60 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#8fffc0"), emissiveIntensity: 0.6, roughness: 0.5 });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(8.4, 0.86, 0.12), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, 0.7 + colH + 0.55, colZ + 0.86); bg.add(sign);

    // ===== ENTRADA (atrás da colunata): QUADRO de cotações + portas de vidro =====
    const boardW = 8.4, boardH = 2.0;
    const boardFrame = new THREE.Mesh(new THREE.BoxGeometry(boardW + 0.5, boardH + 0.5, 0.3), steelDk);
    boardFrame.position.set(0, 4.75, hd + 0.1); bg.add(boardFrame);
    const board = new THREE.Mesh(new THREE.BoxGeometry(boardW, boardH, 0.12), [steelDk, steelDk, steelDk, steelDk, boardMat, steelDk]);
    board.position.set(0, 4.75, hd + 0.27); bg.add(board);
    const doorW = 5.6, doorH = 2.8;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), glassDoor);
    door.position.set(0, doorH / 2 + 0.7, hd + 0.18); bg.add(door);
    for (const mx of [-doorW / 2, -doorW / 6, doorW / 6, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), steelDk); ml.position.set(mx, doorH / 2 + 0.7, hd + 0.28); bg.add(ml); }

    // ===== jardineiras (vida no térreo, como nos demais prédios) =====
    for (const bx of [-1, 1]) {
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.62, 1.0, 12), steelDk);
        pot.position.set(bx * 6.9, 0.5, hd + 4.7); pot.castShadow = true; bg.add(pot);
        const bush = new THREE.Mesh(new THREE.SphereGeometry(0.85, 12, 10), accent);
        bush.position.set(bx * 6.9, 1.4, hd + 4.7); bush.castShadow = true; bg.add(bush);
    }

    // ===== TETO: palco girável + TOURO (Charging Bull) — símbolo da bolsa em ALTA =====
    const deckR = Math.min(b.w, b.d) * 0.30;
    const baseT = new THREE.Mesh(new THREE.CylinderGeometry(deckR + 0.5, deckR + 0.7, 0.5, 32), steelDk);
    baseT.position.set(0, roofY + 0.25, 0); baseT.castShadow = true; bg.add(baseT);
    const deck = new THREE.Mesh(new THREE.CylinderGeometry(deckR, deckR, 0.35, 32), trim);
    deck.position.set(0, roofY + 0.6, 0); deck.receiveShadow = true; bg.add(deck);
    const ringR = new THREE.Mesh(new THREE.TorusGeometry(deckR - 0.12, 0.13, 8, 44), accent);
    ringR.rotation.x = Math.PI / 2; ringR.position.set(0, roofY + 0.8, 0); bg.add(ringR);
    for (const sx of [-1, 1]) { // casas de máquinas discretas atrás do touro (teto não fica vazio)
        const uu = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.2, 1.8), steelDk);
        uu.position.set(sx * (hw - 2.0), roofY + 0.85, -hd + 1.8); uu.castShadow = true; bg.add(uu);
    }
    const bull = mkBull();
    bull.scale.setScalar(1.5); // +50% (pés seguem no deck; cabeça avança sobre a borda — pose de ataque)
    bull.position.set(0, roofY + 0.78, 0.2);
    bull.rotation.y = 0.55; // pose 3/4 ESPELHADA na horizontal (era -0.55; lê da câmera aérea e da frente)
    bg.add(bull);

    /** TOURO low-poly (Charging Bull) em bronze: peito/garupa, cupim, cabeça baixada, chifres, rabo. */
    function mkBull() {
        const g = new THREE.Group();
        const hide = new THREE.MeshStandardMaterial({ color: "#7d5a3a", roughness: 0.55, metalness: 0.35 }); // bronze (NÃO dourado)
        const horn = new THREE.MeshStandardMaterial({ color: "#ece5d2", roughness: 0.5, metalness: 0.2 });
        const darkP = new THREE.MeshStandardMaterial({ color: "#241f1a", roughness: 0.6 });
        const legT = 1.7;
        const mkLeg = (x, z, kick) => {
            const leg = new THREE.Group();
            const thigh = new THREE.Mesh(new THREE.BoxGeometry(0.55, legT, 0.6), hide); thigh.position.y = legT / 2; leg.add(thigh);
            const hoof = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.35, 0.55), darkP); hoof.position.y = 0.17; leg.add(hoof);
            leg.position.set(x, 0, z); leg.rotation.x = kick || 0; return leg;
        };
        g.add(mkLeg(-0.95, 1.65, 0)); g.add(mkLeg(0.95, 1.65, 0));       // dianteiras firmes (à frente)
        g.add(mkLeg(-0.9, -1.7, -0.45)); g.add(mkLeg(0.9, -1.7, -0.45)); // traseiras chutando p/ trás
        const chest = new THREE.Mesh(new THREE.BoxGeometry(2.5, 2.2, 2.6), hide); chest.position.set(0, legT + 0.9, 0.9); g.add(chest);
        const rump = new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.9, 2.2), hide); rump.position.set(0, legT + 0.75, -1.4); g.add(rump);
        const hump = new THREE.Mesh(new THREE.BoxGeometry(2.3, 1.0, 1.7), hide); hump.position.set(0, legT + 2.0, 0.6); g.add(hump); // cupim
        const neck = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.4, 1.4), hide); neck.position.set(0, legT + 1.45, 2.1); neck.rotation.x = 0.5; g.add(neck);
        const head = new THREE.Mesh(new THREE.BoxGeometry(1.45, 1.3, 1.6), hide); head.position.set(0, legT + 0.7, 3.05); head.rotation.x = 0.35; g.add(head);
        const snout = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.9, 0.85), hide); snout.position.set(0, legT + 0.3, 3.85); g.add(snout);
        for (const nx of [-0.28, 0.28]) { const n = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), darkP); n.position.set(nx, legT + 0.2, 4.24); g.add(n); }
        for (const ex of [-0.5, 0.5]) { const e = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), darkP); e.position.set(ex, legT + 1.05, 3.55); g.add(e); }
        for (const ex of [-0.85, 0.85]) { const ear = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.6, 6), hide); ear.position.set(ex, legT + 1.4, 2.85); ear.rotation.z = ex < 0 ? 0.8 : -0.8; g.add(ear); }
        for (const hx of [-0.62, 0.62]) { // CHIFRES p/ frente-cima (icônicos)
            const hn = new THREE.Mesh(new THREE.ConeGeometry(0.17, 1.35, 8), horn);
            hn.position.set(hx, legT + 1.6, 3.15); hn.rotation.set(-0.6, 0, hx < 0 ? 0.55 : -0.55); g.add(hn);
        }
        const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.09, 2.0, 8), hide); tail.position.set(0.35, legT + 0.35, -2.5); tail.rotation.set(0.7, 0, 0.3); g.add(tail);
        const tuft = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.5, 0.32), darkP); tuft.position.set(0.62, legT - 0.7, -3.05); g.add(tuft);
        g.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } });
        return g;
    }
}

/**
 * VITAMINAS / SUPLEMENTOS — loja de vitaminas no vocabulário da cidade (prédio VERDE) com
 * identidade CÍTRICA/energética (laranja + amarelo): PILASTRAS e CORNIJA laranjas, FRISO de
 * CÁPSULAS coloridas contornando o último andar, VITRINE de POTES de vitamina no térreo atrás
 * do vidro, TOLDO com valença + letreiro "VITAMINAS", grande EMBLEMA de cápsula sobre a entrada
 * e HERÓI de teto = CÁPSULA GIGANTE 2-tons inclinada no palco giratório, cercada de pílulas.
 */
function buildVitamins(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const orange = new THREE.MeshStandardMaterial({ color: "#f2872e", roughness: 0.5, metalness: 0.05 });
    const orangeDk = new THREE.MeshStandardMaterial({ color: "#d9701e", roughness: 0.55 });
    const yellow = new THREE.MeshStandardMaterial({ color: "#f6c22e", roughness: 0.45 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4ead0", roughness: 0.5 });
    const white = new THREE.MeshStandardMaterial({ color: "#eef2ee", roughness: 0.55 });
    const glassV = new THREE.MeshStandardMaterial({ color: "#cfe8ec", roughness: 0.1, metalness: 0.1, transparent: true, opacity: 0.3 });
    const interior = new THREE.MeshStandardMaterial({ color: "#203029", roughness: 0.85 });
    const capOrange = new THREE.MeshStandardMaterial({ color: "#f2872e", emissive: new THREE.Color("#c85e10"), emissiveIntensity: 0.28, roughness: 0.32 });
    const capCream = new THREE.MeshStandardMaterial({ color: "#f7f0dc", emissive: new THREE.Color("#cdbf94"), emissiveIntensity: 0.14, roughness: 0.34 });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.5 });
    const PILL_COLS = ["#e8563f", "#f2a83c", "#f6c22e", "#4bad63", "#3a86e0", "#9560e6"];

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    /** CÁPSULA 2-tons (eixo X): 2 cilindros + 2 hemisférios, meia m1 / meia m2. */
    function mkCapsule(len, r, m1, m2) {
        const g = new THREE.Group();
        const c1 = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len / 2, 20), m1); c1.rotation.z = Math.PI / 2; c1.position.x = -len / 4; g.add(c1);
        const e1 = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2), m1); e1.rotation.z = Math.PI / 2; e1.position.x = -len / 2; g.add(e1);
        const c2 = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len / 2, 20), m2); c2.rotation.z = Math.PI / 2; c2.position.x = len / 4; g.add(c2);
        const e2 = new THREE.Mesh(new THREE.SphereGeometry(r, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2), m2); e2.rotation.z = -Math.PI / 2; e2.position.x = len / 2; g.add(e2);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }
    /** POTE de vitamina: corpo colorido + tampa branca + faixa de rótulo branca. */
    function mkBottle(col) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.37, 1.0, 14), accentMat(col)); body.position.y = 0.5; g.add(body);
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.24, 14), white); cap.position.y = 1.12; g.add(cap);
        const lab = new THREE.Mesh(new THREE.CylinderGeometry(0.36, 0.38, 0.44, 14, 1, true), white); lab.position.y = 0.5; g.add(lab);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== corpo VERDE + base + CORNIJA LARANJA + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), orange);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas quentes + PILASTRAS laranjas; último andar = FRISO DE CÁPSULAS =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return; // friso de cápsulas no lugar
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < 6.5 && y < 6) || (Math.abs(u) < 2.6 && y < 10))) continue; // vitrine + coluna do emblema
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) { // pilastras laranjas
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, H - 2.4, 0.28), orange);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO de CÁPSULAS coloridas no último andar — contorna as 4 faces
        const friezeBand = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 1.9, 0.2), orangeDk);
        friezeBand.rotation.y = ry;
        friezeBand.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1));
        bg.add(friezeBand);
        for (let ci = 0; ci < 5; ci++) {
            const cu = -6.8 + ci * 3.4;
            const pill = mkCapsule(1.7, 0.42, accentMat(PILL_COLS[ci % PILL_COLS.length]), cream);
            pill.rotation.y = ry;
            pill.position.set(f.nx * (faceDist + 0.28) + tx * cu, topRowY, f.nz * (faceDist + 0.28) + tz * cu);
            bg.add(pill);
        }
    }

    // ===== VITRINE de VITAMINAS (térreo frontal): fundo escuro + 2 prateleiras de POTES + vidro =====
    const vW = 13.5, vH = 5.0;
    const backW = new THREE.Mesh(new THREE.BoxGeometry(vW, vH, 0.1), interior);
    backW.position.set(0, vH / 2 + 0.5, hd + 0.06); bg.add(backW);
    for (const shy of [1.7, 3.4]) {
        const shelf = new THREE.Mesh(new THREE.BoxGeometry(vW - 0.6, 0.14, 0.7), white);
        shelf.position.set(0, shy, hd + 0.5); shelf.castShadow = true; bg.add(shelf);
        const nb = 13;
        for (let k = 0; k < nb; k++) {
            const bx = -vW / 2 + 0.7 + k * ((vW - 1.4) / (nb - 1));
            if (Math.abs(bx) < 2.7) continue; // centro livre p/ a porta
            const bot = mkBottle(PILL_COLS[(k * 3 + Math.round(shy)) % PILL_COLS.length]);
            bot.scale.setScalar(0.72); bot.position.set(bx, shy + 0.05, hd + 0.5); bg.add(bot);
        }
    }
    // VIDRO tingido (transparente) nas laterais da vitrine + moldura branca
    for (const sx of [-1, 1]) {
        const pane = new THREE.Mesh(new THREE.BoxGeometry(vW / 2 - 2.6, vH, 0.08), glassV);
        pane.position.set(sx * (vW / 4 + 1.15), vH / 2 + 0.5, hd + 0.72); bg.add(pane);
    }
    for (const mx of [-vW / 2, -2.7, 2.7, vW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, vH, 0.14), white); ml.position.set(mx, vH / 2 + 0.5, hd + 0.78); bg.add(ml); }
    for (const my of [0.55, vH + 0.45]) { const tr = new THREE.Mesh(new THREE.BoxGeometry(vW, 0.16, 0.14), white); tr.position.set(0, my, hd + 0.78); bg.add(tr); }
    // PORTA de vidro quente central + mulhões laranja + degrau
    const doorW = 4.6, doorH = 4.6;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.3), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.5); bg.add(door);
    for (const mx of [-doorW / 2, 0, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.4), orange); ml.position.set(mx, doorH / 2 + 0.5, hd + 0.6); bg.add(ml); }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.3, 1.2), trim);
    step.position.set(0, 0.15, hd + 0.9); step.receiveShadow = true; bg.add(step);

    // ===== TOLDO (laranja) inclinado + VALENÇA com o letreiro "VITAMINAS" =====
    const awnW = vW + 1.4, awnD = 2.6, awnY = vH + 1.0;
    const awn = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.28, awnD), orange);
    awn.position.set(0, awnY, hd + awnD / 2 + 0.1); awn.rotation.x = -0.14; awn.castShadow = true; bg.add(awn);
    // (sem mãos-francesas — o toldo é assumido fixo à fachada, a pedido do usuário)
    const signTex = makeSignTexture(t("VITAMINAS"), { bg: "#12331f", fg: "#ffe08a", border: "#f2872e", fontPx: 46 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffd98a"), emissiveIntensity: 0.5, roughness: 0.5 });
    const valance = new THREE.Mesh(new THREE.BoxGeometry(awnW, 1.2, 0.12), [orange, orange, orange, orange, signMat, orange]);
    valance.position.set(0, awnY - 0.35, hd + awnD + 0.05); bg.add(valance);
    // grande EMBLEMA de CÁPSULA acima da entrada (o brasão da loja)
    const emblem = mkCapsule(3.0, 0.8, capOrange, capCream);
    emblem.rotation.z = 0.5; emblem.position.set(0, vH + 3.4, hd + 0.3); bg.add(emblem);

    // ===== bolardos brancos c/ topo laranja flanqueando a entrada =====
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.4, 1.5, 14), white);
        boll.position.set(bx * (vW / 2 + 0.6), 0.75, hd + 1.4); boll.castShadow = true; bg.add(boll);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.3, 12, 8), orange);
        cap.position.set(bx * (vW / 2 + 0.6), 1.6, hd + 1.4); bg.add(cap);
    }

    // ===== TETO: palco giratório + CÁPSULA GIGANTE (herói) + pílulas ao redor =====
    const deckR = Math.min(b.w, b.d) * 0.30;
    const baseT = new THREE.Mesh(new THREE.CylinderGeometry(deckR + 0.5, deckR + 0.7, 0.5, 32), orangeDk);
    baseT.position.set(0, roofY + 0.25, 0); baseT.castShadow = true; bg.add(baseT);
    const deck = new THREE.Mesh(new THREE.CylinderGeometry(deckR, deckR, 0.35, 32), trim);
    deck.position.set(0, roofY + 0.6, 0); deck.receiveShadow = true; bg.add(deck);
    const ringR = new THREE.Mesh(new THREE.TorusGeometry(deckR - 0.12, 0.13, 8, 44), yellow);
    ringR.rotation.x = Math.PI / 2; ringR.position.set(0, roofY + 0.8, 0); bg.add(ringR);
    const deckTop = roofY + 0.78;
    for (const sz of [-hd + 1.8, hd - 1.8]) // casas de máquinas discretas — no FUNDO e na FRENTE
        for (const sx of [-1, 1]) {
            const uu = new THREE.Mesh(new THREE.BoxGeometry(2.0, 1.1, 1.7), orangeDk);
            uu.position.set(sx * (hw - 2.2), roofY + 0.8, sz); uu.castShadow = true; bg.add(uu);
        }
    // CÁPSULA GIGANTE 2-tons inclinada (uma ponta p/ cima, lê da câmera aérea e de frente)
    const gcap = mkCapsule(4.6, 1.35, capOrange, capCream);
    const capWrap = new THREE.Group();
    capWrap.add(gcap);
    capWrap.rotation.z = 0.45;
    capWrap.rotation.y = -0.55;
    capWrap.position.set(0, deckTop + 3.2, 0);
    bg.add(capWrap);
    // pedestal/berço da cápsula
    const cradle = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.1, 1.4, 20), white);
    cradle.position.set(0, deckTop + 0.7, 0); cradle.castShadow = true; bg.add(cradle);
    const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.92, 0.92, 0.25, 20), orange);
    collar.position.set(0, deckTop + 1.45, 0); bg.add(collar);
    // PÍLULAS menores em 2 ANÉIS ao redor da cápsula, cobrindo TODA a área elevada do deck
    for (const [rf, n, off] of [[0.66, 8, 0], [0.86, 12, Math.PI / 12]]) {
        const r = deckR * rf;
        for (let i = 0; i < n; i++) {
            const a = off + (i / n) * Math.PI * 2;
            const pill = mkCapsule(1.5, 0.4, accentMat(PILL_COLS[(i * 2 + n) % PILL_COLS.length]), cream);
            pill.rotation.y = a + Math.PI / 2; // tangencial ao anel
            pill.position.set(Math.cos(a) * r, deckTop + 0.5, Math.sin(a) * r); bg.add(pill);
        }
    }
}

/**
 * ESTÉTICA / COSMÉTICOS — marca de CPG do mercado de BELEZA no vocabulário da cidade (prédio VERDE)
 * com identidade ROSA + ROSE-GOLD: cornija/pilastras rosa, FRISO de mini-cosméticos, VITRINE de produtos
 * (batom, perfume, pote de creme, esmalte), toldo + letreiro "ESTÉTICA", EMBLEMA de BATOM sobre a entrada
 * e HERÓI de teto = BATOM GIGANTE no palco giratório, cercado de perfumes/potes/esmaltes. Distinto do
 * vitamins (cápsula/laranja), do boutique (moda/salto) e do spa (zen).
 */
function buildBeauty(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const pink = new THREE.MeshStandardMaterial({ color: "#e35b9a", roughness: 0.5, metalness: 0.05 });
    const pinkDk = new THREE.MeshStandardMaterial({ color: "#c8437f", roughness: 0.55 });
    const rgold = new THREE.MeshStandardMaterial({ color: "#e6b483", roughness: 0.4, metalness: 0.5 }); // rose-gold (metalness moderado)
    const rgoldDk = new THREE.MeshStandardMaterial({ color: "#c9955f", roughness: 0.45, metalness: 0.45 });
    const white = new THREE.MeshStandardMaterial({ color: "#f2eef0", roughness: 0.5 });
    const glassV = new THREE.MeshStandardMaterial({ color: "#f0d8e6", roughness: 0.1, metalness: 0.1, transparent: true, opacity: 0.3 });
    const interior = new THREE.MeshStandardMaterial({ color: "#2a2028", roughness: 0.85 });
    const heroPink = new THREE.MeshStandardMaterial({ color: "#e35b9a", emissive: new THREE.Color("#b83b78"), emissiveIntensity: 0.26, roughness: 0.34 });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.5 });
    const COS_COLS = ["#e35b9a", "#e0555b", "#f2884e", "#c07be0", "#8b5a8f", "#e8b04b"]; // paleta de beleza

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    /** BATOM: tubo metálico rose-gold + anel + bala AFUNILADA com PONTA ARREDONDADA (domo — sem falha). */
    function mkLipstick(r, tubeLen, bulletLen, bulletMat) {
        const g = new THREE.Group();
        const tube = new THREE.Mesh(new THREE.CylinderGeometry(r, r, tubeLen, 16), rgold); tube.position.y = tubeLen / 2; g.add(tube);
        const ring = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.08, r * 1.08, tubeLen * 0.14, 16), rgoldDk); ring.position.y = tubeLen * 0.72; g.add(ring);
        const bullet = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.68, r * 0.86, bulletLen, 16), bulletMat); bullet.position.y = tubeLen + bulletLen / 2; g.add(bullet);
        const dome = new THREE.Mesh(new THREE.SphereGeometry(r * 0.68, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), bulletMat); dome.position.y = tubeLen + bulletLen; g.add(dome);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }
    /** PERFUME: corpo facetado + gargalo rose-gold + tampa + atomizador (bulbo). */
    function mkPerfume(col) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.8, 0.42), accentMat(col)); body.position.y = 0.4; g.add(body);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 0.22, 10), rgold); neck.position.y = 0.9; g.add(neck);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.2, 0.24), rgoldDk); cap.position.y = 1.08; g.add(cap);
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.14, 10, 8), pink); bulb.position.set(0.28, 1.02, 0); g.add(bulb);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }
    /** POTE de creme: corpo baixo largo + tampa rose-gold. */
    function mkJar(col) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.4, 16), accentMat(col)); body.position.y = 0.2; g.add(body);
        const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.44, 0.28, 16), rgold); lid.position.y = 0.54; g.add(lid);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }
    /** ESMALTE: frasco quadrado + tampa/pincel slim. */
    function mkPolish(col) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.4), accentMat(col)); body.position.y = 0.25; g.add(body);
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.5, 10), rgoldDk); cap.position.y = 0.72; g.add(cap);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }
    /** produto variado por índice (mix de cosméticos). */
    function mkProduct(i, col) {
        const k = ((i % 4) + 4) % 4;
        if (k === 0) return mkLipstick(0.2, 0.6, 0.4, accentMat(col));
        if (k === 1) return mkPerfume(col);
        if (k === 2) return mkJar(col);
        return mkPolish(col);
    }
    /** FRASCO DE PERFUME grande (herói do teto): corpo de vidro facetado + rótulo + ombro + gargalo/
     *  tampa rose-gold + atomizador (colar + tubo + pêra) — mais compatível com o prédio que o batom. */
    function mkHeroPerfume() {
        const g = new THREE.Group();
        const bd = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.9, 3.4, 8), heroPink); bd.scale.z = 0.62; bd.position.y = 1.7; g.add(bd); // corpo facetado (octógono achatado)
        const lab = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.3, 0.12), rgold); lab.position.set(0, 1.6, 1.18); g.add(lab); // moldura do rótulo
        const labC = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.85, 0.06), white); labC.position.set(0, 1.6, 1.25); g.add(labC); // rótulo branco
        const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.9, 0.9, 8), heroPink); sh.scale.z = 0.62; sh.position.y = 3.85; g.add(sh); // ombro afunilado
        const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.66, 0.7, 0.2, 16), rgold); collar.position.y = 4.3; g.add(collar);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.58, 0.6, 16), rgold); neck.position.y = 4.7; g.add(neck);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.95, 16, 12), rgoldDk); cap.scale.y = 0.85; cap.position.y = 5.3; g.add(cap); // tampa arredondada
        const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 1.0, 8), rgoldDk); tube.rotation.z = Math.PI / 2; tube.position.set(1.0, 4.5, 0); g.add(tube);
        const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.5, 14, 10), pink); bulb.scale.set(1.1, 0.9, 0.9); bulb.position.set(1.85, 4.4, 0); g.add(bulb); // pêra do atomizador
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== corpo VERDE + base + CORNIJA ROSA + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), pink);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas + pilastras rosa; último andar = FRISO de mini-cosméticos =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return;
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < 6.5 && y < 6) || (Math.abs(u) < 2.6 && y < 10))) continue; // vitrine + coluna do emblema
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) {
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, H - 2.4, 0.28), pink);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO: banda rosa-escura + 5 mini-cosméticos por face
        const band = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 1.9, 0.2), pinkDk);
        band.rotation.y = ry;
        band.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1));
        bg.add(band);
        for (let ci = 0; ci < 5; ci++) {
            if (isFront && ci === 2) continue; // centro da FRENTE livre p/ o emblema (evita conflito)
            const cu = -6.8 + ci * 3.4;
            const wrap = new THREE.Group();
            const prod = mkProduct(ci, COS_COLS[ci % COS_COLS.length]);
            prod.scale.setScalar(1.5); wrap.add(prod);
            wrap.rotation.y = ry;
            wrap.position.set(f.nx * (faceDist + 0.3) + tx * cu, topRowY - 0.9, f.nz * (faceDist + 0.3) + tz * cu);
            bg.add(wrap);
        }
    }

    // ===== VITRINE de cosméticos (térreo frontal): fundo escuro + 2 prateleiras + vidro rosado =====
    const vW = 13.5, vH = 5.0;
    const backW = new THREE.Mesh(new THREE.BoxGeometry(vW, vH, 0.1), interior);
    backW.position.set(0, vH / 2 + 0.5, hd + 0.06); bg.add(backW);
    for (const shy of [1.7, 3.4]) {
        const shelf = new THREE.Mesh(new THREE.BoxGeometry(vW - 0.6, 0.14, 0.7), white);
        shelf.position.set(0, shy, hd + 0.5); shelf.castShadow = true; bg.add(shelf);
        const nb = 12;
        for (let k = 0; k < nb; k++) {
            const bx = -vW / 2 + 0.7 + k * ((vW - 1.4) / (nb - 1));
            if (Math.abs(bx) < 2.7) continue; // centro livre p/ a porta
            const prod = mkProduct(k + Math.round(shy), COS_COLS[(k * 2 + Math.round(shy)) % COS_COLS.length]);
            prod.scale.setScalar(0.95); prod.position.set(bx, shy + 0.05, hd + 0.5); bg.add(prod);
        }
    }
    for (const sx of [-1, 1]) {
        const pane = new THREE.Mesh(new THREE.BoxGeometry(vW / 2 - 2.6, vH, 0.08), glassV);
        pane.position.set(sx * (vW / 4 + 1.15), vH / 2 + 0.5, hd + 0.72); bg.add(pane);
    }
    for (const mx of [-vW / 2, -2.7, 2.7, vW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, vH, 0.14), white); ml.position.set(mx, vH / 2 + 0.5, hd + 0.78); bg.add(ml); }
    for (const my of [0.55, vH + 0.45]) { const tr = new THREE.Mesh(new THREE.BoxGeometry(vW, 0.16, 0.14), white); tr.position.set(0, my, hd + 0.78); bg.add(tr); }
    // PORTA de vidro quente central + mulhões rosa + degrau
    const doorW = 4.6, doorH = 4.6;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.3), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.5); bg.add(door);
    for (const mx of [-doorW / 2, 0, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.4), pink); ml.position.set(mx, doorH / 2 + 0.5, hd + 0.6); bg.add(ml); }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.3, 1.2), trim);
    step.position.set(0, 0.15, hd + 0.9); step.receiveShadow = true; bg.add(step);

    // ===== TOLDO rosa inclinado + VALENÇA com letreiro "ESTÉTICA" + EMBLEMA de BATOM =====
    const awnW = vW + 1.4, awnD = 2.6, awnY = vH + 1.0;
    const awn = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.28, awnD), pink);
    awn.position.set(0, awnY, hd + awnD / 2 + 0.1); awn.rotation.x = -0.14; awn.castShadow = true; bg.add(awn);
    const signTex = makeSignTexture(t("ESTÉTICA"), { bg: "#2a0f1e", fg: "#ffd0e6", border: "#e35b9a", fontPx: 46 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffd0e6"), emissiveIntensity: 0.5, roughness: 0.5 });
    const valance = new THREE.Mesh(new THREE.BoxGeometry(awnW, 1.2, 0.12), [pink, pink, pink, pink, signMat, pink]);
    valance.position.set(0, awnY - 0.35, hd + awnD + 0.05); bg.add(valance);
    const emblem = mkLipstick(0.55, 1.2, 0.8, heroPink); // BATOM-emblema MENOR (não encosta no friso)
    emblem.rotation.z = 0.22; emblem.position.set(0, vH + 1.9, hd + 0.3); bg.add(emblem);

    // ===== bolardos brancos c/ topo rosa flanqueando a entrada =====
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.4, 1.5, 14), white);
        boll.position.set(bx * (vW / 2 + 0.6), 0.75, hd + 1.4); boll.castShadow = true; bg.add(boll);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.3, 12, 8), pink);
        cap.position.set(bx * (vW / 2 + 0.6), 1.6, hd + 1.4); bg.add(cap);
    }

    // ===== TETO: PLATAFORMA QUADRADA (proporcional ao prédio) + FRASCO (herói) + cosméticos ORGANIZADOS =====
    const PLAT = 13; // lado da plataforma (recuo de ~2,5 em relação ao prédio 18×18)
    const platBase = new THREE.Mesh(new THREE.BoxGeometry(PLAT, 0.5, PLAT), pinkDk);
    platBase.position.set(0, roofY + 0.25, 0); platBase.castShadow = true; platBase.receiveShadow = true; bg.add(platBase);
    const platSurf = new THREE.Mesh(new THREE.BoxGeometry(PLAT - 0.8, 0.22, PLAT - 0.8), trim);
    platSurf.position.set(0, roofY + 0.55, 0); platSurf.receiveShadow = true; bg.add(platSurf);
    for (const [w, d, ox, oz] of [[PLAT, 0.3, 0, PLAT / 2 - 0.15], [PLAT, 0.3, 0, -(PLAT / 2 - 0.15)], [0.3, PLAT, PLAT / 2 - 0.15, 0], [0.3, PLAT, -(PLAT / 2 - 0.15), 0]]) {
        const rim = new THREE.Mesh(new THREE.BoxGeometry(w, 0.26, d), rgold); rim.position.set(ox, roofY + 0.6, oz); rim.castShadow = true; bg.add(rim); // moldura rose-gold nas 4 bordas
    }
    const platY = roofY + 0.66; // topo da plataforma (onde os itens ficam)
    // HERÓI centralizado: frasco de perfume sobre coaster quadrado
    const coaster = new THREE.Mesh(new THREE.BoxGeometry(4.0, 0.3, 4.0), white);
    coaster.position.set(0, platY + 0.15, 0); coaster.castShadow = true; bg.add(coaster);
    const gPerf = mkHeroPerfume();
    const perfWrap = new THREE.Group(); perfWrap.add(gPerf);
    perfWrap.rotation.y = -0.3;
    perfWrap.position.set(0, platY + 0.3, 0); bg.add(perfWrap);
    // COSMÉTICOS ORGANIZADOS: 3 por lado (anel quadrado), alinhados e voltados p/ a frente (+Z)
    const S = PLAT / 2 - 1.7;
    const rowP = [-S * 0.66, 0, S * 0.66];
    const edges = [];
    for (const p of rowP) edges.push([p, S], [p, -S], [S, p], [-S, p]); // frente, trás, direita, esquerda
    edges.forEach(([x, z], i) => {
        const prod = mkProduct(i, COS_COLS[i % COS_COLS.length]);
        prod.scale.setScalar(2.2);
        prod.position.set(x, platY, z); bg.add(prod);
    });
}

/**
 * CEREAIS & SNACKS (+ LEITE) — marca de CPG (cereais, snacks e leite) no vocabulário da cidade
 * (prédio VERDE) com identidade ALEGRE (vermelho-cereal + amarelo + branco-leite + azul-leite):
 * PILASTRAS e CORNIJA vermelhas, FRISO-PRATELEIRA de EMBALAGENS (caixas de cereal / leite / snacks)
 * contornando o último andar, VITRINE de produtos no térreo, TOLDO + letreiro "CEREAIS & SNACKS",
 * grande EMBLEMA de caixa de cereal (arte impressa) sobre a entrada e HERÓI de teto = CAIXA DE
 * CEREAL GIGANTE (arte impressa) no palco giratório, cercada de tigela c/ leite, caixa de leite e snacks.
 */
function buildCereals(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    const red = new THREE.MeshStandardMaterial({ color: "#d94436", roughness: 0.5 });
    const redDk = new THREE.MeshStandardMaterial({ color: "#b8372a", roughness: 0.55 });
    const yellow = new THREE.MeshStandardMaterial({ color: "#f5c542", roughness: 0.45 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4ead0", roughness: 0.5 });
    const milkWhite = new THREE.MeshStandardMaterial({ color: "#f2f4f0", roughness: 0.5 });
    const milkBlue = new THREE.MeshStandardMaterial({ color: "#3f9fd0", roughness: 0.5 });
    const glassV = new THREE.MeshStandardMaterial({ color: "#cfe8ec", roughness: 0.1, metalness: 0.1, transparent: true, opacity: 0.3 });
    const interior = new THREE.MeshStandardMaterial({ color: "#203029", roughness: 0.85 });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.5 });
    const PACK_COLS = ["#e8563f", "#f2a43c", "#f5c542", "#8bc34a", "#3f9fd0", "#c25bb0"];

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    // textura da FRENTE da caixa de cereal (arte impressa): banner de marca + tigela c/ leite + colher
    function cerealTex() {
        const cw = 512, ch = 760;
        const canvas = document.createElement("canvas"); canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext("2d");
        const gr = ctx.createLinearGradient(0, 0, 0, ch); gr.addColorStop(0, "#e2503a"); gr.addColorStop(1, "#c5341f");
        ctx.fillStyle = gr; ctx.fillRect(0, 0, cw, ch);
        ctx.strokeStyle = "#ffd23f"; ctx.lineWidth = 20; ctx.strokeRect(14, 14, cw - 28, ch - 28);
        ctx.fillStyle = "#ffd23f"; roundRect(ctx, 44, 54, cw - 88, 150, 28); ctx.fill();
        ctx.fillStyle = "#c02f1d"; ctx.font = "900 96px Inter, Arial, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText("CEREAIS", cw / 2, 130);
        ctx.fillStyle = "#e9dfc4"; ctx.beginPath(); ctx.ellipse(cw / 2, 470, 185, 100, 0, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fbfaf4"; ctx.beginPath(); ctx.ellipse(cw / 2, 452, 160, 80, 0, 0, Math.PI * 2); ctx.fill();
        for (const [dx, dy, c] of [[-100, -6, "#f2a43c"], [-36, 18, "#e8563f"], [36, -8, "#f6c22e"], [104, 16, "#8bc34a"], [10, -30, "#f2a43c"], [74, -18, "#e8563f"], [-70, 26, "#f6c22e"], [-8, 34, "#8bc34a"]]) {
            ctx.strokeStyle = c; ctx.lineWidth = 18; ctx.beginPath(); ctx.arc(cw / 2 + dx, 452 + dy, 22, 0, Math.PI * 2); ctx.stroke();
        }
        ctx.save(); ctx.translate(cw / 2 + 155, 460); ctx.rotate(-0.5); ctx.fillStyle = "#cfd6dd";
        ctx.beginPath(); ctx.ellipse(0, 0, 44, 30, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillRect(-8, 0, 16, 150); ctx.restore();
        ctx.fillStyle = "#ffffff"; ctx.font = "800 62px Inter, Arial, sans-serif"; ctx.fillText("MILK & SNACKS", cw / 2, 662);
        const tex = new THREE.CanvasTexture(canvas); tex.colorSpace = THREE.SRGBColorSpace; return tex;
    }
    const boxTex = cerealTex();
    const boxFront = new THREE.MeshStandardMaterial({ map: boxTex, roughness: 0.6 });
    const boxSide = new THREE.MeshStandardMaterial({ color: "#c5341f", roughness: 0.6 });

    /** CAIXA DE CEREAL (bottom-origin): opt="printed" usa arte na frente (+z); senão caixa colorida c/ banner+tigela. */
    function mkCerealBox(w, h, d, opt) {
        const g = new THREE.Group();
        if (opt === "printed") {
            const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [boxSide, boxSide, boxSide, boxSide, boxFront, boxSide]);
            box.position.y = h / 2; box.castShadow = true; g.add(box);
        } else {
            const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), accentMat(opt || "#d94436")); box.position.y = h / 2; box.castShadow = true; g.add(box);
            const banner = new THREE.Mesh(new THREE.BoxGeometry(w * 0.82, h * 0.2, 0.03), yellow); banner.position.set(0, h * 0.74, d / 2 + 0.015); g.add(banner);
            const bowl = new THREE.Mesh(new THREE.CircleGeometry(Math.min(w, h) * 0.24, 18), cream); bowl.position.set(0, h * 0.4, d / 2 + 0.02); g.add(bowl);
        }
        return g;
    }
    /** CAIXA DE LEITE gable-top (bottom-origin): corpo branco + 2 águas + cumeeira + faixa colorida. */
    function mkMilkCarton(w, h, d, bandCol) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), milkWhite); body.position.y = h / 2; body.castShadow = true; g.add(body);
        const roofH = d * 0.55;
        for (const s of [1, -1]) {
            const slope = new THREE.Mesh(new THREE.BoxGeometry(w * 1.0, 0.06, d * 0.62), milkWhite);
            slope.position.set(0, h + roofH * 0.42, s * d * 0.16); slope.rotation.x = s * 0.82; slope.castShadow = true; g.add(slope);
        }
        const ridge = new THREE.Mesh(new THREE.BoxGeometry(w * 1.02, 0.1, 0.12), milkWhite); ridge.position.y = h + roofH * 0.72; g.add(ridge);
        const band = new THREE.Mesh(new THREE.BoxGeometry(w + 0.02, h * 0.3, d + 0.02), accentMat(bandCol || "#3f9fd0")); band.position.y = h * 0.45; g.add(band);
        return g;
    }
    /** SACO DE SNACK estufado (bottom-origin): corpo colorido + crimps topo/base + faixa clara. */
    function mkSnackBag(w, h, d, col) {
        const g = new THREE.Group();
        const bodyMat = accentMat(col);
        const body = new THREE.Mesh(new THREE.BoxGeometry(w, h * 0.86, d), bodyMat); body.position.y = h / 2; body.castShadow = true; g.add(body);
        for (const yy of [h * 0.04, h * 0.96]) { const cr = new THREE.Mesh(new THREE.BoxGeometry(w * 1.06, h * 0.09, d * 0.34), bodyMat); cr.position.y = yy; g.add(cr); }
        const stripe = new THREE.Mesh(new THREE.BoxGeometry(w + 0.02, h * 0.22, d + 0.02), cream); stripe.position.y = h * 0.5; g.add(stripe);
        return g;
    }
    /** TIGELA de cereal c/ leite + argolinhas (prop de teto; olha-se para dentro do alto). */
    /** GARRAFA DE LEITE (bottom-origin): corpo cilíndrico branco + ombro + gargalo + tampa + rótulo colorido. */
    function mkMilkBottle(r, h, capCol) {
        const g = new THREE.Group();
        const bodyH = h * 0.6;
        const bd = new THREE.Mesh(new THREE.CylinderGeometry(r, r, bodyH, 16), milkWhite); bd.position.y = bodyH / 2; g.add(bd);
        const shoulder = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.45, r, h * 0.18, 16), milkWhite); shoulder.position.y = bodyH + h * 0.09; g.add(shoulder);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.42, r * 0.42, h * 0.14, 14), milkWhite); neck.position.y = bodyH + h * 0.25; g.add(neck);
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.46, r * 0.46, h * 0.1, 14), accentMat(capCol)); cap.position.y = bodyH + h * 0.37; g.add(cap);
        const label = new THREE.Mesh(new THREE.CylinderGeometry(r + 0.02, r + 0.02, bodyH * 0.42, 16), accentMat(capCol)); label.position.y = bodyH * 0.4; g.add(label);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== corpo VERDE + base + CORNIJA VERMELHA + laje =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), red);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas quentes + PILASTRAS vermelhas; último andar = FRISO-PRATELEIRA de EMBALAGENS =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return; // friso-prateleira no lugar
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < 6.5 && y < 6) || (Math.abs(u) < 2.6 && y < 10))) continue; // vitrine + coluna do emblema
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) { // pilastras vermelhas
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, H - 2.4, 0.28), red);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO-PRATELEIRA: banda + ledge + fila de EMBALAGENS (caixa/leite/snack) contornando as 4 faces
        const friezeBand = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 1.9, 0.2), redDk);
        friezeBand.rotation.y = ry; friezeBand.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1)); bg.add(friezeBand);
        const ledge = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 0.16, 0.5), red);
        ledge.rotation.y = ry; ledge.position.set(f.nx * (faceDist + 0.34), topRowY - 0.78, f.nz * (faceDist + 0.34)); bg.add(ledge);
        for (let ci = 0; ci < 5; ci++) {
            const cu = -6.8 + ci * 3.4;
            const kind = ci % 3;
            let pack;
            if (kind === 0) pack = mkCerealBox(0.95, 1.4, 0.55, PACK_COLS[ci % PACK_COLS.length]);
            else if (kind === 1) pack = mkMilkCarton(0.72, 1.05, 0.72, PACK_COLS[(ci + 2) % PACK_COLS.length]);
            else pack = mkSnackBag(0.82, 1.3, 0.42, PACK_COLS[(ci + 4) % PACK_COLS.length]);
            pack.rotation.y = ry;
            pack.position.set(f.nx * (faceDist + 0.34) + tx * cu, topRowY - 0.7, f.nz * (faceDist + 0.34) + tz * cu);
            bg.add(pack);
        }
    }

    // ===== VITRINE de PRODUTOS (térreo frontal): fundo escuro + 2 prateleiras de EMBALAGENS + vidro =====
    const vW = 13.5, vH = 5.0;
    const backW = new THREE.Mesh(new THREE.BoxGeometry(vW, vH, 0.1), interior);
    backW.position.set(0, vH / 2 + 0.5, hd + 0.06); bg.add(backW);
    for (const shy of [1.4, 3.3]) {
        const shelf = new THREE.Mesh(new THREE.BoxGeometry(vW - 0.6, 0.14, 0.8), milkWhite);
        shelf.position.set(0, shy, hd + 0.5); shelf.castShadow = true; bg.add(shelf);
        const nb = 7;
        for (let k = 0; k < nb; k++) {
            const bx = -vW / 2 + 1.0 + k * ((vW - 2.0) / (nb - 1));
            if (Math.abs(bx) < 2.7) continue; // centro livre p/ a porta
            const kind = (k + Math.round(shy)) % 3;
            let pack;
            if (kind === 0) pack = mkCerealBox(1.0, 1.5, 0.6, PACK_COLS[k % PACK_COLS.length]);
            else if (kind === 1) pack = mkMilkCarton(0.8, 1.15, 0.8, PACK_COLS[(k + 2) % PACK_COLS.length]);
            else pack = mkSnackBag(0.9, 1.4, 0.5, PACK_COLS[(k + 4) % PACK_COLS.length]);
            pack.position.set(bx, shy + 0.08, hd + 0.5); bg.add(pack);
        }
    }
    // VIDRO tingido (transparente) nas laterais + moldura branca
    for (const sx of [-1, 1]) {
        const pane = new THREE.Mesh(new THREE.BoxGeometry(vW / 2 - 2.6, vH, 0.08), glassV);
        pane.position.set(sx * (vW / 4 + 1.15), vH / 2 + 0.5, hd + 0.72); bg.add(pane);
    }
    for (const mx of [-vW / 2, -2.7, 2.7, vW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, vH, 0.14), milkWhite); ml.position.set(mx, vH / 2 + 0.5, hd + 0.78); bg.add(ml); }
    for (const my of [0.55, vH + 0.45]) { const trr = new THREE.Mesh(new THREE.BoxGeometry(vW, 0.16, 0.14), milkWhite); trr.position.set(0, my, hd + 0.78); bg.add(trr); }
    // PORTA de vidro quente central + mulhões vermelhos + degrau
    const doorW = 4.6, doorH = 4.6;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.3), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.5); bg.add(door);
    for (const mx of [-doorW / 2, 0, doorW / 2]) { const ml = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.4), red); ml.position.set(mx, doorH / 2 + 0.5, hd + 0.6); bg.add(ml); }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.3, 1.2), trim);
    step.position.set(0, 0.15, hd + 0.9); step.receiveShadow = true; bg.add(step);

    // ===== TOLDO (vermelho) inclinado + VALENÇA com o letreiro "CEREAIS & SNACKS" =====
    const awnW = vW + 1.4, awnD = 2.6, awnY = vH + 1.0;
    const awn = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.28, awnD), red);
    awn.position.set(0, awnY, hd + awnD / 2 + 0.1); awn.rotation.x = -0.14; awn.castShadow = true; bg.add(awn);
    const signTex = makeSignTexture(t("CEREAIS & SNACKS"), { bg: "#12331f", fg: "#ffe08a", border: "#d94436", fontPx: 40 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffd98a"), emissiveIntensity: 0.5, roughness: 0.5 });
    const valance = new THREE.Mesh(new THREE.BoxGeometry(awnW, 1.2, 0.12), [red, red, red, red, signMat, red]);
    valance.position.set(0, awnY - 0.35, hd + awnD + 0.05); bg.add(valance);
    // grande EMBLEMA de CAIXA DE CEREAL (arte impressa) sobre a entrada
    const emblem = mkCerealBox(2.6, 2.8, 0.5, "printed");
    emblem.position.set(0, 6.6, hd + 0.3); bg.add(emblem);

    // ===== bolardos brancos c/ topo vermelho flanqueando a entrada =====
    for (const bx of [-1, 1]) {
        const boll = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.4, 1.5, 14), milkWhite);
        boll.position.set(bx * (vW / 2 + 0.6), 0.75, hd + 1.4); boll.castShadow = true; bg.add(boll);
        const cap = new THREE.Mesh(new THREE.SphereGeometry(0.3, 12, 8), red);
        cap.position.set(bx * (vW / 2 + 0.6), 1.6, hd + 1.4); bg.add(cap);
    }

    // ===== TETO: SUPERMERCADO (CPG) — CARRINHO GIGANTE (herói) + GÔNDOLAS (corredores) =====
    const surfaceY = roofY + 0.02;

    /** GÔNDOLA de supermercado: base + espinha vermelha + header amarelo + produtos nos 2 lados. */
    function mkGondola(len) {
        const g = new THREE.Group();
        const GW = 2.6;
        const shelfMat = new THREE.MeshStandardMaterial({ color: "#d2d5ce", roughness: 0.75 });
        const base = new THREE.Mesh(new THREE.BoxGeometry(GW, 0.5, len), shelfMat); base.position.y = 0.25; base.castShadow = true; g.add(base);
        const spine = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.9, len), accentMat("#b83a2c")); spine.position.y = 1.2; g.add(spine);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.34, len), accentMat("#f2c14e")); cap.position.y = 2.25; g.add(cap);
        const n = Math.max(4, Math.round(len / 1.8));
        for (const side of [-1, 1]) {
            for (let k = 0; k < n; k++) {
                const z = -len / 2 + (k + 0.5) * (len / n);
                const kind = (k + (side > 0 ? 1 : 0)) % 3;
                let p;
                if (kind === 0) p = mkCerealBox(1.0, 1.5, 0.6, PACK_COLS[k % PACK_COLS.length]);
                else if (kind === 1) p = mkMilkCarton(0.8, 1.15, 0.8, PACK_COLS[(k + 2) % PACK_COLS.length]);
                else p = mkSnackBag(0.9, 1.4, 0.5, PACK_COLS[(k + 4) % PACK_COLS.length]);
                p.rotation.y = side > 0 ? 0 : Math.PI;
                p.position.set(side * (GW / 2 - 0.55), 0.5, z); g.add(p);
            }
        }
        return g;
    }

    /** CARRINHO DE COMPRAS GIGANTE: cesto de grade metálica + rodas + alça + compras dentro. */
    function mkCart() {
        const g = new THREE.Group();
        const metal = new THREE.MeshStandardMaterial({ color: "#cfd4d9", roughness: 0.4, metalness: 0.65 });
        const dark = new THREE.MeshStandardMaterial({ color: "#2b2f34", roughness: 0.6, metalness: 0.3 });
        const W = 4.6, D = 6.0, H = 2.5, T = 0.14, y0 = 1.7, y1 = 1.7 + 2.5, ym = y0 + H / 2; // D alongado: carrinho mais PROFUNDO (real)
        const bar = (w, h, d, x, y, z, m = metal) => { const me = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); me.position.set(x, y, z); me.castShadow = true; g.add(me); };
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) bar(T, H, T, sx * W / 2, ym, sz * D / 2);        // 4 montantes
        for (const y of [y0, y1]) { bar(W, T, T, 0, y, D / 2); bar(W, T, T, 0, y, -D / 2); bar(T, T, D, W / 2, y, 0); bar(T, T, D, -W / 2, y, 0); } // aros
        bar(W, T * 0.7, T * 0.7, 0, ym, D / 2); bar(W, T * 0.7, T * 0.7, 0, ym, -D / 2); bar(T * 0.7, T * 0.7, D, W / 2, ym, 0); bar(T * 0.7, T * 0.7, D, -W / 2, ym, 0); // faixa horizontal
        for (const gx of [-W * 0.28, 0, W * 0.28]) { bar(T * 0.7, H, T * 0.7, gx, ym, D / 2); bar(T * 0.7, H, T * 0.7, gx, ym, -D / 2); } // verticais frente/trás
        for (const gz of [-D * 0.3, D * 0.3]) { bar(T * 0.7, H, T * 0.7, W / 2, ym, gz); bar(T * 0.7, H, T * 0.7, -W / 2, ym, gz); } // verticais laterais
        bar(W - T, T, D - T, 0, y0, 0);                                                                     // fundo do cesto
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) {                                               // pernas + rodas
            bar(T * 1.3, y0 - 0.5, T * 1.3, sx * W * 0.4, (y0 - 0.5) / 2 + 0.5, sz * D * 0.36, dark);
            const wh = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.28, 16), dark); wh.rotation.z = Math.PI / 2; wh.position.set(sx * W * 0.4, 0.5, sz * D * 0.36); wh.castShadow = true; g.add(wh);
        }
        for (const sx of [-1, 1]) bar(T, 1.4, T, sx * W * 0.44, y1 + 0.7, -D / 2 - 0.12);                   // alça (2 postes)
        bar(W * 0.94, T * 1.3, T * 1.3, 0, y1 + 1.4, -D / 2 - 0.12);                                        // alça (travessa)
        for (const [px, pz, kind, col] of [[-1.3, 0.5, 0, "#e8563f"], [0.2, -0.6, 2, "#f2c14e"], [1.4, 0.5, 1, "#3f9fd0"], [-0.2, 1.0, 0, "#8bc34a"], [1.0, -0.6, 2, "#c25bb0"]]) {
            let p; // COMPRAS saindo do cesto
            if (kind === 0) p = mkCerealBox(1.1, 1.9, 0.75, col);
            else if (kind === 1) p = mkMilkCarton(0.9, 1.6, 0.9, col);
            else p = mkSnackBag(1.0, 1.8, 0.55, col);
            p.position.set(px, y0 + 1.2, pz); g.add(p);
        }
        return g;
    }

    for (const sx of [-6.2, 6.2]) { const gon = mkGondola(13); gon.position.set(sx, surfaceY, 0); bg.add(gon); } // 2 corredores
    const gcart = mkCart(); gcart.rotation.y = -0.58; gcart.position.set(0, surfaceY, 0.3); bg.add(gcart);         // carrinho gigante ao centro (~35° ESPELHADO na horizontal: era +0.58 anti-horário)
}

/**
 * QUADRA-PARQUE = a MESMA PRAÇA das outras cidades (pedido do cliente), replicada na célula:
 * FONTE central + 4 BANQUINHOS virados p/ o centro + PLACA do WhatsApp ao fundo + caminho em CRUZ
 * + ÁRVORES nos 4 quadrantes. Reusa makeFountain/makeBench/makeWhatsAppSign do Decor.js.
 * Sem prédio; o canteiro de grama por baixo é desenhado pelo CityTexture.
 */
function buildPark(bg, b, shared, rand) {
    const hw = b.w / 2, hd = b.d / 2;
    const trunkMat = new THREE.MeshStandardMaterial({ color: "#5a3d24", roughness: 0.9 });
    const leaves = ["#2f9e44", "#37b24d", "#40c057", "#2b8a3e"].map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.8 }));
    // O chão da praça (cruz pavimentada + largo central ladrilhado) é desenhado na TEXTURA
    // (CityTexture.drawPlazaGround), igual às praças das outras cidades — aqui só entram os
    // objetos 3D: FONTE central + BANQUINHOS encarando a fonte + PLACA do WhatsApp ao fundo +
    // ÁRVORES nos 4 quadrantes de grama.
    const PW = 3.6; // largura da cruz na textura (para posicionar árvores fora dela)
    const RLARGO = 7; // raio do largo ladrilhado (mesmo padrão das outras cidades; casa com CityTexture)
    // COR DO DISTRITO: entra na água/bulbo/halo da FONTE e no LED do letreiro.
    const distColor = b.bed && b.bed.district ? b.bed.district.color : null;
    bg.add(makeFountain(0, 0, distColor));
    // BANCOS ao redor da fonte no MESMO padrão das outras cidades (sobre o largo R=7, a ±4.4)
    const BENCH_OFF = 4.4;
    const benches = [[BENCH_OFF, BENCH_OFF], [-BENCH_OFF, BENCH_OFF], [BENCH_OFF, -BENCH_OFF], [-BENCH_OFF, -BENCH_OFF]];
    for (const [sx, sz] of benches) {
        const bench = makeBench(); bench.position.set(sx, 0, sz); bench.rotation.y = Math.atan2(-sx, -sz); bg.add(bench);
    }
    // BANNER do WhatsApp: escala base p/ ~80% da largura do canteiro; widthMul aumenta a LARGURA
    // +20% (fica ~96% do canteiro) e heightMul reduz a ALTURA à metade (outdoor panorâmico).
    // z=-4.5: trazido 50% mais para a frente (metade da distância antiga de -9 até a fonte em z=0).
    const signZ = -4.5;
    const BANNER_WMUL = 1.2, BANNER_HMUL = 0.4; // altura do painel −20% (era 0.5)
    // Painel de LED (sem logo): a frase do DISTRITO rola na horizontal como um letreiro luminoso.
    const DISTRICT_BANNER = {
        1: 'Finance district',
        2: 'Consumer packaged goods district',
        3: 'Auto, travel, education & real estate district',
        4: 'Retail & e-commerce district'
    };
    const distId = b.bed && b.bed.district ? b.bed.district.id : null;
    const BANNER_TEXT = (DISTRICT_BANNER[distId] || 'WhatsApp Business') + '   •   ';
    const bannerScale = ((b.bed ? b.bed.w : b.w) * 0.8) / 8.4;
    const signPX = 3 * BANNER_WMUL * bannerScale; // x dos 2 mastros (x=±3×widthMul no modelo × scale)
    bg.add(makeWhatsAppSign(0, signZ, bannerScale, { widthMul: BANNER_WMUL, heightMul: BANNER_HMUL, text: BANNER_TEXT, color: distColor }));

    // VEGETAÇÃO dos 4 quadrantes de grama: ÁRVORES + ARBUSTOS preenchendo a área verde até perto das
    // bordas, SEM cair sobre a cruz, o largo/fonte, os bancos, os postes de luz ou a placa. `free()`
    // também impede que os próprios elementos se sobreponham (guarda os já colocados em `placed`).
    const bedHW = (b.bed ? b.bed.w : b.w) / 2; // canteiro CHEIO — postes de luz seguem os cantos do bed
    const bedHD = (b.bed ? b.bed.d : b.d) / 2;
    // grama JUSTA (26×26): vegetação e fonte ficam DENTRO dela (mesma proporção dos prédios)
    const gHW = (b.bed && b.bed.grass ? b.bed.grass.w : (b.bed ? b.bed.w : b.w)) / 2;
    const gHD = (b.bed && b.bed.grass ? b.bed.grass.d : (b.bed ? b.bed.d : b.d)) / 2;
    const posts = [ // postes de luz nos cantos do canteiro (recuo 5.4, ver LightPosts.js) + postes do banner
        [bedHW - 5.4, bedHD - 5.4], [-(bedHW - 5.4), bedHD - 5.4],
        [bedHW - 5.4, -(bedHD - 5.4)], [-(bedHW - 5.4), -(bedHD - 5.4)],
        [signPX, signZ], [-signPX, signZ]
    ];
    // A CIDADE também cria 2 bancos "de calçada" à frente do parque (Benches.js, mesma fórmula abaixo);
    // o buildPark não os desenha, mas a vegetação precisa evitá-los para não plantar em cima deles.
    const cityBenchOffX = Math.min(b.w * 0.34, 7);
    const avoidBenches = [...benches, [cityBenchOffX, hd + 2.6], [-cityBenchOffX, hd + 2.6]];
    const placed = [];
    const free = (x, z, rad) => {
        // folga = raio do próprio elemento, p/ a COPA não invadir os caminhos cinzas (cruz + largo)
        if (Math.abs(x) < PW / 2 + rad + 0.6 || Math.abs(z) < PW / 2 + rad + 0.6) return false; // fora da cruz
        if (Math.hypot(x, z) < RLARGO + rad + 0.4) return false;                                 // fora do largo/fonte
        for (const [bx, bz] of avoidBenches) if (Math.hypot(x - bx, z - bz) < 3.3) return false; // vão livre p/ não encostar no banco
        for (const [px, pz] of posts) if (Math.hypot(x - px, z - pz) < 2.6) return false;
        for (const [ox, oz, orad] of placed) if (Math.hypot(x - ox, z - oz) < rad + orad) return false;
        return true;
    };
    const jit = (v) => v + (rand() - 0.5) * 1.1;

    const mkTree = (s, li) => {
        const g = new THREE.Group();
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.16 * s, 0.22 * s, 1.0 * s, 7), trunkMat); trunk.position.y = 0.5 * s; trunk.castShadow = true; g.add(trunk);
        const c1 = new THREE.Mesh(new THREE.ConeGeometry(0.95 * s, 1.7 * s, 9), leaves[li % leaves.length]); c1.position.y = 1.55 * s; c1.castShadow = true; g.add(c1);
        const c2 = new THREE.Mesh(new THREE.ConeGeometry(0.72 * s, 1.3 * s, 9), leaves[(li + 1) % leaves.length]); c2.position.y = 2.2 * s; c2.castShadow = true; g.add(c2);
        return g;
    };
    // ARBUSTOS baixos: aglomerado de 3 lóbulos facetados (low-poly), distintos das árvores cônicas
    const mkBush = (s, li) => {
        const g = new THREE.Group();
        for (let i = 0; i < 3; i++) {
            const lr = (0.62 - i * 0.11) * s;
            const m = new THREE.Mesh(new THREE.IcosahedronGeometry(lr, 0), leaves[(li + i) % leaves.length]);
            m.position.set((i - 1) * 0.42 * s, 0.36 * s, (i % 2 ? 0.16 : -0.14) * s);
            m.castShadow = true; g.add(m);
        }
        return g;
    };

    let li = 0;
    // passada 1 — ÁRVORES numa grade esparsa (dentro da grama justa)
    for (let x = -gHW + 2.4; x <= gHW - 2.4; x += 3.7) {
        for (let z = -gHD + 2.4; z <= gHD - 2.4; z += 3.7) {
            const px = jit(x), pz = jit(z), rad = 1.35;
            if (!free(px, pz, rad)) continue;
            const t = mkTree(0.85 + rand() * 0.45, li++);
            t.position.set(px, 0, pz); t.rotation.y = rand() * Math.PI * 2; bg.add(t);
            placed.push([px, pz, rad]);
        }
    }
    // passada 2 — ARBUSTOS preenchendo os vãos (grade mais densa e deslocada, dentro da grama justa)
    for (let x = -gHW + 3.0; x <= gHW - 3.0; x += 2.6) {
        for (let z = -gHD + 3.0; z <= gHD - 3.0; z += 2.6) {
            const px = jit(x), pz = jit(z), rad = 0.95;
            if (!free(px, pz, rad)) continue;
            const bsh = mkBush(0.75 + rand() * 0.4, li++);
            bsh.position.set(px, 0, pz); bsh.rotation.y = rand() * Math.PI * 2; bg.add(bsh);
            placed.push([px, pz, rad]);
        }
    }
}

/**
 * Rack RASO de halteres (cabe na vitrine como display, igual aos manequins):
 * frame metálico de 3 prateleiras + halteres (reusa makeDumbbell em escala).
 */
function makeDumbbellRack(shared) {
    const g = new THREE.Group();
    const metal = new THREE.MeshStandardMaterial({ color: "#cfd6db", roughness: 0.4, metalness: 0.6 });
    for (const sx of [-1, 1]) {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.16, 2.5, 0.5), metal);
        leg.position.set(sx * 1.75, 1.25, 0);
        g.add(leg);
    }
    for (let t = 0; t < 3; t++) {
        const ty = 0.6 + t * 0.62;
        const shelf = new THREE.Mesh(new THREE.BoxGeometry(3.7, 0.12, 0.5), metal);
        shelf.position.set(0, ty, 0);
        g.add(shelf);
        for (const dx of [-0.95, 0.95]) {
            const d = makeDumbbell(shared);
            d.scale.setScalar(0.42);
            d.position.set(dx, ty + 0.34, 0); // pousado sobre a prateleira
            g.add(d);
        }
    }
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Academia (Acquisition Town · C1): GINÁSIO na paleta verde — vitrines no térreo
 * com RACKS DE HALTERES, janelas de academia acesas nos andares, HALTER GIGANTE +
 * pilhas de anilhas na cobertura (marco) e marquise verde.
 */
function buildAcademia(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const accent = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.7 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const gymFloor = new THREE.MeshStandardMaterial({
        color: "#3a2e26",
        emissive: new THREE.Color("#ffe2a8"),
        emissiveIntensity: 0.34,
        roughness: 0.75,
    });

    // corpo + base recuada + cornija (verde-escuro) + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 1.0, b.d + 0.2), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 0.9, b.d + 0.9), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);
    const roofY = b.h + 0.5;

    // FRENTE térreo: 2 VITRINES de academia (equipamentos) + entrada central
    const vH = 5.1;
    const vY0 = 0.9;
    const vCY = vY0 + vH / 2;
    // vidro transparente da vitrine (gear fica ATRÁS dele, dentro da caixa)
    const glassPane = new THREE.MeshStandardMaterial({
        color: "#bfe6cf",
        transparent: true,
        opacity: 0.22,
        roughness: 0.15,
        depthWrite: false,
    });
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 4.7;
        // fundo iluminado (gym floor) na parede
        const backing = new THREE.Mesh(new THREE.BoxGeometry(4.7, vH, 0.2), gymFloor);
        backing.position.set(wx, vCY, hd - 0.05);
        bg.add(backing);
        // RACK de halteres DENTRO da caixa, recuado atrás do vidro
        const eq = makeDumbbellRack(shared);
        eq.scale.setScalar(1.15);
        eq.position.set(wx, vY0 + 0.2, hd + 0.4);
        eq.rotation.y = sx * -0.18;
        bg.add(eq);
        // CAIXA da vitrine PROJETADA: laterais + topo + peitoril (paredes de profundidade)
        for (const ox of [-2.45, 2.45]) {
            const sidew = new THREE.Mesh(new THREE.BoxGeometry(0.4, vH + 0.3, 0.95), edgeMat);
            sidew.position.set(wx + ox, vCY, hd + 0.4);
            sidew.castShadow = true;
            bg.add(sidew);
        }
        const topw = new THREE.Mesh(new THREE.BoxGeometry(5.3, 0.5, 0.95), edgeMat);
        topw.position.set(wx, vCY + vH / 2 + 0.15, hd + 0.4);
        topw.castShadow = true;
        bg.add(topw);
        const sillw = new THREE.Mesh(new THREE.BoxGeometry(5.3, 0.5, 1.0), trim);
        sillw.position.set(wx, vY0 - 0.05, hd + 0.42);
        bg.add(sillw);
        // VIDRO transparente na frente da caixa
        const glass = new THREE.Mesh(new THREE.BoxGeometry(4.7, vH, 0.06), glassPane);
        glass.position.set(wx, vCY, hd + 0.82);
        bg.add(glass);
    }
    // entrada central: portas de vidro + moldura + mulhão + marquise verde + halter
    const doorH2 = vH - 0.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(3.2, doorH2, 0.4), warmGlass);
    door.position.set(0, vY0 + doorH2 / 2, hd + 0.05);
    bg.add(door);
    const dframe = new THREE.Mesh(new THREE.BoxGeometry(3.9, doorH2 + 0.5, 0.3), edgeMat);
    dframe.position.set(0, vY0 + (doorH2 + 0.5) / 2 - 0.1, hd + 0.12);
    bg.add(dframe);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH2, 0.5), edgeMat);
    dmull.position.set(0, vY0 + doorH2 / 2, hd + 0.2);
    bg.add(dmull);
    const marq = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.4, 2.0), accent);
    marq.position.set(0, vY0 + doorH2 + 0.55, hd + 1.0);
    marq.castShadow = true;
    bg.add(marq);

    // ANDARES SUPERIORES: janelas de academia acesas. +X vira PAREDE DE ESCALADA
    // (sem janelas); -X deixa a coluna central livre p/ as ANILHAS gigantes
    const faces = [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ];
    for (const f of faces) {
        if (f.nx === 1) continue;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const rows = f.nz === 1 ? [8.2, 11.4] : [3.6, 8.2, 11.4];
        const us = f.nx === -1 ? [-5.5, 5.5] : [-5.5, -1.85, 1.85, 5.5];
        for (const wy of rows) {
            for (const u of us) {
                const win = makeClassWindow(2.4, 2.4, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // LATERAL +X: PAREDE DE ESCALADA (painel + agarras coloridas + viga de topo)
    const climbPanel = new THREE.Mesh(new THREE.BoxGeometry(0.4, 12, 9.4), new THREE.MeshStandardMaterial({ color: "#46504a", roughness: 0.95 }));
    climbPanel.position.set(hw + 0.15, 7, 0);
    climbPanel.castShadow = true;
    climbPanel.receiveShadow = true;
    bg.add(climbPanel);
    const climbBeam = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 9.8), new THREE.MeshStandardMaterial({ color: "#cfd6db", roughness: 0.4, metalness: 0.6 }));
    climbBeam.position.set(hw + 0.3, 13.1, 0);
    climbBeam.castShadow = true;
    bg.add(climbBeam);
    const holdColors = ["#e0473c", "#3f7fe0", "#f2c33c", "#e07f2c", "#36b0c8", "#c84fb0", "#5ed94f"];
    const holds = [
        [-3.5, 2.2],
        [-1.4, 3.0],
        [0.6, 2.4],
        [2.9, 3.4],
        [3.6, 5.2],
        [-3.2, 4.8],
        [-0.8, 5.6],
        [1.7, 6.2],
        [-2.6, 7.4],
        [0.2, 8.0],
        [2.5, 8.6],
        [3.4, 9.8],
        [-3.6, 9.2],
        [-1.2, 10.4],
        [1.1, 11.0],
        [-2.2, 11.8],
    ];
    holds.forEach(([hz, hy], i) => {
        const hold = new THREE.Mesh(new THREE.IcosahedronGeometry(0.3, 0), new THREE.MeshStandardMaterial({ color: holdColors[i % holdColors.length], roughness: 0.6, flatShading: true }));
        hold.position.set(hw + 0.55, hy, hz);
        hold.castShadow = true;
        bg.add(hold);
    });

    // LATERAL -X: ANILHAS coloridas gigantes (mural de peso) na coluna central
    const plMural = ["#e0473c", "#3f7fe0", "#f2c33c"];
    [4.2, 7.5, 10.8].forEach((py, i) => {
        const plate = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.35, 0.45, 24), new THREE.MeshStandardMaterial({ color: plMural[i], roughness: 0.55 }));
        plate.rotation.z = Math.PI / 2;
        plate.position.set(-(hw + 0.22), py, 0);
        plate.castShadow = true;
        bg.add(plate);
        const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.55, 16), dark);
        hub.rotation.z = Math.PI / 2;
        hub.position.set(-(hw + 0.28), py, 0);
        bg.add(hub);
    });

    // COBERTURA: PISTA DE COOPER QUADRADA (acompanha o teto) protegida por GRADIL;
    // VERDE-ESCURO da paleta (era terracota) + raias quadradas brancas (sem halteres)
    const trackMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.95 });
    const laneMat = new THREE.MeshStandardMaterial({ color: "#eef3ee", roughness: 0.85 });
    // quadrado de CANTOS ARREDONDADOS; cantos concêntricos (centro em ±k) p/ a
    // banda/raias ficarem paralelas e as curvas harmônicas
    const rrect = (p, H, R, cw) => {
        const s = H - R; // metade do trecho reto
        if (!cw) {
            p.moveTo(-s, -H);
            p.lineTo(s, -H);
            p.quadraticCurveTo(H, -H, H, -s);
            p.lineTo(H, s);
            p.quadraticCurveTo(H, H, s, H);
            p.lineTo(-s, H);
            p.quadraticCurveTo(-H, H, -H, s);
            p.lineTo(-H, -s);
            p.quadraticCurveTo(-H, -H, -s, -H);
        } else {
            p.moveTo(-s, -H);
            p.quadraticCurveTo(-H, -H, -H, -s);
            p.lineTo(-H, s);
            p.quadraticCurveTo(-H, H, -s, H);
            p.lineTo(s, H);
            p.quadraticCurveTo(H, H, H, s);
            p.lineTo(H, -s);
            p.quadraticCurveTo(H, -H, s, -H);
        }
    };
    // offset do centro do canto (menor = mais arredondado). Menor que a banda p/ a
    // borda INTERNA também ganhar raio (não ficar quase reta nos cantos)
    const k = 3.5;
    const sqRing = (outerH, innerH) => {
        const sh = new THREE.Shape();
        rrect(sh, outerH, outerH - k, false);
        const hole = new THREE.Path();
        rrect(hole, innerH, Math.max(0.3, innerH - k), true);
        sh.holes.push(hole);
        return new THREE.ShapeGeometry(sh, 12);
    };
    // banda mais LARGA (externa quase no gradil, interna avançando no miolo) p/ as
    // 4 faixas ficarem bem mais evidentes
    const track = new THREE.Mesh(sqRing(8.6, 4.6), trackMat);
    track.rotation.x = -Math.PI / 2;
    track.position.set(0, roofY + 0.06, 0);
    track.receiveShadow = true;
    bg.add(track);
    for (const rr of [5.6, 6.6, 7.6]) {
        const lane = new THREE.Mesh(sqRing(rr + 0.07, rr - 0.07), laneMat);
        lane.rotation.x = -Math.PI / 2;
        lane.position.set(0, roofY + 0.11, 0);
        bg.add(lane);
    }
    // SÍMBOLO de KETTLEBELL pintado no centro da pista — verde-escuro (cor da pista)
    // com BORDA EXTERNA (verde médio) e BORDA INTERNA/detalhe (verde claro): 3 tons
    const ketDark = trackMat; // buildingRoofEdge
    const ketMid = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.9 });
    const ketLite = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.9 });
    const Rb = 1.6;
    const hIn = 0.6;
    const hOut = 1.05;
    const hY = 0.85;
    const bY = -0.5;
    const ket = new THREE.Group();
    // borda EXTERNA (verde médio) — silhueta maior, por baixo
    const obBody = new THREE.Mesh(new THREE.CircleGeometry(Rb + 0.22, 32), ketMid);
    obBody.position.set(0, bY, 0);
    ket.add(obBody);
    const obHandle = new THREE.Mesh(new THREE.RingGeometry(hIn - 0.2, hOut + 0.2, 28, 1, 0, Math.PI), ketMid);
    obHandle.position.set(0, hY, 0.002);
    ket.add(obHandle);
    // corpo + alça PRINCIPAIS (verde escuro)
    const kBody = new THREE.Mesh(new THREE.CircleGeometry(Rb, 32), ketDark);
    kBody.position.set(0, bY, 0.01);
    ket.add(kBody);
    const kHandle = new THREE.Mesh(new THREE.RingGeometry(hIn, hOut, 28, 1, 0, Math.PI), ketDark);
    kHandle.position.set(0, hY, 0.012);
    ket.add(kHandle);
    // borda INTERNA / detalhe (verde claro): anel interno no corpo + realce na alça
    const innerRing = new THREE.Mesh(new THREE.RingGeometry(Rb - 0.42, Rb - 0.28, 32), ketLite);
    innerRing.position.set(0, bY, 0.02);
    ket.add(innerRing);
    const handleHi = new THREE.Mesh(new THREE.RingGeometry(hIn + 0.04, hIn + 0.17, 24, 1, 0, Math.PI), ketLite);
    handleHi.position.set(0, hY, 0.022);
    ket.add(handleHi);
    ket.scale.setScalar(1.7); // maior, p/ preencher melhor o miolo da pista
    ket.rotation.x = -Math.PI / 2;
    ket.position.set(0, roofY + 0.1, 0);
    bg.add(ket);
    const perim = hw; // gradil na borda do teto, em volta da pista
    const postH = 1.7;
    const railPosts = [
        [-perim, -perim],
        [perim, -perim],
        [perim, perim],
        [-perim, perim],
        [0, -perim],
        [0, perim],
        [-perim, 0],
        [perim, 0],
    ];
    for (const [px, pz] of railPosts) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.42, postH, 0.42), trim);
        post.position.set(px, roofY + postH / 2, pz);
        post.castShadow = true;
        bg.add(post);
    }
    for (const [fixed, v] of [
        ["z", perim],
        ["z", -perim],
        ["x", perim],
        ["x", -perim],
    ]) {
        const horiz = fixed === "z";
        for (const ry2 of [roofY + 0.45, roofY + 1.5]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(horiz ? 2 * perim : 0.18, 0.16, horiz ? 0.18 : 2 * perim), trim);
            rail.position.set(horiz ? 0 : v, ry2, horiz ? v : 0);
            bg.add(rail);
        }
        const nb = 11;
        for (let i = 1; i < nb; i++) {
            const u = -perim + 2 * perim * (i / nb);
            const pick = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.5, 0.1), edgeMat);
            pick.position.set(horiz ? u : v, roofY + 0.8, horiz ? v : u);
            bg.add(pick);
        }
    }

    // (placa "ACADEMIA" REMOVIDA — identidade vem do halter gigante na cobertura)
}

/** Manequim / dress form da boutique: base + poste + saia + busto ("vestido"). */
function makeMannequin(dressColor) {
    const g = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({ color: "#3a3030", roughness: 0.5, metalness: 0.4 });
    const dress = new THREE.MeshStandardMaterial({ color: dressColor, roughness: 0.6 });
    const cream = new THREE.MeshStandardMaterial({ color: "#e8e2d0", roughness: 0.7 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.42, 0.12, 12), dark);
    base.position.y = 0.06;
    g.add(base);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.1, 8), dark);
    pole.position.y = 0.65;
    g.add(pole);
    const skirt = new THREE.Mesh(new THREE.ConeGeometry(0.72, 1.9, 16), dress);
    skirt.position.y = 1.9;
    g.add(skirt);
    const bust = new THREE.Mesh(new THREE.SphereGeometry(0.5, 14, 10), dress);
    bust.scale.set(1, 1.15, 0.72);
    bust.position.y = 2.95;
    g.add(bust);
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.16, 0.35, 8), cream);
    neck.position.y = 3.45;
    g.add(neck);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Manequim de CAMISETA (torso + mangas curtas) — variação do dress form. */
function makeTshirtMannequin(shirtColor) {
    const g = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({ color: "#3a3030", roughness: 0.5, metalness: 0.4 });
    const shirt = new THREE.MeshStandardMaterial({ color: shirtColor, roughness: 0.6 });
    const cream = new THREE.MeshStandardMaterial({ color: "#e8e2d0", roughness: 0.7 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.42, 0.12, 12), dark);
    base.position.y = 0.06;
    g.add(base);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.6, 8), dark);
    pole.position.y = 0.9;
    g.add(pole);
    const torso = new THREE.Mesh(new THREE.BoxGeometry(1.25, 1.5, 0.58), shirt);
    torso.position.y = 2.35;
    g.add(torso);
    for (const sx of [-1, 1]) {
        const sleeve = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.5, 0.62), shirt);
        sleeve.position.set(sx * 0.84, 2.85, 0);
        g.add(sleeve);
    }
    const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 0.35, 8), cream);
    collar.position.y = 3.25;
    g.add(collar);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Boutique (Acquisition Town · B4): loja de moda elegante — VITRINES com
 * MANEQUINS, toldo chique (vinho) com babado, janelas refinadas de moldura
 * dourada, cornija dourada, floreiras, placa "BOUTIQUE" e letreiro pendurado.
 */
function buildBoutique(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    // molduras/detalhes em VERDE-ESCURO da paleta (eram dourados) + toldo no VERDE
    // do WhatsApp (era vinho) — boutique 100% na paleta de branding verde
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.75 });
    const awnMat = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.7 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#3a2e26",
        emissive: new THREE.Color("#ffe2a8"),
        emissiveIntensity: 0.32,
        roughness: 0.75,
    });

    // corpo + base + cornija DOURADA + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    // base recuada (frente em +0.1, não +0.2): evita que a face do plinto fique
    // COPLANAR com o fundo iluminado das vitrines (z-fighting = piscar verde)
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 1.0, b.d + 0.2), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.2, 0.9, b.d + 1.2), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);

    // VITRINES (térreo, frente): 2 vitrines iluminadas com MANEQUINS + entrada central
    const vH = 4.4;
    const vCY = 0.6 + vH / 2;
    const dressColors = ["#d24a6e", "#5b8def"];
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 4.6;
        const interior = new THREE.Mesh(new THREE.BoxGeometry(4.0, vH, 0.3), interiorMat);
        interior.position.set(wx, vCY, hd + 0.05);
        bg.add(interior);
        const fr = [
            [4.6, 0.4, 0, vH / 2 + 0.2],
            [4.6, 0.4, 0, -vH / 2 - 0.2],
            [0.4, vH, -2.3, 0],
            [0.4, vH, 2.3, 0],
        ];
        for (const [bw, bh, ox, oy] of fr) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.4), edgeMat);
            m.position.set(wx + ox, vCY + oy, hd + 0.12);
            bg.add(m);
        }
        const man = makeMannequin(dressColors[s]);
        man.scale.setScalar(0.92);
        man.position.set(wx, 0.6, hd + 0.35);
        man.rotation.y = sx * 0.25;
        bg.add(man);
    }
    // entrada central de vidro + moldura dourada + mulhão
    const door = new THREE.Mesh(new THREE.BoxGeometry(2.6, vH - 0.2, 0.4), warmGlass);
    door.position.set(0, (vH - 0.2) / 2 + 0.6, hd + 0.12);
    bg.add(door);
    const dframe = new THREE.Mesh(new THREE.BoxGeometry(3.2, vH + 0.3, 0.3), edgeMat);
    dframe.position.set(0, (vH + 0.3) / 2 + 0.45, hd + 0.06);
    bg.add(dframe);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.14, vH - 0.2, 0.5), edgeMat);
    dmull.position.set(0, (vH - 0.2) / 2 + 0.6, hd + 0.2);
    bg.add(dmull);

    // TOLDO chique (vinho) inclinado + BABADO recortado sobre as vitrines
    const awn = new THREE.Mesh(new THREE.BoxGeometry(b.w - 0.4, 0.35, 2.9), awnMat);
    awn.position.set(0, vH + 1.4, hd + 1.35);
    awn.rotation.x = 0.32;
    awn.castShadow = true;
    bg.add(awn);
    const nflap = 7;
    const flapW = ((b.w - 0.4) / nflap) * 0.86;
    for (let i = 0; i < nflap; i++) {
        const fx = (-0.5 + (i + 0.5) / nflap) * (b.w - 0.4);
        const flap = new THREE.Mesh(new THREE.BoxGeometry(flapW, 0.75, 0.12), awnMat);
        flap.position.set(fx, vH + 0.95, hd + 2.65);
        bg.add(flap);
    }

    // FLOREIRAS ladeando a entrada
    for (const sx of [-1, 1]) {
        const planter = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.7, 0.9), edgeMat);
        planter.position.set(sx * 2.6, 0.35, hd + 0.9);
        planter.castShadow = true;
        bg.add(planter);
        const flowers = new THREE.Mesh(new THREE.IcosahedronGeometry(0.6, 0), new THREE.MeshStandardMaterial({ color: "#e06aa0", roughness: 0.8, flatShading: true }));
        flowers.position.set(sx * 2.6, 1.0, hd + 0.9);
        bg.add(flowers);
    }

    // JANELAS refinadas (moldura dourada): frente (andar de cima) + laterais/fundo
    const upperY = b.h - 2.6;
    // 4 janelas no andar de cima (frente): MENORES e centradas na faixa, deixando
    // FOLGA acima (até a cornija/teto) e abaixo (até o toldo)
    for (const wx of [-6.5, -2.2, 2.2, 6.5]) {
        const win = makeClassWindow(2.0, 1.9, warmGlass, edgeMat);
        win.position.set(wx, 7.8, hd + 0.12);
        bg.add(win);
    }
    for (const f of [
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else ry = -Math.PI / 2;
        for (const wy of [3.0, upperY]) {
            for (const u of [-4.6, 0, 4.6]) {
                const win = makeClassWindow(2.0, 2.6, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // LETREIRO PENDURADO ao lado da entrada (banner sobre o toldo REMOVIDO)
    const signTex = makeSignTexture(t("BOUTIQUE"), { bg: "#2a1420", fg: "#f3c9d8", border: "#9c2b4d" });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#f3c9d8"),
        emissiveIntensity: 0.6,
        roughness: 0.8,
    });
    const bracket = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.12, 0.12), edgeMat);
    bracket.position.set(hw - 0.4, vH + 0.9, hd + 0.7);
    bg.add(bracket);
    const hangSign = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.0, 0.15), [dark, dark, dark, dark, signMat, signMat]);
    hangSign.position.set(hw - 1.15, vH + 0.3, hd + 0.7);
    hangSign.castShadow = true;
    bg.add(hangSign);

    // ───────── COBERTURA: terraço chique de boutique (não mais lisa) ─────────
    const slabTop = b.h + 0.5; // topo da laje
    const cornY = b.h + 0.15; // topo da cornija = base do gradil
    const perim = hw + 0.4;
    const bulbMat = new THREE.MeshStandardMaterial({
        color: "#fff2c8",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.95,
        roughness: 0.5,
    });
    const topiaryMat = new THREE.MeshStandardMaterial({ color: "#3fbf63", roughness: 0.85, flatShading: true });

    // 1) GRADIL/balaustrada elegante no perímetro (postes + 2 trilhos + balaústres)
    const postH = 1.25;
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.55, postH + 0.4, 0.55), trim);
            post.position.set(sx * perim, cornY + (postH + 0.4) / 2, sz * perim);
            post.castShadow = true;
            bg.add(post);
            const cap = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.24, 0.72), edgeMat);
            cap.position.set(sx * perim, cornY + postH + 0.55, sz * perim);
            bg.add(cap);
        }
    }
    for (const [fixed, v] of [
        ["z", perim],
        ["z", -perim],
        ["x", perim],
        ["x", -perim],
    ]) {
        const horiz = fixed === "z";
        for (const ry2 of [cornY + 0.45, cornY + postH + 0.15]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(horiz ? 2 * perim - 1.0 : 0.26, 0.2, horiz ? 0.26 : 2 * perim - 1.0), trim);
            rail.position.set(horiz ? 0 : v, ry2, horiz ? v : 0);
            bg.add(rail);
        }
        const nb = 7;
        for (let i = 1; i < nb; i++) {
            const u = -perim + 2 * perim * (i / nb);
            const bal = new THREE.Mesh(new THREE.BoxGeometry(0.15, postH - 0.15, 0.15), edgeMat);
            bal.position.set(horiz ? u : v, cornY + (postH - 0.15) / 2 + 0.45, horiz ? v : u);
            bg.add(bal);
        }
    }

    // 2) PENTHOUSE / 2º pavimento no teto (~78% do teto) — showroom de boutique:
    //    VITRINES com MANEQUINS na frente, janelas de moldura nas faces e CÚPULA
    const php = 7; // meia-largura (14 de 18 ≈ 78%)
    const phBot = slabTop - 0.2; // afunda 0.2 na laje → sem coplanar
    const phH = 4.4;
    const phMid = phBot + phH / 2;
    const phTop = phBot + phH;
    const phBody = new THREE.Mesh(new THREE.BoxGeometry(php * 2, phH, php * 2), wallMat);
    phBody.position.set(0, phMid, 0);
    phBody.castShadow = true;
    phBody.receiveShadow = true;
    bg.add(phBody);
    const phBaseM = new THREE.Mesh(new THREE.BoxGeometry(php * 2 + 0.5, 0.6, php * 2 + 0.5), trim);
    phBaseM.position.set(0, phBot + 0.3, 0);
    bg.add(phBaseM);
    const phCorn = new THREE.Mesh(new THREE.BoxGeometry(php * 2 + 0.8, 0.7, php * 2 + 0.8), edgeMat);
    phCorn.position.set(0, phTop - 0.15, 0);
    phCorn.castShadow = true;
    bg.add(phCorn);
    // FRENTE (+Z): 2 vitrines iluminadas com manequins (acima da base → sem coplanar)
    const pvY0 = phBot + 0.7;
    const pvH = 3.0;
    const pvCY = pvY0 + pvH / 2;
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 3.8;
        const inter = new THREE.Mesh(new THREE.BoxGeometry(3.0, pvH, 0.3), interiorMat);
        inter.position.set(wx, pvCY, php + 0.05);
        bg.add(inter);
        const fr = [
            [3.5, 0.35, 0, pvH / 2 + 0.17],
            [3.5, 0.35, 0, -pvH / 2 - 0.17],
            [0.35, pvH, -1.75, 0],
            [0.35, pvH, 1.75, 0],
        ];
        for (const [fw, fh, ox, oy] of fr) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(fw, fh, 0.4), edgeMat);
            m.position.set(wx + ox, pvCY + oy, php + 0.12);
            bg.add(m);
        }
        // andar de cima: manequins de CAMISETA em 2 cores NOVAS (≠ dos do térreo)
        const man = makeTshirtMannequin(s ? "#9b6dd6" : "#ef8a3c");
        man.scale.setScalar(0.72);
        man.position.set(wx, pvY0, php + 0.32);
        man.rotation.y = sx * 0.22;
        bg.add(man);
    }
    // janelas de moldura: 1 no centro da frente + 4 em cada uma das outras 3 faces
    const winFaces = [
        ["z", 1, [0]],
        ["z", -1, [-5, -1.7, 1.7, 5]],
        ["x", 1, [-5, -1.7, 1.7, 5]],
        ["x", -1, [-5, -1.7, 1.7, 5]],
    ];
    for (const [fixed, sgn, us] of winFaces) {
        const horiz = fixed === "z";
        const fd = php + 0.12;
        for (const u of us) {
            const win = makeClassWindow(2.0, 2.8, warmGlass, edgeMat);
            win.rotation.y = horiz ? (sgn > 0 ? 0 : Math.PI) : sgn > 0 ? Math.PI / 2 : -Math.PI / 2;
            win.position.set(horiz ? u : sgn * fd, phMid, horiz ? sgn * fd : u);
            bg.add(win);
        }
    }
    // CÚPULA: telhado de 4 águas verde-escuro (como os outros tetos) + pináculo
    const phHipH = 2.6;
    const phHip = new THREE.Mesh(new THREE.CylinderGeometry(1.9, (php + 0.6) * Math.SQRT2, phHipH, 4), edgeMat);
    phHip.rotation.y = Math.PI / 4;
    phHip.position.set(0, phTop + phHipH / 2, 0);
    phHip.castShadow = true;
    phHip.receiveShadow = true;
    bg.add(phHip);
    const phRidge = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.3, 2.6), trim);
    phRidge.position.set(0, phTop + phHipH, 0);
    bg.add(phRidge);
    const phFin = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 8), trim);
    phFin.position.set(0, phTop + phHipH + 0.7, 0);
    bg.add(phFin);
    const phSpire = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.9, 8), trim);
    phSpire.position.set(0, phTop + phHipH + 1.4, 0);
    bg.add(phSpire);

    // 3) TOPIARIAS chiques nos cantos do terraço (vaso + bola de buxo)
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const px = sx * (hw - 0.9); // na faixa de terraço entre o penthouse e o gradil
            const pz = sz * (hd - 0.9);
            const pot = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.8, 1.1), edgeMat);
            pot.position.set(px, slabTop + 0.3, pz);
            pot.castShadow = true;
            bg.add(pot);
            const ball = new THREE.Mesh(new THREE.IcosahedronGeometry(0.68, 0), topiaryMat);
            ball.position.set(px, slabTop + 1.25, pz);
            ball.castShadow = true;
            bg.add(ball);
        }
    }

    // 4) VARAL DE LUZES (festão) entre os postes — clima/charme de boutique
    const fy = cornY + postH + 0.45;
    const corners = [
        [-perim, perim],
        [perim, perim],
        [perim, -perim],
        [-perim, -perim],
    ];
    for (let c = 0; c < 4; c++) {
        const [ax, az] = corners[c];
        const [bx2, bz2] = corners[(c + 1) % 4];
        const n = 8;
        for (let i = 1; i < n; i++) {
            const t = i / n;
            const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6), bulbMat);
            bulb.position.set(
                ax + (bx2 - ax) * t,
                fy - 0.85 * 4 * t * (1 - t), // catenária (afunda no meio)
                az + (bz2 - az) * t,
            );
            bg.add(bulb);
        }
    }
}

/**
 * Arranjo de flores em vaso (vitrine da floricultura) — RASO em z (relevo virado
 * p/ frente), p/ caber RECUADO dentro da vitrine sem furar o vidro/moldura.
 */
function makeFlowerDisplay(bloomColors) {
    const g = new THREE.Group();
    const potMat = new THREE.MeshStandardMaterial({ color: "#b5613a", roughness: 0.9 });
    const foliage = new THREE.MeshStandardMaterial({ color: "#2f9e54", roughness: 0.85, flatShading: true });
    const coreMat = new THREE.MeshStandardMaterial({ color: "#ffd24a", roughness: 0.6 });
    const pot = new THREE.Mesh(new THREE.BoxGeometry(1.6, 1.0, 0.38), potMat);
    pot.position.set(0, 0.55, 0);
    g.add(pot);
    const rim = new THREE.Mesh(new THREE.BoxGeometry(1.85, 0.26, 0.44), potMat);
    rim.position.set(0, 1.05, 0);
    g.add(rim);
    for (const [fx, fy, fs] of [
        [0, 1.75, 1.15],
        [-0.6, 1.5, 0.8],
        [0.6, 1.55, 0.85],
    ]) {
        const bush = new THREE.Mesh(new THREE.IcosahedronGeometry(0.62, 0), foliage);
        bush.position.set(fx, fy, -0.02);
        bush.scale.set(fs, fs, 0.28);
        g.add(bush);
    }
    // blooms num leque virado p/ frente — z pequeno e achatado
    const blooms = [
        [0, 2.5, 0.12],
        [-0.78, 2.12, 0.06],
        [0.78, 2.18, 0.06],
        [-0.42, 2.4, 0.1],
        [0.48, 2.45, 0.1],
        [-0.85, 1.7, 0.02],
        [0.85, 1.76, 0.02],
        [0, 1.96, 0.14],
    ];
    blooms.forEach((p, i) => {
        const fl = new THREE.Mesh(new THREE.IcosahedronGeometry(0.32, 0), new THREE.MeshStandardMaterial({ color: bloomColors[i % bloomColors.length], roughness: 0.7, flatShading: true }));
        fl.position.set(p[0], p[1], p[2]);
        fl.scale.set(1, 1, 0.5);
        g.add(fl);
        const core = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), coreMat);
        core.position.set(p[0], p[1], p[2] + 0.12);
        g.add(core);
    });
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Emblema de FLOR (6 pétalas + miolo) virado p/ +Z — letreiro da floricultura. */
function makeFlowerEmblem() {
    const g = new THREE.Group();
    const petalMat = new THREE.MeshStandardMaterial({ color: "#e8557f", roughness: 0.7, flatShading: true });
    const coreMat = new THREE.MeshStandardMaterial({
        color: "#ffd24a",
        emissive: new THREE.Color("#ffd24a"),
        emissiveIntensity: 0.3,
        roughness: 0.6,
    });
    for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const petal = new THREE.Mesh(new THREE.SphereGeometry(0.34, 8, 6), petalMat);
        petal.scale.set(0.62, 1.1, 0.4);
        petal.position.set(Math.cos(a) * 0.5, Math.sin(a) * 0.5, 0);
        petal.rotation.z = a;
        g.add(petal);
    }
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.34, 10, 8), coreMat);
    core.scale.z = 0.5;
    g.add(core);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Floricultura (Acquisition Town · C5): cópia da Boutique (B4), mas de OUTRO tipo
 * — VITRINES com ARRANJOS DE FLORES (não manequins), caixas de flores sob as
 * janelas, emblema de FLOR no letreiro e terraço com canteiros floridos.
 */
function buildFlorist(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.75 });
    const awnMat = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.7 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#3a2e26",
        emissive: new THREE.Color("#ffe2a8"),
        emissiveIntensity: 0.32,
        roughness: 0.75,
    });
    const bloomA = ["#e8557f", "#ffd24a", "#ef6f4a"];
    const bloomB = ["#9b6dd6", "#5b8def", "#e8557f"];

    // corpo + base + cornija + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 1.0, b.d + 0.2), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.2, 0.9, b.d + 1.2), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);

    // VITRINES (térreo, frente): 2 vitrines iluminadas com ARRANJOS DE FLORES + entrada
    const vH = 4.4;
    const vCY = 0.6 + vH / 2;
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 4.6;
        // fundo FINO (não ocluir o arranjo recuado) na face da parede
        const interior = new THREE.Mesh(new THREE.BoxGeometry(4.0, vH, 0.12), interiorMat);
        interior.position.set(wx, vCY, hd + 0.0);
        bg.add(interior);
        const fr = [
            [4.6, 0.4, 0, vH / 2 + 0.2],
            [4.6, 0.4, 0, -vH / 2 - 0.2],
            [0.4, vH, -2.3, 0],
            [0.4, vH, 2.3, 0],
        ];
        for (const [bw, bh, ox, oy] of fr) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.4), edgeMat);
            m.position.set(wx + ox, vCY + oy, hd + 0.12);
            bg.add(m);
        }
        // arranjo RECUADO (atrás da moldura/vidro) → flores DENTRO da vitrine
        const disp = makeFlowerDisplay(s ? bloomB : bloomA);
        disp.position.set(wx, 0.6, hd + 0.04);
        bg.add(disp);
    }
    const door = new THREE.Mesh(new THREE.BoxGeometry(2.6, vH - 0.2, 0.4), warmGlass);
    door.position.set(0, (vH - 0.2) / 2 + 0.6, hd + 0.12);
    bg.add(door);
    const dframe = new THREE.Mesh(new THREE.BoxGeometry(3.2, vH + 0.3, 0.3), edgeMat);
    dframe.position.set(0, (vH + 0.3) / 2 + 0.45, hd + 0.06);
    bg.add(dframe);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.14, vH - 0.2, 0.5), edgeMat);
    dmull.position.set(0, (vH - 0.2) / 2 + 0.6, hd + 0.2);
    bg.add(dmull);

    // TOLDO verde inclinado + BABADO sobre as vitrines
    const awn = new THREE.Mesh(new THREE.BoxGeometry(b.w - 0.4, 0.35, 2.9), awnMat);
    awn.position.set(0, vH + 1.4, hd + 1.35);
    awn.rotation.x = 0.32;
    awn.castShadow = true;
    bg.add(awn);
    const nflap = 7;
    const flapW = ((b.w - 0.4) / nflap) * 0.86;
    for (let i = 0; i < nflap; i++) {
        const fx = (-0.5 + (i + 0.5) / nflap) * (b.w - 0.4);
        const flap = new THREE.Mesh(new THREE.BoxGeometry(flapW, 0.75, 0.12), awnMat);
        flap.position.set(fx, vH + 0.95, hd + 2.65);
        bg.add(flap);
    }

    // BALDES DE FLORES ladeando a entrada (vasos altos + buquês)
    for (const sx of [-1, 1]) {
        const bucket = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.6, 1.1, 12), edgeMat);
        bucket.position.set(sx * 2.6, 0.55, hd + 0.95);
        bucket.castShadow = true;
        bg.add(bucket);
        const bouquet = new THREE.Mesh(new THREE.IcosahedronGeometry(0.85, 0), new THREE.MeshStandardMaterial({ color: sx < 0 ? "#e8557f" : "#ffd24a", roughness: 0.75, flatShading: true }));
        bouquet.position.set(sx * 2.6, 1.6, hd + 0.95);
        bg.add(bouquet);
    }

    // JANELAS refinadas + CAIXAS DE FLORES sob as 4 janelas da frente (andar de cima)
    const upperY = b.h - 2.6;
    const boxColors = ["#e8557f", "#ffd24a", "#9b6dd6"];
    for (const wx of [-6.5, -2.2, 2.2, 6.5]) {
        const win = makeClassWindow(2.0, 1.9, warmGlass, edgeMat);
        win.position.set(wx, 7.8, hd + 0.12);
        bg.add(win);
        const box = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.5, 0.6), edgeMat);
        box.position.set(wx, 6.65, hd + 0.42);
        box.castShadow = true;
        bg.add(box);
        boxColors.forEach((col, bi) => {
            const fl = new THREE.Mesh(new THREE.IcosahedronGeometry(0.3, 0), new THREE.MeshStandardMaterial({ color: col, roughness: 0.7, flatShading: true }));
            fl.position.set(wx + (bi - 1) * 0.62, 7.0, hd + 0.42);
            bg.add(fl);
        });
    }
    for (const f of [
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else ry = -Math.PI / 2;
        for (const wy of [3.0, upperY]) {
            for (const u of [-4.6, 0, 4.6]) {
                const win = makeClassWindow(2.0, 2.6, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // LETREIRO PENDURADO = EMBLEMA DE FLOR ao lado da entrada
    const bracket = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.12, 0.12), edgeMat);
    bracket.position.set(hw - 0.4, vH + 0.9, hd + 0.7);
    bg.add(bracket);
    const emblem = makeFlowerEmblem();
    emblem.scale.setScalar(1.05);
    emblem.position.set(hw - 1.15, vH + 0.2, hd + 0.7);
    bg.add(emblem);

    // ───────── COBERTURA: terraço com canteiros floridos ─────────
    const slabTop = b.h + 0.5;
    const cornY = b.h + 0.15;
    const perim = hw + 0.4;
    const bulbMat = new THREE.MeshStandardMaterial({
        color: "#fff2c8",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.95,
        roughness: 0.5,
    });
    const foliageMat = new THREE.MeshStandardMaterial({ color: "#3fbf63", roughness: 0.85, flatShading: true });

    // 1) GRADIL/balaustrada no perímetro
    const postH = 1.25;
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.55, postH + 0.4, 0.55), trim);
            post.position.set(sx * perim, cornY + (postH + 0.4) / 2, sz * perim);
            post.castShadow = true;
            bg.add(post);
            const cap = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.24, 0.72), edgeMat);
            cap.position.set(sx * perim, cornY + postH + 0.55, sz * perim);
            bg.add(cap);
        }
    }
    for (const [fixed, v] of [
        ["z", perim],
        ["z", -perim],
        ["x", perim],
        ["x", -perim],
    ]) {
        const horiz = fixed === "z";
        for (const ry2 of [cornY + 0.45, cornY + postH + 0.15]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(horiz ? 2 * perim - 1.0 : 0.26, 0.2, horiz ? 0.26 : 2 * perim - 1.0), trim);
            rail.position.set(horiz ? 0 : v, ry2, horiz ? v : 0);
            bg.add(rail);
        }
        const nb = 7;
        for (let i = 1; i < nb; i++) {
            const u = -perim + 2 * perim * (i / nb);
            const bal = new THREE.Mesh(new THREE.BoxGeometry(0.15, postH - 0.15, 0.15), edgeMat);
            bal.position.set(horiz ? u : v, cornY + (postH - 0.15) / 2 + 0.45, horiz ? v : u);
            bg.add(bal);
        }
    }

    // 2) PENTHOUSE / 2º pavimento — ESTUFA: vitrines com ARRANJOS DE FLORES + cúpula
    const php = 7;
    const phBot = slabTop - 0.2;
    const phH = 4.4;
    const phMid = phBot + phH / 2;
    const phTop = phBot + phH;
    const phBody = new THREE.Mesh(new THREE.BoxGeometry(php * 2, phH, php * 2), wallMat);
    phBody.position.set(0, phMid, 0);
    phBody.castShadow = true;
    phBody.receiveShadow = true;
    bg.add(phBody);
    const phBaseM = new THREE.Mesh(new THREE.BoxGeometry(php * 2 + 0.5, 0.6, php * 2 + 0.5), trim);
    phBaseM.position.set(0, phBot + 0.3, 0);
    bg.add(phBaseM);
    const phCorn = new THREE.Mesh(new THREE.BoxGeometry(php * 2 + 0.8, 0.7, php * 2 + 0.8), edgeMat);
    phCorn.position.set(0, phTop - 0.15, 0);
    phCorn.castShadow = true;
    bg.add(phCorn);
    const pvY0 = phBot + 0.7;
    const pvH = 3.0;
    const pvCY = pvY0 + pvH / 2;
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 3.8;
        const inter = new THREE.Mesh(new THREE.BoxGeometry(3.0, pvH, 0.12), interiorMat);
        inter.position.set(wx, pvCY, php + 0.0);
        bg.add(inter);
        const fr = [
            [3.5, 0.35, 0, pvH / 2 + 0.17],
            [3.5, 0.35, 0, -pvH / 2 - 0.17],
            [0.35, pvH, -1.75, 0],
            [0.35, pvH, 1.75, 0],
        ];
        for (const [fw, fh, ox, oy] of fr) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(fw, fh, 0.4), edgeMat);
            m.position.set(wx + ox, pvCY + oy, php + 0.12);
            bg.add(m);
        }
        // arranjo RECUADO (atrás da moldura) → flores DENTRO da vitrine
        const disp = makeFlowerDisplay(s ? bloomA : bloomB);
        disp.scale.setScalar(0.72);
        disp.position.set(wx, pvY0, php + 0.04);
        bg.add(disp);
    }
    const winFaces = [
        ["z", 1, [0]],
        ["z", -1, [-5, -1.7, 1.7, 5]],
        ["x", 1, [-5, -1.7, 1.7, 5]],
        ["x", -1, [-5, -1.7, 1.7, 5]],
    ];
    for (const [fixed, sgn, us] of winFaces) {
        const horiz = fixed === "z";
        const fd = php + 0.12;
        for (const u of us) {
            const win = makeClassWindow(2.0, 2.8, warmGlass, edgeMat);
            win.rotation.y = horiz ? (sgn > 0 ? 0 : Math.PI) : sgn > 0 ? Math.PI / 2 : -Math.PI / 2;
            win.position.set(horiz ? u : sgn * fd, phMid, horiz ? sgn * fd : u);
            bg.add(win);
        }
    }
    const phHipH = 2.6;
    const phHip = new THREE.Mesh(new THREE.CylinderGeometry(1.9, (php + 0.6) * Math.SQRT2, phHipH, 4), edgeMat);
    phHip.rotation.y = Math.PI / 4;
    phHip.position.set(0, phTop + phHipH / 2, 0);
    phHip.castShadow = true;
    phHip.receiveShadow = true;
    bg.add(phHip);
    const phRidge = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.3, 2.6), trim);
    phRidge.position.set(0, phTop + phHipH, 0);
    bg.add(phRidge);
    const phFin = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 8), trim);
    phFin.position.set(0, phTop + phHipH + 0.7, 0);
    bg.add(phFin);
    // pináculo da estufa = botão de flor
    const phBud = makeFlowerEmblem();
    phBud.scale.setScalar(0.8);
    phBud.rotation.x = -Math.PI / 2;
    phBud.position.set(0, phTop + phHipH + 1.2, 0);
    bg.add(phBud);

    // 3) CANTEIROS FLORIDOS nos cantos do terraço (vaso + folhagem + blooms)
    for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
            const px = sx * (hw - 0.9);
            const pz = sz * (hd - 0.9);
            const pot = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.8, 1.1), edgeMat);
            pot.position.set(px, slabTop + 0.3, pz);
            pot.castShadow = true;
            bg.add(pot);
            const bush = new THREE.Mesh(new THREE.IcosahedronGeometry(0.6, 0), foliageMat);
            bush.position.set(px, slabTop + 1.05, pz);
            bush.castShadow = true;
            bg.add(bush);
            for (const [bx, bz, bc] of [
                [-0.3, 0.2, "#e8557f"],
                [0.3, -0.2, "#ffd24a"],
                [0, 0.35, "#9b6dd6"],
            ]) {
                const fl = new THREE.Mesh(new THREE.IcosahedronGeometry(0.26, 0), new THREE.MeshStandardMaterial({ color: bc, roughness: 0.7, flatShading: true }));
                fl.position.set(px + bx, slabTop + 1.45, pz + bz);
                bg.add(fl);
            }
        }
    }

    // 4) VARAL DE LUZES (festão) entre os postes
    const fy = cornY + postH + 0.45;
    const corners = [
        [-perim, perim],
        [perim, perim],
        [perim, -perim],
        [-perim, -perim],
    ];
    for (let c = 0; c < 4; c++) {
        const [ax, az] = corners[c];
        const [bx2, bz2] = corners[(c + 1) % 4];
        const n = 8;
        for (let i = 1; i < n; i++) {
            const t = i / n;
            const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6), bulbMat);
            bulb.position.set(ax + (bx2 - ax) * t, fy - 0.85 * 4 * t * (1 - t), az + (bz2 - az) * t);
            bg.add(bulb);
        }
    }
}

/** Cruz grega (símbolo de farmácia): barra vertical + horizontal. */
function makeCross(arm, thick, depth, mat) {
    const g = new THREE.Group();
    const v = new THREE.Mesh(new THREE.BoxGeometry(thick, arm, depth), mat);
    g.add(v);
    const h = new THREE.Mesh(new THREE.BoxGeometry(arm, thick, depth), mat);
    g.add(h);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Farmácia (Acquisition Town · C4): loja limpa na paleta verde com CRUZ VERDE
 * (símbolo de farmácia) — cruz grande iluminada na fachada, placa de cruz no
 * teto, cruzes nas vitrines, marquise branca "FARMÁCIA" e vidro clínico.
 */
function buildPharmacy(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const whiteMat = new THREE.MeshStandardMaterial({ color: "#eef3ea", roughness: 0.7 });
    const crossMat = new THREE.MeshStandardMaterial({
        color: "#3ae07f",
        emissive: new THREE.Color("#3ae07f"),
        emissiveIntensity: 0.85,
        roughness: 0.45,
    });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    // luz das vitrines AMARELADA (como os outros prédios), não mais branco-azulada
    const cleanGlass = new THREE.MeshStandardMaterial({
        color: "#ffce7a",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.45,
        roughness: 0.35,
    });

    // corpo + base + cornija (verde-escuro) + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 1.0, b.d + 0.3), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 0.9, b.d + 0.9), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);

    // STOREFRONT térreo (frente): 2 vitrines limpas (com cruz) + entrada central
    const vH = 4.6;
    const vY0 = 1.0;
    const vCY = vY0 + vH / 2;
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 4.8;
        const glass = new THREE.Mesh(new THREE.BoxGeometry(4.4, vH, 0.3), cleanGlass);
        glass.position.set(wx, vCY, hd + 0.05);
        bg.add(glass);
        for (const [fw, fh, ox, oy] of [
            [4.9, 0.4, 0, vH / 2 + 0.2],
            [4.9, 0.4, 0, -vH / 2 - 0.2],
            [0.4, vH, -2.45, 0],
            [0.4, vH, 2.45, 0],
        ]) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(fw, fh, 0.4), edgeMat);
            m.position.set(wx + ox, vCY + oy, hd + 0.12);
            bg.add(m);
        }
        const cr = makeCross(1.7, 0.55, 0.2, crossMat);
        cr.position.set(wx, vCY + 0.2, hd + 0.3);
        bg.add(cr);
    }
    const door = new THREE.Mesh(new THREE.BoxGeometry(3.0, vH, 0.4), warmGlass);
    door.position.set(0, vY0 + vH / 2, hd + 0.08);
    bg.add(door);
    // moldura da porta = barras em "U" (OCA) — evita o z-fighting da caixa sólida atrás
    // do vidro (face da porta hd+0.28 vs quadro hd+0.27, a 0.01)
    for (const [bw, bh, bx, by] of [
        [0.3, vH + 0.5, -1.65, vY0 + (vH + 0.5) / 2 - 0.1],
        [0.3, vH + 0.5, 1.65, vY0 + (vH + 0.5) / 2 - 0.1],
        [3.0, 0.3, 0, vY0 + vH + 0.25],
    ]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.3), edgeMat);
        m.position.set(bx, by, hd + 0.12);
        m.castShadow = true;
        bg.add(m);
    }
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.14, vH, 0.5), edgeMat);
    dmull.position.set(0, vY0 + vH / 2, hd + 0.2);
    bg.add(dmull);

    // TOLDO verde LISTRADO com branco sobre a vitrine + valance "FARMÁCIA"
    const greenStr = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.7 });
    const awnW = b.w - 0.6;
    const awnY = vY0 + vH + 0.55;
    const nstr = 14;
    const strW = awnW / nstr;
    for (let i = 0; i < nstr; i++) {
        const px = -awnW / 2 + (i + 0.5) * strW;
        const strip = new THREE.Mesh(new THREE.BoxGeometry(strW * 0.99, 0.3, 2.7), i % 2 ? whiteMat : greenStr);
        strip.position.set(px, awnY, hd + 1.25);
        strip.rotation.x = 0.34;
        strip.castShadow = true;
        bg.add(strip);
    }
    const signTex = makeSignTexture(t("FARMÁCIA"), { bg: "#eef3ea", fg: "#1f8f3e", border: "#25d366" });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#9fe6b8"),
        emissiveIntensity: 0.4,
        roughness: 0.7,
    });
    const valance = new THREE.Mesh(new THREE.BoxGeometry(awnW - 0.6, 0.85, 0.22), [whiteMat, whiteMat, whiteMat, whiteMat, signMat, whiteMat]);
    valance.position.set(0, awnY - 0.85, hd + 2.5);
    valance.castShadow = true;
    bg.add(valance);

    // 2º andar alto → "+" CENTRADO entre o topo do toldo e o início do teto (cornija)
    const upperY = (vY0 + vH + 1.0 + (b.h - 0.75)) / 2;
    const bigCross = makeCross(3.7, 1.25, 0.4, crossMat);
    bigCross.position.set(0, upperY, hd + 0.28);
    bg.add(bigCross);

    // JANELAS: frente só nas pontas (centro = cruz); laterais/fundo em 2 fileiras
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const front = f.nz === 1;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const rows = front ? [upperY] : [3.5, upperY];
        const us = front ? [-7.5, 7.5] : [-5.5, -1.85, 1.85, 5.5];
        for (const wy of rows) {
            for (const u of us) {
                const win = makeClassWindow(2.2, 2.6, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // (CRUZ DO TETO removida a pedido — identidade vem do "+" da fachada)
}

/** Estrela de 5 pontas (avaliação do hotel) — extrudada, virada p/ +Z. */
function makeStar(r, depth, mat) {
    const shape = new THREE.Shape();
    const inner = r * 0.42;
    for (let i = 0; i < 10; i++) {
        const rad = i % 2 ? inner : r;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const x = Math.cos(a) * rad;
        const y = Math.sin(a) * rad;
        if (i === 0) shape.moveTo(x, y);
        else shape.lineTo(x, y);
    }
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
    geo.center();
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    return m;
}

/**
 * Hotel (Acquisition Town · B3): bloco alto com GRADE DE JANELAS de quartos
 * (acesas/apagadas), ENTRADA grandiosa com porte-cochère (marquise sobre colunas),
 * placa "HOTEL" + 5 ESTRELAS douradas, friso dourado e letreiro HOTEL no teto.
 */
function buildHotel(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#e8c45a",
        emissive: new THREE.Color("#caa14a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
        metalness: 0.4,
    });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const offGlass = new THREE.MeshStandardMaterial({ color: COLORS.windowOff, roughness: 0.5 });

    // corpo + base + cornija + friso dourado + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const goldBand = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.42, 0.3, b.d + 0.42), gold);
    goldBand.position.y = b.h - 1.05;
    bg.add(goldBand);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.6, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.3;
    bg.add(roofSlab);

    // GRADE DE JANELAS de quartos (acesas/apagadas). Frente pula a área da entrada.
    const cols = [-6.8, -3.4, 0, 3.4, 6.8];
    const rows = [3.0, 6.0, 9.0, 12.0, 15.0];
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const front = f.nz === 1;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        for (const wy of rows) {
            for (const u of cols) {
                if (front && Math.abs(u) < 5 && wy < 8.5) continue; // vão da entrada/marquise
                const lit = rand() > 0.35;
                const px = f.nx * (faceDist + 0.1) + tx * u;
                const pz = f.nz * (faceDist + 0.1) + tz * u;
                const frame = new THREE.Mesh(new THREE.BoxGeometry(2.35, 2.5, 0.16), edgeMat);
                frame.rotation.y = ry;
                frame.position.set(px, wy, pz);
                bg.add(frame);
                const pane = new THREE.Mesh(new THREE.BoxGeometry(2.0, 2.15, 0.2), lit ? warmGlass : offGlass);
                pane.rotation.y = ry;
                pane.position.set(f.nx * (faceDist + 0.16) + tx * u, wy, f.nz * (faceDist + 0.16) + tz * u);
                bg.add(pane);
            }
        }
    }

    // ENTRADA: portas de vidro + moldura + PORTE-COCHÈRE (marquise + colunas) + HOTEL + ★
    const entW = 6.4;
    const doorH = 4.6;
    const doors = new THREE.Mesh(new THREE.BoxGeometry(entW, doorH, 0.4), warmGlass);
    doors.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(doors);
    for (const mx of [-entW / 2, -entW / 6, entW / 6, entW / 2]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.18, doorH, 0.5), edgeMat);
        mull.position.set(mx, doorH / 2 + 0.5, hd + 0.2);
        bg.add(mull);
    }
    const dframe = new THREE.Mesh(new THREE.BoxGeometry(entW + 0.8, doorH + 0.6, 0.3), edgeMat);
    dframe.position.set(0, (doorH + 0.6) / 2 + 0.4, hd + 0.06);
    bg.add(dframe);
    const canY = doorH + 1.7;
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(entW + 3.4, 0.7, 4.6), edgeMat);
    canopy.position.set(0, canY, hd + 2.0);
    canopy.castShadow = true;
    bg.add(canopy);
    const canopyGold = new THREE.Mesh(new THREE.BoxGeometry(entW + 3.6, 0.26, 0.3), gold);
    canopyGold.position.set(0, canY - 0.32, hd + 4.3);
    bg.add(canopyGold);
    for (const sx of [-1, 1]) {
        const col = new THREE.Mesh(new THREE.BoxGeometry(0.55, canY, 0.55), trim);
        col.position.set(sx * (entW / 2 + 1.4), canY / 2, hd + 4.0);
        col.castShadow = true;
        bg.add(col);
    }
    // placa HOTEL na frente da marquise + 5 estrelas douradas acima
    const signTex = makeSignTexture(t("HOTEL"), { bg: "#0e3a22", fg: "#e8c45a", border: "#caa14a" });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#e8c45a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(entW + 1.6, 1.4, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, canY + 1.0, hd + 4.4);
    sign.castShadow = true;
    bg.add(sign);
    for (let i = 0; i < 5; i++) {
        const star = makeStar(0.36, 0.14, gold);
        star.position.set((i - 2) * 0.98, canY + 2.1, hd + 4.46);
        bg.add(star);
    }

    // (LETREIRO do TETO removido a pedido — mantida só a placa "HOTEL" da fachada)
}

/**
 * Babado PLANO de toldo: faixa reta + FESTÕES semicirculares de DISCOS FINOS
 * voltados p/ frente (a metade de cima some atrás da faixa; só o semicírculo de
 * baixo aparece). Adicionado ao grupo `inner` do toldo (canopy no plano z=0..awnD,
 * inclinado). `mat` = material único OU função (k)=>material (babado listrado).
 * Substitui as esferas achatadas antigas, que pareciam "bolas" na ponta.
 */
function addAwningValance(inner, awnW, awnD, mat) {
    const nSc = Math.max(4, Math.round(awnW / 0.62));
    const scW = awnW / nSc;
    const scR = scW / 2;
    const at = (k) => (typeof mat === "function" ? mat(k) : mat);
    for (let k = 0; k < nSc; k++) {
        const m = at(k);
        const cx = -awnW / 2 + scW * (k + 0.5);
        const strip = new THREE.Mesh(new THREE.BoxGeometry(scW + 0.02, scR + 0.05, 0.06), m);
        strip.position.set(cx, -(scR + 0.05) / 2, awnD + 0.08);
        inner.add(strip);
        // 8 segmentos (era 16): sao 352 destes discos nas 4 cidades = 11.264 tris a
        // menos. A faixa acima cobre a metade de cima do disco; o que se vê é o
        // semicírculo inferior de ~5,5 × 2,7 px, onde 8 lados desviam 0,21 px.
        const sc = new THREE.Mesh(new THREE.CylinderGeometry(scR, scR, 0.06, 8), m);
        sc.rotation.x = Math.PI / 2; // face plana voltada p/ frente
        sc.position.set(cx, -(scR + 0.05), awnD + 0.04);
        inner.add(sc);
    }
}

/**
 * HOTEL RESORT (2º hotel, benchmark = fintech/cafeteria): identidade DISTINTA do
 * hotel clássico dourado —
 *   • PISCINA no TELHADO (o ícone aéreo): água azul emissiva + deck creme +
 *     escadinha + espreguiçadeiras + guarda-sol listrado + boia;
 *   • SACADAS com guarda-corpo em todas as janelas (vs. grade chapada do clássico);
 *   • letreiro VERTICAL "H·O·T·E·L" (blade sign) no canto da fachada;
 *   • entrada com TAPETE VERMELHO até a calçada + CARRINHO DE BAGAGEM com malas
 *     + vasos de planta.
 */
function buildResort(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const deckMat = new THREE.MeshStandardMaterial({ color: "#e8e2d2", roughness: 0.9 });
    const waterMat = new THREE.MeshStandardMaterial({
        color: "#7fdcff",
        emissive: new THREE.Color("#5fd0ff"),
        emissiveIntensity: 0.38,
        roughness: 0.25,
    });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const offGlass = new THREE.MeshStandardMaterial({ color: COLORS.windowOff, roughness: 0.5 });
    const redMat = new THREE.MeshStandardMaterial({ color: "#c0392b", roughness: 0.8 });
    const goldMat = new THREE.MeshStandardMaterial({
        color: "#e8c45a",
        roughness: 0.45,
        metalness: 0.5,
    });

    // ---- corpo + base + cornija + laje/deck do teto ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    // piso do teto em VERDE CLARO (como os telhados dos outros prédios — o creme
    // contrastava demais com a cidade vista de cima)
    const deck = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    deck.position.y = b.h + 0.25;
    deck.receiveShadow = true;
    bg.add(deck);

    // ---- JANELAS com SACADA (porta-janela + laje projetada + guarda-corpo) ----
    const cols = [-6.6, -2.2, 2.2, 6.6];
    const rows = [];
    for (let y = 3.4; y <= b.h - 2.6; y += 3.3) rows.push(y);
    const topRow = rows[rows.length - 1]; // último andar (o toldo vai só aqui)
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const front = f.nz === 1;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        // direção "para fora" da face (p/ projetar a sacada)
        const ox = f.nx,
            oz = f.nz;
        for (const wy of rows) {
            for (const u of cols) {
                if (front && Math.abs(u) < 5 && wy < 8.6) continue; // vão da entrada/marquise
                const lit = rand() > 0.4;
                const px = f.nx * (faceDist + 0.1) + tx * u;
                const pz = f.nz * (faceDist + 0.1) + tz * u;
                // porta-janela alta (chega ao piso da sacada)
                const frame = new THREE.Mesh(new THREE.BoxGeometry(2.5, 2.75, 0.16), edgeMat);
                frame.rotation.y = ry;
                frame.position.set(px, wy + 0.1, pz);
                bg.add(frame);
                const pane = new THREE.Mesh(new THREE.BoxGeometry(2.15, 2.45, 0.2), lit ? warmGlass : offGlass);
                pane.rotation.y = ry;
                pane.position.set(f.nx * (faceDist + 0.16) + tx * u, wy + 0.1, f.nz * (faceDist + 0.16) + tz * u);
                bg.add(pane);
                // SACADA: laje em VERDE CLARO (mesmo tom do teto — o creme destoava) +
                // guarda-corpo frontal + GRADIS LATERAIS ligando a frente à parede
                const balY = wy - 1.35;
                const slab = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.16, 1.0), trim);
                slab.rotation.y = ry;
                slab.position.set(px + ox * 0.55, balY, pz + oz * 0.55);
                slab.castShadow = true;
                bg.add(slab);
                const rail = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.1, 0.1), trim);
                rail.rotation.y = ry;
                rail.position.set(px + ox * 1.0, balY + 0.85, pz + oz * 1.0);
                bg.add(rail);
                const mid = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.06, 0.06), trim);
                mid.rotation.y = ry;
                mid.position.set(px + ox * 1.0, balY + 0.48, pz + oz * 1.0);
                bg.add(mid);
                for (const su of [-1.35, 0, 1.35]) {
                    const post = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.85, 0.08), trim);
                    post.rotation.y = ry;
                    post.position.set(px + ox * 1.0 + tx * su, balY + 0.44, pz + oz * 1.0 + tz * su);
                    bg.add(post);
                }
                // gradis LATERAIS (esq/dir): corrimão + barra do meio, da parede ao frontal
                for (const se of [-1.4, 1.4]) {
                    const sideTop = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.95), trim);
                    sideTop.rotation.y = ry;
                    sideTop.position.set(px + ox * 0.55 + tx * se, balY + 0.85, pz + oz * 0.55 + tz * se);
                    bg.add(sideTop);
                    const sideMid = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.95), trim);
                    sideMid.rotation.y = ry;
                    sideMid.position.set(px + ox * 0.55 + tx * se, balY + 0.48, pz + oz * 0.55 + tz * se);
                    bg.add(sideMid);
                }
                // TOLDO no tom do hotel (VERMELHO, como o tapete de entrada) — SÓ no
                // último andar, no topo da porta-janela de cada sacada
                if (wy === topRow) {
                    const awnW = 3.0,
                        awnD = 1.4;
                    const inner = new THREE.Group();
                    inner.rotation.x = 0.42;
                    const canopy = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.16, awnD), redMat);
                    canopy.position.set(0, 0, awnD / 2);
                    inner.add(canopy);
                    addAwningValance(inner, awnW, awnD, redMat); // babado plano vermelho
                    inner.traverse((o) => {
                        if (o.isMesh) o.castShadow = true;
                    });
                    // no MEIO do vão entre o topo da porta-janela e a base do teto/cornija
                    const awnY = (wy + 1.4 + (b.h - 1.0)) / 2 + 0.4;
                    const holder = new THREE.Group();
                    holder.rotation.y = ry;
                    holder.position.set(px + ox * 0.05, awnY, pz + oz * 0.05);
                    holder.add(inner);
                    bg.add(holder);
                }
            }
        }
    }

    // ---- PISCINA no TELHADO (ícone aéreo) ----
    const deckY = b.h + 0.5;
    const poolW = Math.min(b.w * 0.52, 10);
    const poolD = Math.min(b.d * 0.4, 7.5);
    const poolX = -b.w * 0.14;
    const poolZ = 0;
    // borda elevada branca + água azul emissiva (leve brilho no bloom)
    const curb = new THREE.Mesh(new THREE.BoxGeometry(poolW + 1.2, 0.55, poolD + 1.2), deckMat);
    curb.position.set(poolX, deckY + 0.27, poolZ);
    bg.add(curb);
    const water = new THREE.Mesh(new THREE.BoxGeometry(poolW, 0.3, poolD), waterMat);
    water.position.set(poolX, deckY + 0.48, poolZ);
    bg.add(water);
    // escadinha (2 corrimãos curvos simplificados + degraus)
    for (const sz of [-0.6, 0.6]) {
        const railP = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.4, 8), goldMat);
        railP.position.set(poolX + poolW / 2 + 0.35, deckY + 0.9, poolZ + sz);
        bg.add(railP);
        const railT = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.5, 8), goldMat);
        railT.rotation.z = Math.PI / 2;
        railT.position.set(poolX + poolW / 2 + 0.15, deckY + 1.6, poolZ + sz);
        bg.add(railT);
    }
    // BOIA rosquinha vermelha na água
    const floaty = new THREE.Mesh(new THREE.TorusGeometry(0.7, 0.24, 8, 16), redMat);
    floaty.rotation.x = -Math.PI / 2;
    floaty.position.set(poolX - poolW * 0.24, deckY + 0.66, poolZ + poolD * 0.24);
    bg.add(floaty);
    // BOIA DE FLAMINGO (inflável rosa) na água (pedido do usuário) — o pescoço é
    // uma sequência de esferas SOBREPOSTAS (nunca abre folga como os cilindros
    // ad-hoc antigos) ligando o CORPO à CABEÇA
    {
        const pink = new THREE.MeshStandardMaterial({ color: "#ff8fb0", roughness: 0.7 });
        const fg = new THREE.Group();
        // ANEL (a boia) deitado na água
        const ring = new THREE.Mesh(new THREE.TorusGeometry(0.98, 0.42, 12, 22), pink);
        ring.rotation.x = -Math.PI / 2;
        ring.position.y = 0.3;
        fg.add(ring);
        // CORPO/PEITO (de onde sai o pescoço), na frente do anel
        const body = new THREE.Mesh(new THREE.SphereGeometry(0.56, 12, 10), pink);
        body.scale.set(1.15, 1.0, 1.0);
        body.position.set(0.72, 0.6, 0);
        fg.add(body);
        // CAUDA: cone apontando p/ cima-trás (a ponta é o topo do cone)
        const tail = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.95, 10), pink);
        tail.position.set(-1.0, 0.72, 0);
        tail.rotation.z = Math.PI / 4.5; // inclina p/ trás
        fg.add(tail);
        // PESCOÇO em S (esferas sobrepostas), subindo do peito
        for (const [nx, ny, nr] of [
            [0.74, 1.0, 0.25],
            [0.85, 1.4, 0.22],
            [0.92, 1.78, 0.21],
            [0.88, 2.12, 0.2],
            [0.78, 2.36, 0.19],
        ]) {
            const bead = new THREE.Mesh(new THREE.SphereGeometry(nr, 10, 8), pink);
            bead.position.set(nx, ny, 0);
            fg.add(bead);
        }
        // CABEÇA no fim do pescoço (voltada p/ frente +x)
        const head = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 10), pink);
        head.position.set(0.76, 2.56, 0);
        fg.add(head);
        // BICO preto curvo p/ baixo-frente
        const beak = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.46, 8), new THREE.MeshStandardMaterial({ color: "#2a2a2a", roughness: 0.6 }));
        beak.position.set(1.0, 2.44, 0);
        beak.rotation.z = -Math.PI * 0.62; // ponta p/ baixo-frente
        fg.add(beak);
        // OLHO
        const eye = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), dark);
        eye.position.set(0.86, 2.62, 0.2);
        fg.add(eye);
        fg.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        fg.position.set(poolX + poolW * 0.14, deckY + 0.5, poolZ - poolD * 0.12);
        fg.rotation.y = 0.5;
        bg.add(fg);
    }
    // ESPREGUIÇADEIRAS REFINADAS (acabamento inspirado no Spa, mas com elementos
    // PRÓPRIOS de resort): moldura creme + trilhos dourados + almofada aqua +
    // toalha de piscina enrolada. Pés locais -X p/ a água; o grupo gira conforme
    // o lado. Alinhadas em filas (as antigas eram cruas e espalhadas).
    const aqua = new THREE.MeshStandardMaterial({ color: "#8fd8d0", roughness: 0.8 });
    const addLounger = (cx, cz, ry) => {
        const g = new THREE.Group();
        const seat = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.14, 1.0), deckMat);
        seat.position.set(0, 0.46, 0);
        g.add(seat);
        const cushion = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.16, 0.86), aqua);
        cushion.position.set(-0.05, 0.58, 0);
        g.add(cushion);
        // encosto reclinado (moldura + almofada) na ponta OPOSTA à água (+X local)
        const back = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.14, 1.0), deckMat);
        back.rotation.z = 1.0;
        back.position.set(1.18, 0.88, 0);
        g.add(back);
        const bc = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.14, 0.86), aqua);
        bc.rotation.z = 1.0;
        bc.position.set(1.1, 0.92, 0);
        g.add(bc);
        // trilhos-frame dourados (nos 2 lados) — mais refinado que 4 pés finos
        for (const lx of [-0.9, 0.9]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.42, 0.94), goldMat);
            rail.position.set(lx, 0.22, 0);
            g.add(rail);
        }
        // toalha de piscina enrolada (creme + faixa aqua) no pé do assento
        const towel = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.86, 12), deckMat);
        towel.rotation.x = Math.PI / 2;
        towel.position.set(-0.8, 0.68, 0);
        g.add(towel);
        const tband = new THREE.Mesh(new THREE.CylinderGeometry(0.235, 0.235, 0.18, 12), aqua);
        tband.rotation.x = Math.PI / 2;
        tband.position.set(-0.8, 0.68, 0.28);
        g.add(tband);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.scale.setScalar(1.35); // maiores (pedido do usuário)
        g.position.set(cx, deckY, cz);
        g.rotation.y = ry;
        bg.add(g);
    };
    const loungeX = poolX + poolW / 2 + 3.3;
    addLounger(loungeX, -2.3, 0); // fila LESTE (sob o guarda-sol), alinhadas
    addLounger(loungeX, 2.3, 0);
    addLounger(poolX - 2.6, poolD / 2 + 3.2, -Math.PI / 2); // fila SUL (frente), alinhadas
    addLounger(poolX + 2.6, poolD / 2 + 3.2, -Math.PI / 2);
    // COQUEIROS em vaso nos 2 cantos do fundo (verdura tropical de resort): vaso +
    // tronco alto levemente curvo + coroa de folhas caídas + CACHO DE COCOS
    const potMat = new THREE.MeshStandardMaterial({ color: "#c98b5a", roughness: 0.85 });
    const trunkMat = new THREE.MeshStandardMaterial({ color: "#b58a5c", roughness: 0.85 });
    const frondMat = new THREE.MeshStandardMaterial({ color: "#43b866", roughness: 0.7 });
    const cocoMat = new THREE.MeshStandardMaterial({ color: "#6e4a2c", roughness: 0.8 });
    const addCoconut = (px, pz, lean) => {
        const g = new THREE.Group();
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.62, 1.15, 14), potMat);
        pot.position.y = 0.575; // base no piso (0)
        g.add(pot);
        const potRim = new THREE.Mesh(new THREE.CylinderGeometry(0.88, 0.88, 0.22, 14), deckMat);
        potRim.position.y = 1.08;
        g.add(potRim);
        // TRONCO inteiro (1 peça) que INCLINA a partir da base (grupo `tg` gira na
        // base, no topo do vaso) — coroa e cocos ficam DENTRO do tg, no topo do
        // tronco, então acompanham a inclinação e nada flutua
        const tg = new THREE.Group();
        tg.position.y = 1.0;
        tg.rotation.z = lean;
        g.add(tg);
        const trunkH = 4.2;
        const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.34, trunkH, 8), trunkMat);
        trunk.position.y = trunkH / 2;
        tg.add(trunk);
        for (let s = 1; s <= 4; s++) {
            // anéis do tronco (textura de coqueiro)
            const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.12, 8), cocoMat);
            ring.position.y = s * (trunkH / 5);
            tg.add(ring);
        }
        // COROA no topo do tronco (local y=trunkH) + cocos logo abaixo
        const crown = new THREE.Group();
        crown.position.y = trunkH;
        tg.add(crown);
        for (let i = 0; i < 9; i++) {
            const fg = new THREE.Group();
            const frond = new THREE.Mesh(new THREE.SphereGeometry(0.5, 6, 5), frondMat);
            frond.scale.set(3.0, 0.15, 0.66);
            frond.position.x = 1.7;
            frond.rotation.z = -0.5; // ponta cai
            fg.add(frond);
            fg.rotation.y = (i / 9) * Math.PI * 2;
            crown.add(fg);
        }
        for (const [cx2, cz2] of [
            [0.3, 0.28],
            [-0.28, 0.3],
            [0.14, -0.3],
            [0.34, -0.12],
        ]) {
            const coco = new THREE.Mesh(new THREE.IcosahedronGeometry(0.26, 0), cocoMat);
            coco.position.set(cx2, -0.35, cz2);
            crown.add(coco);
        }
        g.position.set(px, deckY, pz);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        bg.add(g);
    };
    addCoconut(-hw + 1.8, -(hd - 1.8), 0.16); // fundo-esq (curva p/ dentro)
    addCoconut(hw - 1.8, -(hd - 1.8), -0.16); // fundo-dir
    addCoconut(-hw + 1.8, hd - 1.8, 0.16); // frente-esq
    addCoconut(hw - 1.8, hd - 1.8, -0.16); // frente-dir
    // GUARDA-SOL maior (pedido do usuário)
    const upole = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 3.7, 8), dark);
    upole.position.set(loungeX, deckY + 1.85, 0);
    bg.add(upole);
    const ushade = new THREE.Mesh(new THREE.ConeGeometry(2.9, 1.15, 12), shared.cafeAwningMat);
    ushade.position.set(loungeX, deckY + 3.6, 0);
    ushade.castShadow = true;
    bg.add(ushade);

    // ---- LETREIRO VERTICAL "H·O·T·E·L" (blade sign): na PAREDE LATERAL DIREITA
    //      (+X), rente ao canto da frente, SALIENTE — as letras ficam DE FRENTE p/ o
    //      observador (o prédio olha p/ a câmera; na fachada a placa ficava de perfil).
    //      z=hd-0.35 deixa folga da coluna de sacadas laterais (termina em z≈8.05). ----
    const letters = ["H", "O", "T", "E", "L"];
    // PROPORÇÃO: o letreiro ocupa SÓ a faixa da parede principal — abaixo da cornija
    // (b.h-1.8) e acima da mureta/base (2.2). As placas ficam INTEIRAS fora da parede
    // (a borda esquerda não é mais "comida"), penduradas numa ESTRUTURA visível:
    // espinha vertical + braços horizontais fixados no prédio. Neon DOURADO (tom da
    // cidade — o azul destoava).
    const wallTop = b.h - 1.8;
    const wallBot = 2.2;
    const PL = 2.7; // lado da placa
    const step = (wallTop - wallBot - PL) / 4; // passo p/ caber exatamente na faixa
    const bladeZ = hd - 0.35;
    // espinha vertical (estrutura de fixação) + braços no topo/meio/base
    const spine = new THREE.Mesh(new THREE.BoxGeometry(0.2, wallTop - wallBot, 0.2), edgeMat);
    spine.position.set(hw + 0.3, (wallTop + wallBot) / 2, bladeZ);
    spine.castShadow = true;
    bg.add(spine);
    for (const ay of [wallTop - 0.6, (wallTop + wallBot) / 2, wallBot + 0.6]) {
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.22, 0.22), edgeMat);
        arm.position.set(hw + 0.1, ay, bladeZ);
        bg.add(arm);
    }
    for (let i = 0; i < letters.length; i++) {
        const tex = makeLetterTexture(letters[i], { bg: "#0e3a22", fg: "#ffd27a", border: "#ffcf5a" });
        const mat = new THREE.MeshStandardMaterial({
            map: tex,
            emissiveMap: tex,
            emissive: new THREE.Color("#ffcf5a"),
            emissiveIntensity: 0.55,
            roughness: 0.7,
        });
        const plate = new THREE.Mesh(new THREE.BoxGeometry(PL, PL, 0.4), [edgeMat, edgeMat, edgeMat, edgeMat, mat, mat]);
        // placa INTEIRA fora da parede, presa na espinha; face +Z (letra) p/ o observador
        plate.position.set(hw + 0.4 + PL / 2, wallTop - PL / 2 - i * step, bladeZ);
        plate.castShadow = true;
        bg.add(plate);
    }

    // ---- ENTRADA: portas de vidro + marquise moderna + TAPETE VERMELHO + bagagem ----
    const entW = 6.2;
    const doorH = 4.4;
    const doors = new THREE.Mesh(new THREE.BoxGeometry(entW, doorH, 0.4), warmGlass);
    doors.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(doors);
    for (const mx of [-entW / 2, -entW / 6, entW / 6, entW / 2]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.18, doorH, 0.5), edgeMat);
        mull.position.set(mx, doorH / 2 + 0.5, hd + 0.2);
        bg.add(mull);
    }
    // marquise fina com friso azul-piscina embaixo
    const canY = doorH + 1.5;
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(entW + 3.0, 0.5, 4.2), edgeMat);
    canopy.position.set(0, canY, hd + 1.9);
    canopy.castShadow = true;
    bg.add(canopy);
    const canLed = new THREE.Mesh(new THREE.BoxGeometry(entW + 3.2, 0.2, 0.24), waterMat);
    canLed.position.set(0, canY - 0.28, hd + 3.95);
    bg.add(canLed);
    for (const sx of [-1, 1]) {
        const col = new THREE.Mesh(new THREE.BoxGeometry(0.5, canY, 0.5), trim);
        col.position.set(sx * (entW / 2 + 1.2), canY / 2, hd + 3.6);
        col.castShadow = true;
        bg.add(col);
    }
    // SINO DE HOTELARIA sobre o pórtico (elemento lúdico): base escura + cúpula
    // dourada (meia-esfera) + botão de toque no topo
    {
        const bellY = canY + 0.25;
        const bellZ = hd + 1.9;
        const S = 1.2; // sino 20% maior (destaque, pedido do usuário)
        const bBase = new THREE.Mesh(new THREE.CylinderGeometry(1.05 * S, 1.15 * S, 0.18 * S, 16), dark);
        bBase.position.set(0, bellY + 0.09 * S, bellZ);
        bg.add(bBase);
        const dome = new THREE.Mesh(new THREE.SphereGeometry(0.88 * S, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), goldMat);
        dome.position.set(0, bellY + 0.18 * S, bellZ);
        dome.castShadow = true;
        bg.add(dome);
        const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.09 * S, 0.09 * S, 0.22 * S, 8), goldMat);
        stem.position.set(0, bellY + 1.12 * S, bellZ);
        bg.add(stem);
        const knob = new THREE.Mesh(new THREE.SphereGeometry(0.14 * S, 10, 8), dark);
        knob.position.set(0, bellY + 1.28 * S, bellZ);
        bg.add(knob);
    }
    // TAPETE VERMELHO da porta até a calçada
    const carpet = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.12, 4.6), redMat);
    carpet.position.set(0, 0.12, hd + 2.4);
    bg.add(carpet);
    // CARRINHO DE BAGAGEM dourado com malas coloridas (com RODINHAS e estrutura
    // FECHADA: os postes sobem até a barra do topo, com juntas esféricas)
    {
        const cx = entW / 2 + 2.6;
        const cz = hd + 2.2;
        const baseP = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.12, 0.95), goldMat);
        baseP.position.set(cx, 0.35, cz);
        bg.add(baseP);
        // 4 RODINHAS (cilindros deitados) sob a plataforma
        for (const sx of [-0.62, 0.62]) {
            for (const sz of [-0.3, 0.3]) {
                const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.1, 10), dark);
                wheel.rotation.z = Math.PI / 2;
                wheel.position.set(cx + sx, 0.15, cz + sz);
                bg.add(wheel);
            }
        }
        // postes laterais SOBEM ATÉ o topo e a barra os LIGA (junta esférica nos cantos)
        const topY = 2.6;
        for (const sx of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, topY - 0.41, 8), goldMat);
            post.position.set(cx + sx * 0.8, 0.41 + (topY - 0.41) / 2, cz);
            bg.add(post);
            const joint = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 6), goldMat);
            joint.position.set(cx + sx * 0.8, topY, cz);
            bg.add(joint);
        }
        const top = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.6, 8), goldMat);
        top.rotation.z = Math.PI / 2;
        top.position.set(cx, topY, cz);
        bg.add(top);
        const cases = [
            ["#c0392b", 0.9, 0.62, 0.5, 0.66, 0],
            ["#5b8def", 0.7, 0.5, 0.44, 1.22, 0.06],
            ["#f2c14e", 0.55, 0.36, 0.4, 1.65, -0.04],
        ];
        for (const [c, cw, ch, cd2, cy, dz] of cases) {
            const suit = new THREE.Mesh(new THREE.BoxGeometry(cw, ch, cd2), new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 }));
            suit.position.set(cx, cy, cz + dz);
            suit.castShadow = true;
            bg.add(suit);
        }
    }
    // vasos de planta flanqueando a porta
    for (const sx of [-1, 1]) {
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.38, 0.7, 10), deckMat);
        pot.position.set(sx * (entW / 2 + 1.2), 0.45, hd + 1.2);
        bg.add(pot);
        const bush = new THREE.Mesh(new THREE.SphereGeometry(0.62, 10, 8), shared.bushMat);
        bush.position.set(sx * (entW / 2 + 1.2), 1.25, hd + 1.2);
        bush.castShadow = true;
        bg.add(bush);
    }
}

/**
 * ESCOLA INFANTIL (2ª escola, benchmark = fintech/café/resort): identidade LÚDICA,
 * distinta da escola institucional (C3, pórtico/sino/relógio) —
 *   • BLOCOS DE BRINQUEDO "A·B·C" gigantes no TELHADO (o ícone aéreo), coloridos e
 *     levemente girados, um empilhado sobre dois (reusa makeLetterTexture);
 *   • entrada com COLUNAS-LÁPIS (corpo amarelo + ponta de madeira/grafite + borracha
 *     rosa) + relógio escolar + maçã sobre o portalzinho;
 *   • CERQUINHA DE GIZ DE CERA (piquetes-lápis coloridos) na frente;
 *   • PARQUINHO no pátio: escorregador + balanço;
 *   • janelas de sala de aula com molduras COLORIDAS alternadas.
 */
function buildKinder(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const ACCENTS = ["#e6584d", "#f2c14e", "#3ddc84", "#5b8def", "#c879e6", "#ff8c42"];
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });

    // ---- corpo (paredes verdes sólidas) + base + cornija + laje ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- JANELAS de sala de aula com molduras COLORIDAS (alternadas) ----
    const faces = [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ];
    let ci = 0;
    for (const f of faces) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        // GRADE de janelas na PROPORÇÃO PADRÃO dos outros prédios (retrato ~1.8×2.2,
        // como as janelas de quarto do hotel/salas), em 4 FILEIRAS parametrizadas à
        // altura (da mureta à cornija). Frente: 4 colunas flanqueando o pórtico
        // (o centro é da entrada/relógio); laterais/fundos: 4 colunas distribuídas.
        // 4 FILEIRAS com FOLGA: o passo vertical é (faixa da parede)/3 ≈ 2.67, então a
        // janela é 1.75×1.55 (moldura total ≈ 2.35×2.15, como as do hotel) — sobra ~0.5
        // entre fileiras. Frente: 1 coluna por lado do pórtico (2 colunas coladas se
        // fundiam); laterais/fundos: 4 colunas.
        const rows = [];
        const rowBot = 2.7;
        const rowTop = b.h - 2.3;
        for (let r = 0; r < 4; r++) rows.push(rowBot + ((rowTop - rowBot) * r) / 3);
        for (let r = 0; r < rows.length; r++) {
            // FRENTE: 1 coluna por lado nos andares 1-2 (o centro é do pórtico/letreiro);
            // nos andares 3-4, +2 janelas centrais (±2.5) preenchendo o alto da fachada
            const us = f.nz === 1 ? (r >= 2 ? [-6.3, -2.5, 2.5, 6.3] : [-6.3, 6.3]) : [-6.3, -2.1, 2.1, 6.3];
            for (const u of us) {
                const win = makeClassWindow(1.75, 1.55, warmGlass, accentMat(ACCENTS[ci++ % ACCENTS.length]));
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, rows[r], f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // ---- BLOCOS "A·B·C" GIGANTES no TELHADO (ícone aéreo — na escala da xícara da
    //      cafeteria: os brinquedos DOMINAM o teto): 2 na base + 1 em cima ----
    const roofY = b.h + 0.5;
    const BL = 4.0; // lado do bloco (-20%: abre espaço p/ o LIVRO no teto)
    const blocks = [
        { L: "A", c: "#e6584d", x: -2.6, y: 0, rot: 0.14 },
        { L: "B", c: "#5b8def", x: 1.9, y: 0, rot: -0.09 },
        { L: "C", c: "#f2c14e", x: -0.4, y: 1, rot: 0.3 },
    ];
    for (const bk of blocks) {
        const tex = makeLetterTexture(bk.L, { bg: bk.c, fg: "#ffffff", border: "#ffffff" });
        const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.65 });
        const side = new THREE.MeshStandardMaterial({ color: bk.c, roughness: 0.65 });
        // letra nas 4 faces laterais; topo/baixo lisos
        const cube = new THREE.Mesh(new THREE.BoxGeometry(BL, BL, BL), [mat, mat, side, side, mat, mat]);
        cube.position.set(bk.x, roofY + BL / 2 + bk.y * BL, bk.y ? -0.4 : 0);
        cube.rotation.y = bk.rot;
        cube.castShadow = true;
        bg.add(cube);
    }

    // ---- ENTRADA: portal com COLUNAS-LÁPIS + porta acesa + relógio + maçã ----
    const doorW = 4.2;
    const doorH = 3.6;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
    dmull.position.set(0, doorH / 2 + 0.5, hd + 0.2);
    bg.add(dmull);
    // colunas-lápis (apontando p/ CIMA: corpo + madeira + grafite; borracha na base)
    const mkPencil = (px) => {
        const g = new THREE.Group();
        const eraser = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.5, 12), accentMat("#f2a0b5"));
        eraser.position.y = 0.25;
        g.add(eraser);
        const band = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 0.18, 12), new THREE.MeshStandardMaterial({ color: "#cfd6db", roughness: 0.4, metalness: 0.5 }));
        band.position.y = 0.55;
        g.add(band);
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 3.6, 12), accentMat("#f2c14e"));
        shaft.position.y = 2.45;
        g.add(shaft);
        const wood = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.4, 0.7, 12), accentMat("#e0b077"));
        wood.position.y = 4.6;
        g.add(wood);
        const tip = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.4, 12), dark);
        tip.position.y = 5.0;
        g.add(tip);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.position.set(px, 0, hd + 1.3);
        return g;
    };
    bg.add(mkPencil(-(doorW / 2 + 1.1)));
    bg.add(mkPencil(doorW / 2 + 1.1));
    // portalzinho (viga) sobre os lápis + relógio + maçã
    const lintel = new THREE.Mesh(new THREE.BoxGeometry(doorW + 3.6, 0.7, 1.2), trim);
    lintel.position.set(0, doorH + 1.35, hd + 1.3);
    lintel.castShadow = true;
    bg.add(lintel);
    // letreiro "ESCOLA" na parede, entre as janelas do 2º andar (no lugar do relógio)
    const signTex = makeSignTexture(t("ESCOLA"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 70 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    // EQUIDISTANTE entre o topo da viga do pórtico e a base das molduras da 1ª
    // fileira com janelas centrais (r=2) — as fileiras escalam com b.h, então o
    // letreiro acompanha (y fixo colava na viga em prédios mais altos)
    const signRow2 = 2.7 + ((b.h - 2.3 - 2.7) * 2) / 3;
    const signBandTop = signRow2 - 1.075; // base da moldura (janela 1.55 + quadro 0.3)
    const signBandBot = doorH + 1.7; // topo da viga do pórtico
    const signH = Math.max(1.62, Math.min(1.86, signBandTop - signBandBot - 0.24));
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 5.0, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, (signBandTop + signBandBot) / 2, hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);
    const apple = makeApple();
    apple.scale.setScalar(0.9);
    apple.position.set(doorW / 2 + 1.1, doorH + 2.15, hd + 1.3);
    bg.add(apple);
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);

    // ---- PÁTIO: a CERQUINHA de giz de cera fica na FRENTE (z hd+3.7) e o parquinho
    //      DENTRO dela (entre a cerca e o prédio), com folga — antes os brinquedos
    //      brigavam com a cerca e tapavam as janelas ----
    const fenceZ = hd + 3.7;
    for (const sideX of [-1, 1]) {
        const x0 = sideX * 3.4;
        const x1 = sideX * (hw - 0.4);
        const n = 6;
        for (let i = 0; i < n; i++) {
            const px = x0 + ((x1 - x0) * i) / (n - 1);
            const crayon = new THREE.Group();
            const cbody = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 1.1, 10), accentMat(ACCENTS[(i + (sideX > 0 ? 3 : 0)) % ACCENTS.length]));
            cbody.position.y = 0.55;
            crayon.add(cbody);
            const ctip = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.3, 10), cbody.material);
            ctip.position.y = 1.25;
            crayon.add(ctip);
            crayon.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            crayon.position.set(px, 0, fenceZ);
            bg.add(crayon);
        }
        const railF = new THREE.Mesh(new THREE.BoxGeometry(Math.abs(x1 - x0) + 0.4, 0.1, 0.08), trim);
        railF.position.set((x0 + x1) / 2, 0.8, fenceZ);
        bg.add(railF);
    }

    // ---- PARQUINHO (dentro da cerquinha): ESCORREGADOR (esq) + BALANÇO (dir) ----
    {
        // escorregador em GRUPO, rampa deslizando PARA O CENTRO (paralela à fachada —
        // cabe no pátio raso sem furar a cerca)
        const g = new THREE.Group();
        for (const [dx, dz] of [
            [-0.35, -0.35],
            [-0.35, 0.35],
            [0.35, -0.35],
            [0.35, 0.35],
        ]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.6, 0.14), edgeMat);
            post.position.set(dx, 0.8, dz);
            g.add(post);
        }
        const platform = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.12, 0.95), accentMat("#5b8def"));
        platform.position.set(0, 1.62, 0);
        g.add(platform);
        const ramp = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.1, 0.8), accentMat("#e6584d"));
        ramp.rotation.z = -0.6;
        ramp.position.set(1.42, 1.02, 0);
        g.add(ramp);
        // saída plana no fim da rampa (a rampa não termina flutuando)
        const lip = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.1, 0.8), accentMat("#e6584d"));
        lip.position.set(2.62, 0.34, 0);
        g.add(lip);
        // ESCADA de verdade: 2 montantes encostados na plataforma + degraus entre eles
        const ladder = new THREE.Group();
        for (const lz of [-0.3, 0.3]) {
            const rail = new THREE.Mesh(new THREE.BoxGeometry(0.07, 1.75, 0.07), trim);
            rail.position.set(0, 0.875, lz);
            ladder.add(rail);
        }
        for (let r = 0; r < 4; r++) {
            const rung = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.6), trim);
            rung.position.set(0, 0.3 + r * 0.36, 0);
            ladder.add(rung);
        }
        ladder.rotation.z = -0.3; // inclinada: pé afastado, topo apoiado na plataforma
        ladder.position.set(-0.98, 0, 0);
        g.add(ladder);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        // rampa desliza para o lado EXTERNO (canto do prédio) — girado 180° para a
        // rampa não morrer em cima da coluna-lápis do portal
        g.rotation.y = Math.PI;
        g.position.set(-5.6, 0, hd + 1.9);
        bg.add(g);
    }
    {
        // balanço: pórtico em A + travessa + 2 assentos
        const sx = 5.9;
        const sz = hd + 2.1;
        for (const dx of [-1.1, 1.1]) {
            const legA = new THREE.Mesh(new THREE.BoxGeometry(0.12, 1.9, 0.12), edgeMat);
            legA.rotation.z = dx < 0 ? -0.18 : 0.18; // pé aberto p/ FORA, topo na travessa
            legA.position.set(sx + dx, 0.95, sz);
            legA.castShadow = true;
            bg.add(legA);
        }
        const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.4, 8), edgeMat);
        bar.rotation.z = Math.PI / 2;
        bar.position.set(sx, 1.85, sz);
        bg.add(bar);
        for (const dx of [-0.5, 0.5]) {
            for (const dr of [-0.14, 0.14]) {
                const rope = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.1, 0.05), dark);
                rope.position.set(sx + dx + dr, 1.28, sz);
                bg.add(rope);
            }
            const seat = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.3), accentMat("#f2c14e"));
            seat.position.set(sx + dx, 0.7, sz);
            seat.castShadow = true;
            bg.add(seat);
        }
    }

    // ---- MAIS BRINQUEDOS NO TETO (além dos blocos): GLOBO TERRESTRE + GIZES
    //      gigantes deitados — enriquecem a vista aérea ----
    {
        // globo: base + haste inclinada + esfera azul com "continentes" verdes
        const g = new THREE.Group();
        const gbase = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.28, 12), dark);
        gbase.position.y = 0.14;
        g.add(gbase);
        const gpost = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.1, 8), edgeMat);
        gpost.rotation.z = 0.4;
        gpost.position.set(-0.2, 0.75, 0);
        g.add(gpost);
        const globe = new THREE.Mesh(new THREE.SphereGeometry(1.15, 16, 12), accentMat("#5b8def"));
        globe.position.set(-0.42, 2.15, 0);
        g.add(globe);
        // continentes: calotas verdes achatadas sobre a esfera
        for (const [ax, ay, az] of [
            [0.8, 0.5, 0.5],
            [-0.6, -0.2, 0.85],
            [0.1, 0.9, -0.6],
        ]) {
            const cont = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), accentMat("#3ddc84"));
            cont.scale.set(1, 0.45, 0.8);
            const len = Math.hypot(ax, ay, az);
            cont.position.set(-0.42 + (ax / len) * 1.06, 2.15 + (ay / len) * 1.06, (az / len) * 1.06);
            cont.lookAt(-0.42, 2.15, 0);
            g.add(cont);
        }
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.scale.setScalar(1.24); // globo (-20%)
        g.position.set(6.1, roofY, -5.4);
        g.rotation.y = -0.4;
        bg.add(g);
    }
    {
        // gizes de cera deitados (cruzados) — -20%
        const mkCrayon = (color, x, z, ry) => {
            const g = new THREE.Group();
            const cbody = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 3.7, 12), accentMat(color));
            cbody.rotation.z = Math.PI / 2;
            g.add(cbody);
            const ctip = new THREE.Mesh(new THREE.ConeGeometry(0.44, 0.9, 12), accentMat(color));
            ctip.rotation.z = -Math.PI / 2;
            ctip.position.x = 2.3;
            g.add(ctip);
            g.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            g.position.set(x, roofY + 0.44, z);
            g.rotation.y = ry;
            bg.add(g);
        };
        // quase paralelos, lado a lado (cruzados = um furava o outro)
        mkCrayon("#ff8c42", -5.9, 4.9, 0.45);
        mkCrayon("#c879e6", -4.5, 2.95, 0.25);
    }
    {
        // LIVRO ABERTO no teto (novo): capa vermelha + páginas creme em "V" aberto
        const g = new THREE.Group();
        const coverMat = accentMat("#e6584d");
        const pageMat = new THREE.MeshStandardMaterial({ color: "#f6efe2", roughness: 0.9 });
        const a = 0.42; // inclinação das asas do V
        const half = 1.3; // meia-largura de cada asa
        for (const s of [-1, 1]) {
            const cv = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.16, 3.6), coverMat);
            cv.rotation.z = s * a;
            cv.position.set(s * half * Math.cos(a), 0.3 + half * Math.sin(a), 0);
            g.add(cv);
            const pg = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.16, 3.3), pageMat);
            pg.rotation.z = s * a;
            pg.position.set(s * (half - 0.05) * Math.cos(a), 0.44 + (half - 0.05) * Math.sin(a), 0);
            g.add(pg);
        }
        // lombada apoiando o V no teto
        const spineB = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.34, 3.6), coverMat);
        spineB.position.set(0, 0.17, 0);
        g.add(spineB);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        // +25%, afastado dos blocos A·B·C (folga ~1.3) sem passar da borda frontal
        g.scale.setScalar(1.25);
        g.position.set(4.9, roofY, 5.7);
        g.rotation.y = -0.2;
        bg.add(g);
    }
}

/**
 * GRADE PADRÃO de janelas 3D — reproduz o RITMO dos prédios de textura
 * (makeFacadeTextures: vãos de ~3.6u, janela = ~60% do vão, grade completa da
 * base à cornija). É a REFERÊNCIA de cobertura/distribuição p/ prédios novos:
 * usar estas colunas/fileiras/tamanho e só ABRIR EXCEÇÕES onde há marcos
 * (porta, letreiro, vitrine, emblema).
 */
function facadeGrid(faceW, faceH, maxFloors = Infinity) {
    const bays = Math.max(3, Math.round(faceW / 3.6));
    const bayW = faceW / bays;
    const y0 = 2.2; // acima da base/plinto
    const y1 = faceH - 1.1; // abaixo da cornija
    // maxFloors: prédios com nº fixo de andares (os andares ESTICAM p/ preencher)
    const floors = Math.max(2, Math.min(maxFloors, Math.round((y1 - y0) / 3.0)));
    const floorH = (y1 - y0) / floors;
    const cols = [];
    for (let i = 0; i < bays; i++) cols.push(-faceW / 2 + (i + 0.5) * bayW);
    const rows = [];
    for (let f = 0; f < floors; f++) rows.push(y0 + (f + 0.5) * floorH);
    return { cols, rows, winW: bayW * 0.6, winH: floorH * 0.6 };
}

/**
 * AGÊNCIA DE VIAGENS (benchmark = fintech/café/resort/kinder): identidade de
 * "viagem" em 3 marcos —
 *   • GLOBO TERRESTRE GIGANTE no TELHADO com ANEL DE ÓRBITA dourado inclinado e
 *     um AVIÃOZINHO dando a volta (o ícone aéreo, na escala da xícara do café);
 *   • PÔSTERES retroiluminados de destinos AO REDOR do prédio (arte na FACE do
 *     fundo via material-array, sem plano separado/z-fighting): PRAIA e MONTANHA
 *     na frente, CIDADE/DESERTO, FLORESTA/CRUZEIRO e BALÃO/PÔR DO SOL no térreo
 *     das outras faces (no lugar das janelas; a janela central fica);
 *   • letreiro "VIAGENS" dourado + RELÓGIOS DE FUSOS + marquise com friso
 *     azul-céu + MALAS na porta.
 */
function buildTravel(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.35,
        roughness: 0.5,
    });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.8 });

    // ---- corpo + base + cornija + laje (padrão da cidade) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- JANELAS: GRADE PADRÃO (facadeGrid — ritmo dos prédios de textura,
    //      cobertura edge-to-edge). Exceções por marcos: TÉRREO de todas as faces
    //      é dos BANNERS de destino (só a janela/porta central fica); FRENTE pula
    //      o centro da fileira do letreiro (letreiro) e a fileira acima (relógios) --
    const grid = facadeGrid(b.w, b.h, 3); // máx. 3 andares (pedido do usuário)
    const signRow = Math.min(1, grid.rows.length - 1);
    const signH = 1.9; // altura do letreiro "VIAGENS" (largura = signH*4.3)
    const posterMat = (kind) => {
        const tex = makeTravelPosterTexture(kind);
        return new THREE.MeshStandardMaterial({
            map: tex,
            emissiveMap: tex,
            emissive: new THREE.Color("#ffffff"),
            emissiveIntensity: 0.55,
            roughness: 0.6,
        });
    };
    {
        // banner de térreo nas faces laterais/fundos (art 3.4×3.3 em y=3.0 — cabe
        // entre o plinto e a moldura da fileira de cima mesmo em h=14)
        const addBanner = (f, u, kind) => {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            const panel = new THREE.Mesh(new THREE.BoxGeometry(3.4, 3.3, 0.18), [edgeMat, edgeMat, edgeMat, edgeMat, posterMat(kind), edgeMat]);
            panel.rotation.y = ry;
            panel.position.set(f.nx * (faceDist + 0.1) + tx * u, 3.0, f.nz * (faceDist + 0.1) + tz * u);
            bg.add(panel);
            const ft = 0.3;
            for (const [bw, bh, ox, oy] of [
                [3.4 + 2 * ft, ft, 0, 1.65 + ft / 2],
                [3.4 + 2 * ft, ft, 0, -1.65 - ft / 2],
                [ft, 3.3, -1.7 - ft / 2, 0],
                [ft, 3.3, 1.7 + ft / 2, 0],
            ]) {
                const bar = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.34), edgeMat);
                bar.rotation.y = ry;
                bar.position.set(f.nx * (faceDist + 0.12) + tx * (u + ox), 3.0 + oy, f.nz * (faceDist + 0.12) + tz * (u + ox));
                bar.castShadow = true;
                bg.add(bar);
            }
        };
        for (const f of [
            { nx: 0, nz: -1, posters: ["balloon", "island", "sunset"] },
            { nx: 1, nz: 0, posters: ["city", "safari", "desert"] },
            { nx: -1, nz: 0, posters: ["forest", "snow", "cruise"] },
        ]) {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            grid.rows.forEach((y, ri) => {
                if (ri === 0) return; // térreo inteiro = 3 banners (banner+banner+banner)
                for (const u of grid.cols) {
                    const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                    win.rotation.y = ry;
                    win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                    bg.add(win);
                }
            });
            addBanner(f, -5.8, f.posters[0]);
            addBanner(f, 0, f.posters[1]);
            addBanner(f, 5.8, f.posters[2]);
        }
        grid.rows.forEach((y, ri) => {
            if (ri === 0) return; // térreo da frente = porta + pôsteres
            if (ri === signRow + 1) return; // 3º andar = RELÓGIOS DE FUSO (abaixo)
            if (ri === signRow) return; // fileira do letreiro: janelas centradas abaixo
            for (const u of grid.cols) {
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.position.set(u, y, hd + 0.12);
                bg.add(win);
            }
        });
        // fileira do letreiro: 1 janela por lado, CENTRADA entre a borda da placa
        // e a quina do prédio (na coluna da grade ficava colada no canto)
        const uSign = ((signH * 4.3) / 2 + hw) / 2;
        for (const s of [-1, 1]) {
            const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
            win.position.set(s * uSign, grid.rows[signRow], hd + 0.12);
            bg.add(win);
        }
        // RELÓGIOS DE FUSOS HORÁRIOS (clássico de agência): um por vão na fileira
        // acima do letreiro — mostrador creme aceso + plaquinha dourada da cidade
        const clockRowY = grid.rows[signRow + 1];
        if (clockRowY !== undefined) {
            const CLOCKS = [
                { city: "N.YORK", h: 8, m: 10 },
                { city: "RIO", h: 10, m: 10 },
                { city: "LONDRES", h: 13, m: 10 },
                { city: "DUBAI", h: 17, m: 10 },
                { city: "TÓQUIO", h: 22, m: 10 },
            ];
            grid.cols.forEach((u, i) => {
                const ck = CLOCKS[i % CLOCKS.length];
                const faceTex = makeClockTexture(ck.h, ck.m);
                const faceMat = new THREE.MeshStandardMaterial({
                    map: faceTex,
                    emissiveMap: faceTex,
                    emissive: new THREE.Color("#f5e9bf"),
                    emissiveIntensity: 0.35,
                    roughness: 0.6,
                });
                const clock = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 0.16, 20), [edgeMat, faceMat, edgeMat]);
                clock.rotation.x = Math.PI / 2; // mostrador de frente p/ a rua (+Z)
                clock.position.set(u, clockRowY + 0.25, hd + 0.14);
                clock.castShadow = true;
                bg.add(clock);
                const cityTex = makeSignTexture(t(ck.city), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 60 });
                const cityMat = new THREE.MeshStandardMaterial({
                    map: cityTex,
                    emissiveMap: cityTex,
                    emissive: new THREE.Color("#ffd27a"),
                    emissiveIntensity: 0.45,
                    roughness: 0.7,
                });
                const plate = new THREE.Mesh(new THREE.BoxGeometry(1.89, 0.42, 0.12), [edgeMat, edgeMat, edgeMat, edgeMat, cityMat, edgeMat]);
                plate.position.set(u, clockRowY - 1.05, hd + 0.1);
                bg.add(plate);
            });
        }
    }

    // ---- ENTRADA: porta de vidro + marquise com friso azul-céu ----
    const doorW = 4.6;
    const doorH = 3.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
    dmull.position.set(0, doorH / 2 + 0.5, hd + 0.2);
    bg.add(dmull);
    // marquise estreita (doorW+1.2): não cruza na frente dos pôsteres (±5.8, borda
    // interna em ±3.8)
    const marq = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.32, 1.9), trim);
    marq.position.set(0, doorH + 1.05, hd + 0.85);
    marq.castShadow = true;
    bg.add(marq);
    const skyStrip = new THREE.Mesh(
        new THREE.BoxGeometry(doorW + 1.2, 0.34, 0.14),
        new THREE.MeshStandardMaterial({
            color: "#7fdcff",
            emissive: new THREE.Color("#7fdcff"),
            emissiveIntensity: 0.3,
            roughness: 0.5,
        }),
    );
    skyStrip.position.set(0, doorH + 1.05, hd + 1.75);
    bg.add(skyStrip);
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);

    // ---- LETREIRO "VIAGENS": ocupa o CENTRO da fileira do letreiro (janelas
    //      centradas nos flancos, posicionadas junto com a grade acima) ----
    const signTex = makeSignTexture(t("VIAGENS"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 62 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.3, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- VITRINES com PÔSTERES de destino retroiluminados (arte na FACE frontal
    //      do fundo, técnica do laboratório — sem z-fighting) ----
    // ±5.8: afastados da porta (em ±4.9 ficavam colados), mais para os cantos
    for (const [vx, kind] of [
        [-5.8, "beach"],
        [5.8, "mountain"],
    ]) {
        // painel ACIMA do plinto (base 1.4 alta fica À FRENTE da arte — cobria o
        // rótulo e arriscava z-fighting): arte vai de 1.45 a 4.95
        const panel = new THREE.Mesh(new THREE.BoxGeometry(3.4, 3.5, 0.18), [edgeMat, edgeMat, edgeMat, edgeMat, posterMat(kind), edgeMat]);
        panel.position.set(vx, 3.2, hd + 0.1);
        bg.add(panel);
        const ft = 0.3;
        for (const [bw, bh, ox, oy] of [
            [3.4 + 2 * ft, ft, 0, 1.75 + ft / 2],
            [3.4 + 2 * ft, ft, 0, -1.75 - ft / 2],
            [ft, 3.5, -1.7 - ft / 2, 0],
            [ft, 3.5, 1.7 + ft / 2, 0],
        ]) {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.34), edgeMat);
            bar.position.set(vx + ox, 3.2 + oy, hd + 0.12);
            bar.castShadow = true;
            bg.add(bar);
        }
    }

    // ---- MALAS no TETO ao lado do globo: 2x maiores e LADO A LADO (pedido do
    //      usuário — antes eram 1.5x empilhadas), cada uma com sua alça ----
    {
        const g = new THREE.Group();
        const case1 = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.9, 0.6), accentMat("#e6584d"));
        case1.position.set(-0.65, 0.45, 0);
        g.add(case1);
        for (const sx of [-0.38, 0.38]) {
            const strap = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.94, 0.64), dark);
            strap.position.set(-0.65 + sx, 0.45, 0);
            g.add(strap);
        }
        const handle1 = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.12, 0.1), dark);
        handle1.position.set(-0.65, 0.96, 0);
        g.add(handle1);
        const case2 = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.7, 0.5), accentMat("#f2c14e"));
        case2.position.set(0.62, 0.35, 0.1);
        case2.rotation.y = 0.15;
        g.add(case2);
        const handle2 = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.1, 0.08), dark);
        handle2.position.set(0.62, 0.76, 0.1);
        handle2.rotation.y = 0.15;
        g.add(handle2);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.scale.setScalar(3.0);
        g.position.set(4.6, b.h + 0.5, 4.2); // afastadas do pedestal do globo
        g.rotation.y = 0.5;
        bg.add(g);
    }

    // ---- GLOBO com ÓRBITA + AVIÃOZINHO no TELHADO (ícone aéreo) ----
    {
        const roofY = b.h + 0.5;
        const R = 3.9; // +30%
        const ped = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 2.2, 1.2, 14), edgeMat);
        ped.position.set(0, roofY + 0.6, 0);
        ped.castShadow = true;
        bg.add(ped);
        const gy = roofY + 1.2 + R * 0.92;
        const globe = new THREE.Mesh(new THREE.SphereGeometry(R, 18, 14), new THREE.MeshStandardMaterial({ color: "#4a8fe0", roughness: 0.65 }));
        globe.position.set(0, gy, 0);
        globe.castShadow = true;
        bg.add(globe);
        // continentes: calotas verdes achatadas apontando p/ fora
        const contMat = accentMat("#3ddc84");
        const conts = [
            [0.4, 0.9, 1.15],
            [1.9, 0.5, 1.0],
            [3.2, 1.1, 1.1],
            [4.4, 0.4, 0.9],
            [5.5, 1.0, 1.2],
            [2.6, 2.2, 0.8],
            [0.9, 2.5, 0.7],
        ];
        for (const [az, pol, s] of conts) {
            const dir = new THREE.Vector3(Math.cos(az) * Math.sin(pol + 0.6), Math.cos(pol + 0.6) * 0.9, Math.sin(az) * Math.sin(pol + 0.6)).normalize();
            const blob = new THREE.Mesh(new THREE.SphereGeometry(s * 1.3, 8, 6), contMat);
            blob.scale.z = 0.35;
            blob.position
                .copy(dir)
                .multiplyScalar(R * 0.96)
                .add(globe.position);
            blob.lookAt(globe.position);
            bg.add(blob);
        }
        // órbita inclinada + avião
        const orbit = new THREE.Group();
        const ring = new THREE.Mesh(new THREE.TorusGeometry(5.85, 0.16, 8, 48), gold);
        ring.rotation.x = Math.PI / 2;
        orbit.add(ring);
        // aviãozinho CREME de asas VERMELHAS (contraste contra o globo azul/verde)
        const red = accentMat("#e6584d");
        const plane = new THREE.Group();
        const fus = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.24, 1.7, 8), cream);
        fus.rotation.z = Math.PI / 2;
        plane.add(fus);
        // nariz ARREDONDADO (o cone agudo lia como foguete na vista de cima)
        const nose = new THREE.Mesh(new THREE.SphereGeometry(0.24, 8, 6), cream);
        nose.scale.x = 1.35;
        nose.position.x = 0.85;
        plane.add(nose);
        const wing = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.07, 2.5), red);
        wing.position.x = 0.15;
        plane.add(wing);
        const fin = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.55, 0.08), red);
        fin.position.set(-0.72, 0.32, 0);
        plane.add(fin);
        const stab = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.06, 0.95), cream);
        stab.position.set(-0.72, 0.12, 0);
        plane.add(stab);
        // no ponto FRONTAL da órbita (+Z, de frente p/ a rua), tangente = +X;
        // +50% e INCLINADO na curva (banking) — sem o roll, a asa interna do avião
        // maior atravessaria o globo (R 3.9)
        plane.scale.setScalar(2.85);
        plane.rotation.x = 0.45;
        plane.position.set(0, 0.9, 6.5);
        orbit.add(plane);
        orbit.position.set(0, gy, 0);
        orbit.rotation.z = 0.3;
        orbit.rotation.x = -0.15;
        orbit.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        bg.add(orbit);
    }
}

/** Pôster de destino retroiluminado da agência (beach | mountain | city |
 *  desert | forest | cruise | balloon | sunset) — borda verde + rótulo, no
 *  estilo dos pôsteres do laboratório. */
function makeTravelPosterTexture(kind) {
    const LABELS = {
        beach: "PRAIA",
        mountain: "MONTANHA",
        city: "CIDADE",
        desert: "DESERTO",
        forest: "FLORESTA",
        cruise: "CRUZEIRO",
        balloon: "BALÃO",
        sunset: "PÔR DO SOL",
        safari: "SAFÁRI",
        snow: "NEVE",
        island: "ILHA",
    };
    const canvas = document.createElement("canvas");
    canvas.width = 224;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f4efe0";
    ctx.fillRect(0, 0, 224, 256);
    if (kind === "beach") {
        ctx.fillStyle = "#8fd8ff"; // céu
        ctx.fillRect(14, 14, 196, 130);
        ctx.fillStyle = "#ffd94e"; // sol
        ctx.beginPath();
        ctx.arc(160, 58, 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#3f8fe0"; // mar
        ctx.fillRect(14, 144, 196, 34);
        ctx.fillStyle = "#f2d49b"; // areia
        ctx.fillRect(14, 178, 196, 30);
        ctx.strokeStyle = "#7a5230"; // tronco da palmeira
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.moveTo(62, 186);
        ctx.quadraticCurveTo(56, 130, 70, 96);
        ctx.stroke();
        ctx.strokeStyle = "#2fae62"; // folhas
        ctx.lineWidth = 9;
        for (const [ex, ey] of [
            [36, 84],
            [58, 62],
            [96, 68],
            [104, 96],
        ]) {
            ctx.beginPath();
            ctx.moveTo(70, 96);
            ctx.quadraticCurveTo((70 + ex) / 2, Math.min(ey, 96) - 16, ex, ey);
            ctx.stroke();
        }
    } else if (kind === "city") {
        ctx.fillStyle = "#2e3f6e"; // céu de anoitecer
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#f4ead2"; // lua
        ctx.beginPath();
        ctx.arc(168, 52, 16, 0, Math.PI * 2);
        ctx.fill();
        const tower = (x, w, top, colour) => {
            ctx.fillStyle = colour;
            ctx.fillRect(x, top, w, 208 - top);
            ctx.fillStyle = "#ffd27a"; // janelinhas acesas
            for (let wy = top + 8; wy < 198; wy += 14) {
                for (let wx = x + 5; wx < x + w - 6; wx += 12) ctx.fillRect(wx, wy, 6, 7);
            }
        };
        tower(24, 42, 84, "#1c2b4a");
        tower(76, 52, 46, "#243a5e");
        tower(138, 40, 100, "#1c2b4a");
        tower(184, 22, 128, "#243a5e");
    } else if (kind === "desert") {
        ctx.fillStyle = "#ffe3a8"; // céu quente
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#ff9d4e"; // sol
        ctx.beginPath();
        ctx.arc(64, 62, 22, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#dfa958"; // duna de trás
        ctx.beginPath();
        ctx.moveTo(14, 208);
        ctx.quadraticCurveTo(90, 112, 210, 182);
        ctx.lineTo(210, 208);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#c68f3f"; // duna da frente
        ctx.beginPath();
        ctx.moveTo(14, 208);
        ctx.quadraticCurveTo(150, 144, 210, 208);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#2f7d4c"; // cacto (grande, alto contraste)
        ctx.fillRect(48, 118, 14, 72);
        ctx.fillRect(28, 132, 14, 10);
        ctx.fillRect(28, 108, 12, 34);
        ctx.fillRect(68, 142, 14, 10);
        ctx.fillRect(72, 120, 12, 32);
    } else if (kind === "forest") {
        ctx.fillStyle = "#cfe8ff"; // céu
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#ffd94e";
        ctx.beginPath();
        ctx.arc(174, 50, 16, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#8fb573"; // chão
        ctx.fillRect(14, 178, 196, 30);
        const pine = (x, base, s, colour) => {
            ctx.fillStyle = "#7a5230";
            ctx.fillRect(x - 4 * s, base - 12 * s, 8 * s, 12 * s);
            ctx.fillStyle = colour;
            for (let t = 0; t < 3; t++) {
                const w = (34 - t * 8) * s;
                const y0 = base - (12 + t * 22) * s;
                ctx.beginPath();
                ctx.moveTo(x - w / 2, y0);
                ctx.lineTo(x, y0 - 30 * s);
                ctx.lineTo(x + w / 2, y0);
                ctx.closePath();
                ctx.fill();
            }
        };
        pine(60, 186, 1.1, "#2e7d4f");
        pine(122, 190, 0.85, "#3b9d63");
        pine(170, 186, 1.0, "#2e7d4f");
    } else if (kind === "cruise") {
        ctx.fillStyle = "#bfe4ff"; // céu
        ctx.fillRect(14, 14, 196, 130);
        ctx.fillStyle = "#3f8fe0"; // mar
        ctx.fillRect(14, 144, 196, 64);
        ctx.fillStyle = "#ffffff"; // casco
        ctx.beginPath();
        ctx.moveTo(36, 128);
        ctx.lineTo(192, 128);
        ctx.lineTo(174, 156);
        ctx.lineTo(52, 156);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#f4efe0"; // convés
        ctx.fillRect(66, 106, 96, 22);
        ctx.fillStyle = "#e6584d"; // chaminés
        ctx.fillRect(92, 88, 14, 18);
        ctx.fillRect(118, 88, 14, 18);
        ctx.fillStyle = "#243018"; // vigias
        for (let px = 62; px <= 162; px += 20) {
            ctx.beginPath();
            ctx.arc(px, 140, 4, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.strokeStyle = "#ffffff"; // espuma
        ctx.lineWidth = 4;
        for (const [wx, wy, wl] of [
            [30, 172, 34],
            [96, 184, 40],
            [160, 170, 30],
        ]) {
            ctx.beginPath();
            ctx.moveTo(wx, wy);
            ctx.lineTo(wx + wl, wy);
            ctx.stroke();
        }
    } else if (kind === "balloon") {
        ctx.fillStyle = "#d6ecff"; // céu
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#8fd07a"; // campo
        ctx.fillRect(14, 182, 196, 26);
        ctx.fillStyle = "#e6584d"; // envelope
        ctx.beginPath();
        ctx.arc(112, 92, 52, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#ffd27a"; // gomo central
        ctx.fillRect(104, 42, 16, 100);
        ctx.strokeStyle = "#ffd27a"; // gomos laterais curvos
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(112, 42);
        ctx.quadraticCurveTo(76, 92, 108, 142);
        ctx.moveTo(112, 42);
        ctx.quadraticCurveTo(148, 92, 116, 142);
        ctx.stroke();
        ctx.strokeStyle = "#a83a32"; // cordas
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(88, 132);
        ctx.lineTo(102, 164);
        ctx.moveTo(136, 132);
        ctx.lineTo(122, 164);
        ctx.stroke();
        ctx.fillStyle = "#7a5230"; // cesto
        ctx.fillRect(98, 164, 28, 20);
    } else if (kind === "sunset") {
        const sk = ctx.createLinearGradient(0, 14, 0, 150);
        sk.addColorStop(0, "#ff9d5c");
        sk.addColorStop(1, "#ffd27a");
        ctx.fillStyle = sk;
        ctx.fillRect(14, 14, 196, 136);
        ctx.fillStyle = "#ffec9e"; // sol no horizonte
        ctx.beginPath();
        ctx.arc(112, 150, 34, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = "#c76b3f"; // mar escurecido
        ctx.fillRect(14, 150, 196, 58);
        ctx.strokeStyle = "#ffec9e"; // reflexo
        ctx.lineWidth = 4;
        for (const [wy, wl] of [
            [164, 44],
            [178, 30],
            [192, 18],
        ]) {
            ctx.beginPath();
            ctx.moveTo(112 - wl / 2, wy);
            ctx.lineTo(112 + wl / 2, wy);
            ctx.stroke();
        }
    } else if (kind === "safari") {
        ctx.fillStyle = "#ffce8a"; // céu de savana
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#ff8c42"; // sol baixo
        ctx.beginPath();
        ctx.arc(64, 130, 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#c9a24e"; // savana
        ctx.fillRect(14, 160, 196, 48);
        ctx.fillStyle = "#6e4a26"; // acácia: tronco + galho
        ctx.fillRect(142, 92, 8, 72);
        ctx.beginPath();
        ctx.moveTo(146, 108);
        ctx.lineTo(122, 88);
        ctx.lineTo(126, 84);
        ctx.lineTo(148, 100);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#4a7d3c"; // copa achatada
        ctx.beginPath();
        ctx.ellipse(140, 78, 52, 16, 0, 0, Math.PI * 2);
        ctx.fill();
    } else if (kind === "snow") {
        ctx.fillStyle = "#bcd8f0"; // céu de inverno
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#ffffff"; // encosta
        ctx.beginPath();
        ctx.moveTo(14, 208);
        ctx.lineTo(14, 128);
        ctx.lineTo(210, 190);
        ctx.lineTo(210, 208);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#dfe9f2"; // encosta sombreada
        ctx.beginPath();
        ctx.moveTo(210, 208);
        ctx.lineTo(210, 118);
        ctx.lineTo(96, 178);
        ctx.lineTo(96, 208);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#2e7d4f"; // pinheiro nevado
        for (let t = 0; t < 3; t++) {
            const w = 40 - t * 10;
            const y0 = 172 - t * 20;
            ctx.beginPath();
            ctx.moveTo(52 - w / 2, y0);
            ctx.lineTo(52, y0 - 26);
            ctx.lineTo(52 + w / 2, y0);
            ctx.closePath();
            ctx.fill();
        }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(38, 128, 28, 5);
        ctx.fillRect(42, 108, 20, 5);
    } else if (kind === "island") {
        ctx.fillStyle = "#8fd8ff"; // céu
        ctx.fillRect(14, 14, 196, 90);
        ctx.fillStyle = "#ffd94e"; // sol
        ctx.beginPath();
        ctx.arc(58, 52, 20, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#3f8fe0"; // mar aberto
        ctx.fillRect(14, 104, 196, 104);
        ctx.fillStyle = "#e8c98f"; // ilhota
        ctx.beginPath();
        ctx.ellipse(140, 150, 52, 20, 0, Math.PI, 0);
        ctx.fill();
        ctx.strokeStyle = "#7a5230"; // palmeira da ilha
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(140, 148);
        ctx.quadraticCurveTo(136, 112, 148, 92);
        ctx.stroke();
        ctx.strokeStyle = "#2fae62";
        ctx.lineWidth = 8;
        for (const [ex, ey] of [
            [118, 84],
            [142, 64],
            [176, 78],
            [178, 102],
        ]) {
            ctx.beginPath();
            ctx.moveTo(148, 92);
            ctx.quadraticCurveTo((148 + ex) / 2, Math.min(ey, 92) - 14, ex, ey);
            ctx.stroke();
        }
        ctx.strokeStyle = "#ffffff"; // espuma
        ctx.lineWidth = 4;
        for (const [wx, wy, wl] of [
            [30, 176, 36],
            [88, 192, 44],
            [168, 184, 30],
        ]) {
            ctx.beginPath();
            ctx.moveTo(wx, wy);
            ctx.lineTo(wx + wl, wy);
            ctx.stroke();
        }
    } else {
        ctx.fillStyle = "#9cc0f2"; // céu
        ctx.fillRect(14, 14, 196, 194);
        ctx.fillStyle = "#ffd94e";
        ctx.beginPath();
        ctx.arc(52, 54, 18, 0, Math.PI * 2);
        ctx.fill();
        const mount = (peakX, peakY, halfW, colour) => {
            ctx.fillStyle = colour;
            ctx.beginPath();
            ctx.moveTo(peakX - halfW, 208);
            ctx.lineTo(peakX, peakY);
            ctx.lineTo(peakX + halfW, 208);
            ctx.closePath();
            ctx.fill();
            ctx.fillStyle = "#ffffff"; // neve: pico com barra em zigue-zague
            ctx.beginPath();
            ctx.moveTo(peakX - 17, peakY + 34);
            ctx.lineTo(peakX, peakY);
            ctx.lineTo(peakX + 17, peakY + 34);
            ctx.lineTo(peakX + 9, peakY + 26);
            ctx.lineTo(peakX, peakY + 34);
            ctx.lineTo(peakX - 9, peakY + 26);
            ctx.closePath();
            ctx.fill();
        };
        mount(80, 74, 80, "#3f7458");
        mount(166, 108, 72, "#5d9c77");
    }
    ctx.fillStyle = "#0e3a22"; // faixa do rótulo
    ctx.fillRect(14, 208, 196, 34);
    ctx.fillStyle = "#ffd27a";
    ctx.font = "800 26px Inter, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(LABELS[kind] || kind.toUpperCase(), 112, 226);
    ctx.lineWidth = 8;
    ctx.strokeStyle = "#1f8a3a";
    ctx.strokeRect(4, 4, 216, 248);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * LABORATÓRIO DE PESQUISA (2º laboratório, distinto do CLÍNICO de exames do A3
 * — aquele é verde+branco com átomo/pôsteres; este é QUÍMICA/pesquisa lúdica,
 * benchmark = café/agência/escola infantil/resort):
 *   • ERLENMEYER GIGANTE no TELHADO com líquido verde ACESO e BOLHAS emissivas
 *     subindo (o ícone aéreo, na escala da xícara do café) + ESTANTE com 3 TUBOS
 *     DE ENSAIO coloridos + MOLÉCULA H₂O de esferas;
 *   • entrada com COLUNAS DE DNA (reusa makeDNA, que estava sem uso) + marquise
 *     com friso VIOLETA + letreiro "LABORATÓRIO";
 *   • térreo: 2 LOUSAS DE FÓRMULAS retroiluminadas (giz sobre verde-escuro; arte
 *     na FACE do fundo via material-array, sem z-fighting) na frente e AZULEJOS
 *     DE ELEMENTOS da tabela periódica (makeLetterTexture) nas laterais/fundos;
 *   • janelas = facadeGrid (máx. 3 andares, como a agência).
 */
function buildResearch(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.8 });
    const paleGlass = new THREE.MeshStandardMaterial({ color: "#dff0e6", roughness: 0.35 });

    // ---- corpo + base + cornija + laje (padrão da cidade) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- JANELAS: facadeGrid (máx. 3 andares). Laterais/fundos: térreo vira
    //      AZULEJOS DE ELEMENTOS; frente: térreo é porta+lousas, fileira do
    //      letreiro tem 1 janela por lado centrada (linguagem da agência) ----
    const grid = facadeGrid(b.w, b.h, 3);
    const signRow = Math.min(1, grid.rows.length - 1);
    const signH = 2.2; // letreiro maior (as janelas da fileira foram p/ as colunas externas)
    {
        // azulejo de elemento químico: placa colorida com a LETRA (makeLetterTexture)
        const addElemTile = (f, u, letter, colour) => {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            // azulejo amarelo usa letra ESCURA (branco sobre amarelo media 1.3:1 WCAG)
            const darkFg = colour === "#f2c14e";
            const tex = makeLetterTexture(letter, {
                bg: colour,
                fg: darkFg ? "#0e3a22" : "#ffffff",
                border: darkFg ? "#0e3a22" : "#ffffff",
            });
            const face = new THREE.MeshStandardMaterial({
                map: tex,
                emissiveMap: tex,
                emissive: new THREE.Color("#ffffff"),
                emissiveIntensity: 0.28,
                roughness: 0.65,
            });
            const side = accentMat(colour);
            const tile = new THREE.Mesh(new THREE.BoxGeometry(3.0, 3.0, 0.3), [side, side, side, side, face, side]);
            tile.rotation.y = ry;
            tile.position.set(f.nx * (faceDist + 0.15) + tx * u, 3.0, f.nz * (faceDist + 0.15) + tz * u);
            tile.castShadow = true;
            bg.add(tile);
        };
        for (const f of [
            {
                nx: 0,
                nz: -1,
                elems: [
                    ["F", "#f2c14e"],
                    ["P", "#e6584d"],
                    ["B", "#5b8def"],
                ],
            },
            {
                nx: 1,
                nz: 0,
                elems: [
                    ["H", "#e6584d"],
                    ["C", "#5b8def"],
                    ["O", "#f2c14e"],
                ],
            },
            {
                nx: -1,
                nz: 0,
                elems: [
                    ["N", "#3ddc84"],
                    ["K", "#c879e6"],
                    ["S", "#ff8c42"],
                ],
            },
        ]) {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            grid.rows.forEach((y, ri) => {
                if (ri === 0) return; // térreo = azulejos de elementos
                for (const u of grid.cols) {
                    const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                    win.rotation.y = ry;
                    win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                    bg.add(win);
                }
            });
            addElemTile(f, -5.8, f.elems[0][0], f.elems[0][1]);
            addElemTile(f, 0, f.elems[1][0], f.elems[1][1]);
            addElemTile(f, 5.8, f.elems[2][0], f.elems[2][1]);
        }
        // FRENTE: fileiras acima do térreo (a do letreiro só tem as janelas centradas)
        grid.rows.forEach((y, ri) => {
            if (ri === 0 || ri === signRow) return;
            for (const u of grid.cols) {
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.position.set(u, y, hd + 0.12);
                bg.add(win);
            }
        });
        // janelas da fileira do letreiro ALINHADAS às colunas externas da grade
        // (mesma prumada da 1ª/última janela do andar de cima — pedido do usuário)
        for (const u of [grid.cols[0], grid.cols[grid.cols.length - 1]]) {
            const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
            win.position.set(u, grid.rows[signRow], hd + 0.12);
            bg.add(win);
        }
    }

    // ---- ENTRADA: porta de vidro + marquise com friso VIOLETA + degrau ----
    const doorW = 4.6;
    const doorH = 3.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
    dmull.position.set(0, doorH / 2 + 0.5, hd + 0.2);
    bg.add(dmull);
    const marq = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.32, 1.9), trim);
    marq.position.set(0, doorH + 1.05, hd + 0.85);
    marq.castShadow = true;
    bg.add(marq);
    // friso VERDE-NEON (o violeta destoava do trim aprovado da cidade — painel)
    const neonStrip = new THREE.Mesh(
        new THREE.BoxGeometry(doorW + 1.2, 0.34, 0.14),
        new THREE.MeshStandardMaterial({
            color: "#3ddc84",
            emissive: new THREE.Color("#3ddc84"),
            emissiveIntensity: 0.35,
            roughness: 0.5,
        }),
    );
    neonStrip.position.set(0, doorH + 1.05, hd + 1.75);
    bg.add(neonStrip);
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);
    // COLUNAS DE DNA flanqueando a entrada — ESGUIAS e leves (steps/esferas
    // menores): as gordas escondiam as lousas e custavam ~12k tris
    for (const sx of [-1, 1]) {
        const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.5, 12), edgeMat);
        ped.position.set(sx * 3.5, 0.25, hd + 1.5);
        ped.castShadow = true;
        bg.add(ped);
        const dna = makeDNA(3.7, 0.36, 2.2, accentMat("#5b8def"), accentMat("#3ddc84"), cream, { steps: 16, sphereR: 0.22, lowPoly: true });
        dna.position.set(sx * 3.5, 0.5, hd + 1.5);
        bg.add(dna);
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.3, 12), edgeMat);
        cap.position.set(sx * 3.5, 4.35, hd + 1.5);
        cap.castShadow = true;
        bg.add(cap);
    }

    // ---- LETREIRO "LABORATÓRIO" na fileira do meio ----
    const signTex = makeSignTexture(t("LABORATÓRIO"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 52 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.3, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- LOUSAS DE FÓRMULAS retroiluminadas no térreo da frente (±5.8) ----
    for (const [vx, kind] of [
        [-5.8, "formula"],
        [5.8, "molecule"],
    ]) {
        const tex = makeFormulaBoardTexture(kind);
        const artMat = new THREE.MeshStandardMaterial({
            map: tex,
            emissiveMap: tex,
            emissive: new THREE.Color("#ffffff"),
            emissiveIntensity: 0.42,
            roughness: 0.6,
        });
        const panel = new THREE.Mesh(new THREE.BoxGeometry(3.4, 3.5, 0.18), [edgeMat, edgeMat, edgeMat, edgeMat, artMat, edgeMat]);
        panel.position.set(vx, 3.2, hd + 0.1);
        bg.add(panel);
        const ft = 0.3;
        for (const [bw, bh, ox, oy] of [
            [3.4 + 2 * ft, ft, 0, 1.75 + ft / 2],
            [3.4 + 2 * ft, ft, 0, -1.75 - ft / 2],
            [ft, 3.5, -1.7 - ft / 2, 0],
            [ft, 3.5, 1.7 + ft / 2, 0],
        ]) {
            const bar = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.34), edgeMat);
            bar.position.set(vx + ox, 3.2 + oy, hd + 0.12);
            bar.castShadow = true;
            bg.add(bar);
        }
    }

    // ---- TELHADO: ERLENMEYER GIGANTE + BOLHAS + TUBOS DE ENSAIO + MOLÉCULA ----
    const roofY = b.h + 0.5;
    {
        // erlenmeyer: cone de LÍQUIDO verde aceso embaixo + cone de vidro + gargalo
        // (peças EMPILHADAS, sem sobreposição — silhueta contínua, sem z-fight)
        const S = 2.4;
        const fx = -2.6;
        const fz = 0.6;
        // VERMELHO (pedido do usuário): contraste máximo (complementar) com o
        // teto/prédio verdes — o verde sumia e o amarelo confundia com as janelas
        const liqMat = new THREE.MeshStandardMaterial({
            color: "#e6584d",
            emissive: new THREE.Color("#ff5a4d"),
            emissiveIntensity: 0.6,
            roughness: 0.5,
        });
        // líquido em ~63% do cone (nível baixo lia como "funil branco" do alto)
        const liq = new THREE.Mesh(new THREE.CylinderGeometry(0.92 * S, 1.55 * S, 1.45 * S, 18), liqMat);
        liq.position.set(fx, roofY + 0.725 * S, fz);
        liq.castShadow = true;
        bg.add(liq);
        const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.55 * S, 0.92 * S, 0.85 * S, 18), paleGlass);
        glass.position.set(fx, roofY + (1.45 + 0.425) * S, fz);
        glass.castShadow = true;
        bg.add(glass);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.55 * S, 0.55 * S, 0.8 * S, 14), paleGlass);
        neck.position.set(fx, roofY + (2.3 + 0.4) * S, fz);
        neck.castShadow = true;
        bg.add(neck);
        const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.68 * S, 0.68 * S, 0.14 * S, 14), paleGlass);
        rim.position.set(fx, roofY + 2.77 * S, fz);
        bg.add(rim);
        // bolhas emissivas subindo em "S" — vermelho mais claro que o líquido
        const bubMat = new THREE.MeshStandardMaterial({
            color: "#ff8a7a",
            emissive: new THREE.Color("#ff8a7a"),
            emissiveIntensity: 0.8,
            roughness: 0.4,
        });
        const neckTop = roofY + 2.84 * S;
        for (const [oy, ox, s] of [
            [0.5, 0.1, 0.5],
            [1.3, -0.35, 0.62],
            [2.2, 0.3, 0.76],
            [3.3, -0.15, 0.9],
        ]) {
            const bub = new THREE.Mesh(new THREE.SphereGeometry(s, 10, 8), bubMat);
            bub.position.set(fx + ox, neckTop + oy, fz + ox * 0.4);
            bg.add(bub);
        }
    }
    {
        // estante com 3 tubos de ensaio (líquidos coloridos acesos)
        const rx = 4.3;
        const rz = -3.4;
        // estrutura ALTA (~50% a mais) p/ a silhueta destacar de longe
        const rbase = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.5, 1.5), edgeMat);
        rbase.position.set(rx, roofY + 0.25, rz);
        rbase.castShadow = true;
        bg.add(rbase);
        const rail = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.4, 1.2), trim);
        rail.position.set(rx, roofY + 3.85, rz);
        rail.castShadow = true;
        bg.add(rail);
        for (const sp of [-1, 1]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.35, 3.6, 1.2), trim);
            post.position.set(rx + sp * 2.12, roofY + 2.05, rz);
            post.castShadow = true;
            bg.add(post);
        }
        const tubes = [
            ["#e6584d", -1.15],
            ["#5b8def", 0],
            ["#ff8c42", 1.15],
        ];
        for (const [tc, toff] of tubes) {
            const liqT = new THREE.MeshStandardMaterial({
                color: tc,
                emissive: new THREE.Color(tc),
                emissiveIntensity: 0.45,
                roughness: 0.5,
            });
            const bot = new THREE.Mesh(new THREE.SphereGeometry(0.56, 10, 8), liqT);
            bot.position.set(rx + toff, roofY + 1.15, rz);
            bot.castShadow = true;
            bg.add(bot);
            const liquid = new THREE.Mesh(new THREE.CylinderGeometry(0.56, 0.56, 2.3, 12), liqT);
            liquid.position.set(rx + toff, roofY + 2.3, rz);
            bg.add(liquid);
            // vidro CURTO no topo (tampas altas liam como esferas brancas soltas)
            const glassT = new THREE.Mesh(new THREE.CylinderGeometry(0.56, 0.56, 1.35, 12), paleGlass);
            glassT.position.set(rx + toff, roofY + 4.12, rz);
            glassT.castShadow = true;
            bg.add(glassT);
        }
    }
    {
        // molécula H₂O: oxigênio vermelho + 2 hidrogênios creme + ligações
        const g = new THREE.Group();
        const O = new THREE.Mesh(new THREE.SphereGeometry(1.05, 14, 12), accentMat("#e6584d"));
        O.position.y = 1.05;
        g.add(O);
        for (const sx of [-1, 1]) {
            const H = new THREE.Mesh(new THREE.SphereGeometry(0.62, 12, 10), cream);
            H.position.set(sx * 1.35, 2.0, 0);
            g.add(H);
            const bond = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 1.7, 8), edgeMat);
            bond.position.set(sx * 0.68, 1.53, 0);
            bond.rotation.z = sx * -0.96;
            g.add(bond);
        }
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        // canto frontal-DIREITO (área que ficava vazia), 1.25× — o blob com as
        // tampas do rack foi resolvido ENCURTANDO o vidro dos tubos (acima)
        g.scale.setScalar(1.25);
        g.position.set(4.9, roofY, 4.9);
        g.rotation.y = -0.3;
        bg.add(g);
    }
}

/** Lousa de fórmulas retroiluminada do laboratório de pesquisa ('formula' |
 *  'molecule') — giz claro sobre verde-lousa, moldura de madeira. */
function makeFormulaBoardTexture(kind) {
    const canvas = document.createElement("canvas");
    canvas.width = 224;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#0e3a22"; // lousa
    ctx.fillRect(0, 0, 224, 256);
    ctx.strokeStyle = "#7a5230"; // moldura de madeira
    ctx.lineWidth = 12;
    ctx.strokeRect(6, 6, 212, 244);
    const chalk = "#e8f0e4";
    ctx.strokeStyle = chalk;
    ctx.fillStyle = chalk;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (kind === "formula") {
        // UM conteúdo grande por lousa (3 conteúdos empilhados viravam ruído de
        // longe — painel): só E=mc², bem grande, com sublinhado de giz
        ctx.font = "700 52px Inter, Arial, sans-serif";
        ctx.fillText("E=mc²", 112, 108);
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(42, 148);
        ctx.lineTo(182, 148);
        ctx.stroke();
        ctx.fillStyle = "#ffd27a";
        ctx.font = "700 30px Inter, Arial, sans-serif";
        ctx.fillText("π≈3,14", 112, 190);
    } else {
        // molécula: anel de benzeno + ligações
        ctx.lineWidth = 5;
        const cx = 112;
        const cy = 96;
        const R = 52;
        ctx.beginPath();
        for (let i = 0; i <= 6; i++) {
            const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
            const px = cx + Math.cos(a) * R;
            const py = cy + Math.sin(a) * R;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.stroke();
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
            ctx.fillStyle = i % 2 === 0 ? "#ffd27a" : chalk;
            ctx.beginPath();
            ctx.arc(cx + Math.cos(a) * R, cy + Math.sin(a) * R, 9, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.fillStyle = chalk;
        ctx.font = "700 30px Inter, Arial, sans-serif";
        ctx.fillText("C₆H₆", 112, 190);
        // setinha de reação
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(60, 222);
        ctx.lineTo(164, 222);
        ctx.lineTo(150, 212);
        ctx.moveTo(164, 222);
        ctx.lineTo(150, 232);
        ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * BIBLIOTECA (benchmark = café/agência/escola infantil/resort/lab de pesquisa):
 *   • PILHA DE LIVROS GIGANTES no TELHADO com uma CORUJA low-poly empoleirada
 *     (o ícone aéreo) + LUMINÁRIA DE LEITURA clássica (cúpula verde emissiva,
 *     brilha à noite) + ÓCULOS DE LEITURA deitados no teto;
 *   • entrada com COLUNAS DE LIVROS EMPILHADOS (família das colunas temáticas:
 *     lápis da escola infantil, DNA do lab) + marquise com friso dourado;
 *   • térreo: VITRINES DE ESTANTES retroiluminadas (lombadas coloridas, arte na
 *     FACE do fundo via material-array, sem z-fighting) em TODAS as faces (2 na
 *     frente + 3 nas laterais/fundos);
 *   • letreiro "BIBLIOTECA" padrão + janelas facadeGrid (máx. 3 andares).
 */
function buildLibrary(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });

    // ---- corpo + base + cornija + laje (padrão da cidade) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- JANELAS: facadeGrid (máx. 3 andares); térreo de todas as faces é das
    //      VITRINES DE ESTANTE; frente segue a linguagem letreiro+janelas externas --
    const grid = facadeGrid(b.w, b.h, 3);
    const signRow = Math.min(1, grid.rows.length - 1);
    const signH = 2.2;
    const shelfMat = (variant) => {
        const tex = makeBookshelfTexture(variant);
        return new THREE.MeshStandardMaterial({
            map: tex,
            emissiveMap: tex,
            emissive: new THREE.Color("#ffffff"),
            emissiveIntensity: 0.45,
            roughness: 0.6,
        });
    };
    {
        // vitrine de estante (mesma mecânica dos banners da agência)
        const addShelf = (f, u, variant) => {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : f.nx === -1 ? -Math.PI / 2 : 0;
            const panel = new THREE.Mesh(new THREE.BoxGeometry(3.4, 3.3, 0.18), [edgeMat, edgeMat, edgeMat, edgeMat, shelfMat(variant), edgeMat]);
            panel.rotation.y = ry;
            panel.position.set(f.nx * (faceDist + 0.1) + tx * u, 3.0, f.nz * (faceDist + 0.1) + tz * u);
            bg.add(panel);
            const ft = 0.3;
            for (const [bw, bh, ox, oy] of [
                [3.4 + 2 * ft, ft, 0, 1.65 + ft / 2],
                [3.4 + 2 * ft, ft, 0, -1.65 - ft / 2],
                [ft, 3.3, -1.7 - ft / 2, 0],
                [ft, 3.3, 1.7 + ft / 2, 0],
            ]) {
                const bar = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.34), edgeMat);
                bar.rotation.y = ry;
                bar.position.set(f.nx * (faceDist + 0.12) + tx * (u + ox), 3.0 + oy, f.nz * (faceDist + 0.12) + tz * (u + ox));
                bar.castShadow = true;
                bg.add(bar);
            }
        };
        // variant ÚNICO por vitrine (0..8): com 0,1,2 repetidos em toda face, as
        // vitrines dos cantos ficavam gêmeas idênticas lado a lado (painel)
        for (const f of [
            { nx: 0, nz: -1, vbase: 0 },
            { nx: 1, nz: 0, vbase: 3 },
            { nx: -1, nz: 0, vbase: 6 },
        ]) {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            grid.rows.forEach((y, ri) => {
                if (ri === 0) return; // térreo = vitrines de estante
                for (const u of grid.cols) {
                    const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                    win.rotation.y = ry;
                    win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                    bg.add(win);
                }
            });
            addShelf(f, -5.8, f.vbase);
            addShelf(f, 0, f.vbase + 1);
            addShelf(f, 5.8, f.vbase + 2);
        }
        // FRENTE: fileiras acima do térreo; a do letreiro tem janelas nas colunas externas
        grid.rows.forEach((y, ri) => {
            if (ri === 0 || ri === signRow) return;
            for (const u of grid.cols) {
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.position.set(u, y, hd + 0.12);
                bg.add(win);
            }
        });
        for (const u of [grid.cols[0], grid.cols[grid.cols.length - 1]]) {
            const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
            win.position.set(u, grid.rows[signRow], hd + 0.12);
            bg.add(win);
        }
        // vitrines da frente (flanqueando a porta) — variants próprios
        addShelf({ nx: 0, nz: 1 }, -5.8, 9);
        addShelf({ nx: 0, nz: 1 }, 5.8, 10);
    }

    // ---- ENTRADA: porta + marquise com friso DOURADO + colunas de LIVROS ----
    const doorW = 4.6;
    const doorH = 3.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
    dmull.position.set(0, doorH / 2 + 0.5, hd + 0.2);
    bg.add(dmull);
    const marq = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.32, 1.9), trim);
    marq.position.set(0, doorH + 1.05, hd + 0.85);
    marq.castShadow = true;
    bg.add(marq);
    const goldStrip = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.34, 0.14), gold);
    goldStrip.position.set(0, doorH + 1.05, hd + 1.75);
    bg.add(goldStrip);
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);
    // CORUJAS-GUARDIÃS nos pedestais da entrada (como os leões de biblioteca
    // clássica — substituíram as colunas de livros, que liam estranhas)
    const mkOwl = () => {
        const owl = new THREE.Group();
        const featherMat = accentMat("#8a6642");
        const featherDark = accentMat("#6e4f30");
        const obody = new THREE.Mesh(new THREE.SphereGeometry(1.15, 14, 12), featherMat);
        obody.scale.set(1, 1.18, 0.95);
        obody.position.y = 1.3;
        owl.add(obody);
        // papo como DISCO RASO bem projetado: interseção ÍNGREME com o corpo dá
        // borda lisa (a rasante serrilhava e lia como "sorriso de dentes" — painel)
        const belly = new THREE.Mesh(new THREE.SphereGeometry(0.7, 16, 12), cream);
        belly.scale.set(0.9, 1.05, 0.45);
        belly.position.set(0, 1.02, 0.68);
        owl.add(belly);
        for (const ex of [-0.42, 0.42]) {
            const eyeW = new THREE.Mesh(new THREE.SphereGeometry(0.34, 10, 8), cream);
            eyeW.position.set(ex, 1.95, 0.78);
            owl.add(eyeW);
            const pupil = new THREE.Mesh(new THREE.IcosahedronGeometry(0.15, 0), dark);
            pupil.position.set(ex, 1.95, 1.06);
            owl.add(pupil);
            const tuft = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.5, 8), featherDark);
            tuft.position.set(ex * 1.6, 2.6, 0.1);
            tuft.rotation.z = -ex * 0.5;
            owl.add(tuft);
            const wing = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 8), featherDark);
            wing.scale.set(0.5, 1.15, 0.8);
            wing.position.set(ex * 2.35, 1.15, 0);
            owl.add(wing);
        }
        // bico GRANDE, à frente dos olhos e abaixo da linha deles (o pontinho de
        // 2px atrás das esferas dos olhos não ancorava a leitura de pássaro)
        const beak = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.62, 8), accentMat("#f2a83c"));
        beak.position.set(0, 1.58, 1.12);
        beak.rotation.x = Math.PI / 2.2;
        owl.add(beak);
        owl.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return owl;
    };
    for (const sx of [-1, 1]) {
        const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 1.0, 0.5, 12), edgeMat);
        ped.position.set(sx * 3.5, 0.25, hd + 1.5);
        ped.castShadow = true;
        bg.add(ped);
        const guard = mkOwl();
        guard.scale.setScalar(0.55);
        guard.position.set(sx * 3.5, 0.5, hd + 1.5);
        guard.rotation.y = -sx * 0.35; // levemente viradas p/ a porta
        bg.add(guard);
    }

    // ---- LETREIRO "BIBLIOTECA" na fileira do meio ----
    const signTex = makeSignTexture(t("BIBLIOTECA"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 54 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.3, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- TELHADO: PILHA DE LIVROS + CORUJA + LUMINÁRIA + ÓCULOS ----
    const roofY = b.h + 0.5;
    {
        // pilha de 3 livros gigantes (capa colorida + páginas creme nas 3 bordas)
        const stack = new THREE.Group();
        // rot ≈ π/2: LOMBADA (face -x colorida) virada p/ a FRENTE da cidade — com
        // rot pequeno a frente mostrava só as páginas creme (pilha lia "branca")
        const books = [
            { w: 7.6, d: 5.6, h: 1.5, c: "#e6584d", rot: 1.44 },
            { w: 6.8, d: 5.0, h: 1.4, c: "#5b8def", rot: 1.78 },
            { w: 6.0, d: 4.4, h: 1.3, c: "#f2c14e", rot: 1.58 },
        ];
        // anatomia de livro DE VERDADE (pedido do usuário): capas coloridas em cima/
        // embaixo + LOMBADA colorida, com o MIOLO BRANCO das páginas aparecendo
        // entre as capas nas 3 bordas abertas
        let sy = 0;
        const CT = 0.16; // espessura da capa
        for (const bk of books) {
            const cover = accentMat(bk.c);
            const book = new THREE.Group();
            const bot = new THREE.Mesh(new THREE.BoxGeometry(bk.w, CT, bk.d), cover);
            bot.position.y = CT / 2;
            book.add(bot);
            const pages = new THREE.Mesh(new THREE.BoxGeometry(bk.w * 0.96, bk.h - 2 * CT, bk.d * 0.94), cream);
            pages.position.y = bk.h / 2;
            book.add(pages);
            const top = new THREE.Mesh(new THREE.BoxGeometry(bk.w, CT, bk.d), cover);
            top.position.y = bk.h - CT / 2;
            book.add(top);
            const bspine = new THREE.Mesh(new THREE.BoxGeometry(0.4, bk.h, bk.d), cover);
            bspine.position.set(-(bk.w / 2 - 0.2), bk.h / 2, 0);
            book.add(bspine);
            book.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            book.position.y = sy;
            book.rotation.y = bk.rot;
            stack.add(book);
            sy += bk.h;
        }
        // CORUJA empoleirada no topo da pilha (mesma das guardiãs da entrada);
        // +90° compensa o giro da pilha — ela olha p/ a FRENTE do prédio
        const owl = mkOwl();
        owl.position.set(0.4, sy, 0.3);
        owl.rotation.y = 0.18 + Math.PI / 2;
        stack.add(owl);
        // mais à ESQUERDA e à FRENTE (referência do usuário); pilha girada -90°
        stack.position.set(-3.4, roofY, 1.6);
        stack.rotation.y = 0.2 - Math.PI / 2;
        bg.add(stack);
    }
    {
        // LUMINÁRIA DE LEITURA gigante (cúpula meia-cana verde emissiva + haste)
        const g = new THREE.Group();
        const lbase = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.9, 0.4, 12), gold);
        lbase.position.y = 0.2;
        g.add(lbase);
        const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 2.4, 8), gold);
        stem.position.y = 1.6;
        g.add(stem);
        // braço LIGANDO topo do poste (0, 2.8) ao centro da cúpula (1.15, 3.2) —
        // antes terminava no ar (painel: cúpula/bulbo desconectados)
        const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 1.35, 8), gold);
        arm.position.set(0.575, 3.0, 0);
        arm.rotation.z = -Math.atan2(1.15, 0.4);
        g.add(arm);
        const shadeMat = new THREE.MeshStandardMaterial({
            color: "#2f9e5b",
            emissive: new THREE.Color("#3ddc84"),
            emissiveIntensity: 0.55,
            roughness: 0.5,
            side: THREE.DoubleSide, // meia-cana: visível por dentro também
        });
        const shade = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 2.6, 14, 1, false, 0, Math.PI), shadeMat);
        shade.rotation.z = Math.PI / 2;
        shade.rotation.y = Math.PI;
        shade.position.set(1.15, 3.2, 0);
        g.add(shade);
        // tampas de meia-lua fechando as pontas da cúpula
        for (const sy of [-1.3, 1.3]) {
            const capEnd = new THREE.Mesh(new THREE.CircleGeometry(1.0, 12, 0, Math.PI), shadeMat);
            capEnd.rotation.y = sy > 0 ? -Math.PI / 2 : Math.PI / 2;
            capEnd.position.set(1.15 + sy, 3.2, 0);
            g.add(capEnd);
        }
        const finial = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), gold);
        finial.position.set(1.15, 3.45, 0);
        g.add(finial);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.scale.setScalar(1.6);
        // ATRÁS à direita (referência do usuário), cúpula apontando p/ a pilha
        g.position.set(4.7, roofY, -3.4);
        g.rotation.y = -2.6;
        bg.add(g);
    }
    {
        // TINTEIRO COM PENA (substituiu os óculos, que não ficaram bons): pote
        // escuro com aro dourado + pena creme inclinada mergulhada na tinta
        const g = new THREE.Group();
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.95, 1.1, 12), dark);
        pot.position.y = 0.55;
        g.add(pot);
        const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.84, 0.84, 0.2, 12), gold);
        rim.position.y = 1.12;
        g.add(rim);
        const ink = new THREE.Mesh(new THREE.CylinderGeometry(0.66, 0.66, 0.08, 12), new THREE.MeshStandardMaterial({ color: "#101812", roughness: 0.3 }));
        ink.position.y = 1.24;
        g.add(ink);
        const quill = new THREE.Group();
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.09, 3.1, 8), cream);
        shaft.position.y = 1.55;
        quill.add(shaft);
        // vano da pena: 2 elipsoides achatados escalonados no alto da haste
        for (const [vy, vs] of [
            [2.1, 1.0],
            [2.9, 0.82],
        ]) {
            const vane = new THREE.Mesh(new THREE.SphereGeometry(0.62 * vs, 10, 8), cream);
            vane.scale.set(0.16, 1.15, 0.5);
            vane.position.set(0, vy, 0);
            quill.add(vane);
        }
        const nib = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.35, 8), gold);
        nib.rotation.x = Math.PI;
        nib.position.y = -0.15;
        quill.add(nib);
        quill.rotation.z = 0.42; // inclinada, mergulhada no tinteiro
        quill.rotation.x = -0.15;
        quill.position.set(0.1, 1.15, 0);
        g.add(quill);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.scale.setScalar(1.6);
        g.position.set(4.6, roofY, 4.4);
        g.rotation.y = -0.5 - Math.PI / 2; // girado -90° (pedido do usuário)
        bg.add(g);
    }
}

/** Vitrine de estante retroiluminada da biblioteca — lombadas coloridas em 3
 *  prateleiras de madeira (variant gira as cores/arranjo). */
function makeBookshelfTexture(variant) {
    const canvas = document.createElement("canvas");
    canvas.width = 224;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#503618"; // fundo interno QUENTE (o quase-preto não lia
    ctx.fillRect(0, 0, 224, 256); // como retroiluminado à noite — painel)
    const SPINES = ["#e6584d", "#5b8def", "#f2c14e", "#3ddc84", "#c879e6", "#ff8c42", "#f4efe0"];
    const widths = [18, 24, 15, 28, 20, 16, 26, 22, 14, 25, 19, 17];
    for (let shelf = 0; shelf < 3; shelf++) {
        const yBot = 78 + shelf * 76;
        // livros em pé (altura máx. 55: lombadas de 63 encostavam na moldura)
        let x = 16;
        let i = variant * 3 + shelf;
        while (x < 190) {
            const w = widths[i % widths.length];
            const h = 46 + ((i * 7) % 10);
            const spine = SPINES[(i + variant) % SPINES.length];
            ctx.fillStyle = spine;
            ctx.fillRect(x, yBot - h, w, h);
            // friso da lombada: ESCURO nos livros creme (branco sumia neles)
            ctx.fillStyle = spine === "#f4efe0" ? "rgba(40,30,15,0.5)" : "rgba(255,255,255,0.35)";
            ctx.fillRect(x + 3, yBot - h + 8, w - 6, 4);
            ctx.fillRect(x + 3, yBot - 14, w - 6, 3);
            x += w + 3;
            i++;
        }
        // pilha deitada fechando a prateleira
        if ((shelf + variant) % 2 === 0) {
            ctx.fillStyle = SPINES[(variant + shelf + 2) % SPINES.length];
            ctx.fillRect(158, yBot - 14, 48, 14);
            ctx.fillStyle = SPINES[(variant + shelf + 4) % SPINES.length];
            ctx.fillRect(164, yBot - 26, 40, 12);
        }
        ctx.fillStyle = "#9a744a"; // tábua da prateleira (mais clara)
        ctx.fillRect(10, yBot, 204, 9);
    }
    ctx.strokeStyle = "#7a5230"; // moldura de madeira
    ctx.lineWidth = 14;
    ctx.strokeRect(7, 7, 210, 242);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * LOJA DE ELETRÔNICOS (benchmark = café/agência/kinder/lab/biblioteca):
 *   • TV RETRÔ GIGANTE no TELHADO (casco creme, tela de BARRAS COLORIDAS acesa,
 *     orelhas-de-coelho — o ícone aéreo) + CONTROLE REMOTO deitado;
 *   • JANELÕES contínuos de ponta a ponta nos 2º e 3º pisos das 4 paredes
 *     (na frente o 2º piso é do letreiro "ELETRÔNICOS");
 *   • térreo de TODAS as faces: VITRINES DE VERDADE — caixa projetada com
 *     interior aceso, PRODUTO 3D dentro e vidro transparente (TV, GAMES,
 *     CELULAR, ÁUDIO, PC, FOTO, DRONE, ROBÔ, TABLET, WI-FI, SMART);
 *   • entrada com COLUNAS-PILHA AA (família das colunas temáticas) + marquise
 *     com friso azul-elétrico.
 */
function buildElectronics(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });

    // ---- corpo + base + cornija + laje (padrão da cidade) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- JANELÕES + VITRINES DE VERDADE ----
    // 2º/3º pisos: JANELÃO contínuo de ponta a ponta (pedido do usuário) em todas
    // as faces — na FRENTE o 2º piso é do letreiro (janelas nas colunas externas).
    // Térreo: vitrine = caixa projetada com interior ACESO + PRODUTO 3D + vidro
    // transparente na frente (técnica da academia — vitrine de loja de verdade).
    const grid = facadeGrid(b.w, b.h, 3);
    const signRow = Math.min(1, grid.rows.length - 1);
    const signH = 2.2;
    const glassPane = new THREE.MeshStandardMaterial({
        color: "#bfe4d8",
        transparent: true,
        opacity: 0.22,
        roughness: 0.15,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#fff3d8",
        emissive: new THREE.Color("#f5e9bf"),
        emissiveIntensity: 0.45,
        roughness: 0.8,
    });
    const screenDark = new THREE.MeshStandardMaterial({ color: "#16233a", roughness: 0.4 });
    const emissiveAcc = (c, i = 0.5) =>
        new THREE.MeshStandardMaterial({
            color: c,
            emissive: new THREE.Color(c),
            emissiveIntensity: i,
            roughness: 0.5,
        });
    const BAR_COLORS = ["#f4efe0", "#f2c14e", "#7fdcff", "#3ddc84", "#c879e6", "#e6584d", "#5b8def"];
    // produto 3D em miniatura (base do grupo em y=0, de frente p/ +z)
    const mkProduct = (kind) => {
        const p = new THREE.Group();
        if (kind === "tv") {
            const fr = new THREE.Mesh(new THREE.BoxGeometry(2.7, 1.7, 0.16), dark);
            fr.position.y = 1.25;
            p.add(fr);
            BAR_COLORS.forEach((c, i) => {
                const bar = new THREE.Mesh(new THREE.BoxGeometry(0.34, 1.4, 0.05), emissiveAcc(c, 0.45));
                bar.position.set(-1.02 + i * 0.34, 1.25, 0.1);
                p.add(bar);
            });
            const foot = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.32, 0.25), dark);
            foot.position.y = 0.24;
            p.add(foot);
            const bs = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.1, 0.45), dark);
            bs.position.y = 0.05;
            p.add(bs);
        } else if (kind === "game") {
            // CONSOLE moderno em torre (placas claras + núcleo escuro + LED azul,
            // estilo PlayStation — o joystick sozinho lia estranho, pedido do usuário)
            const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.65, 0.12, 12), dark);
            stand.position.y = 0.06;
            p.add(stand);
            // face LARGA das placas p/ a FRENTE (de perfil não dava pra ver — usuário)
            const core = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.1, 0.45), screenDark);
            core.position.y = 1.2;
            p.add(core);
            for (const szz of [-1, 1]) {
                const plateC = new THREE.Mesh(new THREE.BoxGeometry(1.05, 2.4, 0.14), cream);
                plateC.position.set(0, 1.25, szz * 0.3);
                plateC.rotation.x = -szz * 0.05;
                p.add(plateC);
            }
            // LEDs NA FRENTE da placa (atrás dela ficavam invisíveis) + emblema
            for (const sxx of [-1, 1]) {
                const led = new THREE.Mesh(new THREE.BoxGeometry(0.07, 2.0, 0.07), emissiveAcc("#7fdcff", 0.6));
                led.position.set(sxx * 0.48, 1.2, 0.4);
                p.add(led);
            }
            const emblem = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.06, 12), screenDark);
            emblem.rotation.x = Math.PI / 2;
            emblem.position.set(0, 1.65, 0.39);
            p.add(emblem);
            // controlinho aos pés do console
            const padB = new THREE.Mesh(new THREE.BoxGeometry(0.75, 0.24, 0.4), dark);
            padB.position.set(0.85, 0.14, 0.45);
            padB.rotation.y = -0.5;
            p.add(padB);
            for (const gx of [-0.34, 0.34]) {
                const grip = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), dark);
                grip.position.set(gx, -0.04, 0.1);
                padB.add(grip);
            }
            const padLed = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.05, 0.08), emissiveAcc("#7fdcff", 0.6));
            padLed.position.set(0, 0.13, -0.1);
            padB.add(padLed);
        } else if (kind === "phone") {
            const ph = new THREE.Mesh(new THREE.BoxGeometry(1.1, 2.0, 0.15), dark);
            ph.position.y = 1.1;
            p.add(ph);
            const tex = makePhoneScreenTexture();
            const scr = new THREE.Mesh(new THREE.BoxGeometry(0.95, 1.8, 0.05), [
                dark,
                dark,
                dark,
                dark,
                new THREE.MeshStandardMaterial({
                    map: tex,
                    emissiveMap: tex,
                    emissive: new THREE.Color("#ffffff"),
                    emissiveIntensity: 0.55,
                    roughness: 0.5,
                }),
                dark,
            ]);
            scr.position.set(0, 1.1, 0.09);
            p.add(scr);
        } else if (kind === "audio") {
            const bx = new THREE.Mesh(new THREE.BoxGeometry(1.4, 2.2, 0.8), dark);
            bx.position.y = 1.1;
            p.add(bx);
            for (const [sy, sr] of [
                [1.65, 0.3],
                [0.75, 0.45],
            ]) {
                const cone = new THREE.Mesh(new THREE.CylinderGeometry(sr, sr, 0.1, 12), accentMat("#8a6642"));
                cone.rotation.x = Math.PI / 2;
                cone.position.set(0, sy, 0.42);
                p.add(cone);
                const dome = new THREE.Mesh(new THREE.SphereGeometry(sr * 0.4, 8, 6), cream);
                dome.position.set(0, sy, 0.46);
                p.add(dome);
            }
        } else if (kind === "laptop") {
            // tela mais à FRENTE (em z -0.42 ficava enterrada no fundo da vitrine)
            const kb = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.12, 1.2), accentMat("#57cf63"));
            kb.position.set(0, 0.06, 0.35);
            p.add(kb);
            const scr = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.5, 0.1), dark);
            scr.rotation.x = -0.18;
            scr.position.set(0, 0.82, -0.15);
            p.add(scr);
            for (const [ly, lw] of [
                [1.15, 1.5],
                [0.9, 1.9],
                [0.65, 1.1],
            ]) {
                const line = new THREE.Mesh(new THREE.BoxGeometry(lw, 0.12, 0.03), emissiveAcc("#3ddc84", 0.55));
                line.rotation.x = -0.18;
                line.position.set(-0.95 + lw / 2 + 0.1, ly, -0.08 + (0.82 - ly) * 0.18);
                p.add(line);
            }
        } else if (kind === "camera") {
            const bodyC = new THREE.Mesh(new THREE.BoxGeometry(1.9, 1.15, 0.75), dark);
            bodyC.position.y = 0.95;
            p.add(bodyC);
            const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.5, 12), accentMat("#5b8def"));
            lens.rotation.x = Math.PI / 2;
            lens.position.set(0, 0.95, 0.55);
            p.add(lens);
            const inner = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.26, 0.1, 10), screenDark);
            inner.rotation.x = Math.PI / 2;
            inner.position.set(0, 0.95, 0.82);
            p.add(inner);
            const flash = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.18, 0.1), gold);
            flash.position.set(-0.6, 1.42, 0.35);
            p.add(flash);
        } else if (kind === "drone") {
            const bodyD = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.3, 0.7), dark);
            bodyD.position.y = 1.1;
            p.add(bodyD);
            for (const [ax, az] of [
                [-0.75, -0.75],
                [0.75, -0.75],
                [-0.75, 0.75],
                [0.75, 0.75],
            ]) {
                const arm = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.08, 0.12), dark);
                arm.rotation.y = Math.atan2(az, ax);
                arm.position.set(ax * 0.55, 1.1, az * 0.55);
                p.add(arm);
                const rotor = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.05, 10), accentMat("#5b8def"));
                rotor.position.set(ax, 1.2, az);
                p.add(rotor);
            }
            const light = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), emissiveAcc("#e6584d", 0.6));
            light.position.set(0, 0.93, 0.36);
            p.add(light);
        } else if (kind === "robot") {
            const head = new THREE.Mesh(new THREE.BoxGeometry(1.3, 1.1, 0.9), accentMat("#5b8def"));
            head.position.y = 1.0;
            p.add(head);
            const visor = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.45, 0.1), screenDark);
            visor.position.set(0, 1.1, 0.48);
            p.add(visor);
            for (const ex of [-0.26, 0.26]) {
                const eye = new THREE.Mesh(new THREE.SphereGeometry(0.11, 8, 6), emissiveAcc("#3ddc84", 0.6));
                eye.position.set(ex, 1.1, 0.53);
                p.add(eye);
            }
            const ant = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.5, 8), dark);
            ant.position.y = 1.8;
            p.add(ant);
            const ball = new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6), emissiveAcc("#e6584d", 0.5));
            ball.position.y = 2.1;
            p.add(ball);
        } else if (kind === "tablet") {
            const tb = new THREE.Mesh(new THREE.BoxGeometry(2.0, 1.5, 0.12), dark);
            tb.rotation.x = -0.35;
            tb.position.set(0, 0.85, -0.1);
            p.add(tb);
            const scr = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.3, 0.05), emissiveAcc("#c879e6", 0.4));
            scr.rotation.x = -0.35;
            scr.position.set(0, 0.87, -0.03);
            p.add(scr);
            // grade de apps na tela (tela lisa roxa lia como quadro vazio — painel)
            ["#e6584d", "#f2c14e", "#3ddc84", "#5b8def", "#f4efe0", "#ff8c42"].forEach((c, i) => {
                const app = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.04), emissiveAcc(c, 0.5));
                app.rotation.x = -0.35;
                const col = i % 3;
                const row = Math.floor(i / 3);
                app.position.set(-0.55 + col * 0.55, 1.15 - row * 0.52 + 0.08, 0.02 + (row === 0 ? -0.12 : 0.06));
                p.add(app);
            });
            const stand = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.1, 0.5), edgeMat);
            stand.position.set(0, 0.05, 0.1);
            p.add(stand);
        } else if (kind === "wifi") {
            const rt = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.45, 0.95), dark);
            rt.position.y = 0.55;
            p.add(rt);
            for (const ax of [-0.55, 0.55]) {
                const ant = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.9, 8), dark);
                ant.position.set(ax, 1.15, -0.2);
                ant.rotation.z = ax < 0 ? 0.2 : -0.2;
                p.add(ant);
            }
            const wave = new THREE.Mesh(
                new THREE.TorusGeometry(0.55, 0.08, 8, 16, Math.PI * 0.7),
                new THREE.MeshStandardMaterial({
                    color: "#3ddc84",
                    emissive: new THREE.Color("#3ddc84"),
                    emissiveIntensity: 0.6,
                    roughness: 0.5,
                    side: THREE.DoubleSide,
                }),
            );
            wave.rotation.z = Math.PI * 0.15;
            wave.position.set(0, 1.35, 0);
            p.add(wave);
            for (const lx of [-0.45, 0, 0.45]) {
                const led = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 0.05), emissiveAcc("#3ddc84", 0.55));
                led.position.set(lx, 0.55, 0.5);
                p.add(led);
            }
        } else {
            // smart(watch)
            const strap = new THREE.Mesh(new THREE.BoxGeometry(0.55, 2.2, 0.14), accentMat("#3ddc84"));
            strap.position.y = 1.15;
            p.add(strap);
            const caseW = new THREE.Mesh(new THREE.BoxGeometry(1.0, 1.1, 0.28), dark);
            caseW.position.y = 1.15;
            p.add(caseW);
            const scr = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.9, 0.06), emissiveAcc("#5b8def", 0.45));
            scr.position.set(0, 1.15, 0.16);
            p.add(scr);
            const heart = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), emissiveAcc("#e6584d", 0.6));
            heart.scale.set(1.3, 1, 0.6);
            heart.position.set(0, 1.15, 0.2);
            p.add(heart);
        }
        p.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return p;
    };
    const SHOP_LABELS = {
        tv: "TV",
        game: "GAMES",
        phone: "CELULAR",
        audio: "ÁUDIO",
        laptop: "PC",
        camera: "FOTO",
        drone: "DRONE",
        robot: "ROBÔ",
        tablet: "TABLET",
        wifi: "WI-FI",
        smart: "SMART",
    };
    {
        // vitrine: caixa projetada (paredes verde-escuras) + fundo aceso + produto
        // 3D no "chão" + vidro transparente + plaquinha do produto
        const addDisplay = (f, u, kind) => {
            const tx = f.nz;
            const tz = f.nx;
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : f.nx === -1 ? -Math.PI / 2 : 0;
            const g = new THREE.Group();
            const back = new THREE.Mesh(new THREE.BoxGeometry(3.4, 3.0, 0.12), interiorMat);
            back.position.z = 0.06;
            g.add(back);
            for (const [bw2, bh2, ox, oy] of [
                [3.9, 0.25, 0, 1.62],
                [3.9, 0.25, 0, -1.62],
                [0.25, 3.5, -1.82, 0],
                [0.25, 3.5, 1.82, 0],
            ]) {
                const wallV = new THREE.Mesh(new THREE.BoxGeometry(bw2, bh2, 1.15), edgeMat);
                wallV.position.set(ox, oy, 0.55);
                wallV.castShadow = true;
                g.add(wallV);
            }
            const product = mkProduct(kind);
            product.position.set(0, -1.48, 0.35);
            g.add(product);
            const pane = new THREE.Mesh(new THREE.BoxGeometry(3.35, 2.95, 0.06), glassPane);
            pane.position.z = 1.05;
            g.add(pane);
            const labTex = makeSignTexture(t(SHOP_LABELS[kind] || kind), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 60 });
            const labMat = new THREE.MeshStandardMaterial({
                map: labTex,
                emissiveMap: labTex,
                emissive: new THREE.Color("#ffd27a"),
                emissiveIntensity: 0.45,
                roughness: 0.7,
            });
            const plate = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.42, 0.1), [edgeMat, edgeMat, edgeMat, edgeMat, labMat, edgeMat]);
            plate.position.set(0, -1.62, 1.16);
            g.add(plate);
            g.rotation.y = ry;
            g.position.set(f.nx * faceDist + tx * u, 3.0, f.nz * faceDist + tz * u);
            bg.add(g);
        };
        // JANELÃO contínuo de ponta a ponta (vidro quente + trilhos + montantes)
        const addRibbon = (f, y) => {
            const faceDist = f.nz !== 0 ? hd : hw;
            const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : f.nx === -1 ? -Math.PI / 2 : 0;
            const W = b.w - 1.4;
            const g = new THREE.Group();
            const glass = new THREE.Mesh(new THREE.BoxGeometry(W, 2.0, 0.2), warmGlass);
            g.add(glass);
            for (const oy of [1.15, -1.15]) {
                const rail = new THREE.Mesh(new THREE.BoxGeometry(W + 0.5, 0.3, 0.32), edgeMat);
                rail.position.set(0, oy, 0.02);
                rail.castShadow = true;
                g.add(rail);
            }
            for (let m = 1; m < 8; m++) {
                const mull = new THREE.Mesh(new THREE.BoxGeometry(0.16, 2.0, 0.28), edgeMat);
                mull.position.set(-W / 2 + (m * W) / 8, 0, 0.03);
                g.add(mull);
            }
            g.rotation.y = ry;
            g.position.set(f.nx * (faceDist + 0.14), y, f.nz * (faceDist + 0.14));
            bg.add(g);
        };
        for (const f of [
            { nx: 0, nz: -1, kinds: ["tablet", "wifi", "smart"] },
            { nx: 1, nz: 0, kinds: ["phone", "audio", "laptop"] },
            { nx: -1, nz: 0, kinds: ["camera", "drone", "robot"] },
        ]) {
            addRibbon(f, grid.rows[1]);
            addRibbon(f, grid.rows[2]);
            addDisplay(f, -5.8, f.kinds[0]);
            addDisplay(f, 0, f.kinds[1]);
            addDisplay(f, 5.8, f.kinds[2]);
        }
        // FRENTE: janelão só no 3º piso (o 2º é do letreiro, janelas nas pontas)
        addRibbon({ nx: 0, nz: 1 }, grid.rows[2]);
        for (const u of [grid.cols[0], grid.cols[grid.cols.length - 1]]) {
            const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
            win.position.set(u, grid.rows[signRow], hd + 0.12);
            bg.add(win);
        }
        addDisplay({ nx: 0, nz: 1 }, -5.8, "tv");
        addDisplay({ nx: 0, nz: 1 }, 5.8, "game");
    }

    // ---- ENTRADA: porta + marquise com friso AZUL-ELÉTRICO + colunas-PILHA ----
    const doorW = 4.6;
    const doorH = 3.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
    dmull.position.set(0, doorH / 2 + 0.5, hd + 0.2);
    bg.add(dmull);
    const marq = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.32, 1.9), trim);
    marq.position.set(0, doorH + 1.05, hd + 0.85);
    marq.castShadow = true;
    bg.add(marq);
    const blueStrip = new THREE.Mesh(
        new THREE.BoxGeometry(doorW + 1.2, 0.34, 0.14),
        new THREE.MeshStandardMaterial({
            color: "#5b8def",
            emissive: new THREE.Color("#5b8def"),
            emissiveIntensity: 0.35,
            roughness: 0.5,
        }),
    );
    blueStrip.position.set(0, doorH + 1.05, hd + 1.75);
    bg.add(blueStrip);
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);
    // colunas-PILHA AA: corpo verde com faixa e terminal dourados
    for (const sx of [-1, 1]) {
        const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.75, 0.9, 0.5, 12), edgeMat);
        ped.position.set(sx * 3.5, 0.25, hd + 1.5);
        ped.castShadow = true;
        bg.add(ped);
        const bodyB = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 3.4, 14), accentMat("#2f9e5b"));
        bodyB.position.set(sx * 3.5, 2.2, hd + 1.5);
        bodyB.castShadow = true;
        bg.add(bodyB);
        const band = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 0.5, 14), gold);
        band.position.set(sx * 3.5, 3.4, hd + 1.5);
        bg.add(band);
        // tampa e borne DOURADOS (cinza não lia como pilha — painel)
        const capB = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 0.22, 14), gold);
        capB.position.set(sx * 3.5, 4.01, hd + 1.5);
        bg.add(capB);
        const term = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.24, 10), gold);
        term.position.set(sx * 3.5, 4.24, hd + 1.5);
        term.castShadow = true;
        bg.add(term);
    }

    // ---- LETREIRO "ELETRÔNICOS" na fileira do meio ----
    const signTex = makeSignTexture(t("ELETRÔNICOS"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 52 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.3, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- TELHADO v7 (usuário: v6 "torta/desalinhada/bizarra" — refeita com
    //      ISOMETRIA RIGOROSA): PLACA-MÃE realista, TODOS os componentes
    //      alinhados aos eixos (só 0°/90°), em regiões limpas — cooler+aletas na
    //      CPU, fileira de 4 PENTES DE RAM paralelos, capacitores em LINHA, slots
    //      PCIe, conector ATX 24-pinos, chipset, bateria de moeda. ----
    const roofY = b.h + 0.5;
    {
        const g = new THREE.Group();
        const traceMat = emissiveAcc("#3ddc84", 0.4);
        const silver = new THREE.MeshStandardMaterial({ color: "#cfd6db", roughness: 0.4, metalness: 0.5 });
        const pcbGreen = accentMat("#2f7d4c");
        // ==== TRILHAS ortogonais (só H/V) + PONTOS DE SOLDA nas juntas ====
        const segs = [
            [-2.5, 3.4, 0.24, 4.6],
            [-5.3, 5.6, 5.8, 0.24], // CPU → chipset/frente
            [2.4, -1.5, 5.4, 0.24],
            [5.1, 1.6, 0.24, 3.4], // CPU → RAM
            [-2.5, -4.6, 0.24, 3.2],
            [-5.0, -6.1, 5.2, 0.24], // CPU → caps (fundo)
            [0.5, 5.4, 8.2, 0.24],
            [1.2, 6.8, 6.4, 0.24], // barramento dos slots PCIe
        ];
        for (const [sx2, sz2, w, d] of segs) {
            const tr = new THREE.Mesh(new THREE.BoxGeometry(w, 0.05, d), traceMat);
            tr.position.set(sx2, 0.05, sz2);
            g.add(tr);
        }
        for (const [dx, dz] of [
            [-2.5, 5.6],
            [4.9, 1.6],
            [-2.5, -6.1],
            [4.6, 5.4],
            [-2.6, 5.4],
        ]) {
            const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.08, 8), gold);
            dot.position.set(dx, 0.06, dz);
            g.add(dot);
        }
        // ==== CPU: socket + aletas + ventoinha TOP-FLOW (tudo centrado/alinhado) ====
        const cx = -2.5,
            cz = -1.4;
        const sub = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.35, 4.6), edgeMat);
        sub.position.set(cx, 0.17, cz);
        g.add(sub);
        for (let i = 0; i < 6; i++) {
            // pinos dourados, 6 por lado, alinhados
            const off = -1.65 + i * 0.66;
            for (const [px, pz, w, d] of [
                [cx + off, cz + 2.45, 0.26, 0.55],
                [cx + off, cz - 2.45, 0.26, 0.55],
                [cx + 2.45, cz + off, 0.55, 0.26],
                [cx - 2.45, cz + off, 0.55, 0.26],
            ]) {
                const pin = new THREE.Mesh(new THREE.BoxGeometry(w, 0.14, d), gold);
                pin.position.set(px, 0.14, pz);
                g.add(pin);
            }
        }
        const hsBase = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.45, 3.4), silver);
        hsBase.position.set(cx, 0.57, cz);
        g.add(hsBase);
        for (let i = 0; i < 9; i++) {
            // aletas paralelas ao eixo X, empilhadas em Z
            const fin = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.2, 0.14), silver);
            fin.position.set(cx, 1.35, cz - 1.44 + i * 0.36);
            fin.castShadow = true;
            g.add(fin);
        }
        {
            // ventoinha deitada no topo (eixo Y, pás simétricas — sem inclinação torta)
            const fanY = 2.2;
            const rim = new THREE.Mesh(new THREE.TorusGeometry(1.6, 0.22, 8, 24), dark);
            rim.rotation.x = Math.PI / 2;
            rim.position.set(cx, fanY, cz);
            g.add(rim);
            const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.2, 14), edgeMat);
            hub.position.set(cx, fanY + 0.02, cz);
            g.add(hub);
            const logo = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.06, 12), emissiveAcc("#7fdcff", 0.5));
            logo.position.set(cx, fanY + 0.14, cz);
            g.add(logo);
            for (let i = 0; i < 9; i++) {
                const a = (i / 9) * Math.PI * 2;
                const blade = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.05, 0.42), silver);
                blade.position.set(cx + Math.cos(a) * 0.95, fanY, cz + Math.sin(a) * 0.95);
                blade.rotation.y = a; // no plano do disco — leitura de ventoinha, não torto
                g.add(blade);
            }
            rim.castShadow = true;
        }
        // ==== 4 PENTES DE RAM: fileira paralela alinhada em Z, à direita da CPU ====
        for (let s = 0; s < 4; s++) {
            const ram = new THREE.Group();
            const pcb = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.9, 5.2), pcbGreen);
            pcb.position.y = 1.55;
            ram.add(pcb);
            for (let c = 0; c < 8; c++) {
                // 8 chips pretos alinhados
                const chip = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.8, 0.5), screenDark);
                chip.position.set(0.14, 1.75, -2.3 + c * 0.66);
                ram.add(chip);
            }
            const contacts = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.4, 5.0), gold);
            contacts.position.y = 0.65;
            ram.add(contacts);
            const slot = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, 5.6), dark);
            slot.position.y = 0.25;
            ram.add(slot);
            ram.position.set(3.3 + s * 0.95, 0, -1.2);
            ram.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            g.add(ram);
        }
        // ==== CAPACITORES em LINHA reta (VRM, borda de trás, acima da CPU) ====
        for (let i = 0; i < 5; i++) {
            const cx2 = -6.4 + i * 1.15,
                cz2 = -6.4,
                ch = 2.2;
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, ch, 12), screenDark);
            cap.position.set(cx2, ch / 2, cz2);
            cap.castShadow = true;
            g.add(cap);
            const capTop = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.1, 12), silver);
            capTop.position.set(cx2, ch + 0.05, cz2);
            g.add(capTop);
            const cross = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.04, 0.12), dark); // topo "K"
            cross.position.set(cx2, ch + 0.11, cz2);
            g.add(cross);
            const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.2, ch * 0.85, 0.04), cream);
            stripe.position.set(cx2, ch / 2, cz2 + 0.55); // faixa toda p/ a FRENTE (alinhada)
            g.add(stripe);
        }
        // ==== CHIPSET com dissipador baixo (canto frente-esquerda, alinhado) ====
        {
            const csx = -6.0,
                csz = 4.4;
            const csub = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.3, 3.0), edgeMat);
            csub.position.set(csx, 0.15, csz);
            g.add(csub);
            const chs = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.55, 2.4), silver);
            chs.position.set(csx, 0.55, csz);
            chs.castShadow = true;
            g.add(chs);
            for (let a = 0; a < 3; a++) {
                // grade 3×3 de mini-aletas
                for (let b2 = 0; b2 < 3; b2++) {
                    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.5, 0.55), silver);
                    fin.position.set(csx - 0.7 + a * 0.7, 1.05, csz - 0.7 + b2 * 0.7);
                    g.add(fin);
                }
            }
        }
        // ==== SLOTS PCIe: 2 barras longas paralelas alinhadas em X (borda frente) ==
        for (const pz of [5.4, 6.8]) {
            const slot = new THREE.Mesh(new THREE.BoxGeometry(8.4, 0.55, 0.7), dark);
            slot.position.set(0.4, 0.28, pz);
            g.add(slot);
            const groove = new THREE.Mesh(new THREE.BoxGeometry(7.8, 0.1, 0.3), screenDark);
            groove.position.set(0.4, 0.56, pz);
            g.add(groove);
            const latch = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.8, 0.7), accentMat("#5b8def"));
            latch.position.set(4.8, 0.4, pz);
            g.add(latch);
        }
        // ==== CONECTOR ATX 24-pinos (borda direita, alinhado) ====
        {
            const ax = 8.0,
                az = -3.6;
            const shell = new THREE.Mesh(new THREE.BoxGeometry(1.1, 1.5, 4.6), dark);
            shell.position.set(ax, 0.75, az);
            shell.castShadow = true;
            g.add(shell);
            for (let r = 0; r < 2; r++) {
                for (let c = 0; c < 12; c++) {
                    const pinh = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.1, 0.28), screenDark);
                    pinh.position.set(ax - 0.28 + r * 0.56, 1.52, az - 2.06 + c * 0.375);
                    g.add(pinh);
                }
            }
        }
        // ==== BATERIA DE MOEDA CR2032 (deitada, canto frente-esquerda, alinhada) ==
        {
            const bx = -7.0,
                bz = 7.2;
            const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.3, 20), silver);
            coin.position.set(bx, 0.15, bz);
            coin.castShadow = true;
            g.add(coin);
            const plus1 = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.04, 0.14), dark);
            plus1.position.set(bx, 0.31, bz);
            g.add(plus1);
            const plus2 = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.04, 0.6), dark);
            plus2.position.set(bx, 0.31, bz);
            g.add(plus2);
        }
        // ==== CHIPS SMD extras (alinhados, rotação 0) preenchendo gaps ====
        for (const [mx, mz, mw, md] of [
            [7.4, 3.8, 1.4, 1.4],
            [-2.5, 7.4, 2.0, 1.0],
            [7.6, 6.4, 1.2, 1.2],
        ]) {
            const smd = new THREE.Group();
            const chip = new THREE.Mesh(new THREE.BoxGeometry(mw, 0.4, md), screenDark);
            chip.position.y = 0.2;
            smd.add(chip);
            const notch = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.06, 8), cream);
            notch.position.set(-mw / 2 + 0.22, 0.41, 0);
            smd.add(notch);
            for (let i = 0; i < 4; i++) {
                for (const sd of [-1, 1]) {
                    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.06, md * 0.16), gold);
                    leg.position.set(-mw / 2 + 0.28 + (i * (mw - 0.56)) / 3, 0.05, sd * (md / 2 + 0.04));
                    smd.add(leg);
                }
            }
            smd.position.set(mx, 0, mz);
            smd.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            g.add(smd);
        }
        g.position.set(0, roofY, 0);
        bg.add(g);
    }
}

/** Tela de apps do smartphone (vitrine CELULAR da loja de eletrônicos). */
function makePhoneScreenTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#16233a"; // wallpaper escuro
    ctx.fillRect(0, 0, 128, 256);
    ctx.fillStyle = "#ffffff"; // status bar
    ctx.fillRect(10, 8, 30, 5);
    ctx.fillRect(96, 8, 22, 5);
    const APPS = ["#3ddc84", "#e6584d", "#f2c14e", "#5b8def", "#c879e6", "#ff8c42", "#7fdcff", "#f4efe0"];
    let i = 0;
    for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 4; col++) {
            ctx.fillStyle = APPS[i % APPS.length];
            roundRect(ctx, 12 + col * 28, 26 + row * 32, 20, 20, 5);
            ctx.fill();
            i++;
        }
    }
    // dock
    ctx.fillStyle = "rgba(255,255,255,0.14)";
    roundRect(ctx, 8, 206, 112, 34, 8);
    ctx.fill();
    for (let d = 0; d < 4; d++) {
        ctx.fillStyle = APPS[(d + 2) % APPS.length];
        roundRect(ctx, 16 + d * 28, 213, 20, 20, 5);
        ctx.fill();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/** Vitrine de produto retroiluminada da loja de eletrônicos (pictograma +
 *  rótulo, no padrão dos pôsteres da agência). */
function makeElectronicsPosterTexture(kind) {
    const LABELS = {
        tv: "TV",
        game: "GAMES",
        phone: "CELULAR",
        audio: "ÁUDIO",
        laptop: "PC",
        camera: "FOTO",
        drone: "DRONE",
        robot: "ROBÔ",
        tablet: "TABLET",
        wifi: "WI-FI",
        smart: "SMART",
    };
    const canvas = document.createElement("canvas");
    canvas.width = 224;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f4efe0";
    ctx.fillRect(0, 0, 224, 256);
    const cx = 112;
    if (kind === "tv") {
        ctx.fillStyle = "#243018"; // moldura da TV
        roundRect(ctx, 24, 40, 176, 110, 8);
        ctx.fill();
        const bars = ["#f4efe0", "#f2c14e", "#7fdcff", "#3ddc84", "#c879e6", "#e6584d", "#5b8def"];
        bars.forEach((c, i) => {
            ctx.fillStyle = c;
            ctx.fillRect(32 + i * 23, 48, 23, 94);
        });
        ctx.fillStyle = "#243018"; // pé
        ctx.fillRect(92, 150, 40, 10);
        ctx.fillRect(70, 160, 84, 8);
    } else if (kind === "game") {
        ctx.fillStyle = "#5b8def"; // controle
        roundRect(ctx, 32, 70, 160, 74, 34);
        ctx.fill();
        ctx.fillStyle = "#243018"; // d-pad
        ctx.fillRect(58, 96, 14, 40);
        ctx.fillRect(45, 109, 40, 14);
        for (const [bx, by, c] of [
            [152, 92, "#e6584d"],
            [172, 106, "#f2c14e"],
            [152, 120, "#3ddc84"],
            [132, 106, "#f4efe0"],
        ]) {
            ctx.fillStyle = c;
            ctx.beginPath();
            ctx.arc(bx, by, 8, 0, Math.PI * 2);
            ctx.fill();
        }
    } else if (kind === "phone") {
        ctx.fillStyle = "#243018";
        roundRect(ctx, 70, 30, 84, 150, 12);
        ctx.fill();
        ctx.fillStyle = "#7fdcff";
        roundRect(ctx, 78, 42, 68, 118, 6);
        ctx.fill();
        for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 3; c++) {
                ctx.fillStyle = ["#e6584d", "#f2c14e", "#3ddc84", "#5b8def", "#c879e6", "#ff8c42", "#243018", "#f4efe0", "#16233a"][r * 3 + c];
                roundRect(ctx, 84 + c * 20, 50 + r * 20, 14, 14, 3);
                ctx.fill();
            }
        }
        ctx.fillStyle = "#f4efe0"; // home
        ctx.fillRect(96, 166, 32, 5);
    } else if (kind === "audio") {
        ctx.fillStyle = "#243018"; // caixa
        roundRect(ctx, 44, 36, 88, 144, 10);
        ctx.fill();
        for (const [sy, sr] of [
            [76, 22],
            [136, 32],
        ]) {
            ctx.fillStyle = "#8a6642";
            ctx.beginPath();
            ctx.arc(88, sy, sr, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = "#f4efe0";
            ctx.beginPath();
            ctx.arc(88, sy, sr * 0.4, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.strokeStyle = "#3ddc84"; // ondas
        ctx.lineWidth = 6;
        for (let w = 0; w < 3; w++) {
            ctx.beginPath();
            ctx.arc(140, 108, 22 + w * 16, -0.7, 0.7);
            ctx.stroke();
        }
    } else if (kind === "laptop") {
        ctx.fillStyle = "#243018";
        roundRect(ctx, 40, 40, 144, 96, 8);
        ctx.fill();
        ctx.fillStyle = "#16233a";
        ctx.fillRect(48, 48, 128, 80);
        ctx.fillStyle = "#3ddc84"; // linhas de código
        for (const [ly, lw] of [
            [56, 70],
            [68, 96],
            [80, 54],
            [92, 84],
            [104, 64],
            [116, 40],
        ]) {
            ctx.fillRect(54, ly, lw, 6);
        }
        ctx.fillStyle = "#57cf63"; // base/teclado
        roundRect(ctx, 28, 138, 168, 16, 6);
        ctx.fill();
    } else if (kind === "camera") {
        ctx.fillStyle = "#243018";
        roundRect(ctx, 36, 60, 152, 104, 12);
        ctx.fill();
        ctx.fillStyle = "#5b8def"; // lente
        ctx.beginPath();
        ctx.arc(112, 112, 40, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#16233a";
        ctx.beginPath();
        ctx.arc(112, 112, 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#f4efe0"; // brilho
        ctx.beginPath();
        ctx.arc(122, 102, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#f2c14e"; // flash
        ctx.fillRect(48, 68, 24, 12);
        ctx.fillStyle = "#e6584d"; // botão
        ctx.beginPath();
        ctx.arc(170, 74, 8, 0, Math.PI * 2);
        ctx.fill();
    } else if (kind === "drone") {
        ctx.fillStyle = "#243018"; // corpo
        roundRect(ctx, 88, 88, 48, 40, 10);
        ctx.fill();
        ctx.strokeStyle = "#243018";
        ctx.lineWidth = 8;
        for (const [ex, ey] of [
            [52, 56],
            [172, 56],
            [52, 160],
            [172, 160],
        ]) {
            ctx.beginPath();
            ctx.moveTo(112, 108);
            ctx.lineTo(ex, ey);
            ctx.stroke();
            ctx.strokeStyle = "#5b8def"; // hélices
            ctx.lineWidth = 6;
            ctx.beginPath();
            ctx.ellipse(ex, ey, 26, 9, 0, 0, Math.PI * 2);
            ctx.stroke();
            ctx.strokeStyle = "#243018";
            ctx.lineWidth = 8;
        }
        ctx.fillStyle = "#e6584d"; // luz
        ctx.beginPath();
        ctx.arc(112, 120, 6, 0, Math.PI * 2);
        ctx.fill();
    } else if (kind === "robot") {
        ctx.fillStyle = "#5b8def"; // cabeça
        roundRect(ctx, 52, 56, 120, 96, 14);
        ctx.fill();
        ctx.fillStyle = "#16233a";
        roundRect(ctx, 64, 76, 96, 44, 8);
        ctx.fill();
        for (const ex of [88, 136]) {
            ctx.fillStyle = "#3ddc84"; // olhos
            ctx.beginPath();
            ctx.arc(ex, 98, 11, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.fillStyle = "#f2c14e"; // boca
        ctx.fillRect(84, 130, 56, 8);
        ctx.strokeStyle = "#243018"; // antena
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(112, 56);
        ctx.lineTo(112, 36);
        ctx.stroke();
        ctx.fillStyle = "#e6584d";
        ctx.beginPath();
        ctx.arc(112, 30, 8, 0, Math.PI * 2);
        ctx.fill();
    } else if (kind === "tablet") {
        ctx.fillStyle = "#243018";
        roundRect(ctx, 44, 40, 136, 150, 10);
        ctx.fill();
        ctx.fillStyle = "#c879e6";
        roundRect(ctx, 54, 52, 116, 126, 6);
        ctx.fill();
        ctx.fillStyle = "#f4efe0"; // desenho na tela
        ctx.beginPath();
        ctx.moveTo(70, 150);
        ctx.quadraticCurveTo(112, 70, 154, 130);
        ctx.lineWidth = 7;
        ctx.strokeStyle = "#f4efe0";
        ctx.stroke();
        ctx.fillStyle = "#f2c14e"; // caneta
        ctx.save();
        ctx.translate(176, 90);
        ctx.rotate(0.6);
        ctx.fillRect(-5, -46, 10, 74);
        ctx.restore();
    } else if (kind === "wifi") {
        ctx.fillStyle = "#243018"; // roteador
        roundRect(ctx, 48, 128, 128, 44, 10);
        ctx.fill();
        ctx.fillStyle = "#3ddc84";
        for (const lx of [72, 96, 120]) ctx.fillRect(lx, 144, 10, 10);
        ctx.strokeStyle = "#243018"; // antenas
        ctx.lineWidth = 7;
        for (const ax of [70, 154]) {
            ctx.beginPath();
            ctx.moveTo(ax, 128);
            ctx.lineTo(ax + (ax < 112 ? -10 : 10), 96);
            ctx.stroke();
        }
        ctx.strokeStyle = "#3ddc84"; // sinal
        ctx.lineWidth = 8;
        for (let w = 0; w < 3; w++) {
            ctx.beginPath();
            ctx.arc(112, 96, 20 + w * 18, Math.PI * 1.22, Math.PI * 1.78);
            ctx.stroke();
        }
        ctx.fillStyle = "#3ddc84";
        ctx.beginPath();
        ctx.arc(112, 92, 7, 0, Math.PI * 2);
        ctx.fill();
    } else {
        // smart(watch)
        ctx.fillStyle = "#3ddc84"; // pulseira
        roundRect(ctx, 88, 24, 48, 200, 12);
        ctx.fill();
        ctx.fillStyle = "#243018"; // caixa
        roundRect(ctx, 60, 76, 104, 96, 22);
        ctx.fill();
        ctx.fillStyle = "#16233a";
        roundRect(ctx, 70, 86, 84, 76, 14);
        ctx.fill();
        ctx.fillStyle = "#e6584d"; // coração
        ctx.beginPath();
        ctx.arc(102, 114, 10, 0, Math.PI * 2);
        ctx.arc(122, 114, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(93, 120);
        ctx.lineTo(112, 142);
        ctx.lineTo(131, 120);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#f4efe0";
        ctx.fillRect(82, 152, 60, 5);
    }
    ctx.fillStyle = "#0e3a22"; // faixa do rótulo
    ctx.fillRect(14, 208, 196, 34);
    ctx.fillStyle = "#ffd27a";
    ctx.font = "800 26px Inter, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(LABELS[kind] || kind.toUpperCase(), cx, 226);
    ctx.lineWidth = 8;
    ctx.strokeStyle = "#1f8a3a";
    ctx.strokeRect(4, 4, 216, 248);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * SPA / BEM-ESTAR (benchmark = café/agência/kinder/lab/biblioteca/eletrônicos):
 *   • JARDIM ZEN no TELHADO — PILHA DE PEDRAS DE EQUILÍBRIO (cairn, ícone-herói)
 *     sobre area de AREIA RASTELADA + moita de BAMBU + LÓTUS na água + toalhas
 *     enroladas + velas acesas; tudo alinhado aos eixos (isometria rigorosa);
 *   • entrada com COLUNAS DE BAMBU (família das colunas temáticas) + marquise
 *     com friso lilás suave;
 *   • térreo de TODAS as faces: VITRINES DE VERDADE com produtos de spa 3D
 *     (velas, pedras quentes, óleos, lótus, toalhas, bambu);
 *   • letreiro "SPA" padrão + janelas facadeGrid (máx. 3 andares).
 */
function buildSpa(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });
    const emissiveMat = (c, i = 0.5) =>
        new THREE.MeshStandardMaterial({
            color: c,
            emissive: new THREE.Color(c),
            emissiveIntensity: i,
            roughness: 0.5,
        });
    const stone = accentMat("#9aa3a0");
    const stoneDk = accentMat("#727b77");
    const bamboo = accentMat("#7cae52");
    const lotusPink = accentMat("#f2a0b5");

    // ---- ESTRUTURA EM 2 VOLUMES (pedido do usuário): pisos 1-2 = bloco cheio;
    //      piso 3 = METADE de trás fechada (c/ telhado + beiral) + METADE da
    //      frente aberta = VARANDA (fonte + bambu), no topo do piso 2. ----
    const f1 = b.h * 0.37; // topo do piso 1
    const f2 = b.h * 0.7; // topo do piso 2 (= piso da varanda)
    const backDepth = b.d * 0.5; // profundidade do bloco de trás (piso 3)
    const backZc = -b.d / 4; // centro Z do bloco de trás
    const terrZc = b.d / 4; // centro Z da varanda (metade da frente)
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const lower = new THREE.Mesh(new THREE.BoxGeometry(b.w, f2, b.d), wallMat);
    lower.position.y = f2 / 2;
    lower.castShadow = true;
    lower.receiveShadow = true;
    bg.add(lower);
    // piso da VARANDA (topo do piso 2, metade da frente) — pedra clara
    const deck = new THREE.Mesh(new THREE.BoxGeometry(b.w - 0.2, 0.3, backDepth + 0.2), accentMat("#d8cfb8"));
    deck.position.set(0, f2 + 0.15, terrZc - 0.1);
    deck.receiveShadow = true;
    bg.add(deck);
    // BLOCO DE TRÁS (piso 3, metade de trás) + cornija
    const back = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h - f2, backDepth), wallMat);
    back.position.set(0, (f2 + b.h) / 2, backZc);
    back.castShadow = true;
    back.receiveShadow = true;
    bg.add(back);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 0.9, backDepth + 0.9), edgeMat);
    cornice.position.set(0, b.h - 0.35, backZc);
    cornice.castShadow = true;
    bg.add(cornice);
    // TELHADO cobrindo APENAS o bloco de trás (o beiral avançado ficava grande
    // demais visto de cima — pedido do usuário)
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, backDepth + 0.6), trim);
    roofSlab.position.set(0, b.h + 0.25, backZc);
    roofSlab.receiveShadow = true;
    roofSlab.castShadow = true;
    bg.add(roofSlab);
    // GUARDA-CORPO da varanda (frente + 2 laterais da metade da frente)
    const addRail = (axis, fixed, from, to) => {
        const yr = f2 + 0.3;
        const len = Math.abs(to - from) + 0.2;
        const topR = new THREE.Mesh(new THREE.BoxGeometry(axis === "x" ? len : 0.18, 0.18, axis === "z" ? len : 0.18), edgeMat);
        topR.position.set(axis === "x" ? (from + to) / 2 : fixed, yr + 0.95, axis === "z" ? (from + to) / 2 : fixed);
        topR.castShadow = true;
        bg.add(topR);
        const nb = Math.max(2, Math.round(Math.abs(to - from) / 0.8));
        for (let i = 0; i <= nb; i++) {
            const t = from + (to - from) * (i / nb);
            const bal = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.95, 0.1), trim);
            bal.position.set(axis === "x" ? t : fixed, yr + 0.47, axis === "z" ? t : fixed);
            bg.add(bal);
        }
    };
    addRail("x", hd - 0.3, -(hw - 0.3), hw - 0.3); // frente
    addRail("z", -(hw - 0.3), 0.2, hd - 0.3); // lateral esquerda
    addRail("z", hw - 0.3, 0.2, hd - 0.3); // lateral direita

    // ---- produto de spa 3D em miniatura (base y=0, frente +z) ----
    const mkSpaProduct = (kind) => {
        const p = new THREE.Group();
        if (kind === "candle") {
            const jar = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 1.6, 14), accentMat("#dfeee4"));
            jar.position.y = 0.8;
            p.add(jar);
            const wax = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.3, 14), cream);
            wax.position.y = 1.55;
            p.add(wax);
            const wick = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.2, 6), dark);
            wick.position.y = 1.75;
            p.add(wick);
            const flame = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8), emissiveMat("#ffb43c", 0.85));
            flame.scale.set(1, 1.7, 1);
            flame.position.y = 1.98;
            p.add(flame);
        } else if (kind === "stones") {
            const sizes = [
                [0.95, 0.42],
                [0.72, 0.36],
                [0.5, 0.3],
            ];
            let sy = 0;
            for (const [r, h] of sizes) {
                const st = new THREE.Mesh(new THREE.SphereGeometry(r, 12, 8), stone);
                st.scale.set(1, h / r, 0.8);
                st.position.y = sy + h;
                p.add(st);
                sy += h * 2 + 0.04;
            }
        } else if (kind === "oils") {
            const cols = ["#f2c14e", "#c879e6", "#7cae52"];
            cols.forEach((c, i) => {
                const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 1.2, 10), emissiveMat(c, 0.3));
                bottle.position.set(-0.75 + i * 0.75, 0.6, 0);
                p.add(bottle);
                const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.35, 8), accentMat("#dfeee4"));
                neck.position.set(-0.75 + i * 0.75, 1.35, 0);
                p.add(neck);
                const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.22, 8), stoneDk);
                cap.position.set(-0.75 + i * 0.75, 1.62, 0);
                p.add(cap);
            });
        } else if (kind === "lotus") {
            const pad = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.1, 0.12, 16), accentMat("#3ddc84"));
            pad.position.y = 0.06;
            p.add(pad);
            // pétala = grupo girado por 'a' (RADIAL por construção — o método antigo
            // com rotation.y=-a+x deixava as pétalas tangenciais/espalhadas, painel)
            const mkPetal = (r, y, a, tilt, sc) => {
                const pg = new THREE.Group();
                const petal = new THREE.Mesh(new THREE.SphereGeometry(0.34, 8, 6), lotusPink);
                petal.scale.set(1.15 * sc, 0.3 * sc, 0.5 * sc); // longa no +X (radial)
                petal.position.x = r;
                petal.rotation.z = tilt; // levanta a ponta externa
                pg.add(petal);
                pg.rotation.y = a;
                pg.position.y = y;
                return pg;
            };
            const outer = 8,
                inner = 6;
            for (let i = 0; i < outer; i++) {
                p.add(mkPetal(0.6, 0.28, (i / outer) * Math.PI * 2, 0.5, 1.0));
            }
            for (let i = 0; i < inner; i++) {
                p.add(mkPetal(0.4, 0.46, (i / inner) * Math.PI * 2 + Math.PI / inner, 0.9, 0.82));
            }
            const heart = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), gold);
            heart.position.y = 0.62;
            p.add(heart);
        } else if (kind === "towels") {
            for (let i = 0; i < 2; i++) {
                const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 1.8, 14), cream);
                roll.rotation.x = Math.PI / 2;
                roll.position.set(0, 0.55 + i * 0.95, 0);
                p.add(roll);
                const band = new THREE.Mesh(new THREE.CylinderGeometry(0.57, 0.57, 0.3, 14), lotusPink);
                band.rotation.x = Math.PI / 2;
                band.position.set(0, 0.55 + i * 0.95, 0.5);
                p.add(band);
            }
        } else {
            // bambu no vaso
            const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.5, 0.9, 12), stoneDk);
            pot.position.y = 0.45;
            p.add(pot);
            for (const sx of [-0.28, 0, 0.28]) {
                const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 2.4, 8), bamboo);
                stalk.position.set(sx, 2.0, sx * 0.5);
                p.add(stalk);
                for (const ly of [1.4, 2.1, 2.8]) {
                    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.24, 6, 5), accentMat("#5f9440"));
                    leaf.scale.set(1.4, 0.2, 0.5);
                    leaf.position.set(sx + 0.3, ly, sx * 0.5);
                    leaf.rotation.z = 0.5;
                    p.add(leaf);
                }
            }
        }
        p.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return p;
    };
    const SPA_LABELS = {
        candle: "VELAS",
        stones: "PEDRAS",
        oils: "ÓLEOS",
        lotus: "LÓTUS",
        towels: "TOALHAS",
        bamboo: "BAMBU",
    };

    // ---- FACHADA: piso 1 (grande ENTRADA + 2 janelas por face) + piso 2
    //      (janelas; frente reserva o centro p/ a placa SPA) + piso 3 (bloco de
    //      trás: janelas no fundo/laterais + porta de vidro p/ a varanda) ----
    const faces = [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ];
    const entH = f1 - 0.5;
    const y2 = (f1 + f2) / 2;
    const y3 = (f2 + b.h) / 2;
    for (const f of faces) {
        const fd = f.nz !== 0 ? hd : hw;
        const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        const tx = f.nz,
            tz = f.nx;
        const wx = (o, d) => f.nx * (fd + d) + tx * o;
        const wz = (o, d) => f.nz * (fd + d) + tz * o;
        // PISO 1: grande ENTRADA de vidro (porta) + moldura em U + mulhão
        const door = new THREE.Mesh(new THREE.BoxGeometry(5.0, entH, 0.4), warmGlass);
        door.rotation.y = ry;
        door.position.set(wx(0, 0.1), entH / 2 + 0.3, wz(0, 0.1));
        bg.add(door);
        for (const jx of [-2.65, 2.65]) {
            const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.32, entH + 0.3, 0.55), edgeMat);
            jamb.rotation.y = ry;
            jamb.position.set(wx(jx, 0.12), 0.3 + (entH + 0.3) / 2 - 0.15, wz(jx, 0.12));
            jamb.castShadow = true;
            bg.add(jamb);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(5.9, 0.42, 0.6), edgeMat);
        lintel.rotation.y = ry;
        lintel.position.set(wx(0, 0.12), 0.3 + entH + 0.06, wz(0, 0.12));
        lintel.castShadow = true;
        bg.add(lintel);
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.16, entH, 0.5), edgeMat);
        mull.rotation.y = ry;
        mull.position.set(wx(0, 0.14), entH / 2 + 0.3, wz(0, 0.14));
        bg.add(mull);
        // PISO 1: 2 janelas laterais
        for (const u of [-6.6, 6.6]) {
            const win = makeClassWindow(2.2, 2.6, warmGlass, edgeMat);
            win.rotation.y = ry;
            win.position.set(wx(u, 0.12), f1 * 0.52, wz(u, 0.12));
            bg.add(win);
        }
        // TOLDO LISTRADO sobre a entrada (charme, pedido do usuário) — grupo externo
        // orienta à face (ry), grupo interno inclina a frente p/ baixo (tilt)
        {
            const awnW = 6.2,
                awnD = 1.8,
                awnY = 0.3 + entH + 0.05,
                nS = 8;
            const outer = new THREE.Group();
            outer.rotation.y = ry;
            outer.position.set(f.nx * (fd + 0.1), awnY, f.nz * (fd + 0.1));
            const inner = new THREE.Group();
            inner.rotation.x = 0.42; // a borda da frente pende p/ baixo
            outer.add(inner);
            const sw = awnW / nS;
            for (let i = 0; i < nS; i++) {
                const col = i % 2 === 0 ? accentMat(COLORS.waGreen) : cream;
                const stripe = new THREE.Mesh(new THREE.BoxGeometry(sw * 0.94, 0.14, awnD), col);
                stripe.position.set(-awnW / 2 + sw / 2 + i * sw, 0, awnD / 2);
                inner.add(stripe);
            }
            // babado PLANO (festões semicirculares, alternando verde/creme)
            addAwningValance(inner, awnW, awnD, (k) => (k % 2 === 0 ? cream : accentMat(COLORS.waGreen)));
            outer.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            bg.add(outer);
        }
        // PISO 2: janelas (a frente pula o centro p/ a placa SPA)
        const cols2 = f.nz === 1 ? [-6.6, 6.6] : [-6.6, -2.4, 2.4, 6.6];
        for (const u of cols2) {
            const win = makeClassWindow(2.2, 2.6, warmGlass, edgeMat);
            win.rotation.y = ry;
            win.position.set(wx(u, 0.12), y2, wz(u, 0.12));
            bg.add(win);
        }
    }
    // PISO 3 (bloco de trás): janelas no fundo + nas laterais (metade z<0)
    for (const u of [-5.4, -1.8, 1.8, 5.4]) {
        const win = makeClassWindow(2.0, 2.4, warmGlass, edgeMat);
        win.rotation.y = Math.PI;
        win.position.set(u, y3, -hd - 0.12);
        bg.add(win);
    }
    for (const sgn of [1, -1]) {
        for (const z of [-6.4, -2.6]) {
            const win = makeClassWindow(2.0, 2.4, warmGlass, edgeMat);
            win.rotation.y = sgn > 0 ? Math.PI / 2 : -Math.PI / 2;
            win.position.set(sgn * (hw + 0.12), y3, z);
            bg.add(win);
        }
    }
    // porta de vidro do bloco de trás p/ a VARANDA (face z=0)
    const tDoor = new THREE.Mesh(new THREE.BoxGeometry(4.0, 3.2, 0.4), warmGlass);
    tDoor.position.set(0, f2 + 1.9, 0.1);
    bg.add(tDoor);
    const tMull = new THREE.Mesh(new THREE.BoxGeometry(0.16, 3.2, 0.5), edgeMat);
    tMull.position.set(0, f2 + 1.9, 0.14);
    bg.add(tMull);
    // TOLDO listrado na porta da varanda (idêntico ao das entradas do térreo)
    {
        const awnW = 5.2,
            awnD = 1.7,
            awnY = f2 + 3.55,
            nS = 7;
        const outer = new THREE.Group();
        outer.position.set(0, awnY, 0.1); // face z=0 do bloco de trás, sobre a porta
        const inner = new THREE.Group();
        inner.rotation.x = 0.42;
        outer.add(inner);
        const sw = awnW / nS;
        for (let i = 0; i < nS; i++) {
            const col = i % 2 === 0 ? accentMat(COLORS.waGreen) : cream;
            const stripe = new THREE.Mesh(new THREE.BoxGeometry(sw * 0.94, 0.14, awnD), col);
            stripe.position.set(-awnW / 2 + sw / 2 + i * sw, 0, awnD / 2);
            inner.add(stripe);
        }
        // babado PLANO (festões semicirculares, alternando verde/creme)
        addAwningValance(inner, awnW, awnD, (k) => (k % 2 === 0 ? cream : accentMat(COLORS.waGreen)));
        outer.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        bg.add(outer);
    }

    // ---- VARANDA (piso 3, frente): FONTE central + BAMBU nas laterais ----
    const ty = f2 + 0.3; // superfície do deck
    {
        // FONTE de 2 níveis (bacia + água + pedestal + taça + jato) no centro
        const fx = 0,
            fz = terrZc;
        const basin = new THREE.Mesh(new THREE.CylinderGeometry(2.7, 2.9, 0.95, 22), stone);
        basin.position.set(fx, ty + 0.48, fz);
        basin.castShadow = true;
        bg.add(basin);
        const water1 = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.4, 0.12, 22), emissiveMat("#7fdcff", 0.35));
        water1.position.set(fx, ty + 0.9, fz);
        bg.add(water1);
        const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.75, 1.5, 14), stoneDk);
        ped.position.set(fx, ty + 1.65, fz);
        bg.add(ped);
        const bowl = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.0, 0.4, 18), stone);
        bowl.position.set(fx, ty + 2.5, fz);
        bowl.castShadow = true;
        bg.add(bowl);
        const water2 = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 0.1, 18), emissiveMat("#7fdcff", 0.4));
        water2.position.set(fx, ty + 2.7, fz);
        bg.add(water2);
        const jet = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.24, 0.9, 10), emissiveMat("#bfeeff", 0.45));
        jet.position.set(fx, ty + 3.2, fz);
        bg.add(jet);
        const drop = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), emissiveMat("#bfeeff", 0.5));
        drop.scale.set(1, 1.5, 1);
        drop.position.set(fx, ty + 3.8, fz);
        bg.add(drop);
        const lotus = mkSpaProduct("lotus");
        lotus.scale.setScalar(0.75);
        lotus.position.set(fx + 1.4, ty + 0.86, fz + 0.9);
        bg.add(lotus);
    }
    {
        // BAMBU nas laterais (talos VERTICAIS + anéis + folhas) num GRUPO p/ poder
        // rotacionar — o da ESQUERDA gira 180° p/ não ser cópia exata (pedido)
        const bambooAt = (bx, bz, rotY) => {
            const grp = new THREE.Group();
            for (const [ox, oz, ht] of [
                [-0.5, -0.3, 4.6],
                [0.35, 0.4, 5.4],
                [0.65, -0.45, 4.0],
            ]) {
                const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, ht, 10), bamboo);
                stalk.position.set(ox, ht / 2, oz);
                grp.add(stalk);
                for (let s = 1; s < ht / 1.0; s++) {
                    const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.12, 10), accentMat("#5f9440"));
                    ring.position.set(ox, s * 1.0, oz);
                    grp.add(ring);
                }
                for (const lo of [0.72, 0.42]) {
                    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.5, 6, 5), accentMat("#5f9440"));
                    leaf.scale.set(1.7, 0.24, 0.7);
                    leaf.position.set(ox + 0.5, ht * lo + 0.8, oz);
                    leaf.rotation.z = 0.5;
                    leaf.rotation.y = ox;
                    grp.add(leaf);
                }
            }
            grp.position.set(bx, ty, bz);
            grp.rotation.y = rotY;
            grp.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            bg.add(grp);
        };
        bambooAt(-(hw - 1.9), terrZc, Math.PI); // esquerda girada 180°
        bambooAt(hw - 1.9, terrZc, 0); // direita
    }

    // ---- PLACA "SPA" na frente do 2º andar ----
    const signH = 2.2;
    const signTex = makeSignTexture(t("SPA"), { bg: "#0e3a22", fg: "#ffd27a", border: "#3ddc84", fontPx: 66 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 3.4, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, y2, hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- SOB O BEIRAL (na varanda, encostado na parede do bloco de trás):
    //      PEDRAS de equilíbrio + VELAS acesas (pedido do usuário) ----
    {
        // pilhas de pedras (cairn) à esquerda
        const stackAt = (sx, sz, sizes) => {
            let sy = 0;
            for (const [r, h] of sizes) {
                const st = new THREE.Mesh(new THREE.SphereGeometry(r, 12, 8), sy > 0.5 ? stoneDk : stone);
                st.scale.set(1, h / r, 0.82);
                st.position.set(sx, ty + sy + h, sz);
                st.castShadow = true;
                bg.add(st);
                sy += h * 2 + 0.04;
            }
        };
        stackAt(-4.4, 1.5, [
            [0.95, 0.44],
            [0.72, 0.38],
            [0.5, 0.32],
        ]);
        stackAt(-2.6, 1.3, [
            [0.7, 0.36],
            [0.5, 0.3],
        ]);
        // fileira de VELAS acesas à direita, alinhadas
        for (const [vx, vh] of [
            [1.8, 1.4],
            [2.9, 1.0],
            [3.9, 1.6],
            [4.9, 1.1],
        ]) {
            const jar = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, vh, 12), accentMat("#dfeee4"));
            jar.position.set(vx, ty + vh / 2, 1.4);
            jar.castShadow = true;
            bg.add(jar);
            const flame = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8), emissiveMat("#ffb43c", 0.85));
            flame.scale.set(1, 1.8, 1);
            flame.position.set(vx, ty + vh + 0.2, 1.4);
            bg.add(flame);
        }
    }

    // ---- TETO DO 4º PAVIMENTO (topo do bloco de trás): composição ZEN de spa
    //      (ícone aéreo, como o globo da agência / blocos da escola) — grupo em
    //      roofY sobre o bloco de trás (backZc), senão fica enterrado ----
    {
        const roofY = b.h + 0.5;
        const rg = new THREE.Group();
        rg.position.set(0, roofY, backZc);
        // plataforma de pedra clara (deck zen) sob o cairn
        const plat = new THREE.Mesh(new THREE.CylinderGeometry(3.4, 3.4, 0.22, 24), accentMat("#d8cfb8"));
        plat.position.set(0, 0.11, 0.5);
        plat.receiveShadow = true;
        rg.add(plat);
        // CAIRN GIGANTE (herói): 6 pedras de equilíbrio empilhadas
        {
            const sizes = [
                [1.7, 0.66],
                [1.38, 0.58],
                [1.08, 0.5],
                [0.8, 0.44],
                [0.56, 0.38],
                [0.36, 0.3],
            ];
            let sy = 0.22;
            for (let i = 0; i < sizes.length; i++) {
                const [r, h] = sizes[i];
                const st = new THREE.Mesh(new THREE.SphereGeometry(r, 14, 10), i % 2 ? stoneDk : stone);
                st.scale.set(1, h / r, 0.82);
                st.position.set(0, sy + h, 0.5);
                st.castShadow = true;
                rg.add(st);
                sy += h * 2 + 0.03;
            }
        }
        // LÓTUS na ÁGUA (canto frente-direita)
        {
            const wx = 5.6,
                wz = 2.6;
            const water = new THREE.Mesh(new THREE.CylinderGeometry(2.0, 2.0, 0.2, 22), emissiveMat("#7fdcff", 0.35));
            water.position.set(wx, 0.1, wz);
            water.receiveShadow = true;
            rg.add(water);
            const rim = new THREE.Mesh(new THREE.TorusGeometry(2.0, 0.16, 8, 26), stone);
            rim.rotation.x = Math.PI / 2;
            rim.position.set(wx, 0.15, wz);
            rg.add(rim);
            const lotus = mkSpaProduct("lotus");
            lotus.scale.setScalar(1.4);
            lotus.position.set(wx, 0.18, wz);
            rg.add(lotus);
        }
        // BAMBU (verticalidade) — 2 moitas, a esquerda girada 180°
        const bambooCluster = (bx, bz, rotY) => {
            const grp = new THREE.Group();
            for (const [ox, oz, ht] of [
                [-0.5, -0.3, 4.6],
                [0.35, 0.4, 5.4],
                [0.65, -0.45, 4.0],
            ]) {
                const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.26, ht, 10), bamboo);
                stalk.position.set(ox, ht / 2, oz);
                grp.add(stalk);
                for (let s = 1; s < ht / 1.0; s++) {
                    const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.12, 10), accentMat("#5f9440"));
                    ring.position.set(ox, s * 1.0, oz);
                    grp.add(ring);
                }
                for (const lo of [0.72, 0.42]) {
                    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.5, 6, 5), accentMat("#5f9440"));
                    leaf.scale.set(1.7, 0.24, 0.7);
                    leaf.position.set(ox + 0.5, ht * lo + 0.8, oz);
                    leaf.rotation.z = 0.5;
                    leaf.rotation.y = ox;
                    grp.add(leaf);
                }
            }
            grp.position.set(bx, 0, bz);
            grp.rotation.y = rotY;
            grp.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            rg.add(grp);
        };
        bambooCluster(-6.4, -2.6, Math.PI);
        bambooCluster(6.2, -2.9, 0);
        // TOALHAS enroladas (canto frente-esquerda, alinhadas)
        for (let i = 0; i < 2; i++) {
            const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.6, 0.6, 2.0, 14), cream);
            roll.rotation.x = Math.PI / 2;
            roll.position.set(-5.8, 0.6 + i * 1.05, 2.8);
            roll.castShadow = true;
            rg.add(roll);
            const band = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.34, 14), lotusPink);
            band.rotation.x = Math.PI / 2;
            band.position.set(-5.8, 0.6 + i * 1.05, 3.35);
            rg.add(band);
        }
        // VELAS acesas (fileira alinhada perto do cairn)
        for (const [vx2, vh] of [
            [-2.9, 1.3],
            [-2.0, 0.95],
            [-3.7, 1.0],
        ]) {
            const jar = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, vh, 12), accentMat("#dfeee4"));
            jar.position.set(vx2, vh / 2, -3.2);
            jar.castShadow = true;
            rg.add(jar);
            const flame = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8), emissiveMat("#ffb43c", 0.85));
            flame.scale.set(1, 1.8, 1);
            flame.position.set(vx2, vh + 0.2, -3.2);
            rg.add(flame);
        }
        bg.add(rg);
    }
}

/**
 * PIZZARIA (benchmark = café/agência/kinder/lab/biblioteca/eletrônicos/spa):
 *   • FATIA DE PIZZA GIGANTE em pé no TELHADO (ícone aéreo) com molho, queijo,
 *     pepperoni e manjericão + FORNO A LENHA de tijolos (cúpula + boca acesa +
 *     chaminé com fumaça) + ENGRADADO de tomates e manjericão;
 *   • fachada com TOLDO XADREZ vermelho/branco (clássico de pizzaria) sobre a
 *     grande entrada + letreiro "PIZZARIA" + janelas facadeGrid (máx. 3 andares).
 */
function buildPizza(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const dough = new THREE.MeshStandardMaterial({ color: "#f2d79b", roughness: 0.85 }); // massa/crosta
    const cheese = new THREE.MeshStandardMaterial({ color: "#ffcf6a", roughness: 0.7 });
    const sauce = accentMat("#c0392b");
    const pepp = accentMat("#a5281d");
    const basil = accentMat("#3e9e4e");
    const brick = new THREE.MeshStandardMaterial({ color: "#c46b47", roughness: 0.9 });
    const ovenGlow = new THREE.MeshStandardMaterial({
        color: "#ff8c2a",
        emissive: new THREE.Color("#ff8c2a"),
        emissiveIntensity: 0.75,
        roughness: 0.5,
    });

    // ---- corpo + base + cornija + laje (padrão da cidade) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);

    // ---- FACHADA: janelas facadeGrid (frente reserva o térreo p/ a ENTRADA e a
    //      fileira do meio p/ o letreiro) ----
    const grid = facadeGrid(b.w, b.h, 3);
    const signRow = Math.min(1, grid.rows.length - 1);
    const signH = 2.2;
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const fd = f.nz !== 0 ? hd : hw;
        const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        const tx = f.nz,
            tz = f.nx;
        const front = f.nz === 1;
        grid.rows.forEach((y, ri) => {
            for (const u of grid.cols) {
                if (front && ri === 0 && Math.abs(u) < 5.5) continue; // entrada larga (só ±7.2)
                if (front && ri === signRow && Math.abs(u) < 5) continue; // letreiro
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (fd + 0.12) + tx * u, y, f.nz * (fd + 0.12) + tz * u);
                bg.add(win);
            }
        });
    }

    // ---- ENTRADA (frente): grande PORTA de vidro (larga, só 1 janela por lado)
    //      + moldura em U + 2 mulhões + degrau + TOLDO XADREZ da largura da porta.
    //      Vidro FINO EM FRENTE à parede (não atravessa) — corrige o "piscar
    //      verde" (z-fighting) que aparecia nas laterais da porta antiga. ----
    const doorW = 9.0,
        doorH = grid.rows[0] + grid.winH / 2 + 0.3;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.24), warmGlass);
    door.position.set(0, doorH / 2 + 0.4, hd + 0.22);
    bg.add(door);
    for (const jx of [-doorW / 2, doorW / 2]) {
        // jambas (cobrem as bordas do vidro)
        const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.34, doorH + 0.3, 0.5), edgeMat);
        jamb.position.set(jx, (doorH + 0.3) / 2 + 0.25, hd + 0.16);
        jamb.castShadow = true;
        bg.add(jamb);
    }
    const lintelD = new THREE.Mesh(new THREE.BoxGeometry(doorW + 0.68, 0.4, 0.55), edgeMat); // verga
    lintelD.position.set(0, doorH + 0.45, hd + 0.16);
    lintelD.castShadow = true;
    bg.add(lintelD);
    for (const mx of [-doorW / 6, doorW / 6]) {
        // 2 mulhões (vitrine em 3 panos)
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.32), edgeMat);
        mull.position.set(mx, doorH / 2 + 0.4, hd + 0.28);
        bg.add(mull);
    }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.2, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.7);
    step.castShadow = true;
    bg.add(step);
    // TOLDO XADREZ (mesa de pizzaria) da largura da porta
    {
        const awnW = doorW + 1.4,
            awnD = 2.2,
            awnY = doorH + 0.95;
        const checkTex = makeCheckerTexture("#c0392b", "#f4efe0", 2);
        checkTex.wrapS = checkTex.wrapT = THREE.RepeatWrapping;
        checkTex.repeat.set(Math.round(awnW / 1.1), 2); // células ≈ quadradas
        const checkMat = new THREE.MeshStandardMaterial({ map: checkTex, roughness: 0.8 });
        const inner = new THREE.Group();
        inner.rotation.x = 0.42;
        const canopy = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.16, awnD), checkMat);
        canopy.position.set(0, 0, awnD / 2);
        inner.add(canopy);
        // babado PLANO (festões semicirculares, alternando vermelho/creme)
        addAwningValance(inner, awnW, awnD, (k) => (k % 2 === 0 ? accentMat("#c0392b") : accentMat("#f4efe0")));
        inner.position.set(0, awnY, hd + 0.1);
        inner.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        bg.add(inner);
    }

    // ---- LETREIRO "PIZZARIA" na fileira do meio ----
    const signTex = makeSignTexture(t("PIZZARIA"), { bg: "#0e3a22", fg: "#ffd27a", border: "#c0392b", fontPx: 58 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.3, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- TOLDO XADREZ no TOPO das 4 FACES (ar italiano no prédio todo) ----
    {
        const awnW = b.w - 0.4,
            awnD = 1.35,
            awnY = b.h - 1.5;
        const checkTex = makeCheckerTexture("#c0392b", "#f4efe0", 2);
        checkTex.wrapS = checkTex.wrapT = THREE.RepeatWrapping;
        checkTex.repeat.set(Math.round(awnW / 1.1), 1); // células ≈ quadradas
        const checkMat = new THREE.MeshStandardMaterial({ map: checkTex, roughness: 0.8 });
        const nTab = Math.round(awnW / 0.95);
        for (const f of [
            { nx: 0, nz: 1 },
            { nx: 0, nz: -1 },
            { nx: 1, nz: 0 },
            { nx: -1, nz: 0 },
        ]) {
            const fd = f.nz !== 0 ? hd : hw;
            const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
            const outer = new THREE.Group();
            outer.rotation.y = ry;
            outer.position.set(f.nx * (fd + 0.05), awnY, f.nz * (fd + 0.05));
            const inner = new THREE.Group();
            inner.rotation.x = 0.5; // pende mais que o da entrada (faixa de topo)
            outer.add(inner);
            const canopy = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.14, awnD), checkMat);
            canopy.position.set(0, 0, awnD / 2);
            inner.add(canopy);
            // babado PLANO (festões semicirculares, alternando vermelho/creme)
            addAwningValance(inner, awnW, awnD, (k) => (k % 2 === 0 ? accentMat("#c0392b") : accentMat("#f4efe0")));
            outer.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            bg.add(outer);
        }
    }

    // ---- TELHADO: só uma FATIA DE PIZZA GIGANTE, DEITADA no teto (o prédio é
    //      visto quase de cima); proporção da xícara do café (~9 de largura).
    //      Forno e engradado removidos (simplificação a pedido do usuário). ----
    const roofY = b.h + 0.5;
    {
        const cheeseMelt = new THREE.MeshStandardMaterial({ color: "#ffe0a0", roughness: 0.6 });
        const charCheese = new THREE.MeshStandardMaterial({ color: "#e0a94a", roughness: 0.7 });
        const crustMat = new THREE.MeshStandardMaterial({ color: "#e0b06a", roughness: 0.85 });
        const charMat = new THREE.MeshStandardMaterial({ color: "#8a5a2a", roughness: 0.9 });
        const peppDk = new THREE.MeshStandardMaterial({ color: "#7a1e15", roughness: 0.7 });
        const mushMat = new THREE.MeshStandardMaterial({ color: "#e8dcc0", roughness: 0.8 });
        const oliveMat = new THREE.MeshStandardMaterial({ color: "#3a3a3a", roughness: 0.6 });
        const slice = new THREE.Group();
        const W = 3.0,
            H = 6.2; // base ±3, ponta em 6.2 (+15% de altura = mais alongado)
        const Td = 0.34; // MASSA FINA (altura menor — pedido do usuário)
        const slope = W / H; // razão p/ seguir o ângulo das laterais do triângulo
        // MASSA (triângulo FINO) que recebe o recheio
        const shape = new THREE.Shape();
        shape.moveTo(0, H);
        shape.lineTo(-W, 0);
        shape.lineTo(W, 0);
        shape.closePath();
        slice.add(new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: Td, bevelEnabled: false }), cheese));
        // MOLHO (triângulo menor) sobre a massa, começando acima da crosta
        const sShape = new THREE.Shape();
        sShape.moveTo(0, H - 0.6);
        sShape.lineTo(-2.4, 0.95);
        sShape.lineTo(2.4, 0.95);
        sShape.closePath();
        const sauceMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(sShape, { depth: 0.1, bevelEnabled: false }), sauce);
        sauceMesh.position.z = Td;
        slice.add(sauceMesh);
        // BORDA/CROSTA: faixa TRAPEZOIDAL na base cujas laterais SEGUEM o ângulo do
        // triângulo (miter — não um cilindro reto) + bevel p/ ficar fofa/arredondada
        const crustW = 1.05;
        const crustShape = new THREE.Shape();
        crustShape.moveTo(-W, 0);
        crustShape.lineTo(W, 0);
        crustShape.lineTo(W - slope * crustW, crustW);
        crustShape.lineTo(-(W - slope * crustW), crustW);
        crustShape.closePath();
        const crust = new THREE.Mesh(
            new THREE.ExtrudeGeometry(crustShape, {
                depth: 0.34,
                bevelEnabled: true,
                bevelThickness: 0.2,
                bevelSize: 0.22,
                bevelSegments: 3,
            }),
            crustMat,
        );
        crust.position.z = 0.2; // base RENTE à massa (não afunda abaixo dela)
        slice.add(crust);
        for (const cxp of [-2.0, -0.7, 0.7, 2.0]) {
            // char na crosta
            const ch = new THREE.Mesh(new THREE.SphereGeometry(0.18, 6, 5), charMat);
            ch.scale.set(1, 0.55, 0.7);
            ch.position.set(cxp, 0.5, 0.66);
            slice.add(ch);
        }
        // ===== TOPPINGS em LAYOUT DETERMINÍSTICO (fileiras triangulares, sem
        //       acúmulo aleatório) — todos dentro do triângulo em cada altura =====
        const zt = Td + 0.1; // topo do molho
        // queijo derretido (blobs claros) distribuído uniformemente
        for (const [px, py, r] of [
            [-1.2, 1.5, 0.5],
            [1.0, 1.5, 0.5],
            [-0.6, 2.6, 0.45],
            [0.7, 2.6, 0.45],
            [0, 3.5, 0.42],
            [-0.3, 4.4, 0.36],
            [0.3, 4.4, 0.36],
        ]) {
            const blob = new THREE.Mesh(new THREE.SphereGeometry(r, 8, 6), cheeseMelt);
            blob.scale.set(1, 1, 0.22);
            blob.position.set(px, py, zt - 0.04);
            slice.add(blob);
        }
        // pepperoni em fileiras (3 na base, 2, 2, 1 — segue o afunilamento)
        for (const [px, py] of [
            [-1.35, 1.6],
            [0.05, 1.55],
            [1.35, 1.6],
            [-0.8, 2.7],
            [0.75, 2.7],
            [-0.45, 3.7],
            [0.6, 3.7],
            [0.0, 4.6],
        ]) {
            const p = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.16, 14), pepp);
            p.rotation.x = Math.PI / 2;
            p.position.set(px, py, zt + 0.08);
            slice.add(p);
            const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.32, 0.05, 14), peppDk);
            rim.rotation.x = Math.PI / 2;
            rim.position.set(px, py, zt + 0.16);
            slice.add(rim);
        }
        // manjericão (folhas) nos vãos
        for (const [px, py] of [
            [0.7, 2.1],
            [-1.1, 3.2],
            [0.6, 4.25],
            [0.0, 5.2],
        ]) {
            const bl = new THREE.Mesh(new THREE.SphereGeometry(0.3, 6, 5), basil);
            bl.scale.set(1.25, 1.25, 0.28);
            bl.position.set(px, py, zt + 0.1);
            slice.add(bl);
        }
        // cogumelos (meia-esfera) + azeitonas (anéis)
        for (const [px, py] of [
            [-1.45, 2.15],
            [1.0, 3.2],
        ]) {
            const mush = new THREE.Mesh(new THREE.SphereGeometry(0.32, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), mushMat);
            mush.scale.set(1, 0.5, 1);
            mush.position.set(px, py, zt + 0.02);
            slice.add(mush);
        }
        for (const [px, py] of [
            [0.35, 3.15],
            [-0.55, 2.15],
            [0.15, 4.95],
        ]) {
            const olive = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.08, 6, 10), oliveMat);
            olive.rotation.x = Math.PI / 2;
            olive.position.set(px, py, zt + 0.06);
            slice.add(olive);
        }
        slice.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        // pivô no CENTROIDE do triângulo (0, H/3) p/ girar em torno do próprio centro
        const pivot = new THREE.Group();
        slice.position.set(0, -H / 3, 0);
        pivot.add(slice);
        pivot.rotation.x = -Math.PI / 2; // DEITADA no teto (horizontal)
        const spin = new THREE.Group();
        spin.add(pivot);
        spin.scale.setScalar(1.8); // +20% (era 1.5)
        spin.rotation.y = -(Math.PI * 130) / 180; // 130° sentido HORÁRIO (perspectiva)
        // GUARDANAPO XADREZ quadrado sob a fatia (mesmo estilo dos toldos — charme)
        const napTex = makeCheckerTexture("#c0392b", "#f4efe0", 2);
        napTex.wrapS = napTex.wrapT = THREE.RepeatWrapping;
        napTex.repeat.set(9, 9); // células quadradas finas
        const napMat = new THREE.MeshStandardMaterial({ map: napTex, roughness: 0.85 });
        const nap = new THREE.Mesh(new THREE.BoxGeometry(12, 0.14, 12), napMat);
        nap.position.set(0, roofY + 0.07, 0); // CENTRADO no teto
        nap.rotation.y = 0; // ALINHADO 90° ao teto (não diamante)
        nap.receiveShadow = true;
        bg.add(nap);
        // fatia assenta SOBRE o guardanapo (crosta rente ao topo do guardanapo),
        // também centralizada no teto
        spin.position.set(0, roofY + 0.14, 0);
        bg.add(spin);
    }
}

/** Textura xadrez (n×n) de 2 cores — toldo de pizzaria etc. */
function makeCheckerTexture(a, b, n) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext("2d");
    const cell = 128 / n;
    for (let i = 0; i < n; i++) {
        for (let j = 0; j < n; j++) {
            ctx.fillStyle = (i + j) % 2 === 0 ? a : b;
            ctx.fillRect(i * cell, j * cell, cell, cell);
        }
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/**
 * E-COMMERCE (`ecommerce`, logística + loja online) — bloco institucional VERDE com tema CORAL-COURIER
 * + papelão KRAFT. Herói de teto = CAIXA de entrega gigante sobre PALETE (fita em cruz + etiqueta em
 * bloco). Frente = DOCA DE CARGA (porta-de-rolo ENTREABERTA e acesa + marquise industrial + pilha de
 * pacotes + carrinho de mão) e VITRINES (esteira de pacotes / tela "comprar online"). Anel de pacotes
 * kraft contornando o teto. SEM seta-sorriso (evita mimetismo de marca), SEM carrinho gigante (é do
 * cereals), SEM furgão na via (a frente dá p/ calçada de pedestre). Substitui a PIZZARIA no D4.
 */
function buildEcommerce(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2, hd = b.d / 2, H = b.h, roofY = H + 0.5;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({ color: "#ffc24e", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.5, roughness: 0.4 });
    // TEMA e-commerce: coral-courier + papelão kraft (claro p/ ler à noite) + fita + etiqueta branca
    const accent = new THREE.MeshStandardMaterial({ color: "#ef7c3c", roughness: 0.5, metalness: 0.05 });
    const accentDk = new THREE.MeshStandardMaterial({ color: "#d9642a", roughness: 0.55, metalness: 0.05 });
    const kraft = new THREE.MeshStandardMaterial({ color: "#cfa774", roughness: 0.72 });
    const kraftTop = new THREE.MeshStandardMaterial({ color: "#ddba8b", roughness: 0.72 });
    const kraftDk = new THREE.MeshStandardMaterial({ color: "#a97f52", roughness: 0.75 });
    const tape = new THREE.MeshStandardMaterial({ color: "#e8dcc0", roughness: 0.6 });
    const labelW = new THREE.MeshStandardMaterial({ color: "#f6f3ec", emissive: new THREE.Color("#cfcabd"), emissiveIntensity: 0.15, roughness: 0.55 });
    const labelDk = new THREE.MeshStandardMaterial({ color: "#2b2822", roughness: 0.6 });
    const wood = new THREE.MeshStandardMaterial({ color: "#8a6a43", roughness: 0.8 });
    const rubber = new THREE.MeshStandardMaterial({ color: "#22201d", roughness: 0.85 });
    const dockGlow = new THREE.MeshStandardMaterial({ color: "#ffd9a0", emissive: new THREE.Color("#ffb45a"), emissiveIntensity: 0.5, roughness: 0.5 });

    const FACES = [{ nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 }];
    const info = (f) => ({ tx: f.nz, tz: f.nx, faceDist: f.nz !== 0 ? hd : hw, faceW: f.nz !== 0 ? b.w : b.d, ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2, isFront: f.nz === 1 });

    // CAIXA de entrega (base y=0): kraft (tampa clara) + [full] fita em cruz + etiqueta em BLOCO
    function mkParcel(w, h, d, full) {
        const g = new THREE.Group();
        const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [kraft, kraft, kraftTop, kraftDk, kraft, kraft]);
        body.position.y = h / 2; body.castShadow = true; g.add(body);
        if (full) {
            const tX = new THREE.Mesh(new THREE.BoxGeometry(w + 0.02, 0.06, d * 0.2), tape); tX.position.y = h + 0.01; g.add(tX);
            const tZ = new THREE.Mesh(new THREE.BoxGeometry(w * 0.2, 0.06, d + 0.02), tape); tZ.position.y = h + 0.01; g.add(tZ);
            const tFront = new THREE.Mesh(new THREE.BoxGeometry(w * 0.2, h * 0.6, 0.05), tape); tFront.position.set(0, h * 0.7, d / 2 + 0.02); g.add(tFront);
            const lw = w * 0.5, ld = d * 0.32;
            const lab = new THREE.Mesh(new THREE.BoxGeometry(lw, 0.04, ld), labelW); lab.position.set(w * 0.15, h + 0.04, -d * 0.16); g.add(lab);
            const labBand = new THREE.Mesh(new THREE.BoxGeometry(lw * 0.86, 0.05, ld * 0.3), labelDk); labBand.position.set(w * 0.15, h + 0.05, -d * 0.16 - ld * 0.26); g.add(labBand);
            const labBlk = new THREE.Mesh(new THREE.BoxGeometry(lw * 0.66, 0.05, ld * 0.24), labelDk); labBlk.position.set(w * 0.15, h + 0.05, -d * 0.16 + ld * 0.22); g.add(labBlk);
        } else {
            const lab = new THREE.Mesh(new THREE.BoxGeometry(w * 0.42, 0.03, d * 0.42), labelW); lab.position.set(0, h + 0.03, 0); g.add(lab);
            const tp = new THREE.Mesh(new THREE.BoxGeometry(w * 0.16, 0.04, d + 0.02), tape); tp.position.y = h + 0.01; g.add(tp);
        }
        g.scale.setScalar(0.9); // −10% no tamanho de TODAS as caixas (a fábrica cobre todas)
        return g;
    }
    // chevron NEUTRO de despacho (»), coral emissivo leve — no lugar da seta-sorriso
    function mkChevron(s) {
        const g = new THREE.Group();
        const m = new THREE.MeshStandardMaterial({ color: "#ef7c3c", emissive: new THREE.Color("#ef7c3c"), emissiveIntensity: 0.4, roughness: 0.5 });
        for (const sy of [-1, 1]) {
            const arm = new THREE.Mesh(new THREE.BoxGeometry(0.95 * s, 0.22 * s, 0.12 * s), m);
            arm.position.set(0, sy * 0.3 * s, 0); arm.rotation.z = sy * -0.95; g.add(arm);
        }
        return g;
    }
    // carrinho de mão (sack truck) c/ caixa — tell de doca no térreo (seguro na calçada, sem via)
    function mkHandTruck() {
        const g = new THREE.Group();
        const frame = new THREE.Mesh(new THREE.BoxGeometry(1.4, 3.0, 0.14), accentDk); frame.position.set(0, 1.5, 0); g.add(frame);
        const plate = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.16, 0.7), accentDk); plate.position.set(0, 0.2, 0.35); g.add(plate);
        for (const sx of [-1, 1]) { const wl = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.18, 12), rubber); wl.rotation.z = Math.PI / 2; wl.position.set(sx * 0.7, 0.4, -0.1); g.add(wl); }
        const box = mkParcel(1.3, 1.3, 1.0, false); box.position.set(0, 0.36, 0.2); g.add(box);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    }

    // ===== CASCA verde institucional + cornija coral =====
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 1.6, b.d + 0.5), dark);
    base.position.y = 0.8; bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.1, 1.0, b.d + 1.1), accent);
    cornice.position.y = H - 0.4; cornice.castShadow = true; bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ===== FACHADA: janelas + pilastras coral + FRISO-ESTANTE (banda + ledge + mini caixas) =====
    const grid = facadeGrid(b.w, H, 5);
    const topRowY = grid.rows[grid.rows.length - 1];
    for (const f of FACES) {
        const { tx, tz, faceDist, faceW, ry, isFront } = info(f);
        grid.rows.forEach((y) => {
            if (y === topRowY) return;
            for (const u of grid.cols) {
                if (isFront && ((Math.abs(u) < 5.5 && y < H * 0.34) || (Math.abs(u) < hw * 0.34 && y < H * 0.82))) continue; // só o miolo da doca no térreo (bay ±7.2 fica com janela, alinhada ao 2º pav.)
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
        for (let i = 0; i < grid.cols.length - 1; i++) {
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, H - 2.4, 0.28), accent);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, H / 2 + 0.1, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
        // FRISO-ESTANTE de galpão: banda coralDk + ledge coral + 3 mini-caixas kraft por face
        const band = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 1.9, 0.2), accentDk);
        band.rotation.y = ry; band.position.set(f.nx * (faceDist + 0.1), topRowY, f.nz * (faceDist + 0.1)); bg.add(band);
        const ledge = new THREE.Mesh(new THREE.BoxGeometry(faceW + 0.3, 0.16, 0.5), accent);
        ledge.rotation.y = ry; ledge.position.set(f.nx * (faceDist + 0.24), topRowY - 0.78, f.nz * (faceDist + 0.24)); bg.add(ledge);
        [-0.28, 0, 0.28].forEach((fr) => {
            const cu = fr * faceW;
            const wrap = new THREE.Group();
            const boxx = mkParcel(1.0, 0.85, 0.8, false); wrap.add(boxx);
            wrap.rotation.y = ry;
            wrap.position.set(f.nx * (faceDist + 0.36) + tx * cu, topRowY - 0.7, f.nz * (faceDist + 0.36) + tz * cu);
            bg.add(wrap);
        });
    }

    // ===== ENTRADA +Z = DOCA DE CARGA (porta-de-rolo entreaberta + acesa) =====
    const dockW = 5.6, dockH = 5.4;
    const dockGlowP = new THREE.Mesh(new THREE.BoxGeometry(dockW, dockH, 0.3), dockGlow);
    dockGlowP.position.set(0, dockH / 2 + 0.5, hd - 0.05); bg.add(dockGlowP);
    for (const [bw, bh, ox, oy] of [[0.4, dockH + 0.7, -dockW / 2 - 0.2, dockH / 2 + 0.5], [0.4, dockH + 0.7, dockW / 2 + 0.2, dockH / 2 + 0.5], [dockW + 0.8, 0.5, 0, dockH + 0.9]]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.5), accentDk); m.position.set(ox, oy, hd + 0.05); m.castShadow = true; bg.add(m);
    }
    // porta-de-rolo ENTREABERTA: ripas só na parte de cima (base aberta mostra o interior aceso)
    for (let sy = dockH * 0.42; sy <= dockH + 0.2; sy += 0.34) {
        const slat = new THREE.Mesh(new THREE.BoxGeometry(dockW, 0.28, 0.16), accent);
        slat.position.set(0, sy + 0.5, hd + 0.14); bg.add(slat);
    }
    // MARQUISE industrial plana + valança com letreiro "E-SHOP"
    const marqW = dockW + 3.0, marqY = dockH + 1.3;
    const marq = new THREE.Mesh(new THREE.BoxGeometry(marqW, 0.28, 2.6), accent);
    marq.position.set(0, marqY, hd + 1.3); marq.rotation.x = -0.14; marq.castShadow = true; bg.add(marq);
    const signTex = makeSignTexture(t("E-SHOP"), { bg: "#12251c", fg: "#ffd27a", border: "#ef7c3c", fontPx: 40 });
    const signMat = new THREE.MeshStandardMaterial({ map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffd27a"), emissiveIntensity: 0.5, roughness: 0.5 });
    const valance = new THREE.Mesh(new THREE.BoxGeometry(marqW, 1.1, 0.14), [accentDk, accentDk, accentDk, accentDk, signMat, accentDk]);
    valance.position.set(0, marqY - 0.55, hd + 2.55); bg.add(valance);
    for (const sx of [-1, 1]) { // para-choques de doca
        const bump = new THREE.Mesh(new THREE.BoxGeometry(0.4, 2.4, 0.5), rubber);
        bump.position.set(sx * (dockW / 2 + 0.5), 1.7, hd + 0.35); bg.add(bump);
    }
    const dockStep = new THREE.Mesh(new THREE.BoxGeometry(dockW + 3.4, 0.5, 2.2), trim);
    dockStep.position.set(0, 0.25, hd + 1.6); dockStep.castShadow = true; dockStep.receiveShadow = true; bg.add(dockStep);
    // EMBLEMA sobre a porta = caixa média + chevron neutro
    const emblem = mkParcel(2.6, 2.4, 1.0, true);
    emblem.position.set(0, marqY + 0.35, hd + 0.3); bg.add(emblem);
    const chevD = mkChevron(0.9); chevD.position.set(0, marqY + 1.4, hd + 0.78); bg.add(chevD);
    // pilha de pacotes + carrinho de mão na doca
    const pileBase = mkParcel(1.6, 1.5, 1.3, true); pileBase.position.set(dockW / 2 + 0.9, 0.5, hd + 1.5); bg.add(pileBase); // caixa (dir) p/ dentro do degrau
    const pileTop = mkParcel(1.2, 1.0, 1.0, false); pileTop.position.set(dockW / 2 + 0.9, 2.0, hd + 1.5); bg.add(pileTop);
    const truck = mkHandTruck(); truck.scale.setScalar(0.62); truck.position.set(-(dockW / 2 + 0.9), 0.5, hd + 1.6); truck.rotation.y = 0.4; bg.add(truck); // carrinho (esq) p/ dentro do degrau
    for (const sx of [-1, 1]) { // bolardos rolo-de-fita — mais perto do degrau, ainda no chão verde
        const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.7, 16), tape);
        roll.rotation.x = Math.PI / 2; roll.position.set(sx * (dockW / 2 + 2.4), 0.55, hd + 2.0); roll.castShadow = true; bg.add(roll); // base (centro−raio) no chão, sem flutuar
        const hole = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.72, 12), dark);
        hole.rotation.x = Math.PI / 2; hole.position.set(sx * (dockW / 2 + 2.4), 0.55, hd + 2.0); bg.add(hole);
    }

    // (VITRINES removidas a pedido: as laterais do térreo agora têm JANELAS normais — ver o vão
    //  da fachada acima, que só reserva o miolo central da doca; o bay ±7.2 alinha com o 2º pavimento)

    // ===== TETO: PALETE + CAIXA GIGANTE (herói) + ANEL de pacotes kraft =====
    const palW = 7.2;
    const palTop = new THREE.Mesh(new THREE.BoxGeometry(palW, 0.28, palW), wood); palTop.position.set(0, roofY + 0.14, 0); palTop.castShadow = true; bg.add(palTop);
    for (const sz of [-1, 0, 1]) { const skid = new THREE.Mesh(new THREE.BoxGeometry(palW, 0.3, 0.7), wood); skid.position.set(0, roofY - 0.15, sz * (palW / 2 - 0.4)); bg.add(skid); }
    const hero = mkParcel(6.0, 5.0, 6.0, true); // footprint < palete → palete espia no topo
    hero.position.set(0, roofY + 0.28, 0); bg.add(hero);
    const chevH = mkChevron(1.62); chevH.position.set(0, roofY + 0.28 + 2.3, 2.75); bg.add(chevH);
    const R = hw - 2.8;
    const ringPos = [[-R, -R], [R, -R], [R, R], [-R, R], [0, -R], [R, 0], [0, R], [-R, 0]];
    ringPos.forEach(([x, z]) => {
        const s = 3.3 + rand() * 0.7; // tamanhos VARIADOS (não uniformes); a caixa-herói central segue a MAIOR
        const pk = mkParcel(s, s * 0.8, s * 0.92, true); // caixa COMPLETA (fita+etiqueta) — mkParcel já aplica o −10%
        pk.position.set(x, roofY, z); // eixo-alinhadas, nas posições exatas do anel (sem rotação/jitter)
        bg.add(pk);
    });
}

/**
 * BOUTIQUE MODERNA (`boutique`, 2ª geração — o buildBoutique clássico segue no
 * arquivo mas o registro aponta p/ cá; benchmark = café/agência/spa/pizzaria):
 *   • SALTO-ALTO (stiletto) GIGANTE vermelho no TELHADO (ícone de moda) + PILHA
 *     DE CAIXAS DE CHAPÉU + SACOLA DE COMPRAS c/ laço;
 *   • térreo com VITRINES elegantes de DRESS-FORM (manequim com vestido) atrás
 *     de vidro; letreiro "BOUTIQUE" + toldo ROSÉ + janelas facadeGrid (máx 3).
 */
function buildBoutique2(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });
    const rose = accentMat("#e88aa6");
    const red = accentMat("#c0392b");
    const glassPane = new THREE.MeshStandardMaterial({
        color: "#bfe4d8",
        transparent: true,
        opacity: 0.22,
        roughness: 0.15,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#fff3d8",
        emissive: new THREE.Color("#f5e9bf"),
        emissiveIntensity: 0.45,
        roughness: 0.8,
    });
    const DRESS = ["#e6584d", "#5b8def", "#c879e6", "#f2c14e", "#3ddc84", "#e88aa6"];

    // ---- corpo + base + cornija + laje ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);
    // moldura horizontal (string course) no topo do térreo — separa a vitrine do
    // pavimento de SACADAS acima
    {
        const band = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 0.55, b.d + 0.5), edgeMat);
        band.position.y = 6.6;
        band.castShadow = true;
        bg.add(band);
    }

    // ---- DRESS-FORM (manequim c/ vestido): base + pole + saia + busto + faixa ----
    const mkDressForm = (col) => {
        const p = new THREE.Group();
        const st = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.55, 0.16, 14), edgeMat);
        st.position.y = 0.08;
        p.add(st);
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.85, 8), edgeMat);
        pole.position.y = 0.55;
        p.add(pole);
        const skirt = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 1.0, 1.7, 16), accentMat(col));
        skirt.position.y = 1.85;
        p.add(skirt);
        const belt = new THREE.Mesh(new THREE.CylinderGeometry(0.44, 0.44, 0.16, 14), gold);
        belt.position.y = 2.5;
        p.add(belt);
        const bust = new THREE.Mesh(new THREE.CylinderGeometry(0.52, 0.4, 1.0, 14), accentMat(col));
        bust.position.y = 3.05;
        p.add(bust);
        const shoulders = new THREE.Mesh(new THREE.SphereGeometry(0.5, 12, 8), accentMat(col));
        shoulders.scale.set(1, 0.5, 0.8);
        shoulders.position.y = 3.55;
        p.add(shoulders);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.15, 0.28, 10), cream);
        neck.position.y = 3.85;
        p.add(neck);
        const knob = new THREE.Mesh(new THREE.IcosahedronGeometry(0.16, 0), cream);
        knob.position.y = 4.08;
        p.add(knob);
        p.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return p;
    };

    // ---- FACHADA: térreo = VITRINES de dress-form; 2º pavimento = SACADAS
    //      (porta-janela + varanda projetada + guarda-corpo), estilo hotel ----
    const signH = 1.9;
    // sacada: laje logo acima da moldura do térreo; porta-janela até sob a cornija
    const balSlabY = 6.95;
    const balDoorTop = b.h - 1.1;
    const balDoorH = balDoorTop - balSlabY;
    const balDoorCY = (balSlabY + balDoorTop) / 2;
    const addBalcony = (f, u) => {
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz,
            tz = f.nx;
        const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        const ox = f.nx,
            oz = f.nz;
        const px = f.nx * faceDist + tx * u;
        const pz = f.nz * faceDist + tz * u;
        // porta-janela (moldura + vidro), rente à parede
        const frame = new THREE.Mesh(new THREE.BoxGeometry(2.5, balDoorH, 0.18), edgeMat);
        frame.rotation.y = ry;
        frame.position.set(px + ox * 0.06, balDoorCY, pz + oz * 0.06);
        bg.add(frame);
        const pane = new THREE.Mesh(new THREE.BoxGeometry(2.1, balDoorH - 0.3, 0.22), warmGlass);
        pane.rotation.y = ry;
        pane.position.set(px + ox * 0.12, balDoorCY, pz + oz * 0.12);
        bg.add(pane);
        // laje projetada + guarda-corpo (verde do teto) + gradis laterais
        const slab = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.18, 1.1), trim);
        slab.rotation.y = ry;
        slab.position.set(px + ox * 0.6, balSlabY, pz + oz * 0.6);
        slab.castShadow = true;
        bg.add(slab);
        const railY = balSlabY + 0.9;
        const rail = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.1, 0.1), trim);
        rail.rotation.y = ry;
        rail.position.set(px + ox * 1.1, railY, pz + oz * 1.1);
        bg.add(rail);
        for (const su of [-1.35, 0, 1.35]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.9, 0.08), trim);
            post.rotation.y = ry;
            post.position.set(px + ox * 1.1 + tx * su, balSlabY + 0.45, pz + oz * 1.1 + tz * su);
            bg.add(post);
        }
        for (const se of [-1.5, 1.5]) {
            const sideTop = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 1.1), trim);
            sideTop.rotation.y = ry;
            sideTop.position.set(px + ox * 0.6 + tx * se, railY, pz + oz * 0.6 + tz * se);
            bg.add(sideTop);
        }
        // FLOREIRA de rosas na borda da sacada (toque boutique, casa com o jardim do teto)
        const boxP = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.34, 0.34), rose);
        boxP.rotation.y = ry;
        boxP.position.set(px + ox * 1.1, railY + 0.22, pz + oz * 1.1);
        boxP.castShadow = true;
        bg.add(boxP);
        const fcol = ["#e6584d", "#f2c14e", "#c879e6", "#ffffff"];
        let fk = 0;
        for (const su of [-1.0, -0.34, 0.34, 1.0]) {
            // Flores/bagas: icosaedro de 20 faces no lugar da esfera 8x6 (96 faces). Sao
            // peças de 1,5 a 4,6 px na projeção real; o padrão já existe neste arquivo.
            const fl = new THREE.Mesh(new THREE.IcosahedronGeometry(0.17, 0), accentMat(fcol[fk++ % 4]));
            fl.rotation.y = ry;
            fl.position.set(px + ox * 1.1 + tx * su, railY + 0.5, pz + oz * 1.1 + tz * su);
            bg.add(fl);
        }
        // TOLDO rosé (igual ao da entrada: canopy inclinado + babado de meias-luas)
        // acima da porta-janela da sacada
        const awnW = 3.2,
            awnD = 1.6,
            awnY = balDoorTop - 0.1;
        const inner = new THREE.Group();
        inner.rotation.x = 0.42;
        const canopy = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.16, awnD), rose);
        canopy.position.set(0, 0, awnD / 2);
        inner.add(canopy);
        // babado PLANO (festões semicirculares rosé)
        addAwningValance(inner, awnW, awnD, rose);
        inner.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        const holder = new THREE.Group();
        holder.rotation.y = ry;
        holder.position.set(px + ox * 0.1, awnY, pz + oz * 0.1);
        holder.add(inner);
        bg.add(holder);
    };
    let dressI = 0;
    const VITH = 4.7; // vitrine alta (piso-a-teto do térreo) p/ preencher o andar
    const addVitrine = (f, u) => {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : f.nx === -1 ? -Math.PI / 2 : 0;
        const halfV = VITH / 2;
        const gp = new THREE.Group();
        const back = new THREE.Mesh(new THREE.BoxGeometry(3.4, VITH, 0.12), interiorMat);
        back.position.z = 0.06;
        gp.add(back);
        for (const [bw2, bh2, ox, oy] of [
            [3.9, 0.25, 0, halfV + 0.12],
            [3.9, 0.25, 0, -(halfV + 0.12)],
            [0.25, VITH + 0.5, -1.82, 0],
            [0.25, VITH + 0.5, 1.82, 0],
        ]) {
            const wallV = new THREE.Mesh(new THREE.BoxGeometry(bw2, bh2, 1.15), edgeMat);
            wallV.position.set(ox, oy, 0.55);
            wallV.castShadow = true;
            gp.add(wallV);
        }
        const dress = mkDressForm(DRESS[dressI++ % DRESS.length]);
        // ACHATADO em z (0.6) + recuado (z=0.3): a saia (raio 1.0) atravessava o
        // vidro (pane em z=1.05) quando o manequim cresceu p/ 0.92; visto de frente
        // (largura×altura) fica igual, só cabe na profundidade rasa da vitrine
        dress.scale.set(0.92, 0.92, 0.6);
        dress.position.set(0, -halfV + 0.15, 0.3);
        gp.add(dress);
        const pane = new THREE.Mesh(new THREE.BoxGeometry(3.35, VITH - 0.1, 0.06), glassPane);
        pane.position.z = 1.05;
        gp.add(pane);
        gp.rotation.y = ry;
        gp.position.set(f.nx * faceDist + tx * u, 1.4 + halfV, f.nz * faceDist + tz * u);
        bg.add(gp);
    };
    // fundo/laterais: térreo = 2 vitrines; 2º piso = 3 sacadas (centro + laterais)
    for (const f of [
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        addVitrine(f, -5.8);
        addVitrine(f, 5.8);
        addBalcony(f, -5.8);
        addBalcony(f, 0);
        addBalcony(f, 5.8);
    }
    // frente: 2 vitrines no térreo; 2 sacadas laterais (o centro fica p/ o letreiro)
    const FRONT = { nx: 0, nz: 1 };
    addVitrine(FRONT, -5.8);
    addVitrine(FRONT, 5.8);
    addBalcony(FRONT, -5.8);
    addBalcony(FRONT, 5.8);

    // ---- ENTRADA (repetida nas 4 faces): porta de vidro + toldo ROSÉ + degrau ----
    const doorW = 4.6;
    const doorH = 4.4;
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const fd = f.nz !== 0 ? hd : hw;
        const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        const px = (d) => f.nx * (fd + d);
        const pz = (d) => f.nz * (fd + d);
        const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
        door.rotation.y = ry;
        door.position.set(px(0.12), doorH / 2 + 0.5, pz(0.12));
        bg.add(door);
        const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
        dmull.rotation.y = ry;
        dmull.position.set(px(0.2), doorH / 2 + 0.5, pz(0.2));
        bg.add(dmull);
        const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
        step.rotation.y = ry;
        step.position.set(px(0.7), 0.22, pz(0.7));
        step.castShadow = true;
        bg.add(step);
        // toldo rosé com babado (grupo inclinado dentro de um suporte girado p/ a face)
        const awnW = doorW + 1.4,
            awnD = 2.0,
            awnY = doorH + 1.05;
        const inner = new THREE.Group();
        inner.rotation.x = 0.42;
        const canopy = new THREE.Mesh(new THREE.BoxGeometry(awnW, 0.16, awnD), rose);
        canopy.position.set(0, 0, awnD / 2);
        inner.add(canopy);
        // babado PLANO (festões semicirculares rosé)
        addAwningValance(inner, awnW, awnD, rose);
        inner.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        const holder = new THREE.Group();
        holder.rotation.y = ry;
        holder.position.set(px(0.1), awnY, pz(0.1));
        holder.add(inner);
        bg.add(holder);
    }

    // ---- LETREIRO "BOUTIQUE" (centro da frente, entre as sacadas) ----
    const signTex = makeSignTexture(t("BOUTIQUE"), { bg: "#0e3a22", fg: "#ffd27a", border: "#e88aa6", fontPx: 56 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#ffd27a"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH * 4.5, signH, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, balDoorCY, hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ---- TELHADO: TERRAÇO DE BOUTIQUE — caramanchão (pérgola) creme c/ rosas
    //      trepadeiras + banco de jardim; canteiros de flores nas 4 bordas ----
    const roofY = b.h + 0.5;
    {
        // PÉRGOLA / CARAMANCHÃO central: frame perimetral + treliça CRUZADA + rosas em cachos
        const g = new THREE.Group();
        const S = 3.0; // meio-vão da pérgola
        const postH = 3.4;
        const petals = ["#e6584d", "#f2c14e", "#e88aa6", "#c879e6", "#ffffff"];
        for (const [px, pz] of [
            [-S, -S],
            [S, -S],
            [-S, S],
            [S, S],
        ]) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.4, postH, 0.4), cream);
            post.position.set(px, postH / 2, pz);
            g.add(post);
        }
        const beamY = postH + 0.1;
        // frame perimetral (4 vigas formando o quadrado do topo)
        for (const horiz of [true, false])
            for (const off of [-S, S]) {
                const beam = new THREE.Mesh(new THREE.BoxGeometry(horiz ? S * 2 + 0.8 : 0.3, 0.4, horiz ? 0.3 : S * 2 + 0.8), cream);
                beam.position.set(horiz ? 0 : off, beamY, horiz ? off : 0);
                g.add(beam);
            }
        // treliça CRUZADA (caibros nos dois eixos)
        const nR = 5;
        for (let i = 0; i <= nR; i++) {
            const t = -S + (i / nR) * (S * 2);
            const rx = new THREE.Mesh(new THREE.BoxGeometry(S * 2 + 0.9, 0.16, 0.14), cream);
            rx.position.set(0, beamY + 0.28, t);
            g.add(rx);
            const rz = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.16, S * 2 + 0.9), cream);
            rz.position.set(t, beamY + 0.28, 0);
            g.add(rz);
        }
        // ROSAS TREPADEIRAS em CACHOS (folhagem + 3 flores) nos cantos e meios das vigas
        const clusters = [
            [-S, -S],
            [S, -S],
            [-S, S],
            [S, S],
            [0, -S],
            [0, S],
            [-S, 0],
            [S, 0],
        ];
        let rp = 0;
        for (const [cx, cz] of clusters) {
            const leaves = new THREE.Mesh(new THREE.SphereGeometry(0.5, 8, 6), accentMat("#3e9e4e"));
            leaves.scale.set(1, 0.7, 1);
            leaves.position.set(cx, beamY + 0.5, cz);
            g.add(leaves);
            for (const [dx, dy, dz] of [
                [0.22, 0.32, 0.02],
                [-0.2, 0.28, 0.18],
                [0.02, 0.46, -0.2],
            ]) {
                const flower = new THREE.Mesh(new THREE.IcosahedronGeometry(0.19, 0), accentMat(petals[rp++ % petals.length]));
                flower.position.set(cx + dx, beamY + 0.5 + dy, cz + dz);
                g.add(flower);
            }
        }
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        g.position.set(0, roofY, 0);
        bg.add(g);
        // BANCO DE JARDIM sob a pérgola
        const bench = new THREE.Group();
        const seat = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.18, 0.9), cream);
        seat.position.y = 0.85;
        bench.add(seat);
        const backrest = new THREE.Mesh(new THREE.BoxGeometry(3.0, 1.0, 0.16), cream);
        backrest.position.set(0, 1.35, -0.37);
        bench.add(backrest);
        for (const lx of [-1.3, 1.3])
            for (const lz of [-0.33, 0.33]) {
                const leg = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.85, 0.14), edgeMat);
                leg.position.set(lx, 0.42, lz);
                bench.add(leg);
            }
        bench.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        bench.position.set(0, roofY, 0.5);
        bg.add(bench);
    }
    {
        // CANTEIROS DE FLORES ao redor das 4 bordas do teto
        const petals = ["#e6584d", "#f2c14e", "#e88aa6", "#c879e6", "#5b8def", "#ffffff"];
        let fi = 0;
        const addBed = (cx, cz, len, horiz) => {
            const planter = new THREE.Mesh(new THREE.BoxGeometry(horiz ? len : 1.0, 0.6, horiz ? 1.0 : len), cream);
            planter.position.set(cx, roofY + 0.3, cz);
            planter.castShadow = true;
            bg.add(planter);
            const rim = new THREE.Mesh(new THREE.BoxGeometry(horiz ? len + 0.1 : 1.12, 0.16, horiz ? 1.12 : len + 0.1), edgeMat);
            rim.position.set(cx, roofY + 0.64, cz);
            bg.add(rim);
            // HEDGE contínua (base verde tosada) dentro da jardineira
            const hedge = new THREE.Mesh(new THREE.BoxGeometry(horiz ? len - 0.3 : 0.7, 0.44, horiz ? 0.7 : len - 0.3), accentMat("#3e9e4e"));
            hedge.position.set(cx, roofY + 0.8, cz);
            hedge.castShadow = true;
            bg.add(hedge);
            // flores em HASTES, uniformemente espaçadas, saindo da hedge
            const n = Math.max(3, Math.round(len / 1.3));
            for (let i = 0; i < n; i++) {
                const t = -len / 2 + (i + 0.5) * (len / n);
                const fx = horiz ? cx + t : cx;
                const fz = horiz ? cz : cz + t;
                const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.5, 6), accentMat("#2f7d3e"));
                stem.position.set(fx, roofY + 1.15, fz);
                bg.add(stem);
                const flower = new THREE.Mesh(new THREE.IcosahedronGeometry(0.26, 0), accentMat(petals[fi++ % petals.length]));
                flower.position.set(fx, roofY + 1.46, fz);
                flower.castShadow = true;
                bg.add(flower);
            }
        };
        const E = 7.2,
            L = 13.0;
        addBed(0, -E, L, true);
        addBed(0, E, L, true);
        addBed(-E, 0, L, false);
        addBed(E, 0, L, false);
    }
}

/**
 * COWORKING (benchmark = café/fintech/boutique novos): hub de trabalho
 * colaborativo/startup — identidade em marcos:
 *   • LÂMPADA GIGANTE ACESA no TELHADO (ícone de IDEIA/inovação; brilha à noite
 *     como os elementos quentes do café) = herói;
 *   • TERRAÇO-LOUNGE ao redor: sofá + mesa de centro + MESA DE TRABALHO
 *     comunitária (laptops de tela acesa + banquetas) + plantas + varais de luz;
 *   • FACHADA de escritório envidraçado (multi-andar) com VITRINES-LOUNGE no
 *     térreo da frente (sofá + planta vistos da rua) e letreiro "COWORKING".
 */
function buildCoworking(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const teal = accentMat("#22b8a0");
    const coral = accentMat("#ff7a5c");
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const wood = accentMat("#b5844e");
    const leafMat = accentMat("#3e9e4e");
    const metal = new THREE.MeshStandardMaterial({ color: "#c9ccd2", roughness: 0.5, metalness: 0.2 });
    const glassPane = new THREE.MeshStandardMaterial({
        color: "#bfe4d8",
        transparent: true,
        opacity: 0.22,
        roughness: 0.15,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#fff3d8",
        emissive: new THREE.Color("#f5e9bf"),
        emissiveIntensity: 0.45,
        roughness: 0.8,
    });
    const bulbGlow = new THREE.MeshStandardMaterial({
        color: "#fff1b8",
        emissive: new THREE.Color("#ffd85a"),
        emissiveIntensity: 0.7,
        transparent: true,
        opacity: 0.6,
        roughness: 0.2,
    });
    const filamentMat = new THREE.MeshStandardMaterial({
        color: "#fff6c8",
        emissive: new THREE.Color("#ffcf3a"),
        emissiveIntensity: 1.0,
        roughness: 0.4,
    });
    const litBulb = new THREE.MeshStandardMaterial({
        color: "#fff8e0",
        emissive: new THREE.Color("#ffe89a"),
        emissiveIntensity: 1.6,
        roughness: 0.45,
    });

    // conecta 2 pontos [x,y,z] com um cilindro (qualquer orientação) — filamento
    const connect = (parent, p1, p2, r, mat) => {
        const v = new THREE.Vector3(p2[0] - p1[0], p2[1] - p1[1], (p2[2] || 0) - (p1[2] || 0));
        const len = v.length();
        const seg = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 6), mat);
        seg.position.set((p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, ((p1[2] || 0) + (p2[2] || 0)) / 2);
        seg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
        parent.add(seg);
    };

    // ---- corpo + base + cornija + laje ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = b.h - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = b.h + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);
    const roofY = b.h + 0.5;

    // ================= SUB-BUILDERS (reuso teto + vitrines) =================
    const mkSofa = (mat, w = 3.0) => {
        const s = new THREE.Group();
        const seat = new THREE.Mesh(new THREE.BoxGeometry(w, 0.5, 1.4), mat);
        seat.position.y = 0.78;
        s.add(seat);
        const back = new THREE.Mesh(new THREE.BoxGeometry(w, 1.05, 0.35), mat);
        back.position.set(0, 1.28, -0.52);
        s.add(back);
        for (const ax of [-1, 1]) {
            const arm = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.85, 1.4), mat);
            arm.position.set(ax * (w / 2 - 0.17), 1.05, 0);
            s.add(arm);
        }
        const nCush = Math.max(2, Math.round(w / 1.5));
        for (let i = 0; i < nCush; i++) {
            const cush = new THREE.Mesh(new THREE.BoxGeometry(w / nCush - 0.18, 0.32, 1.05), i % 2 ? cream : gold);
            cush.position.set(-w / 2 + (i + 0.5) * (w / nCush), 1.08, 0.06);
            s.add(cush);
        }
        for (const lx of [-1, 1])
            for (const lz of [-1, 1]) {
                const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.5, 6), wood);
                leg.position.set(lx * (w / 2 - 0.35), 0.25, lz * 0.55);
                s.add(leg);
            }
        s.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return s;
    };
    const mkPlant = () => {
        const p = new THREE.Group();
        const pot = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.4, 0.85, 12), coral);
        pot.position.y = 0.42;
        p.add(pot);
        for (const [rx, ry, rz, rr] of [
            [0, 1.35, 0, 0.85],
            [0.28, 1.9, 0.12, 0.6],
            [-0.24, 1.85, -0.16, 0.55],
        ]) {
            const fol = new THREE.Mesh(new THREE.SphereGeometry(rr, 8, 6), leafMat);
            fol.scale.set(1, 1.15, 1);
            fol.position.set(rx, ry, rz);
            p.add(fol);
        }
        p.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return p;
    };
    const mkCoffeeTable = () => {
        const g = new THREE.Group();
        const top = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 0.95, 0.16, 16), wood);
        top.position.y = 0.82;
        g.add(top);
        const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.82, 8), metal);
        leg.position.y = 0.41;
        g.add(leg);
        const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.08, 12), metal);
        foot.position.y = 0.05;
        g.add(foot);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return g;
    };
    const mkWorkTable = () => {
        // mesa de trabalho COMUNITÁRIA (essência do coworking)
        const g = new THREE.Group();
        const top = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.16, 1.8), cream);
        top.position.y = 1.55;
        g.add(top);
        for (const lx of [-1, 1])
            for (const lz of [-1, 1]) {
                const leg = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.55, 0.14), metal);
                leg.position.set(lx * 1.85, 0.78, lz * 0.72);
                g.add(leg);
            }
        for (const sx of [-1.1, 1.1])
            for (const sz of [-1.55, 1.55]) {
                // 4 banquetas
                const seat = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.14, 12), coral);
                seat.position.set(sx, 1.02, sz);
                g.add(seat);
                const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.0, 8), metal);
                post.position.set(sx, 0.5, sz);
                g.add(post);
                const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.07, 12), metal);
                foot.position.set(sx, 0.04, sz);
                g.add(foot);
            }
        // 2 laptops abertos c/ tela ACESA (viram p/ lados opostos = mesa compartilhada)
        const screenMat = new THREE.MeshStandardMaterial({
            color: "#bfeff0",
            emissive: new THREE.Color("#4fd6e0"),
            emissiveIntensity: 0.75,
            roughness: 0.4,
        });
        for (const [lx, lz, ry] of [
            [-1.0, 0.4, 0],
            [1.0, -0.4, Math.PI],
        ]) {
            const lap = new THREE.Group();
            const kb = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.07, 0.72), metal);
            kb.position.y = 0.035;
            lap.add(kb);
            const scr = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.72, 0.05), screenMat);
            scr.position.set(0, 0.42, -0.36);
            scr.rotation.x = -0.32; // dobradiça atrás; tela inclina p/ trás
            lap.add(scr);
            lap.position.set(lx, 1.63, lz);
            lap.rotation.y = ry;
            lap.traverse((o) => {
                if (o.isMesh) o.castShadow = true;
            });
            g.add(lap);
        }
        const mug = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.13, 0.28, 10), gold); // caneca
        mug.position.set(0.1, 1.77, 0.5);
        g.add(mug);
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return g;
    };
    const mkLightbulb = () => {
        const g = new THREE.Group();
        const envelope = new THREE.Mesh(new THREE.SphereGeometry(2.0, 20, 16), bulbGlow);
        envelope.scale.set(1, 1.12, 1);
        envelope.position.y = 3.7;
        g.add(envelope);
        const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.55, 1.3, 18), bulbGlow);
        neck.position.y = 1.95;
        g.add(neck);
        const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 1.3, 18), metal);
        cap.position.y = 1.05;
        g.add(cap);
        for (let i = 0; i < 3; i++) {
            const ring = new THREE.Mesh(new THREE.TorusGeometry(0.92, 0.1, 6, 18), metal);
            ring.rotation.x = Math.PI / 2;
            ring.position.y = 0.72 + i * 0.34;
            g.add(ring);
        }
        const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.2, 0.34, 12), accentMat("#5a5f66"));
        tip.position.y = 0.28;
        g.add(tip);
        for (const fx of [-0.5, 0.5]) {
            // filamento: postes + zigue-zague aceso
            const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.5, 6), filamentMat);
            post.position.set(fx, 3.15, 0);
            g.add(post);
        }
        const zz = [
            [-0.5, 3.9],
            [-0.17, 3.5],
            [0.17, 3.9],
            [0.5, 3.5],
        ];
        for (let i = 0; i < zz.length - 1; i++) {
            connect(g, [zz[i][0], zz[i][1], 0], [zz[i + 1][0], zz[i + 1][1], 0], 0.05, filamentMat);
        }
        g.traverse((o) => {
            if (o.isMesh) o.castShadow = true;
        });
        return g;
    };

    // ================= FACHADA (escritório envidraçado) =================
    const grid = facadeGrid(b.w, b.h, 4); // até 4 andares de janelas de vidro
    const signRow = Math.min(1, grid.rows.length - 1);
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        const ry = f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        const isFront = f.nz === 1;
        grid.rows.forEach((y, ri) => {
            // frente: térreo = entrada/vitrines; fileira do letreiro = "COWORKING"
            if (isFront && (ri === 0 || ri === signRow)) return;
            for (const u of grid.cols) {
                const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, y, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        });
    }

    // ---- MOLDURAS horizontais (string courses) nas divisas de piso: dão ritmo
    //      de andares que CONTORNA a esquina (senão a frente lê como bloco baixo
    //      de 2 andares e as laterais como torre de 4) ----
    if (grid.rows.length >= 2) {
        const floorH = grid.rows[1] - grid.rows[0];
        for (let ri = 1; ri < grid.rows.length; ri++) {
            const band = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 0.45, b.d + 0.5), edgeMat);
            band.position.y = grid.rows[ri] - floorH / 2;
            band.castShadow = true;
            bg.add(band);
        }
    }

    // ---- VITRINES-LOUNGE no térreo da frente (sofá + planta vistos da rua) ----
    const addLoungeVitrine = (u, sofaMat) => {
        const gp = new THREE.Group();
        const back = new THREE.Mesh(new THREE.BoxGeometry(4.2, 4.2, 0.12), interiorMat);
        back.position.z = 0.06;
        gp.add(back);
        for (const [bw2, bh2, ox, oy] of [
            [4.7, 0.25, 0, 2.22],
            [4.7, 0.25, 0, -2.22],
            [0.25, 4.7, -2.22, 0],
            [0.25, 4.7, 2.22, 0],
        ]) {
            const wallV = new THREE.Mesh(new THREE.BoxGeometry(bw2, bh2, 1.15), edgeMat);
            wallV.position.set(ox, oy, 0.55);
            wallV.castShadow = true;
            gp.add(wallV);
        }
        const sofa = mkSofa(sofaMat, 2.7);
        sofa.scale.setScalar(0.64);
        sofa.position.set(-0.55, -2.0, 0.35);
        gp.add(sofa);
        const plant = mkPlant();
        plant.scale.setScalar(0.6);
        plant.position.set(1.45, -2.05, 0.35);
        gp.add(plant);
        const pane = new THREE.Mesh(new THREE.BoxGeometry(4.15, 4.15, 0.06), glassPane);
        pane.position.z = 1.05;
        gp.add(pane);
        gp.position.set(u, 3.1, hd + 0.02);
        bg.add(gp);
    };
    addLoungeVitrine(-6.0, teal);
    addLoungeVitrine(6.0, teal); // par de vitrines coerente na paleta (era coral/vermelho)

    // ---- ENTRADA de vidro (frente) + marquise teal + degrau ----
    const doorW = 5.0;
    const doorH = 4.4;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.4), warmGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.12);
    bg.add(door);
    for (const mx of [-doorW / 3, 0, doorW / 3]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.16, doorH, 0.5), edgeMat);
        mull.position.set(mx, doorH / 2 + 0.5, hd + 0.2);
        bg.add(mull);
    }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 2.0, 0.45, 1.6), trim);
    step.position.set(0, 0.22, hd + 0.8);
    step.castShadow = true;
    bg.add(step);
    const marquee = new THREE.Mesh(new THREE.BoxGeometry(doorW + 2.4, 0.4, 2.2), teal);
    marquee.position.set(0, doorH + 1.1, hd + 1.0);
    marquee.castShadow = true;
    bg.add(marquee);

    // ---- LETREIRO "COWORKING" (fileira do meio, sem janelas ali na frente) ----
    const signTex = makeSignTexture(t("COWORKING"), { bg: "#0e3a22", fg: "#ffffff", border: "#22b8a0", fontPx: 50 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex,
        emissiveMap: signTex,
        emissive: new THREE.Color("#bff3e6"),
        emissiveIntensity: 0.5,
        roughness: 0.7,
    });
    const signH2 = 3.0; // placa MAIOR (usa o espaço das laterais); ratio 4.5:1 = canvas (texto nítido)
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH2 * 4.5, signH2, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ================= TELHADO: LÂMPADA (herói) + LOUNGE =================
    const bulb = mkLightbulb();
    bulb.scale.setScalar(1.15);
    bulb.position.set(0, roofY, -2.6);
    bg.add(bulb);

    const sofa = mkSofa(teal, 3.4);
    sofa.position.set(-3.6, roofY, 2.6);
    bg.add(sofa);
    const ctable = mkCoffeeTable();
    ctable.position.set(-3.6, roofY, 4.6);
    bg.add(ctable);

    const worktable = mkWorkTable();
    worktable.position.set(4.4, roofY, 2.6); // alinhada ao sofá (mesmo z), à direita dele
    bg.add(worktable);

    for (const [px, pz] of [
        [-7.2, -3.2],
        [7.0, 5.2],
    ]) {
        const plant = mkPlant();
        plant.scale.setScalar(1.15);
        plant.position.set(px, roofY, pz);
        bg.add(plant);
    }

    // ---- VARAIS DE LUZES (bistrô): fio ligando lâmpadas ACESAS entre 4 postes ----
    const wireMat = accentMat("#2f333a");
    const poles = [
        [-7.5, -7.5],
        [7.5, -7.5],
        [7.5, 7.5],
        [-7.5, 7.5],
    ];
    for (const [px, pz] of poles) {
        const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 4.8, 8), metal);
        pole.position.set(px, roofY + 2.4, pz);
        pole.castShadow = true;
        bg.add(pole);
    }
    const stringTop = roofY + 4.6;
    for (let i = 0; i < poles.length; i++) {
        const a = poles[i];
        const c = poles[(i + 1) % poles.length];
        const n = 8;
        const pt = (k) => {
            const t = k / n;
            const sag = Math.sin(t * Math.PI) * 0.75;
            return [a[0] + (c[0] - a[0]) * t, stringTop - sag, a[1] + (c[1] - a[1]) * t];
        };
        for (let k = 0; k < n; k++) connect(bg, pt(k), pt(k + 1), 0.03, wireMat); // fio do varal
        for (let k = 1; k < n; k++) {
            // lâmpadas penduradas (maiores e bem acesas)
            const p = pt(k);
            const lb = new THREE.Mesh(new THREE.IcosahedronGeometry(0.21, 0), litBulb);
            lb.position.set(p[0], p[1] - 0.16, p[2]);
            bg.add(lb);
        }
    }
}

/**
 * AUTOMÓVEIS (benchmark = café/fintech/boutique/coworking novos): concessionária/
 * showroom de carros — identidade em marcos:
 *   • PORSCHE VERDE sobre PALCO GIRATÓRIO no TELHADO (o display clássico de
 *     concessionária) = herói, com uplights e BANDEIRINHAS no perímetro;
 *   • SHOWROOM envidraçado no térreo da frente: 2 PORSCHES (vermelho/azul) em
 *     EXPOSIÇÃO (perfil p/ a rua) sobre plinto, atrás do vidro; entrada de vidro + marquise;
 *   • letreiro "AUTOMÓVEIS" (acento azul). Os 3 carros reusam o modelo real do
 *     jogo (`mkPorsche`/`porscheParts` sobre `buildCarParts` de carModel.js).
 */
/**
 * Monta o modelo REAL do Porsche (peças procedurais de carModel.js — as MESMAS do
 * jogo/Fleet) como um Group estático, pintado na cor dada. As peças `color:'paint'`
 * recebem a cor cheia; `color:'paintDark'` um tom escuro dela; o resto (vidro, preto,
 * cromado, pneu, faróis, pinça amarela) mantém os materiais originais.
 */
/** Peças SÓ do Porsche (roda `buildCarParts()` e filtra). Construa UMA vez e reuse
 *  entre os carros do prédio (geometria compartilhada; só a pintura muda por carro). */
function porscheParts() {
    return buildCarParts().filter((d) => d.option === "porsche");
}
function mkPorsche(bodyHex, parts) {
    const g = new THREE.Group();
    const body = new THREE.MeshStandardMaterial({ color: bodyHex, roughness: 0.42, metalness: 0.15 });
    const bodyDark = new THREE.MeshStandardMaterial({ color: new THREE.Color(bodyHex).multiplyScalar(0.45), roughness: 0.5 });
    for (const d of parts || porscheParts()) {
        const mat = d.color === "paint" ? body : d.color === "paintDark" ? bodyDark : d.mat;
        const mesh = new THREE.Mesh(d.geo, mat);
        mesh.position.set(d.pos[0], d.pos[1], d.pos[2]);
        if (d.rot) mesh.rotation.set(d.rot[0] || 0, d.rot[1] || 0, d.rot[2] || 0);
        mesh.castShadow = true;
        g.add(mesh);
    }
    return g;
}

function buildAutos(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffc24e",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    const brand = accentMat("#3a7bd5"); // acento azul da concessionária
    const gold = new THREE.MeshStandardMaterial({
        color: "#ffd27a",
        emissive: new THREE.Color("#ffcf5a"),
        emissiveIntensity: 0.3,
        roughness: 0.5,
    });
    const cream = new THREE.MeshStandardMaterial({ color: "#f4efe0", roughness: 0.85 });
    const metal = new THREE.MeshStandardMaterial({ color: "#c9ccd2", roughness: 0.5, metalness: 0.25 });
    const litBulb = new THREE.MeshStandardMaterial({
        color: "#fff8e0", emissive: new THREE.Color("#ffe89a"), emissiveIntensity: 1.5, roughness: 0.45,
    });
    const glassPane = new THREE.MeshStandardMaterial({
        color: "#bfe4d8", transparent: true, opacity: 0.22, roughness: 0.15,
    });
    const interiorMat = new THREE.MeshStandardMaterial({
        color: "#fff3d8", emissive: new THREE.Color("#f5e9bf"), emissiveIntensity: 0.45, roughness: 0.8,
    });
    // ALTURA de PROJETO da concessionária = 2 pavimentos FIXOS (térreo/showroom + 2º piso).
    // Desacoplada de b.h e limitada a 11: como a moldura (6.6) e as janelas do 2º piso (8.5)
    // são fixas, um b.h maior (ex.: fallback 16 do /models) só criava uma parede verde vazia
    // acima das janelas (o "3º pavimento" que o usuário pediu p/ remover). Aqui o teto fica
    // sempre logo acima das janelas do 2º piso, independente do b.h passado pelo lote/viewer.
    const H = Math.min(b.h, 11);

    // peças do Porsche construídas UMA vez → geometria compartilhada pelos 3 carros
    // (2 vitrines + herói do teto); só a cor da pintura muda por carro.
    const pParts = porscheParts();

    // ---- corpo + base + cornija + laje (altura = H de projeto, não b.h) ----
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), dark);
    base.position.y = 0.7;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 1.0, b.d + 1.0), edgeMat);
    cornice.position.y = H - 0.4;
    cornice.castShadow = true;
    bg.add(cornice);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25;
    roofSlab.receiveShadow = true;
    bg.add(roofSlab);
    // moldura no topo do térreo (separa o showroom do 2º pavimento)
    const band = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 0.55, b.d + 0.5), edgeMat);
    band.position.y = 6.6;
    band.castShadow = true;
    bg.add(band);

    // ---- FACHADA: 2º pavimento = janelas (todas as faces); frente térreo = SHOWROOM ----
    const grid = facadeGrid(b.w, H, 2);
    const signRow = Math.min(1, grid.rows.length - 1);
    // 2º piso em ALTURA FIXA (acima da moldura em 6.6) — desacoplado de b.h para que
    // o teto fique LOGO acima das janelas (sem muro verde vazio = "3º pavimento").
    if (grid.rows[signRow] != null) grid.rows[signRow] = 8.5;
    grid.winH = 2.7;
    for (const f of [
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const tx = f.nz;
        const tz = f.nx;
        const faceDist = f.nz !== 0 ? hd : hw;
        const ry = f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2;
        // térreo (fundo/laterais): janelas altas de "oficina/serviço"
        for (const u of grid.cols) {
            const win = makeClassWindow(grid.winW, 3.4, warmGlass, edgeMat);
            win.rotation.y = ry;
            win.position.set(f.nx * (faceDist + 0.12) + tx * u, 3.6, f.nz * (faceDist + 0.12) + tz * u);
            bg.add(win);
            const win2 = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
            win2.rotation.y = ry;
            win2.position.set(f.nx * (faceDist + 0.12) + tx * u, grid.rows[signRow], f.nz * (faceDist + 0.12) + tz * u);
            bg.add(win2);
        }
    }
    // frente 2º piso: 2 janelas de cada lado do letreiro (evita muro verde vazio)
    for (const u of [-7.5, -5.4, 5.4, 7.5]) {
        const win = makeClassWindow(grid.winW, grid.winH, warmGlass, edgeMat);
        win.position.set(u, grid.rows[signRow], hd + 0.12);
        bg.add(win);
    }

    // ---- SHOWROOM (térreo frontal): 2 caixas ENVIDRAÇADAS (frente+laterais) c/
    //      carro sobre PÓDIO em exposição; vidro levemente tingido (visível) ----
    const showGlass = new THREE.MeshStandardMaterial({ color: "#aee0ff", transparent: true, opacity: 0.28, roughness: 0.12 });
    const addShowroom = (u, paint) => {
        const gp = new THREE.Group();
        const SW = 6.6,
            SH = 3.8,
            SD = 2.8; // caixa mais funda → o carro (girado) fica INTEIRO atrás do vidro
        const backW = new THREE.Mesh(new THREE.BoxGeometry(SW, SH, 0.12), interiorMat);
        backW.position.z = 0.06;
        gp.add(backW);
        for (const [bw, bh, ox, oy] of [
            [SW + 0.5, 0.3, 0, SH / 2 + 0.12],
            [SW + 0.5, 0.3, 0, -(SH / 2 + 0.12)],
            [0.3, SH + 0.7, -(SW / 2 + 0.14), 0],
            [0.3, SH + 0.7, SW / 2 + 0.14, 0],
        ]) {
            const w = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, SD), edgeMat);
            w.position.set(ox, oy, SD / 2);
            w.castShadow = true;
            gp.add(w);
        }
        const floor = new THREE.Mesh(new THREE.BoxGeometry(SW, 0.14, SD), cream);
        floor.position.set(0, -SH / 2 + 0.07, SD / 2);
        gp.add(floor);
        const podium = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.8, 2.5), brand); // plinto (eleva o Porsche baixo)
        podium.position.set(0, -SH / 2 + 0.54, SD / 2);
        gp.add(podium);
        // PORSCHE em exposição: frente é +Z → giro de ~90° deixa o eixo LONGO ao longo da
        // vitrine (perfil), com o nariz levemente p/ o observador; escala p/ caber INTEIRO
        // atrás do vidro (a profundidade SD=2.8 é o gargalo).
        const car = mkPorsche(paint, pParts);
        car.scale.setScalar(0.9);
        car.position.set(0, -SH / 2 + 0.94, SD / 2);
        car.rotation.y = Math.PI / 2 - 0.14;
        gp.add(car);
        const pane = new THREE.Mesh(new THREE.BoxGeometry(SW, SH, 0.06), showGlass); // frente
        pane.position.z = SD;
        gp.add(pane);
        for (const sx of [-1, 1]) {
            const side = new THREE.Mesh(new THREE.BoxGeometry(0.06, SH, SD), showGlass); // laterais (fecha a caixa)
            side.position.set((sx * SW) / 2, 0, SD / 2);
            gp.add(side);
        }
        gp.position.set(u, 1.4 + SH / 2, hd + 0.02);
        bg.add(gp);
    };
    addShowroom(-5.5, "#d8352a"); // Porsche VERMELHO
    addShowroom(5.5, "#2f6fd6"); // Porsche AZUL

    // ---- ENTRADA: porta de VIDRO ESCURO (lê como porta, não janela) + LOBBY
    //      aceso atrás + marquise azul (estreita, sem invadir os showrooms) + degrau ----
    const doorW = 4.0,
        doorH = 4.4;
    const entryGlass = new THREE.MeshStandardMaterial({ color: "#2a3a42", transparent: true, opacity: 0.62, roughness: 0.18 });
    const lobby = new THREE.Mesh(new THREE.BoxGeometry(doorW - 0.3, doorH - 0.3, 0.1), interiorMat);
    lobby.position.set(0, doorH / 2 + 0.5, hd + 0.02);
    bg.add(lobby);
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.16), entryGlass);
    door.position.set(0, doorH / 2 + 0.5, hd + 0.16);
    bg.add(door);
    for (const mx of [-doorW / 2, 0, doorW / 2]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.14, doorH, 0.24), edgeMat);
        mull.position.set(mx, doorH / 2 + 0.5, hd + 0.22);
        bg.add(mull);
    }
    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.6, 0.45, 1.4), trim);
    step.position.set(0, 0.22, hd + 0.75);
    step.castShadow = true;
    bg.add(step);
    const marquee = new THREE.Mesh(new THREE.BoxGeometry(doorW + 0.2, 0.4, 1.8), brand);
    marquee.position.set(0, doorH + 1.0, hd + 0.85);
    marquee.castShadow = true;
    bg.add(marquee);

    // ---- LETREIRO "AUTOMÓVEIS" (fileira do meio, centro da frente) ----
    const signTex = makeSignTexture(t("AUTOMÓVEIS"), { bg: "#0e3a22", fg: "#ffffff", border: "#3a7bd5", fontPx: 48 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#bcd6ff"), emissiveIntensity: 0.5, roughness: 0.7,
    });
    const signH2 = 1.9;
    const sign = new THREE.Mesh(new THREE.BoxGeometry(signH2 * 4.2, signH2, 0.25), [edgeMat, edgeMat, edgeMat, edgeMat, signMat, edgeMat]);
    sign.position.set(0, grid.rows[signRow], hd + 0.16);
    sign.castShadow = true;
    bg.add(sign);

    // ================= TELHADO: PALCO GIRATÓRIO + carro herói =================
    const roofY = H + 0.5;
    {
        const R = Math.min(b.w, b.d) * 0.36; // deck menor → o carro preenche melhor
        const baseT = new THREE.Mesh(new THREE.CylinderGeometry(R + 0.4, R + 0.6, 0.4, 32), edgeMat);
        baseT.position.set(0, roofY + 0.2, 0);
        baseT.castShadow = true;
        bg.add(baseT);
        const deck = new THREE.Mesh(new THREE.CylinderGeometry(R, R, 0.35, 32), cream);
        deck.position.set(0, roofY + 0.55, 0);
        deck.receiveShadow = true;
        bg.add(deck);
        const ring = new THREE.Mesh(new THREE.TorusGeometry(R - 0.1, 0.15, 8, 44), brand);
        ring.rotation.x = Math.PI / 2;
        ring.position.set(0, roofY + 0.74, 0);
        bg.add(ring);
        // HERÓI: Porsche VERDE numa RAMPA de exposição (nariz p/ cima) → a frente/perfil do
        // carro ficam explícitos vistos DE CIMA (câmera aérea do jogo), não só o teto
        const carInner = mkPorsche("#2fae4a", pParts);
        carInner.scale.setScalar(1.5);
        carInner.rotation.x = -0.30; // nariz (frente +Z) p/ cima
        const ramp = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.4, 7.6), metal);
        ramp.rotation.x = -0.30;
        ramp.position.set(0, -0.05, 0);
        ramp.castShadow = true;
        const hero = new THREE.Group();
        hero.add(ramp, carInner);
        hero.rotation.y = -0.6; // 3/4 voltado à rua
        hero.position.set(0, roofY + 1.5, 0);
        bg.add(hero);
        // holofotes BAIXOS na borda do deck (uplights de vitrine — sem postes altos)
        for (let i = 0; i < 4; i++) {
            const a = (i / 4) * Math.PI * 2 + 0.5;
            const px = Math.cos(a) * (R - 0.5),
                pz = Math.sin(a) * (R - 0.5);
            const fix = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.28, 0.5, 12), metal);
            fix.position.set(px, roofY + 0.98, pz);
            fix.castShadow = true;
            bg.add(fix);
            const lens = new THREE.Mesh(new THREE.SphereGeometry(0.24, 10, 8), litBulb);
            lens.position.set(px, roofY + 1.28, pz);
            bg.add(lens);
        }
    }
    // ---- BANDEIRINHAS (pennants) no perímetro do teto, entre 4 postes ----
    {
        const flagCols = ["#e6584d", "#f2c14e", "#3a7bd5", "#2fce5e", "#c879e6"]; // branca saía no fundo claro → roxo
        const wireMat = accentMat("#2f333a");
        const corners = [
            [-hw + 1.2, -hd + 1.2],
            [hw - 1.2, -hd + 1.2],
            [hw - 1.2, hd - 1.2],
            [-hw + 1.2, hd - 1.2],
        ];
        for (const [px, pz] of corners) {
            const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 5.6, 8), metal);
            pole.position.set(px, roofY + 2.8, pz);
            pole.castShadow = true;
            bg.add(pole);
        }
        const triShape = new THREE.Shape();
        triShape.moveTo(-0.32, 0);
        triShape.lineTo(0.32, 0);
        triShape.lineTo(0, -0.62);
        triShape.closePath();
        const triGeo = new THREE.ExtrudeGeometry(triShape, { depth: 0.03, bevelEnabled: false });
        let fi = 0;
        const topY = roofY + 5.0; // acima do carro-herói (o fio não cruza a carroceria)
        for (let i = 0; i < corners.length; i++) {
            const a = corners[i];
            const c = corners[(i + 1) % corners.length];
            const n = 7;
            const dx = c[0] - a[0],
                dz = c[1] - a[1];
            const pt = (k) => {
                const t = k / n;
                const sag = Math.sin(t * Math.PI) * 0.4; // flecha menor
                return [a[0] + dx * t, topY - sag, a[1] + dz * t];
            };
            const rotY = Math.atan2(-dz, dx); // largura do flag ao longo do fio; face perpendicular
            for (let k = 0; k < n; k++) {
                connectAutos(bg, wireMat, pt(k), pt(k + 1));
                const m = pt(k + 0.5);
                const flag = new THREE.Mesh(triGeo, accentMat(flagCols[fi++ % flagCols.length]));
                flag.position.set(m[0], m[1] - 0.04, m[2]);
                flag.rotation.y = rotY;
                flag.castShadow = true;
                bg.add(flag);
            }
        }
    }
}

/** Liga 2 pontos [x,y,z] com um fino cilindro (fio de bandeirinha da concessionária). */
function connectAutos(bg, mat, p1, p2) {
    const v = new THREE.Vector3(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]);
    const len = v.length();
    if (len < 1e-4) return;
    const seg = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, len, 6), mat);
    seg.position.set((p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2, (p1[2] + p2[2]) / 2);
    seg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
    bg.add(seg);
}

/**
 * IMOBILIÁRIA (benchmark = autos/coworking/café): operação de "Real Estate".
 * Identidade em 3 marcos —
 *   • CASA-MAQUETE no TELHADO (o símbolo universal do setor) sobre um GRAMADO, com
 *     placa "À VENDA" fincada e arbustos → um "imóvel em oferta" = herói;
 *   • VITRINES de LISTAGENS no térreo da frente: quadros iluminados de imóveis
 *     (casinha ilustrada + selo À VENDA/VENDIDO) atrás do vidro, como a janela de
 *     uma imobiliária;
 *   • letreiro "IMÓVEIS" (borda dourada) + marquise azul-marinho (confiança).
 * Reusa `makeHouse`/`makeBush` (a mesma casa dos lotes residenciais) no teto.
 */
function buildRealEstate(bg, b, shared, rand) {
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffce7a", emissive: new THREE.Color(COLORS.windowOn), emissiveIntensity: 0.55, roughness: 0.4,
    });
    const accentMat = (c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.7 });
    // OURO ACETINADO (não espelhado — anti-kitsch) c/ emissivo tênue p/ o bloom noturno
    const gold = new THREE.MeshStandardMaterial({ color: "#f4b942", emissive: new THREE.Color("#b9821f"), emissiveIntensity: 0.25, roughness: 0.32, metalness: 0.85 });
    const brass = new THREE.MeshStandardMaterial({ color: "#d9a94e", roughness: 0.42, metalness: 0.7 });
    const stone = new THREE.MeshStandardMaterial({ color: "#efe4c8", roughness: 0.92 }); // pedra creme (domina)
    const velvet = new THREE.MeshStandardMaterial({ color: "#6e1f2a", roughness: 0.95 }); // veludo bordô
    // LAR ACESO dentro da chave (e teto de luz do pórtico): base ESCURA + emissivo âmbar
    // FORTE p/ ler como brilho quente (não creme lavado) mesmo sem bloom no swiftshader
    const litHomeMat = new THREE.MeshStandardMaterial({ color: "#5a3410", emissive: new THREE.Color("#ffa236"), emissiveIntensity: 2.3, roughness: 0.5 });
    const beamMat = new THREE.MeshStandardMaterial({ color: "#ffe6a6", emissive: new THREE.Color("#ffdf95"), emissiveIntensity: 0.5, transparent: true, opacity: 0.12, roughness: 0.5, side: THREE.DoubleSide, depthWrite: false });
    const uplightMat = new THREE.MeshStandardMaterial({ color: "#fff2c8", emissive: new THREE.Color("#ffe08a"), emissiveIntensity: 1.6, roughness: 0.4 });

    const H = Math.min(b.h, 14); // 3 andares clássicos (galeria + 2 pisos); cap desacoplado do b.h
    const roofY = H + 0.5;
    const groundTop = 5.4; // topo do embasamento de pedra creme
    const FACES = [
        { nx: 0, nz: 1 }, { nx: 0, nz: -1 }, { nx: 1, nz: 0 }, { nx: -1, nz: 0 },
    ];
    const faceOf = (f) => ({
        tx: f.nz, tz: f.nx,
        faceDist: f.nz !== 0 ? hd : hw,
        ry: f.nz === 1 ? 0 : f.nz === -1 ? Math.PI : f.nx === 1 ? Math.PI / 2 : -Math.PI / 2,
        isFront: f.nz === 1,
    });

    // ---------- helper: JANELA/PAINEL em ARCO (retângulo + topo semicircular) ----------
    const mkArchShape = (w, hRect) => {
        const s = new THREE.Shape();
        const a = w / 2;
        s.moveTo(-a, 0);
        s.lineTo(-a, hRect);
        s.absarc(0, hRect, a, Math.PI, 0, true);
        s.lineTo(a, 0);
        s.closePath();
        return s;
    };
    const mkArchWindow = (w, hRect, glassMat) => {
        const g = new THREE.Group();
        const fGeo = new THREE.ExtrudeGeometry(mkArchShape(w + 0.34, hRect + 0.17), { depth: 0.22, bevelEnabled: false });
        fGeo.translate(0, -0.17, 0);
        g.add(new THREE.Mesh(fGeo, gold));
        const gGeo = new THREE.ExtrudeGeometry(mkArchShape(w, hRect), { depth: 0.14, bevelEnabled: false });
        const glass = new THREE.Mesh(gGeo, glassMat);
        glass.position.z = 0.12;
        g.add(glass);
        g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        return g;
    };

    // ---------- corpo + base + CORNIJA DOURADA + laje ----------
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, H, b.d), wallMat);
    body.position.y = H / 2; body.castShadow = true; body.receiveShadow = true; bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.4, 1.4, b.d + 0.4), shared.darkMat);
    base.position.y = 0.7; bg.add(base); // base verde-escuro padrão (embasamento agora é verde, então casa)
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.0, 0.85, b.d + 1.0), gold);
    cornice.position.y = H - 0.35; cornice.castShadow = true; bg.add(cornice);
    const corniceLip = new THREE.Mesh(new THREE.BoxGeometry(b.w + 1.5, 0.3, b.d + 1.5), gold);
    corniceLip.position.y = H - 0.72; bg.add(corniceLip);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.6, 0.5, b.d + 0.6), trim);
    roofSlab.position.y = H + 0.25; roofSlab.receiveShadow = true; bg.add(roofSlab);

    // ---------- TÉRREO: embasamento de PEDRA CREME + galeria de ARCOS de vidro ----------
    const plinthB = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, groundTop, b.d + 0.3), wallMat); // embasamento VERDE padrão (pedido do usuário)
    plinthB.position.y = groundTop / 2; plinthB.castShadow = true; plinthB.receiveShadow = true; bg.add(plinthB);
    const ledge = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.7, 0.4, b.d + 0.7), gold); // faixa dourada no topo do embasamento
    ledge.position.y = groundTop; ledge.castShadow = true; bg.add(ledge);
    for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) { // pilastras douradas de canto
        const pil = new THREE.Mesh(new THREE.BoxGeometry(0.5, groundTop, 0.5), gold);
        pil.position.set(sx * (hw + 0.05), groundTop / 2, sz * (hd + 0.05));
        pil.castShadow = true; bg.add(pil);
    }

    const grid = facadeGrid(b.w, H, 3);
    // dims dos arcos dos ANDARES SUPERIORES (todas as fileiras em arco, mesmo padrão)
    const awW = grid.winW * 0.84, awH = grid.winH * 0.72, awTotal = awH + awW / 2;
    // arcos altos de vidro quente no embasamento (frente evita o vão do pórtico)
    for (const f of FACES) {
        const { tx, tz, faceDist, ry, isFront } = faceOf(f);
        for (const u of grid.cols) {
            if (isFront && Math.abs(u) < 4.6) continue; // vão da entrada/pórtico
            const arch = mkArchWindow(2.0, 2.2, warmGlass);
            arch.rotation.y = ry;
            arch.position.set(f.nx * (faceDist + 0.16) + tx * u, 1.6, f.nz * (faceDist + 0.16) + tz * u);
            bg.add(arch);
        }
    }

    // ---------- ANDARES SUPERIORES: janelas quentes + pilastras douradas + topo em arco ----------
    for (const f of FACES) {
        const { tx, tz, faceDist, ry } = faceOf(f);
        grid.rows.forEach((y, ri) => {
            if (ri === 0) return; // térreo = galeria (acima)
            for (const u of grid.cols) {
                // TODAS as fileiras superiores em ARCO (2º piso segue o padrão do 1º/3º),
                // centradas na fileira p/ não colidir com a faixa dourada entre pisos
                const aw = mkArchWindow(awW, awH, warmGlass);
                aw.rotation.y = ry;
                aw.position.set(f.nx * (faceDist + 0.12) + tx * u, y - awTotal / 2, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(aw);
            }
        });
        // pilastras douradas verticais entre as colunas de janelas (verticalidade clássica)
        const pilBot = groundTop + 0.2, pilTopY = H - 0.9;
        for (let i = 0; i < grid.cols.length - 1; i++) {
            const u = (grid.cols[i] + grid.cols[i + 1]) / 2;
            const pil = new THREE.Mesh(new THREE.BoxGeometry(0.22, pilTopY - pilBot, 0.28), gold);
            pil.rotation.y = ry;
            pil.position.set(f.nx * (faceDist + 0.06) + tx * u, (pilBot + pilTopY) / 2, f.nz * (faceDist + 0.06) + tz * u);
            bg.add(pil);
        }
    }
    // string-course DOURADO entre os 2 pisos superiores (faixa que separa os andares)
    if (grid.rows.length >= 3) {
        const sc = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.5, 0.35, b.d + 0.5), gold);
        sc.position.y = (grid.rows[1] + grid.rows[2]) / 2; sc.castShadow = true; bg.add(sc);
    }

    // ---------- ENTRADA: PÓRTICO clássico (colunas + entablamento + frontão) ----------
    const doorW = 4.4, doorH = 4.2, porZ = hd + 1.9, colH = 4.2;
    const door = new THREE.Mesh(new THREE.BoxGeometry(doorW, doorH, 0.3), warmGlass);
    door.position.set(0, doorH / 2 + 0.55, hd + 0.16); bg.add(door);
    for (const mx of [-doorW / 2, -doorW / 6, doorW / 6, doorW / 2]) {
        const mull = new THREE.Mesh(new THREE.BoxGeometry(0.13, doorH, 0.4), brass);
        mull.position.set(mx, doorH / 2 + 0.55, hd + 0.24); bg.add(mull);
    }
    for (const sx of [-1, 1]) { // colunas de pedra creme com capitel/base dourados
        const cx = sx * (doorW / 2 + 1.1);
        const col = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.44, colH, 16), stone);
        col.position.set(cx, colH / 2 + 0.45, porZ); col.castShadow = true; bg.add(col);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.32, 1.05), gold);
        cap.position.set(cx, colH + 0.45, porZ); bg.add(cap);
        const basc = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.35, 1.05), gold);
        basc.position.set(cx, 0.6, porZ); bg.add(basc);
    }
    const entabW = doorW + 3.4;
    const entab = new THREE.Mesh(new THREE.BoxGeometry(entabW, 0.72, 1.25), stone);
    entab.position.set(0, colH + 0.95, porZ); entab.castShadow = true; bg.add(entab);
    // teto de luz sob o entablamento (porte-cochère aceso)
    const luz = new THREE.Mesh(new THREE.BoxGeometry(entabW - 0.5, 0.08, 1.0), litHomeMat);
    luz.position.set(0, colH + 0.6, porZ + 0.05); bg.add(luz);
    // frontão triangular (prisma) + friso dourado
    {
        const s = new THREE.Shape();
        const fw = entabW / 2 + 0.25;
        s.moveTo(-fw, 0); s.lineTo(fw, 0); s.lineTo(0, 1.5); s.closePath();
        const fg = new THREE.ExtrudeGeometry(s, { depth: 1.3, bevelEnabled: false });
        fg.translate(0, 0, -0.65);
        const ped = new THREE.Mesh(fg, stone);
        ped.position.set(0, colH + 1.31, porZ); ped.castShadow = true; bg.add(ped);
        const cap = new THREE.Mesh(new THREE.BoxGeometry(entabW + 0.6, 0.18, 1.35), gold);
        cap.position.set(0, colH + 1.28, porZ); bg.add(cap);
        // MEDALHÃO latão "casa + chave" no tímpano (emblema-corretor discreto)
        const medal = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.14, 22), brass);
        medal.rotation.x = Math.PI / 2;
        medal.position.set(0, colH + 1.85, porZ + 0.66); bg.add(medal);
        const glyph = new THREE.Shape();
        glyph.moveTo(-0.24, -0.12); glyph.lineTo(-0.24, 0.12); glyph.lineTo(0, 0.34);
        glyph.lineTo(0.24, 0.12); glyph.lineTo(0.24, -0.12); glyph.closePath();
        const gHouse = new THREE.Mesh(new THREE.ExtrudeGeometry(glyph, { depth: 0.06, bevelEnabled: false }), gold);
        gHouse.position.set(0, colH + 1.83, porZ + 0.74); bg.add(gHouse);
        // BURACO DE FECHADURA na casa (funde casa+chave = "a chave do lar")
        const khCircle = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.06, 12), velvet);
        khCircle.rotation.x = Math.PI / 2; khCircle.position.set(0, colH + 1.85, porZ + 0.81); bg.add(khCircle);
        const khSlot = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.13, 0.06), velvet);
        khSlot.position.set(0, colH + 1.77, porZ + 0.81); bg.add(khSlot);
    }
    // LETREIRO "CHAVE DE OURO" no friso do entablamento
    const signTex = makeSignTexture("IMOBILIÁRIA", { bg: "#241a0e", fg: "#ffe6a6", border: "#f4b942", fontPx: 46 });
    const signMat = new THREE.MeshStandardMaterial({
        map: signTex, emissiveMap: signTex, emissive: new THREE.Color("#ffe9b0"), emissiveIntensity: 0.55, roughness: 0.6,
    });
    const sign = new THREE.Mesh(new THREE.BoxGeometry(entabW - 0.3, 0.95, 0.16), [gold, gold, gold, gold, signMat, gold]);
    sign.position.set(0, colH + 0.95, porZ + 0.64); sign.castShadow = true; bg.add(sign);
    // degraus de pedra + passadeira bordô + barras de latão
    const step = new THREE.Mesh(new THREE.BoxGeometry(entabW + 0.4, 0.42, 2.4), stone);
    step.position.set(0, 0.21, hd + 1.5); step.castShadow = true; bg.add(step);
    const step2 = new THREE.Mesh(new THREE.BoxGeometry(entabW + 1.6, 0.24, 3.4), stone);
    step2.position.set(0, 0.12, hd + 2.0); bg.add(step2);
    const runner = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.08, 3.6), velvet);
    runner.position.set(0, 0.44, hd + 1.9); bg.add(runner);
    for (const rz of [hd + 0.9, hd + 2.6]) {
        const barr = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 2.2, 8), brass);
        barr.rotation.z = Math.PI / 2; barr.position.set(0, 0.5, rz); bg.add(barr);
    }
    for (const sx of [-1, 1]) { // vasos dourados com topiária
        const vase = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.3, 0.8, 14), gold);
        vase.position.set(sx * (entabW / 2 + 0.4), 0.85, hd + 1.5); vase.castShadow = true; bg.add(vase);
        const topi = new THREE.Mesh(new THREE.IcosahedronGeometry(0.62, 0), accentMat("#3e8e4e"));
        topi.position.set(sx * (entabW / 2 + 0.4), 1.7, hd + 1.5); topi.castShadow = true; bg.add(topi);
    }

    // ================= TELHADO: CHAVE DE OURO + MAQUETE (grupo escalável) =================
    // Todo o conjunto do teto vai num GRUPO escalado +25% (pedido do usuário), com o PIVÔ no
    // centro do teto (0,roofY,0): `position.y=roofY·(1-s)` faz só o TAMANHO crescer e a base
    // seguir apoiada em roofY. (O teto é a ÚLTIMA seção da função → reatribuir `bg` é seguro.)
    const roofScene = new THREE.Group();
    roofScene.position.y = roofY * (1 - 1.25);
    roofScene.scale.setScalar(1.25);
    bg.add(roofScene);
    bg = roofScene; // ↓ tudo do teto entra no grupo escalável
    // palco giratório (deck creme + aro dourado) — irmão do palco da concessionária
    const R = Math.min(b.w, b.d) * 0.34; // "área redonda" maior p/ as casinhas maiores caberem
    const baseT = new THREE.Mesh(new THREE.CylinderGeometry(R + 0.4, R + 0.6, 0.4, 32), gold);
    baseT.position.set(0, roofY + 0.2, 0); baseT.castShadow = true; bg.add(baseT);
    const deck = new THREE.Mesh(new THREE.CylinderGeometry(R, R, 0.35, 32), stone);
    deck.position.set(0, roofY + 0.55, 0); deck.receiveShadow = true; bg.add(deck);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(R - 0.1, 0.12, 8, 44), gold);
    ring.rotation.x = Math.PI / 2; ring.position.set(0, roofY + 0.74, 0); bg.add(ring);
    // pedestal de pedra estriado + molduras
    const pedY = roofY + 0.72;
    const ped = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.65, 1.5, 16), stone);
    ped.position.set(0, pedY + 0.75, 0); ped.castShadow = true; bg.add(ped);
    for (const my of [pedY + 0.06, pedY + 1.44]) {
        const mold = new THREE.Mesh(new THREE.CylinderGeometry(1.72, 1.72, 0.18, 20), gold);
        mold.position.set(0, my, 0); bg.add(mold);
    }
    const cushY = pedY + 1.5;
    // almofada de veludo bordô + 4 borlas douradas
    const cushion = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.5, 2.4), velvet);
    cushion.position.set(0, cushY + 0.25, 0); cushion.castShadow = true; bg.add(cushion);
    for (const [sx, sz] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
        const knot = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), gold);
        knot.position.set(sx * 1.1, cushY + 0.32, sz * 1.1); bg.add(knot);
        const tas = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.34, 8), gold);
        tas.position.set(sx * 1.1, cushY + 0.02, sz * 1.1); bg.add(tas);
    }
    const cushTop = cushY + 0.5;

    // ---- A CHAVE dourada (cabeça = casa vazada e ACESA) ----
    const mkGoldKey = () => {
        const g = new THREE.Group();
        const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 3.2, 16), gold);
        shaft.position.y = 2.1; shaft.castShadow = true; g.add(shaft);
        // palhetão (dentes) recortados como uma linha de telhado, na base
        for (const [tw, ty] of [[0.6, 0.95], [0.44, 1.4], [0.3, 1.8]]) {
            const tooth = new THREE.Mesh(new THREE.BoxGeometry(tw, 0.32, 0.42), gold);
            tooth.position.set(0.24 + tw / 2 - 0.02, ty, 0); tooth.castShadow = true; g.add(tooth);
        }
        const tip = new THREE.Mesh(new THREE.SphereGeometry(0.24, 12, 8), gold);
        tip.position.y = 0.55; g.add(tip);
        for (const cy of [3.56, 3.8]) { // colar
            const collar = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.08, 8, 22), gold);
            collar.rotation.x = Math.PI / 2; collar.position.y = cy; g.add(collar);
        }
        const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.2), gold);
        gem.position.set(0, 3.68, 0.34); g.add(gem);
        // cabeça = casa vazada (Shape casa com furo casa)
        const housePath = (Ctor, s, yoff) => {
            const a = s * 0.5, wallH = s * 0.62, peak = s * 1.02;
            const p = new Ctor();
            p.moveTo(-a, yoff); p.lineTo(-a, yoff + wallH); p.lineTo(0, yoff + peak);
            p.lineTo(a, yoff + wallH); p.lineTo(a, yoff); p.closePath();
            return p;
        };
        const headShape = housePath(THREE.Shape, 2.5, 0);
        headShape.holes.push(housePath(THREE.Path, 1.5, 0.5));
        const headGeo = new THREE.ExtrudeGeometry(headShape, { depth: 0.42, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.06, bevelSegments: 1 });
        headGeo.translate(0, 0, -0.21);
        const head = new THREE.Mesh(headGeo, gold);
        head.position.y = 3.95; head.castShadow = true; g.add(head);
        // (o furo da casa fica VAZADO — o usuário removeu o preenchimento amarelo "lar aceso")
        return g;
    };
    // Chave RECLINADA sobre a almofada — a câmera do jogo é AÉREA (~45° de cima) e a chave
    // em pé (90°) some vista de cima. Wrapper centraliza a chave e a deita a ~18° acima da
    // horizontal, com a FACE (casa vazada + lar aceso) voltada p/ CIMA → explícita de cima.
    const keyInner = mkGoldKey();
    keyInner.position.y = -3.4; // centraliza o eixo (extent local ~0.3..6.5) no wrapper
    const key = new THREE.Group();
    key.add(keyInner);
    key.rotation.x = -1.28; // ~18° acima da horizontal (face p/ cima)
    key.rotation.y = 0.5;   // diagonal (dinâmico no 3/4 aéreo)
    const keyYaw = new THREE.Group(); // yaw EXTERNO (world Y) → rotação LIMPA vista de cima
    keyYaw.add(key);
    keyYaw.rotation.y = -Math.PI / 4; // 45° HORÁRIO visto de cima (pedido do usuário)
    keyYaw.position.set(0, cushTop + 1.05, 0);
    bg.add(keyYaw);

    // feixe de luz suave sobre a chave + HALO quente rasteiro em volta do pedestal
    // (uplighting elegante — no lugar de tocos brancos crus)
    const beam = new THREE.Mesh(new THREE.ConeGeometry(2.5, 6.0, 24, 1, true), beamMat);
    beam.position.set(0, cushTop + 5.0, 0); bg.add(beam);
    const halo = new THREE.Mesh(new THREE.TorusGeometry(2.15, 0.09, 8, 44), uplightMat);
    halo.rotation.x = Math.PI / 2; halo.position.set(0, roofY + 0.75, 0); bg.add(halo);

    // MAQUETE DE IMÓVEIS no deck, em anel ao redor da chave → reforça "vende IMÓVEIS"
    // (mini casas + mini prédio, com janelas acesas; lê bem de cima)
    {
        const litWin = new THREE.MeshStandardMaterial({ color: "#ffd98a", emissive: new THREE.Color("#ffcf6a"), emissiveIntensity: 0.9, roughness: 0.5 });
        const terracotta = accentMat("#bf5a3c");
        const creamB = accentMat("#efe4c8");
        const mkMiniHouse = () => {
            const g = new THREE.Group();
            const w = new THREE.Mesh(new THREE.BoxGeometry(1.2, 1.0, 1.2), creamB);
            w.position.y = 0.5; w.castShadow = true; g.add(w);
            const rs = new THREE.Shape();
            rs.moveTo(-0.75, 0); rs.lineTo(0.75, 0); rs.lineTo(0, 0.72); rs.closePath();
            const rg = new THREE.ExtrudeGeometry(rs, { depth: 1.3, bevelEnabled: false });
            rg.translate(0, 0, -0.65);
            const roof = new THREE.Mesh(rg, terracotta); roof.position.y = 1.0; roof.castShadow = true; g.add(roof);
            const win = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.4, 0.06), litWin); win.position.set(0, 0.55, 0.61); g.add(win);
            return g;
        };
        const mkMiniTower = () => {
            const g = new THREE.Group();
            const w = new THREE.Mesh(new THREE.BoxGeometry(1.0, 2.3, 1.0), creamB);
            w.position.y = 1.15; w.castShadow = true; g.add(w);
            for (const wy of [0.7, 1.25, 1.8]) {
                for (const sz of [0.51, -0.51]) {
                    const m = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.3, 0.06), litWin);
                    m.position.set(0, wy, sz); g.add(m);
                }
            }
            const capm = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.16, 1.16), gold); capm.position.y = 2.35; g.add(capm);
            return g;
        };
        const props = [mkMiniHouse(), mkMiniTower(), mkMiniHouse()];
        props.forEach((p, i) => {
            const a = (i / 3) * Math.PI * 2 + 0.7;
            p.scale.setScalar(1.8); // +50% A MAIS que antes (era 1.2) — pedido do usuário
            p.position.set(Math.cos(a) * 4.4, roofY + 0.73, Math.sin(a) * 4.4); // raio maior → cabem sem colidir com a chave
            p.rotation.y = -a + Math.PI / 2;
            bg.add(p);
        });
        // (placas "SE VENDE"/"SE RENTA" REMOVIDAS — ficavam ilegíveis vistas de cima)
    }
}

/** Dupla hélice de DNA (marco do laboratório): 2 fitas de esferas + travessas. */
function makeDNA(height, radius, turns, matA, matB, rungMat, opts = {}) {
    const g = new THREE.Group();
    const n = opts.steps || 30; // menos passos = hélice mais leve (colunas do lab)
    const sr = opts.sphereR || 0.3;
    const seg = opts.lowPoly ? [6, 5] : [8, 6];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        const y = t * height;
        const ang = t * turns * Math.PI * 2;
        const s1 = new THREE.Mesh(new THREE.SphereGeometry(sr, seg[0], seg[1]), matA);
        s1.position.set(Math.cos(ang) * radius, y, Math.sin(ang) * radius);
        g.add(s1);
        const s2 = new THREE.Mesh(new THREE.SphereGeometry(sr, seg[0], seg[1]), matB);
        s2.position.set(Math.cos(ang + Math.PI) * radius, y, Math.sin(ang + Math.PI) * radius);
        g.add(s2);
        if (i % 3 === 0) {
            const rung = new THREE.Mesh(new THREE.BoxGeometry(radius * 2, 0.16, 0.16), rungMat);
            rung.position.set(0, y, 0);
            rung.rotation.y = -ang;
            g.add(rung);
        }
    }
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Átomo estilizado (símbolo de ciência): núcleo + 3 órbitas elípticas + elétrons. */
function makeAtom(nucMat, orbitMat, elecMat) {
    const g = new THREE.Group();
    for (const [nx, ny] of [
        [0, 0],
        [0.2, 0.12],
        [-0.16, 0.14],
        [0.06, -0.2],
        [-0.05, -0.03],
    ]) {
        const nuc = new THREE.Mesh(new THREE.SphereGeometry(0.26, 10, 8), nucMat);
        nuc.position.set(nx, ny, 0.1);
        g.add(nuc);
    }
    for (let k = 0; k < 3; k++) {
        const a = (k * Math.PI) / 3;
        const orbit = new THREE.Mesh(new THREE.TorusGeometry(1.05, 0.06, 8, 40), orbitMat);
        orbit.scale.set(1, 0.4, 1);
        orbit.rotation.z = a;
        g.add(orbit);
        const e = new THREE.Mesh(new THREE.SphereGeometry(0.17, 8, 6), elecMat);
        e.position.set(Math.cos(a) * 1.05, Math.sin(a) * 1.05, 0.08);
        g.add(e);
    }
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Bancada de vidraria p/ a vitrine (RASA): rack de tubos + erlenmeyer + becker. */
function makeLabDisplay(tubeColors, flaskColor, beakerColor) {
    const g = new THREE.Group();
    const steel = new THREE.MeshStandardMaterial({ color: "#cfd6db", roughness: 0.5, metalness: 0.3 });
    const glassMat = new THREE.MeshStandardMaterial({ color: "#e6f5ee", roughness: 0.25 });
    const counterMat = new THREE.MeshStandardMaterial({ color: "#dfe6dc", roughness: 0.6 });
    const liq = (c) =>
        new THREE.MeshStandardMaterial({
            color: c,
            emissive: new THREE.Color(c),
            emissiveIntensity: 0.3,
            roughness: 0.4,
        });
    const counter = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.26, 0.5), counterMat);
    counter.position.set(0, 1.3, 0);
    g.add(counter);
    const apron = new THREE.Mesh(new THREE.BoxGeometry(3.8, 1.3, 0.4), counterMat);
    apron.position.set(0, 0.65, -0.05);
    g.add(apron);
    const rackBack = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.55, 0.1), steel);
    rackBack.position.set(-1.0, 1.85, -0.1);
    g.add(rackBack);
    const rackBase = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.14, 0.32), steel);
    rackBase.position.set(-1.0, 1.5, 0.02);
    g.add(rackBase);
    for (let i = 0; i < 4; i++) {
        const tx = -1.55 + i * 0.37;
        const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.9, 8), glassMat);
        tube.position.set(tx, 1.95, 0.02);
        g.add(tube);
        const tl = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.45, 8), liq(tubeColors[i % tubeColors.length]));
        tl.position.set(tx, 1.72, 0.02);
        g.add(tl);
    }
    const fb = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.46, 0.72, 12), glassMat);
    fb.position.set(0.5, 1.79, 0.05);
    g.add(fb);
    const fl = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.42, 0.32, 12), liq(flaskColor));
    fl.position.set(0.5, 1.62, 0.05);
    g.add(fl);
    const fn = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.42, 8), glassMat);
    fn.position.set(0.5, 2.22, 0.05);
    g.add(fn);
    const bk = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.28, 0.62, 12), glassMat);
    bk.position.set(1.4, 1.75, 0.05);
    g.add(bk);
    const bl = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.32, 12), liq(beakerColor));
    bl.position.set(1.4, 1.59, 0.05);
    g.add(bl);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Arte de laboratório (pôster em canvas) p/ a vitrine: 'flask' ou 'molecule'. */
function makeLabArtTexture(kind, label) {
    const cw = 256;
    const ch = 288;
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    // fundo claro + borda verde
    const bg = ctx.createLinearGradient(0, 0, 0, ch);
    bg.addColorStop(0, "#f6fbf7");
    bg.addColorStop(1, "#e6f5ec");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, cw, ch);
    ctx.strokeStyle = "#1f8f3e";
    ctx.lineWidth = 12;
    ctx.strokeRect(8, 8, cw - 16, ch - 16);

    const cx = cw / 2;
    if (kind === "flask") {
        // ERLENMEYER com líquido verde + bolhas
        const topY = 56;
        const nW = 18;
        const bodyY = 196;
        const bW = 68;
        ctx.beginPath();
        ctx.moveTo(cx - nW, topY);
        ctx.lineTo(cx - nW, topY + 36);
        ctx.lineTo(cx - bW, bodyY);
        ctx.lineTo(cx + bW, bodyY);
        ctx.lineTo(cx + nW, topY + 36);
        ctx.lineTo(cx + nW, topY);
        ctx.closePath();
        ctx.fillStyle = "#dff1ea";
        ctx.fill();
        ctx.lineWidth = 7;
        ctx.strokeStyle = "#1f8f3e";
        ctx.stroke();
        ctx.save(); // líquido recortado no corpo
        ctx.beginPath();
        ctx.moveTo(cx - nW, topY + 36);
        ctx.lineTo(cx - bW, bodyY);
        ctx.lineTo(cx + bW, bodyY);
        ctx.lineTo(cx + nW, topY + 36);
        ctx.closePath();
        ctx.clip();
        ctx.fillStyle = "#2bd49a";
        ctx.fillRect(cx - bW, bodyY - 66, bW * 2, 66);
        ctx.restore();
        ctx.fillStyle = "#1f8f3e"; // boca
        ctx.fillRect(cx - nW - 6, topY - 8, nW * 2 + 12, 12);
        ctx.fillStyle = "rgba(255,255,255,0.75)"; // bolhas
        for (const [bx, by, br] of [
            [cx - 22, bodyY - 34, 8],
            [cx + 8, bodyY - 48, 5],
            [cx + 26, bodyY - 22, 6],
        ]) {
            ctx.beginPath();
            ctx.arc(bx, by, br, 0, Math.PI * 2);
            ctx.fill();
        }
    } else {
        // MOLÉCULA: átomo central + 3 ligados
        const cy = 120;
        const nodes = [
            [cx, cy],
            [cx - 62, cy - 46],
            [cx + 62, cy - 46],
            [cx, cy + 76],
        ];
        ctx.strokeStyle = "#1f8f3e";
        ctx.lineWidth = 9;
        for (let i = 1; i < nodes.length; i++) {
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(nodes[i][0], nodes[i][1]);
            ctx.stroke();
        }
        const cols = ["#2bd49a", "#5b8def", "#f2c84b", "#e0473c"];
        nodes.forEach(([x, y], i) => {
            ctx.beginPath();
            ctx.arc(x, y, i === 0 ? 28 : 21, 0, Math.PI * 2);
            ctx.fillStyle = cols[i];
            ctx.fill();
            ctx.lineWidth = 6;
            ctx.strokeStyle = "#1f8f3e";
            ctx.stroke();
        });
    }
    ctx.fillStyle = "#1c7e38";
    ctx.font = "bold 30px Inter, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(label, cx, ch - 30);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
}

/**
 * Laboratório de exames (Acquisition Town · A3): prédio clínico verde+branco —
 * VITRINES com PÔSTER/arte de laboratório (erlenmeyer + molécula), ÁTOMO na
 * fachada e marquise verde.
 */
function buildLab(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const whiteMat = new THREE.MeshStandardMaterial({ color: "#eef3ea", roughness: 0.7 });
    // luzes BRANCAS puxadas p/ amarelo claro — mesmo tom nas janelas e na vitrine
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#fbf3d8",
        emissive: new THREE.Color("#f5e9bf"),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });
    const labGlow = new THREE.MeshStandardMaterial({
        color: "#fbf3d8",
        emissive: new THREE.Color("#f5e9bf"),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });

    // corpo + base + cornija + friso BRANCO (clínico) + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 1.0, b.d + 0.2), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 0.9, b.d + 0.9), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const friso = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.35, 0.4, b.d + 0.35), whiteMat);
    friso.position.y = b.h - 1.0;
    bg.add(friso);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);

    // TÉRREO frente: 2 VITRINES com BANCADAS de vidraria (lab visível) + entrada
    const vH = 5.0;
    const vY0 = 1.0;
    const vCY = vY0 + vH / 2;
    for (let s = 0; s < 2; s++) {
        const sx = s ? 1 : -1;
        const wx = sx * 4.7;
        // arte de laboratório na FACE FRONTAL do fundo (superfície única → sem z-fighting:
        // antes era um plano de pôster ~0.04 à frente do fundo, que brigava no z de longe)
        const artTex = makeLabArtTexture(s ? "molecule" : "flask", t(s ? "EXAMES" : "ANÁLISES"));
        const artMat = new THREE.MeshStandardMaterial({
            map: artTex,
            emissiveMap: artTex,
            emissive: new THREE.Color("#ffffff"),
            emissiveIntensity: 0.4,
            roughness: 0.75,
        });
        const backing = new THREE.Mesh(
            new THREE.BoxGeometry(4.6, vH, 0.12),
            [labGlow, labGlow, labGlow, labGlow, artMat, labGlow], // +Z = arte
        );
        backing.position.set(wx, vCY, hd + 0.0);
        bg.add(backing);
        for (const [fw, fh, ox, oy] of [
            [5.0, 0.4, 0, vH / 2 + 0.2],
            [5.0, 0.4, 0, -vH / 2 - 0.2],
            [0.4, vH, -2.5, 0],
            [0.4, vH, 2.5, 0],
        ]) {
            const m = new THREE.Mesh(new THREE.BoxGeometry(fw, fh, 0.4), edgeMat);
            m.position.set(wx + ox, vCY + oy, hd + 0.12);
            bg.add(m);
        }
    }
    // entrada central de vidro + moldura + degrau
    const door = new THREE.Mesh(new THREE.BoxGeometry(3.0, vH - 0.3, 0.4), labGlow);
    door.position.set(0, (vH - 0.3) / 2 + vY0, hd + 0.08);
    bg.add(door);
    // moldura da porta = barras em "U" (OCA) — sem caixa sólida atrás do vidro: a face
    // da porta (hd+0.28) e a do quadro (hd+0.27) ficavam a 0.01 → z-fighting de longe.
    // As barras ficam AO LADO do vidro (não se sobrepõem) → some o conflito de z.
    for (const [bw, bh, bx, by] of [
        [0.3, vH + 0.2, -1.65, (vH + 0.2) / 2 + vY0 - 0.15],
        [0.3, vH + 0.2, 1.65, (vH + 0.2) / 2 + vY0 - 0.15],
        [3.0, 0.3, 0, vY0 + vH - 0.1],
    ]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.3), edgeMat);
        m.position.set(bx, by, hd + 0.12);
        m.castShadow = true;
        bg.add(m);
    }
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.14, vH - 0.3, 0.5), edgeMat);
    dmull.position.set(0, (vH - 0.3) / 2 + vY0, hd + 0.2);
    bg.add(dmull);
    const step = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.4, 1.4), edgeMat);
    step.position.set(0, 0.2, hd + 1.0);
    step.castShadow = true;
    bg.add(step);
    // marquise/TOLDO VERDE (era branco) — SEM placa "LABORATÓRIO" (removida a pedido)
    const awnGreen = new THREE.MeshStandardMaterial({ color: COLORS.waGreen, roughness: 0.7 });
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(b.w - 1.0, 0.5, 2.4), awnGreen);
    canopy.position.set(0, vY0 + vH + 0.7, hd + 1.0);
    canopy.castShadow = true;
    bg.add(canopy);

    // ÁTOMO GRANDE na fachada — centrado e dimensionado p/ caber entre o toldo
    // (marquise) e o friso/cornija, ocupando o máximo do andar de cima
    const atomY = (vY0 + vH + 0.95 + (b.h - 1.25)) / 2;
    const atom = makeAtom(
        new THREE.MeshStandardMaterial({ color: "#2bd49a", emissive: new THREE.Color("#2bd49a"), emissiveIntensity: 0.4, roughness: 0.4 }),
        whiteMat,
        new THREE.MeshStandardMaterial({ color: "#5b8def", emissive: new THREE.Color("#5b8def"), emissiveIntensity: 0.4, roughness: 0.4 }),
    );
    atom.scale.setScalar(2.4);
    atom.position.set(0, atomY, hd + 0.3);
    bg.add(atom);

    // JANELAS: frente só nas pontas em cima (centro=átomo); laterais/fundo = grade
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const front = f.nz === 1;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const wrows = front ? [atomY] : [3.3, 7.0, 10.7];
        const us = front ? [-6.8, 6.8] : [-6.5, -2.2, 2.2, 6.5];
        for (const wy of wrows) {
            for (const u of us) {
                const win = makeClassWindow(2.2, 2.6, warmGlass, edgeMat);
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // (DNA do teto REMOVIDO a pedido — identidade vem do átomo + vidraria das vitrines)
}

/** Flor de LÓTUS (símbolo de spa/estética): pétalas em leque + joia dourada. */
function makeLotus(petalMat, gemMat) {
    const g = new THREE.Group();
    const baseY = -0.3;
    for (const rot of [0, 0.55, -0.55, 1.1, -1.1]) {
        const petal = new THREE.Mesh(new THREE.SphereGeometry(0.5, 10, 8), petalMat);
        petal.scale.set(0.4, 1.0, 0.2);
        petal.rotation.z = rot;
        petal.position.set(-0.5 * Math.sin(rot), baseY + 0.5 * Math.cos(rot), 0.05 + Math.abs(rot) * 0.05);
        g.add(petal);
    }
    for (const sx of [-1, 1]) {
        const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 6), petalMat);
        leaf.scale.set(0.5, 0.25, 0.2);
        leaf.position.set(sx * 0.4, baseY - 0.05, 0.05);
        leaf.rotation.z = sx * -0.5;
        g.add(leaf);
    }
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.22), gemMat);
    gem.scale.z = 0.6;
    gem.position.set(0, baseY + 0.4, 0.2);
    g.add(gem);
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/** Brilho de 4 pontas (sparkle ✦) — glamour. */
function makeSparkle(r, mat) {
    const shape = new THREE.Shape();
    const inner = r * 0.22;
    for (let i = 0; i < 8; i++) {
        const rad = i % 2 ? inner : r;
        const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
        if (i === 0) shape.moveTo(Math.cos(a) * rad, Math.sin(a) * rad);
        else shape.lineTo(Math.cos(a) * rad, Math.sin(a) * rad);
    }
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.1, bevelEnabled: false });
    geo.center();
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = true;
    return m;
}

/** Display de cosméticos p/ a vitrine (RASO): prateleira + frascos/potes + espelho. */
function makeCosmeticDisplay(bottleColors, gold) {
    const g = new THREE.Group();
    const counterMat = new THREE.MeshStandardMaterial({ color: "#f0e6ea", roughness: 0.6 });
    const mirrorMat = new THREE.MeshStandardMaterial({ color: "#cfe6ee", roughness: 0.15, metalness: 0.6 });
    const shelf = new THREE.Mesh(new THREE.BoxGeometry(3.7, 0.2, 0.42), counterMat);
    shelf.position.set(0, 1.5, 0);
    g.add(shelf);
    const leg = new THREE.Mesh(new THREE.BoxGeometry(3.5, 1.5, 0.32), counterMat);
    leg.position.set(0, 0.75, -0.05);
    g.add(leg);
    const mirror = new THREE.Mesh(new THREE.CircleGeometry(0.72, 24), mirrorMat);
    mirror.position.set(-1.15, 2.5, -0.06);
    g.add(mirror);
    const mFrame = new THREE.Mesh(new THREE.TorusGeometry(0.75, 0.08, 8, 28), gold);
    mFrame.position.set(-1.15, 2.5, -0.04);
    g.add(mFrame);
    const items = [
        [-1.35, 0],
        [-0.75, 1],
        [-0.1, 2],
        [0.5, 0],
        [1.05, 1],
        [1.55, 2],
    ];
    items.forEach(([ix, type], i) => {
        const col = bottleColors[i % bottleColors.length];
        const bodyMat = new THREE.MeshStandardMaterial({ color: col, roughness: 0.4, emissive: new THREE.Color(col), emissiveIntensity: 0.12 });
        if (type === 0) {
            const bd = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.7, 10), bodyMat);
            bd.position.set(ix, 1.95, 0.06);
            g.add(bd);
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.2, 8), gold);
            cap.position.set(ix, 2.4, 0.06);
            g.add(cap);
        } else if (type === 1) {
            const bd = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.3, 12), bodyMat);
            bd.position.set(ix, 1.75, 0.06);
            g.add(bd);
            const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.1, 12), gold);
            cap.position.set(ix, 1.94, 0.06);
            g.add(cap);
        } else {
            const bd = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.17, 0.5, 10), bodyMat);
            bd.position.set(ix, 1.85, 0.06);
            g.add(bd);
            const pump = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.26, 0.1), gold);
            pump.position.set(ix, 2.22, 0.06);
            g.add(pump);
        }
    });
    g.traverse((o) => {
        if (o.isMesh) o.castShadow = true;
    });
    return g;
}

/**
 * Clínica de estética (Acquisition Town · B1): prédio elegante verde+rosé+dourado —
 * VITRINES com COSMÉTICOS (frascos/potes + espelho), LÓTUS + sparkles na fachada,
 * marquise rosé "ESTÉTICA" e friso dourado.
 */
function buildClinic(bg, b, shared, rand) {
    const dark = shared.darkMat;
    const hw = b.w / 2;
    const hd = b.d / 2;
    const wallMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingWall, roughness: 0.85 });
    const trim = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoof, roughness: 0.8 });
    const edgeMat = new THREE.MeshStandardMaterial({ color: COLORS.buildingRoofEdge, roughness: 0.78 });
    const gold = new THREE.MeshStandardMaterial({
        color: "#e8c45a",
        emissive: new THREE.Color("#caa14a"),
        emissiveIntensity: 0.25,
        roughness: 0.5,
        metalness: 0.4,
    });
    const rose = new THREE.MeshStandardMaterial({ color: "#e58aa8", roughness: 0.6 });
    const warmGlass = new THREE.MeshStandardMaterial({
        color: "#ffce86",
        emissive: new THREE.Color(COLORS.windowOn),
        emissiveIntensity: 0.5,
        roughness: 0.4,
    });

    // corpo + base + cornija + friso DOURADO + laje
    const body = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), wallMat);
    body.position.y = b.h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);
    const base = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.2, 1.0, b.d + 0.2), dark);
    base.position.y = 0.5;
    bg.add(base);
    const cornice = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.9, 0.9, b.d + 0.9), edgeMat);
    cornice.position.y = b.h - 0.3;
    cornice.castShadow = true;
    bg.add(cornice);
    const friso = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.35, 0.32, b.d + 0.35), gold);
    friso.position.y = b.h - 1.0;
    bg.add(friso);
    const roofSlab = new THREE.Mesh(new THREE.BoxGeometry(b.w + 0.3, 0.5, b.d + 0.3), trim);
    roofSlab.position.y = b.h + 0.25;
    bg.add(roofSlab);

    // TÉRREO frente: 2 JANELAS padrão (uma cada lado) + entrada de vidro
    // (vitrines de cosméticos REMOVIDAS a pedido)
    const vH = 5.0;
    const vY0 = 1.0;
    for (const sx of [-1, 1]) {
        const win = makeClassWindow(2.4, 3.2, warmGlass, edgeMat);
        win.position.set(sx * 4.7, 3.4, hd + 0.12);
        bg.add(win);
    }
    const door = new THREE.Mesh(new THREE.BoxGeometry(3.0, vH - 0.3, 0.4), warmGlass);
    door.position.set(0, (vH - 0.3) / 2 + vY0, hd + 0.08);
    bg.add(door);
    // moldura da porta = barras em "U" (OCA) — evita o z-fighting da caixa sólida atrás
    // do vidro (face da porta hd+0.28 vs quadro hd+0.27, a 0.01)
    for (const [bw, bh, bx, by] of [
        [0.3, vH + 0.2, -1.65, (vH + 0.2) / 2 + vY0 - 0.15],
        [0.3, vH + 0.2, 1.65, (vH + 0.2) / 2 + vY0 - 0.15],
        [3.0, 0.3, 0, vY0 + vH - 0.1],
    ]) {
        const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, 0.3), gold);
        m.position.set(bx, by, hd + 0.12);
        m.castShadow = true;
        bg.add(m);
    }
    const dmull = new THREE.Mesh(new THREE.BoxGeometry(0.14, vH - 0.3, 0.5), gold);
    dmull.position.set(0, (vH - 0.3) / 2 + vY0, hd + 0.2);
    bg.add(dmull);
    const step = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.4, 1.4), edgeMat);
    step.position.set(0, 0.2, hd + 1.0);
    step.castShadow = true;
    bg.add(step);

    // MARQUISE rosé + "ESTÉTICA" (texto dourado) + borda dourada
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(b.w - 1.0, 0.5, 2.4), rose);
    canopy.position.set(0, vY0 + vH + 0.7, hd + 1.0);
    canopy.castShadow = true;
    bg.add(canopy);
    const canopyGold = new THREE.Mesh(new THREE.BoxGeometry(b.w - 1.0, 0.18, 0.3), gold);
    canopyGold.position.set(0, vY0 + vH + 0.5, hd + 2.18);
    bg.add(canopyGold);
    // (placa "ESTÉTICA" REMOVIDA a pedido — identidade vem do lótus + cosméticos)

    // LÓTUS + SPARKLES na fachada (andar de cima, centro)
    const emY = (vY0 + vH + 1.4 + (b.h - 1.0)) / 2;
    const lotus = makeLotus(rose, gold);
    lotus.scale.setScalar(1.8);
    lotus.position.set(0, emY, hd + 0.3);
    bg.add(lotus);
    for (const [px, py] of [
        [-2.4, 0.9],
        [2.4, 0.9],
        [-1.9, -1.0],
        [1.9, -1.0],
    ]) {
        const sp = makeSparkle(0.35, gold);
        sp.position.set(px, emY + py, hd + 0.3);
        bg.add(sp);
    }

    // JANELAS: frente só nas pontas em cima (centro=lótus); laterais/fundo = grade
    for (const f of [
        { nx: 0, nz: 1 },
        { nx: 0, nz: -1 },
        { nx: 1, nz: 0 },
        { nx: -1, nz: 0 },
    ]) {
        const front = f.nz === 1;
        const faceDist = f.nz !== 0 ? hd : hw;
        const tx = f.nz;
        const tz = f.nx;
        let ry = 0;
        if (f.nz === -1) ry = Math.PI;
        else if (f.nx === 1) ry = Math.PI / 2;
        else if (f.nx === -1) ry = -Math.PI / 2;
        const wrows = front ? [emY] : [3.3, emY];
        const us = front ? [-6.8, 6.8] : [-6.5, -2.2, 2.2, 6.5];
        for (const wy of wrows) {
            for (const u of us) {
                const win = makeClassWindow(2.2, 2.6, warmGlass, edgeMat); // janela PADRÃO (moldura verde)
                win.rotation.y = ry;
                win.position.set(f.nx * (faceDist + 0.12) + tx * u, wy, f.nz * (faceDist + 0.12) + tz * u);
                bg.add(win);
            }
        }
    }

    // 2 PLACAS DE CHÃO (no lugar dos bancos) — pylon rosé com moldura dourada + lótus
    const signLeaf = new THREE.MeshStandardMaterial({ color: "#fde4ec", roughness: 0.6 });
    const signOffX = Math.min(b.w * 0.34, 7);
    const signZ = hd + 2.6;
    for (const sx of [-1, 1]) {
        const sbase = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.32, 16), edgeMat);
        sbase.position.set(sx * signOffX, 0.16, signZ);
        sbase.castShadow = true;
        bg.add(sbase);
        const spost = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 1.3, 10), gold);
        spost.position.set(sx * signOffX, 0.85, signZ);
        bg.add(spost);
        const sframe = new THREE.Mesh(new THREE.BoxGeometry(1.75, 2.05, 0.14), gold);
        sframe.position.set(sx * signOffX, 2.35, signZ);
        sframe.castShadow = true;
        bg.add(sframe);
        const spanel = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.8, 0.18), rose);
        spanel.position.set(sx * signOffX, 2.35, signZ + 0.06);
        bg.add(spanel);
        const slotus = makeLotus(signLeaf, gold);
        slotus.scale.setScalar(0.62);
        slotus.position.set(sx * signOffX, 2.45, signZ + 0.18);
        bg.add(slotus);
    }
}

/** Casa isolada (corpo + telhado de duas águas + chaminé + porta). */
function makeHouse(shared, rand, w, d, h) {
    const g = new THREE.Group();
    // fachada da frente: janela | porta | janela (porta desenhada na textura)
    addBody(g, { w, d, h }, shared, rand, {
        palette: "amber",
        fascia: false,
        door: true,
        house: true,
    });
    const roofH = Math.min(w, d) * 0.6;
    const roof = makeGableRoof(w, d, roofH, shared);
    roof.position.y = h;
    roof.castShadow = true;
    g.add(roof);
    // Chaminé com capa + dois flues
    const chim = new THREE.Mesh(new THREE.BoxGeometry(1.0, 2.2, 1.0), shared.darkMat);
    chim.position.set(w * 0.28, h + roofH * 0.5, -d * 0.15);
    chim.castShadow = true;
    g.add(chim);
    const cap = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.3, 1.3), shared.roofTrimMat);
    cap.position.set(w * 0.28, h + roofH * 0.5 + 1.25, -d * 0.15);
    g.add(cap);
    for (const fx of [-0.22, 0.22]) {
        const flue = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.5, 8), shared.roofTrimMat);
        flue.position.set(w * 0.28 + fx, h + roofH * 0.5 + 1.55, -d * 0.15);
        g.add(flue);
    }
    // degrau na frente da porta (a porta em si é desenhada na fachada)
    const dd = doorDims(w, h, true);
    const step = new THREE.Mesh(new THREE.BoxGeometry(dd.w + 1.2, 0.4, 0.9), shared.columnMat);
    step.position.set(0, 0.2, d / 2 + 0.45);
    g.add(step);
    return g;
}

/** Rooftop: guarda-corpo (railing) na borda + mesinha com banquinhos + floreiras. */
function addRooftopTerrace(bg, b, shared) {
    const topY = b.h + 1.5; // piso do terraço
    const hw = b.w / 2;
    const hd = b.d / 2;
    const rail = shared.roofEquipMat; // metálico claro
    const dark = shared.darkMat;

    // Guarda-corpo: montantes + corrimão superior nos 4 lados (com vão de acesso atrás)
    const postH = 1.1;
    const railY = topY + postH;
    const inset = 0.5;
    const edges = [
        { x: 0, z: hd - inset, w: b.w - 2 * inset, d: 0, horiz: true, gap: false },
        { x: 0, z: -(hd - inset), w: b.w - 2 * inset, d: 0, horiz: true, gap: true },
        { x: hw - inset, z: 0, w: 0, d: b.d - 2 * inset, horiz: false, gap: false },
        { x: -(hw - inset), z: 0, w: 0, d: b.d - 2 * inset, horiz: false, gap: false },
    ];
    for (const e of edges) {
        const len = e.horiz ? e.w : e.d;
        // corrimão superior
        const barGeo = e.horiz ? new THREE.BoxGeometry(len, 0.12, 0.12) : new THREE.BoxGeometry(0.12, 0.12, len);
        const bar = new THREE.Mesh(barGeo, rail);
        bar.position.set(e.x, railY, e.z);
        bg.add(bar);
        // montantes ao longo da borda
        const n = Math.max(2, Math.round(len / 2.2));
        for (let i = 0; i <= n; i++) {
            const t = -0.5 + i / n; // -0.5..0.5
            if (e.gap && Math.abs(t) < 0.18) continue; // vão de acesso (escada)
            const px = e.horiz ? e.x + t * len : e.x;
            const pz = e.horiz ? e.z : e.z + t * len;
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.1, postH, 0.1), rail);
            post.position.set(px, topY + postH / 2, pz);
            bg.add(post);
        }
    }

    // Mesinha central (tampo + pé)
    const tableTop = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.1, 0.16, 16), shared.benchMat);
    tableTop.position.set(0, topY + 1.0, 0);
    tableTop.castShadow = true;
    bg.add(tableTop);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 1.0, 10), dark);
    leg.position.set(0, topY + 0.5, 0);
    bg.add(leg);

    // Banquinhos ao redor da mesa
    for (const [sx, sz] of [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
    ]) {
        const stool = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.6, 12), shared.benchMat);
        stool.position.set(sx * 2.0, topY + 0.3, sz * 2.0);
        stool.castShadow = true;
        bg.add(stool);
    }

    // Duas floreiras nos cantos do fundo
    for (const sx of [-1, 1]) {
        const planter = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.7, 1.0), dark);
        planter.position.set(sx * (hw - 1.6), topY + 0.35, -(hd - 1.2));
        planter.castShadow = true;
        bg.add(planter);
        const green = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.5, 0.8), shared.bushMat);
        green.position.set(sx * (hw - 1.6), topY + 0.85, -(hd - 1.2));
        bg.add(green);
    }
}

function makeBush(shared, r) {
    const g = new THREE.Group();
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 1.0, 6), shared.trunkMat);
    trunk.position.y = 0.5;
    const canopy = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 1), shared.bushMat);
    canopy.position.y = 1.0 + r * 0.6;
    canopy.castShadow = true;
    g.add(trunk, canopy);
    return g;
}

function makeBenchSimple(shared) {
    const g = new THREE.Group();
    const seat = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.15, 0.8), shared.benchMat);
    seat.position.y = 0.7;
    const back = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.7, 0.15), shared.benchMat);
    back.position.set(0, 1.05, -0.32);
    g.add(seat, back);
    return g;
}

function makePlayground(shared) {
    const g = new THREE.Group();
    const red = new THREE.MeshStandardMaterial({ color: "#d94b45", roughness: 0.7 });
    const yellow = new THREE.MeshStandardMaterial({ color: "#e6b53a", roughness: 0.7 });
    const blue = new THREE.MeshStandardMaterial({ color: "#3f7fd0", roughness: 0.7 });

    // escorregador
    const plat = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.3, 2.4), yellow);
    plat.position.set(0, 2, 0);
    plat.castShadow = true;
    g.add(plat);
    for (const [px, pz] of [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
    ]) {
        const post = new THREE.Mesh(new THREE.BoxGeometry(0.2, 2, 0.2), blue);
        post.position.set(px, 1, pz);
        g.add(post);
    }
    const slide = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.2, 3.2), red);
    slide.position.set(0, 1.2, 2.1);
    slide.rotation.x = 0.5;
    slide.castShadow = true;
    g.add(slide);

    // balanço (A-frame)
    const bar = new THREE.Mesh(new THREE.BoxGeometry(3.4, 0.2, 0.2), blue);
    bar.position.set(-4.5, 2.4, 0);
    g.add(bar);
    for (const sx of [-6.0, -3.0]) {
        const l1 = new THREE.Mesh(new THREE.BoxGeometry(0.18, 2.7, 0.18), blue);
        l1.position.set(sx, 1.3, -0.6);
        l1.rotation.x = -0.2;
        g.add(l1);
        const l2 = new THREE.Mesh(new THREE.BoxGeometry(0.18, 2.7, 0.18), blue);
        l2.position.set(sx, 1.3, 0.6);
        l2.rotation.x = 0.2;
        g.add(l2);
    }
    for (const sx of [-5.2, -3.8]) {
        const seat = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.1, 0.4), red);
        seat.position.set(sx, 1.0, 0);
        g.add(seat);
    }
    return g;
}

/* ----------------------------- peças comuns --------------------------- */

function addBody(bg, dims, shared, rand, o = {}) {
    const w = dims.w;
    const d = dims.d;
    const h = dims.h;
    const palette = o.palette || "amber";
    const wallX = getWallMaterial(d, h, rand, palette);
    const wallBack = getWallMaterial(w, h, rand, palette);
    // só a face da frente (+Z) reserva a área da porta; demais ficam só janelas
    const wallFront = o.door ? getWallMaterial(w, h, rand, palette, { door: true, house: o.house }) : wallBack;
    const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), [wallX, wallX, shared.darkMat, shared.darkMat, wallFront, wallBack]);
    body.position.y = h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    bg.add(body);

    const base = new THREE.Mesh(new THREE.BoxGeometry(w + 0.4, 1.6, d + 0.4), shared.darkMat);
    base.position.y = 0.8;
    base.castShadow = true;
    bg.add(base);

    if (o.fascia !== false) {
        const fascia = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 1.5, d + 0.6), shared.fasciaMat);
        fascia.position.y = h - 0.2;
        fascia.castShadow = true;
        bg.add(fascia);
    }
}

function flatRoof(bg, b, shared, rand) {
    const roofTop = new THREE.MeshStandardMaterial({
        map: makeRoofTexture(b, rand),
        roughness: 0.7,
    });
    const s = shared.roofSideMat;
    const roof = new THREE.Mesh(new THREE.BoxGeometry(b.w + 2, 1.4, b.d + 2), [s, s, roofTop, s, s, s]);
    roof.position.y = b.h + 0.75;
    roof.castShadow = true;
    roof.receiveShadow = true;
    bg.add(roof);
}

function addEntrance(bg, dims, shared, opts = {}) {
    const d = dims.d;
    const { w: doorW, h: doorH } = doorDims(dims.w, dims.h);
    const zFace = d / 2;

    // A porta é desenhada NA fachada (textura). Aqui só o volume 3D da entrada:
    // colunas, marquise e degrau, emoldurando a porta da textura.
    if (opts.simple) {
        const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 1.4, 0.4, 1.0), shared.columnMat);
        step.position.set(0, 0.2, zFace + 0.5);
        bg.add(step);
        return;
    }

    const colH = doorH + 0.6;
    const colGeo = new THREE.BoxGeometry(0.8, colH, 0.8);
    for (const sx of [-1, 1]) {
        const col = new THREE.Mesh(colGeo, shared.columnMat);
        col.position.set(sx * (doorW / 2 + 0.6), colH / 2, zFace + 0.35);
        col.castShadow = true;
        bg.add(col);
    }

    const awningW = doorW + (opts.wide ? 6 : 3);
    const awning = new THREE.Mesh(new THREE.BoxGeometry(awningW, 0.8, opts.wide ? 3.4 : 2.6), opts.awningMat || shared.awningMat);
    awning.position.set(0, doorH + 0.5, zFace + (opts.wide ? 1.3 : 1.0));
    awning.castShadow = true;
    bg.add(awning);

    const step = new THREE.Mesh(new THREE.BoxGeometry(doorW + 2, 0.5, 1.4), shared.columnMat);
    step.position.set(0, 0.25, zFace + 0.7);
    bg.add(step);
}

/**
 * Telhado de duas águas (prisma triangular) com detalhes residenciais:
 * cumeeira, beirais, tabeiras no frontão, óculo (janela de sótão) e uma
 * água-furtada (dormer) com janela acesa. Construído com a cumeeira ao longo
 * de Z e, se a casa for mais larga que profunda, o grupo inteiro gira 90°.
 */
function makeGableRoof(w, d, roofH, shared) {
    const ridgeAlongZ = d >= w;
    const span = (ridgeAlongZ ? w : d) + 1.4; // base do triângulo (+ beiral)
    const length = (ridgeAlongZ ? d : w) + 1.4; // comprimento da cumeeira
    const g = new THREE.Group();

    const shape = new THREE.Shape();
    shape.moveTo(-span / 2, 0);
    shape.lineTo(span / 2, 0);
    shape.lineTo(0, roofH);
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, {
        depth: length,
        bevelEnabled: false,
    });
    geo.translate(0, 0, -length / 2);
    const roof = new THREE.Mesh(geo, shared.gableMat);
    roof.castShadow = true;
    roof.receiveShadow = true;
    g.add(roof);

    addGableDetails(g, span, length, roofH, shared);

    if (!ridgeAlongZ) g.rotateY(Math.PI / 2);
    return g;
}

/** Detalhes do telhado de duas águas (frame: cumeeira ao longo de Z). */
function addGableDetails(g, span, length, roofH, shared) {
    const trim = shared.roofTrimMat;
    const half = span / 2;
    const slopeLen = Math.hypot(half, roofH);

    // Cumeeira (ridge cap) no topo
    const ridge = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.5, length + 0.25), trim);
    ridge.position.set(0, roofH - 0.12, 0);
    ridge.castShadow = true;
    g.add(ridge);

    // Beirais (fáscia) ao longo das duas bordas baixas
    for (const sx of [-1, 1]) {
        const fascia = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.55, length + 0.25), shared.fasciaMat);
        fascia.position.set(sx * (half - 0.05), 0.12, 0);
        fascia.castShadow = true;
        g.add(fascia);
    }

    // Tabeiras (barge boards) nos dois frontões, acompanhando as arestas
    for (const sz of [-1, 1]) {
        for (const sx of [-1, 1]) {
            const board = new THREE.Mesh(new THREE.BoxGeometry(slopeLen + 0.2, 0.32, 0.34), trim);
            board.position.set(sx * half * 0.5, roofH * 0.5, sz * (length / 2 + 0.06));
            board.rotation.z = Math.atan2(roofH, -sx * half);
            board.castShadow = true;
            g.add(board);
        }
    }

    // Óculo (janela acesa) em cada frontão, sob a cumeeira
    for (const sz of [-1, 1]) {
        const win = litWindow(Math.min(span * 0.3, 1.6), roofH * 0.34, shared);
        win.position.set(0, roofH * 0.46, sz * (length / 2 + 0.04));
        if (sz < 0) win.rotation.y = Math.PI;
        g.add(win);
    }
}

/** Quadro + vidro aceso (óculo / janela), voltado para +Z. */
function litWindow(w, h, shared) {
    const g = new THREE.Group();
    const frame = new THREE.Mesh(new THREE.BoxGeometry(w + 0.3, h + 0.3, 0.16), shared.roofTrimMat);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.26), shared.doorMat);
    glass.position.z = 0.06;
    g.add(frame, glass);
    return g;
}

/** Água-furtada: corpinho + janela acesa + telhadinho (pirâmide). */
function makeDormer(shared) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.9, 1.5, 1.5), shared.gableMat);
    body.position.y = 0.75;
    body.castShadow = true;
    g.add(body);
    const win = litWindow(1.0, 0.85, shared);
    win.position.set(0, 0.85, 0.76);
    g.add(win);
    const cap = new THREE.Mesh(new THREE.ConeGeometry(1.55, 0.9, 4), shared.roofTrimMat);
    cap.rotation.y = Math.PI / 4;
    cap.position.y = 1.95;
    cap.castShadow = true;
    g.add(cap);
    return g;
}

/* ---------------- texturas de parede (fachada) ---------------- */

function getWallMaterial(spanUnits, heightUnits, rand, palette = "amber", opts = {}) {
    const hasDoor = !!opts.door;
    const house = !!opts.house;
    let bays = clampInt(Math.round(spanUnits / 6.5), 2, 14);
    let floors = clampInt(Math.round(heightUnits / 7), 1, 6);
    if (house) {
        bays = 3; // janela | porta | janela
        floors = 1;
    }
    // chave inclui dimensão (textura proporcional) e se tem porta/casa
    const key = `${bays}x${floors}x${palette}x${hasDoor ? "d" : "p"}x${house ? "h" : "n"}x${Math.round(spanUnits)}x${Math.round(heightUnits)}`;
    if (wallCache.has(key)) return wallCache.get(key);

    const pal = PALETTES[palette];
    const { map, emissiveMap, roughnessMap } = makeFacadeTextures(spanUnits, heightUnits, bays, floors, rand, pal, hasDoor, house);
    const mat = new THREE.MeshStandardMaterial({
        map,
        emissiveMap,
        roughnessMap,
        emissive: new THREE.Color(pal.glow),
        emissiveIntensity: 0.65,
        roughness: 1,
    });
    wallCache.set(key, mat);
    return mat;
}

/** Dimensões (unidades) da porta na fachada — partilhadas pela textura e pelo
 *  pórtico 3D (addEntrance), para ficarem alinhados. */
function doorDims(spanUnits, heightUnits, house) {
    if (house) {
        return {
            w: Math.min(spanUnits * 0.28, 3.2), // mais estreita que 1 vão (cabe no centro)
            h: Math.min(heightUnits * 0.6, 4.4),
        };
    }
    return {
        w: Math.min(spanUnits * 0.34, 9),
        h: Math.min(heightUnits * 0.42, 6),
    };
}

function rectsOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function makeFacadeTextures(spanUnits, heightUnits, bays, floors, rand, pal, hasDoor, house) {
    // px por unidade — textura PROPORCIONAL à face (não estica). 22 é calibração
    // de 1080p; no zoom da câmera dinâmica em 4K dá 0,57 texel/px e a moldura das
    // janelas vira borrão. O nível Ultra sobe para 33 (ver TEX_TIER). Como o valor
    // é fixo na SESSÃO (decidido no boot), a chave do wallCache não precisa dele.
    const K = TEX_TIER.facadeK;
    const W = Math.max(96, Math.round(spanUnits * K));
    const H = Math.max(96, Math.round(heightUnits * K));

    const color = document.createElement("canvas");
    color.width = W;
    color.height = H;
    const c = color.getContext("2d");
    const emis = document.createElement("canvas");
    emis.width = W;
    emis.height = H;
    const e = emis.getContext("2d");

    // Broad material separation needs little resolution: 1/16 the texels of
    // albedo, linear data, shared through the existing facade cache.
    const rough = document.createElement("canvas");
    rough.width = Math.ceil(W / 4);
    rough.height = Math.ceil(H / 4);
    const rc = rough.getContext("2d");
    rc.scale(rough.width / W, rough.height / H);
    rc.fillStyle = "#eeeeee"; // matte masonry
    rc.fillRect(0, 0, W, H);

    // parede (gradiente vertical)
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, COLORS.buildingWall);
    g.addColorStop(1, COLORS.buildingWallDark);
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);
    // Baked contact shading under the cornice and at the foot of the facade.
    // It adds depth without screen-space AO, lights or another render pass.
    const contact = c.createLinearGradient(0, 0, 0, H);
    contact.addColorStop(0, "rgba(8,25,19,0.32)");
    contact.addColorStop(0.10, "rgba(8,25,19,0)");
    contact.addColorStop(0.78, "rgba(8,25,19,0)");
    contact.addColorStop(1, "rgba(8,25,19,0.24)");
    c.fillStyle = contact;
    c.fillRect(0, 0, W, H);
    e.fillStyle = "#000";
    e.fillRect(0, 0, W, H);

    const cornice = Math.round(0.7 * K);
    const base = Math.round(1.9 * K);
    const top = cornice;
    const bottom = H - base;
    const gridH = bottom - top;
    const floorH = gridH / floors;
    const bayW = W / bays;

    // pilastras entre os vãos
    const pilW = Math.max(4, bayW * 0.075);
    for (let i = 0; i <= bays; i++) {
        const px = i * bayW;
        c.fillStyle = "rgba(255,255,255,0.06)";
        c.fillRect(px - pilW / 2, top, pilW, gridH);
        c.fillStyle = "rgba(0,0,0,0.20)";
        c.fillRect(px + pilW / 2 - 2, top, 2, gridH);
    }

    // cornija (topo)
    c.fillStyle = "rgba(255,255,255,0.10)";
    c.fillRect(0, 0, W, cornice);
    c.fillStyle = "rgba(0,0,0,0.20)";
    c.fillRect(0, cornice - 2, W, 2);

    // base / rodapé
    c.fillStyle = "rgba(0,0,0,0.34)";
    c.fillRect(0, bottom, W, base);
    c.fillStyle = "rgba(255,255,255,0.05)";
    c.fillRect(0, bottom, W, 3);

    // Porta (só na frente): reserva a área central do térreo
    let doorRect = null;
    let guard = null;
    if (hasDoor) {
        const dd = doorDims(spanUnits, heightUnits, house);
        const dW = dd.w * K;
        const dH = dd.h * K;
        doorRect = { x: (W - dW) / 2, y: H - dH, w: dW, h: dH }; // até o chão
        const m = 0.4 * K;
        guard = { x: doorRect.x - m, y: doorRect.y - m, w: doorRect.w + 2 * m, h: doorRect.h + 2 * m };
    }

    // Janelas — centradas no vão, pulando as que invadiriam a porta.
    // BANCO: TODAS as janelas acesas (luzes acesas) — demais prédios sorteiam.
    const allLit = pal === PALETTES.bank;
    const mX = bayW * 0.2;
    const mY = floorH * 0.18;
    for (let f = 0; f < floors; f++) {
        for (let b = 0; b < bays; b++) {
            const wx = b * bayW + mX;
            const wy = top + f * floorH + mY;
            const ww = bayW - 2 * mX;
            const wh = floorH - 2 * mY;
            if (guard && rectsOverlap(wx, wy, ww, wh, guard.x, guard.y, guard.w, guard.h)) {
                continue;
            }
            const lit = rand() > 0.42 || allLit;
            drawWindow(c, e, wx, wy, ww, wh, lit, rand, pal);
            rc.fillStyle = "#606060"; // satin glass, never a perfect mirror
            rc.fillRect(wx, wy, ww, wh);
            drawMullions(rc, wx, wy, ww, wh, wh > ww * 1.15, "#bbbbbb", Math.max(2, Math.min(ww, wh) * 0.05));
        }
    }

    if (doorRect) {
        const draw = house ? drawHouseDoor : drawDoor;
        draw(c, e, doorRect.x, doorRect.y, doorRect.w, doorRect.h, pal);
    }

    const map = new THREE.CanvasTexture(color);
    map.colorSpace = THREE.SRGBColorSpace;
    map.anisotropy = 8;
    const emissiveMap = new THREE.CanvasTexture(emis);
    emissiveMap.anisotropy = 8;
    const roughnessMap = new THREE.CanvasTexture(rough);
    roughnessMap.anisotropy = 4;
    return { map, emissiveMap, roughnessMap };
}

function drawWindow(c, e, x, y, w, h, lit, rand, pal) {
    const r = Math.min(w, h) * 0.09;
    const tall = h > w * 1.15; // janela alta ganha travessa horizontal (4 vidros)

    // moldura (sombra externa + moldura)
    c.fillStyle = pal.frameLo;
    roundRect(c, x - 2.5, y - 2.5, w + 5, h + 6, r + 2.5);
    c.fill();
    c.fillStyle = pal.frame;
    roundRect(c, x - 1.5, y - 1.5, w + 3, h + 3, r + 1.5);
    c.fill();

    // vidro com gradiente (céu refletido)
    const gg = c.createLinearGradient(x, y, x, y + h);
    gg.addColorStop(0, lit ? pal.litTop : pal.glassTop);
    gg.addColorStop(1, lit ? pal.on : pal.glassBot);
    c.fillStyle = gg;
    roundRect(c, x, y, w, h, r);
    c.fill();

    // reflexo diagonal sutil
    const rg = c.createLinearGradient(x, y, x + w * 0.7, y + h * 0.7);
    rg.addColorStop(0, "rgba(255,255,255,0.16)");
    rg.addColorStop(0.45, "rgba(255,255,255,0)");
    c.fillStyle = rg;
    roundRect(c, x, y, w, h, r);
    c.fill();

    // montantes (2 ou 4 vidros)
    const lw = Math.max(2, Math.min(w, h) * 0.05);
    drawMullions(c, x, y, w, h, tall, pal.frame, lw);

    // peitoril
    c.fillStyle = pal.sill;
    roundRect(c, x - 2.5, y + h + 1, w + 5, Math.max(3, h * 0.06), 2);
    c.fill();

    // janela acesa → brilho no mapa emissivo (recorta os montantes)
    if (lit) {
        e.fillStyle = rand() > 0.5 ? pal.glowA : pal.glowB;
        roundRect(e, x, y, w, h, r);
        e.fill();
        drawMullions(e, x, y, w, h, tall, "#000", lw);
    }
}

/** Travessas (montantes) da janela: 1 vertical + 1 horizontal se for alta. */
function drawMullions(ctx, x, y, w, h, tall, color, lineW) {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineW;
    ctx.beginPath();
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w / 2, y + h);
    if (tall) {
        ctx.moveTo(x, y + h / 2);
        ctx.lineTo(x + w, y + h / 2);
    }
    ctx.stroke();
}

/** Porta de entrada desenhada na fachada: pórtico + bandeira (transom) + porta
 *  dupla de vidro com puxadores, tudo iluminado (entrada de lobby). */
function drawDoor(c, e, x, y, w, h, pal) {
    const r = Math.min(w, h) * 0.05;

    // pórtico / moldura
    c.fillStyle = pal.frameLo;
    roundRect(c, x - 4, y - 5, w + 8, h + 5, r + 3);
    c.fill();
    c.fillStyle = pal.frame;
    roundRect(c, x - 1.5, y - 2, w + 3, h + 2, r + 2);
    c.fill();

    // bandeira (transom) iluminada
    const tH = h * 0.18;
    const tg = c.createLinearGradient(x, y, x, y + tH);
    tg.addColorStop(0, pal.litTop);
    tg.addColorStop(1, pal.on);
    c.fillStyle = tg;
    roundRect(c, x, y, w, tH, r);
    c.fill();
    c.strokeStyle = pal.frame;
    c.lineWidth = Math.max(2, w * 0.025);
    for (let i = 1; i < 3; i++) {
        const lx = x + (w * i) / 3;
        c.beginPath();
        c.moveTo(lx, y);
        c.lineTo(lx, y + tH);
        c.stroke();
    }

    // folhas de vidro (porta dupla)
    const ly = y + tH + 4;
    const lh = y + h - ly;
    const gap = Math.max(3, w * 0.02);
    const half = (w - gap) / 2;
    for (let i = 0; i < 2; i++) {
        const lx = i === 0 ? x : x + half + gap;
        const lg = c.createLinearGradient(lx, ly, lx, ly + lh);
        lg.addColorStop(0, pal.litTop);
        lg.addColorStop(1, pal.on);
        c.fillStyle = lg;
        roundRect(c, lx, ly, half, lh, r);
        c.fill();
        // puxador vertical (junto à fenda central)
        c.fillStyle = pal.frame;
        const hx = i === 0 ? lx + half - 6 : lx + 3;
        roundRect(c, hx, ly + lh * 0.42, 4, lh * 0.18, 2);
        c.fill();
    }

    // emissivo: entrada acesa (bandeira + folhas), com a fenda central escura
    e.fillStyle = pal.glowA;
    roundRect(e, x, y, w, tH, r);
    e.fill();
    e.fillStyle = pal.glowB;
    roundRect(e, x, ly, w, lh, r);
    e.fill();
    e.strokeStyle = "#000";
    e.lineWidth = Math.max(2, w * 0.03);
    e.beginPath();
    e.moveTo(x + w / 2, ly);
    e.lineTo(x + w / 2, ly + lh);
    e.stroke();
}

/** Porta residencial (casa): folha creme quente que brilha, com bandeira no
 *  topo, dois painéis e maçaneta — centrada entre duas janelas. */
function drawHouseDoor(c, e, x, y, w, h, pal) {
    const r = Math.min(w, h) * 0.08;

    // moldura
    c.fillStyle = pal.frameLo;
    roundRect(c, x - 3, y - 4, w + 6, h + 4, r + 2);
    c.fill();
    c.fillStyle = pal.frame;
    roundRect(c, x - 1.5, y - 2, w + 3, h + 2, r + 1.5);
    c.fill();

    // folha (creme quente)
    const lg = c.createLinearGradient(x, y, x, y + h);
    lg.addColorStop(0, "#ffe0a0");
    lg.addColorStop(1, "#e6b765");
    c.fillStyle = lg;
    roundRect(c, x, y, w, h, r);
    c.fill();

    // bandeira (janelinha) no topo, com cruz
    const tH = h * 0.2;
    c.strokeStyle = pal.frame;
    c.lineWidth = Math.max(2, w * 0.04);
    c.beginPath();
    c.moveTo(x, y + tH);
    c.lineTo(x + w, y + tH);
    c.moveTo(x + w / 2, y);
    c.lineTo(x + w / 2, y + tH);
    c.stroke();

    // dois painéis na parte de baixo
    c.lineWidth = Math.max(2, w * 0.035);
    const pX = x + w * 0.18;
    const pW = w * 0.64;
    const pTop = y + tH + h * 0.08;
    const pH = h * 0.3;
    c.strokeRect(pX, pTop, pW, pH);
    c.strokeRect(pX, pTop + pH + h * 0.06, pW, pH);

    // maçaneta
    c.fillStyle = pal.frameLo;
    c.beginPath();
    c.arc(x + w * 0.8, y + h * 0.56, Math.max(2.2, w * 0.05), 0, Math.PI * 2);
    c.fill();

    // emissivo: a folha brilha suave (entrada aconchegante), juntas escuras
    e.fillStyle = "#ffce7a";
    roundRect(e, x, y, w, h, r);
    e.fill();
    e.strokeStyle = "#000";
    e.lineWidth = Math.max(2, w * 0.04);
    e.beginPath();
    e.moveTo(x, y + tH);
    e.lineTo(x + w, y + tH);
    e.stroke();
}

function clampInt(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}

/* ---------------- textura do telhado (laje + ícone) ---------------- */

function makeRoofTexture(b, rand) {
    const aspect = b.w / b.d;
    const base = 512;
    const cw = aspect >= 1 ? base : Math.round(base * aspect);
    const ch = aspect >= 1 ? Math.round(base / aspect) : base;

    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");

    // membrana do telhado (sem logo/rótulo)
    const g = ctx.createLinearGradient(0, 0, cw, ch);
    g.addColorStop(0, "#79b38a");
    g.addColorStop(1, "#5c946e");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cw, ch);

    // bordas (parapeito desenhado)
    ctx.strokeStyle = "rgba(255,255,255,0.14)";
    ctx.lineWidth = 8;
    ctx.strokeRect(12, 12, cw - 24, ch - 24);
    ctx.strokeStyle = "rgba(0,0,0,0.10)";
    ctx.lineWidth = 3;
    ctx.strokeRect(22, 22, cw - 44, ch - 44);

    // juntas/painéis da membrana
    ctx.strokeStyle = "rgba(0,0,0,0.07)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    const step = 64;
    for (let x = step; x < cw - 24; x += step) {
        ctx.moveTo(x, 24);
        ctx.lineTo(x, ch - 24);
    }
    for (let y = step; y < ch - 24; y += step) {
        ctx.moveTo(24, y);
        ctx.lineTo(cw - 24, y);
    }
    ctx.stroke();

    // granulado leve (varia por edifício via rand)
    const r = rand || Math.random;
    for (let i = 0; i < 90; i++) {
        ctx.fillStyle = r() > 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)";
        const s = 4 + r() * 9;
        ctx.fillRect(r() * cw, r() * ch, s, s);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/* ---------------- texturas auxiliares ---------------- */

/**
 * Textura de placas fotovoltaicas: uma matriz de módulos (painéis), cada um
 * com grade fina de células azul-escuras, moldura metálica e um reflexo
 * diagonal — para se parecer com placas solares de verdade.
 */
function makeSolarTexture() {
    const size = 512;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");

    // estrutura/moldura metálica (entre os módulos)
    ctx.fillStyle = "#10141c";
    ctx.fillRect(0, 0, size, size);

    const cols = 4;
    const rows = 4;
    const gap = 7;
    const pw = (size - gap * (cols + 1)) / cols;
    const ph = (size - gap * (rows + 1)) / rows;

    for (let pc = 0; pc < cols; pc++) {
        for (let pr = 0; pr < rows; pr++) {
            const x = gap + pc * (pw + gap);
            const y = gap + pr * (ph + gap);

            // vidro azul com gradiente (reflexo)
            const g = ctx.createLinearGradient(x, y, x + pw, y + ph);
            g.addColorStop(0, "#1a4488");
            g.addColorStop(0.45, "#0e2c5e");
            g.addColorStop(0.55, "#10306a");
            g.addColorStop(1, "#1b4a90");
            ctx.fillStyle = g;
            ctx.fillRect(x, y, pw, ph);

            // células (grade fina)
            const cc = 6;
            const cr = 6;
            ctx.strokeStyle = "rgba(150,190,240,0.22)";
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            for (let i = 1; i < cc; i++) {
                const lx = x + (pw * i) / cc;
                ctx.moveTo(lx, y);
                ctx.lineTo(lx, y + ph);
            }
            for (let j = 1; j < cr; j++) {
                const ly = y + (ph * j) / cr;
                ctx.moveTo(x, ly);
                ctx.lineTo(x + pw, ly);
            }
            ctx.stroke();

            // brilho/reflexo diagonal
            const sh = ctx.createLinearGradient(x, y, x + pw * 0.7, y + ph * 0.7);
            sh.addColorStop(0, "rgba(255,255,255,0.12)");
            sh.addColorStop(0.3, "rgba(255,255,255,0)");
            ctx.fillStyle = sh;
            ctx.fillRect(x, y, pw, ph);

            // moldura do módulo
            ctx.strokeStyle = "rgba(190,210,240,0.45)";
            ctx.lineWidth = 2;
            ctx.strokeRect(x + 1, y + 1, pw - 2, ph - 2);
        }
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

function makeStripeTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    const stripe = 32;
    for (let i = 0; i * stripe < canvas.width; i++) {
        ctx.fillStyle = i % 2 ? "#1f8a3a" : "#f1f7f0";
        ctx.fillRect(i * stripe, 0, stripe, canvas.height);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/** Mostrador de relógio analógico (torre da escola) — apontando ~10:10. */
function makeClockTexture(hours = 10.1, minutes = 4) {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f4ead2";
    ctx.beginPath();
    ctx.arc(64, 64, 60, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.strokeStyle = "#243018";
    ctx.stroke();
    for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const r2 = i % 3 === 0 ? 40 : 47;
        ctx.lineWidth = i % 3 === 0 ? 5 : 3;
        ctx.beginPath();
        ctx.moveTo(64 + Math.cos(a) * 53, 64 + Math.sin(a) * 53);
        ctx.lineTo(64 + Math.cos(a) * r2, 64 + Math.sin(a) * r2);
        ctx.stroke();
    }
    // ponteiros parametrizados (fusos horários da agência; default ≈ 10:04, o
    // visual original do relógio da escola)
    const ha = ((hours % 12) / 12 + minutes / 720) * Math.PI * 2 - Math.PI / 2;
    const ma = (minutes / 60) * Math.PI * 2 - Math.PI / 2;
    ctx.lineCap = "round";
    ctx.lineWidth = 6;
    ctx.beginPath();
    ctx.moveTo(64, 64);
    ctx.lineTo(64 + Math.cos(ha) * 24, 64 + Math.sin(ha) * 24);
    ctx.stroke();
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(64, 64);
    ctx.lineTo(64 + Math.cos(ma) * 33, 64 + Math.sin(ma) * 33);
    ctx.stroke();
    ctx.fillStyle = "#243018";
    ctx.beginPath();
    ctx.arc(64, 64, 4, 0, Math.PI * 2);
    ctx.fill();
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

/** Placa retangular com texto (ex.: "ESCOLA") — cores temáticas por prédio. */
/** Textura QUADRADA de 1 letra (blade signs verticais) — a makeSignTexture é 4.5:1
 *  e espremia a letra em placas quadradas. */
function makeLetterTexture(letter, opts = {}) {
    const bg = opts.bg || "#14331f";
    const fg = opts.fg || "#ffd98a";
    const border = opts.border || "#1f8a3a";
    const canvas = document.createElement("canvas");
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = bg;
    roundRect(ctx, 4, 4, 120, 120, 16);
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = border;
    ctx.stroke();
    ctx.fillStyle = fg;
    ctx.font = "800 84px Inter, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(letter, 64, 70);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

function makeSignTexture(text, opts = {}) {
    const bg = opts.bg || "#14331f";
    const fg = opts.fg || "#ffd98a";
    const border = opts.border || "#1f8a3a";
    const canvas = document.createElement("canvas");
    canvas.width = 450;
    canvas.height = 100;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = bg;
    roundRect(ctx, 4, 4, 442, 92, 14);
    ctx.fill();
    ctx.lineWidth = 5;
    ctx.strokeStyle = border;
    ctx.stroke();
    ctx.fillStyle = fg;
    ctx.font = `800 ${opts.fontPx || (text.length > 7 ? 50 : 58)}px Inter, Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, 225, 56);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

/**
 * PAINEL DE LED de TICKER da bolsa (cobre os 2 últimos andares, faixa contínua nas 4 faces):
 * fundo escuro estilo PLACA (mesma linguagem das placas de nome dos edifícios) + borda, com
 * TICKERS FICTÍCIOS (símbolos inventados — NADA de ações reais): símbolo/preço em AMARELO e a
 * variação de alta/baixa em VERDE/VERMELHO. Painel de 3 FILEIRAS (~3:1) com fonte GRANDE p/ legibilidade.
 */
function makeExchangeTickerTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 2048;
    canvas.height = 680;
    const ctx = canvas.getContext("2d");
    // painel escuro no canvas INTEIRO (sem borda vertical → rola sem emenda; o bezel de aço emoldura)
    ctx.fillStyle = "#0a1526";
    ctx.fillRect(0, 0, 2048, 680);
    // molduras horizontais topo/base (full-width, contínuas sob a rolagem)
    ctx.fillStyle = "#1f8f3e";
    ctx.fillRect(0, 0, 2048, 10); ctx.fillRect(0, 670, 2048, 10);
    // linha divisória sutil entre as 2 fileiras (full-width)
    ctx.strokeStyle = "rgba(90,150,110,0.22)";
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(0, 340); ctx.lineTo(2048, 340); ctx.stroke();
    // TICKERS FICTÍCIOS: [símbolo, preço, variação%] (símbolos inventados, não são ações reais)
    const tickers = [
        ["ZYX", "142.30", 1.8], ["QBR", "88.15", -0.7],
        ["VLX", "310.4", 2.4], ["MVX", "54.90", -1.2],
    ];
    ctx.textBaseline = "middle";
    const rowsY = [195, 485]; // 2 fileiras (era 3 — 1 fileira removida a pedido)
    const slotW = 2048 / 2;
    tickers.forEach(([sym, , chg], i) => {
        const y = rowsY[Math.floor(i / 2)];
        const up = chg >= 0;
        let x = (i % 2) * slotW + 52;
        ctx.textAlign = "left";
        ctx.font = "800 150px Inter, Arial, sans-serif";
        ctx.fillStyle = "#ffd23f"; // AMARELO — símbolo (ticker)
        ctx.fillText(sym, x, y);
        x += ctx.measureText(sym).width + 32;
        ctx.font = "800 132px Inter, Arial, sans-serif";
        ctx.fillStyle = up ? "#37d07a" : "#e0503f"; // VERDE alta / VERMELHO baixa
        ctx.fillText((up ? "▲ +" : "▼ -") + Math.abs(chg).toFixed(1) + "%", x, y);
    });
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    return tex;
}

/**
 * PAINEL DE LED da bolsa (herói do teto): tela de trading com GRADE + CANDLESTICKS de alta
 * (verde/vermelho) + LINHA de tendência + cabeçalho "BOLSA ▲ +%". Emissiva num painel plano.
 */
function makeStockPanelTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = 576;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#081018";
    ctx.fillRect(0, 0, 1024, 576);
    // grade
    ctx.strokeStyle = "rgba(90,120,150,0.16)";
    ctx.lineWidth = 2;
    for (let gx = 44; gx <= 990; gx += 118) { ctx.beginPath(); ctx.moveTo(gx, 70); ctx.lineTo(gx, 540); ctx.stroke(); }
    for (let gy = 96; gy <= 540; gy += 74) { ctx.beginPath(); ctx.moveTo(44, gy); ctx.lineTo(990, gy); ctx.stroke(); }
    // cabeçalho
    ctx.fillStyle = "#eaf2ff";
    ctx.font = "800 58px Inter, Arial, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText("BOLSA", 46, 40);
    ctx.fillStyle = "#3ad07a";
    ctx.font = "800 48px Inter, Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText("▲ +2.41%", 988, 40);
    // candlesticks (tendência de ALTA) + linha
    const n = 14, x0 = 60, x1 = 980, span = x1 - x0;
    const wig = [0, -6, 8, -10, 4, -2, 10, -14, 6, -12, 2, -8, 12, -16];
    let price = 480;
    const pts = [];
    for (let i = 0; i < n; i++) {
        const cx = x0 + (i + 0.5) * span / n;
        const o = price;
        const c = Math.max(150, price - 15 + wig[i]);
        const up = c <= o;
        const col = up ? "#3ad07a" : "#e0503f";
        ctx.strokeStyle = col; ctx.fillStyle = col; ctx.lineWidth = 3;
        const top = Math.min(o, c), bot = Math.max(o, c);
        ctx.beginPath(); ctx.moveTo(cx, top - 16); ctx.lineTo(cx, bot + 16); ctx.stroke();
        ctx.fillRect(cx - 15, top, 30, Math.max(6, bot - top));
        pts.push([cx, c]); price = c;
    }
    ctx.strokeStyle = "#8affc0";
    ctx.lineWidth = 4;
    ctx.lineJoin = "round";
    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
    ctx.stroke();
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}
